// v2.1 - fix monthlySchedule localStorage + ignoreNext colacion
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, onValue, get } from 'firebase/database';

import MonthlyCalendar from './MonthlyCalendar';
import CameraModal from './CameraModal';
import { MapPin, Clock, Users, TrendingUp, Download, LogOut, AlertCircle, CheckCircle, Calendar, FileText, Settings, Edit2, Trash2, Plus, Save, X } from 'lucide-react';


// Configuración de Firebase
const firebaseConfig = {
  apiKey: "AIzaSyxMuXDqaT7B2MrajXxbwSBegE8Z68QSLSA",
  authDomain: "control--asistencia.firebaseapp.com",
  databaseURL: "https://control--asistencia-default-rtdb.firebaseio.com",
  projectId: "control--asistencia",
  storageBucket: "control--asistencia.firebasestorage.app",
  messagingSenderId: "556444874225",
  appId: "1:556444874225:web:e4285d399473e8840f8050",
  measurementId: "G-SXN62PFP9Y"
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// FIX ZONA HORARIA: toISOString() usa UTC, causando fecha incorrecta en Chile (UTC-3/UTC-4)
// Función global de parseo de hora española → minutos desde medianoche
// Ejemplos: "10:04 a. m." → 604, "08:00 p. m." → 1200, "12:00 p. m." → 720
// Función global para sanitizar datos antes de enviar a Firebase
// Firebase rechaza valores undefined — los convierte a null
const sanitizeForFirebase = (data) => JSON.parse(JSON.stringify(data, (k,v) => v === undefined ? null : v));

const parseTimeToMin = (str) => {
  if (!str) return null;
  const t = str.replace(/[^0-9:]/g,'').trim();
  const parts = t.split(':').map(Number);
  const h = parts[0], m = parts[1];
  if (isNaN(h) || isNaN(m)) return null;
  const isPM = /p[\s.]*m/i.test(str) && h !== 12;
  const isMidnight = h === 12 && /a[\s.]*m/i.test(str);
  const hour = isMidnight ? 0 : isPM ? h + 12 : h;
  return hour * 60 + m;
};

const getLocalDateStr = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// App Context
const AppContext = React.createContext({});

const AbsenceManager = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  if (!selectedEmployeeForAbsences) return null;
  
  const employee = employees.find(e => e.id === selectedEmployeeForAbsences);
  if (!employee) return null;
  
  const employeeAbsences = absences.filter(a => a.employeeId === employee.id);
  
  const vacationDays = employeeAbsences.filter(a => a.type === 'vacation').reduce((sum, a) => sum + a.days, 0);
  const medicalDays = employeeAbsences.filter(a => a.type === 'medical').reduce((sum, a) => sum + a.days, 0);
  const absenceDays = employeeAbsences.filter(a => a.type === 'absence').reduce((sum, a) => sum + a.days, 0);
  
  return (
    <div className="modal-overlay" onClick={() => setShowAbsenceManager(false)}>
      <div className="absence-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 Gestión de Ausencias: {employee.name}</h2>
          <button className="modal-close" onClick={() => setShowAbsenceManager(false)}>
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="absence-summary">
            <div className="summary-card">
              <div className="summary-icon">🏖️</div>
              <div className="summary-value">{vacationDays}/{employee.vacationDays || 15}</div>
              <div className="summary-label">Días de Vacaciones</div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">🏥</div>
              <div className="summary-value">{medicalDays}</div>
              <div className="summary-label">Días con Licencia</div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">❌</div>
              <div className="summary-value">{absenceDays}</div>
              <div className="summary-label">Faltas Injustificadas</div>
            </div>
          </div>
          
          <div className="absence-form">
            <h3>➕ Registrar Nueva Ausencia</h3>
            <div className="form-row">
              <label>Tipo:</label>
              <select value={absenceForm.type} onChange={(e) => setAbsenceForm({...absenceForm, type: e.target.value})}>
                <option value="vacation">🏖️ Vacaciones</option>
                <option value="medical">🏥 Licencia Médica</option>
                <option value="absence">❌ Falta Injustificada</option>
              </select>
            </div>
            <div className="form-row">
              <label>Fecha Inicio:</label>
              <input type="date" value={absenceForm.startDate} onChange={(e) => setAbsenceForm({...absenceForm, startDate: e.target.value})} />
            </div>
            <div className="form-row">
              <label>Fecha Fin:</label>
              <input type="date" value={absenceForm.endDate} onChange={(e) => setAbsenceForm({...absenceForm, endDate: e.target.value})} />
              <small>Dejar vacío si es solo 1 día</small>
            </div>
            <div className="form-row">
              <label>Notas:</label>
              <textarea value={absenceForm.notes} onChange={(e) => setAbsenceForm({...absenceForm, notes: e.target.value})} placeholder="Observaciones (opcional)" rows="2" />
            </div>
            <button className="btn-register" onClick={handleAddAbsence}>✅ Registrar</button>
          </div>
          
          <div className="absence-history">
            <h3>📋 Historial de Ausencias</h3>
            {employeeAbsences.length === 0 ? (
              <p className="no-data">No hay registros de ausencias</p>
            ) : (
              <table className="absence-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Desde</th>
                    <th>Hasta</th>
                    <th>Días</th>
                    <th>Notas</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeAbsences.map(absence => (
                    <tr key={absence.id}>
                      <td>
                        {absence.type === 'vacation' && '🏖️ Vacaciones'}
                        {absence.type === 'medical' && '🏥 Licencia'}
                        {absence.type === 'absence' && '❌ Falta'}
                      </td>
                      <td>{absence.startDate}</td>
                      <td>{absence.endDate}</td>
                      <td>{absence.days}</td>
                      <td>{absence.notes || '-'}</td>
                      <td>
                        <button className="btn-delete-small" onClick={() => handleDeleteAbsence(absence.id)}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const ConfigModal = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const tempConfig = tempConfigState || config;
  const setTempConfig = (v) => setTempConfigState(typeof v === 'function' ? v(tempConfigState || config) : v);

  return (
    <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Configuración del Sistema</h2>
          <button className="modal-close" onClick={() => setShowConfigModal(false)}>
            <X size={24} />
          </button>
        </div>

        {/* Pestañas de navegación */}
        <div className="config-tabs">
          <button
            className={`config-tab ${configActiveTab === 'location' ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setConfigActiveTab('location'); }}
          >
            📍 Ubicación
          </button>
          <button
            className={`config-tab ${configActiveTab === 'schedules' ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setConfigActiveTab('schedules'); }}
          >
            ⏰ Horarios
          </button>
          <button
            className={`config-tab ${configActiveTab === 'tolerances' ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setConfigActiveTab('tolerances'); }}
          >
            ⏱️ Tolerancias
          </button>
          <button
            className={`config-tab ${configActiveTab === 'alerts' ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setConfigActiveTab('alerts'); }}
          >
            🔔 Alertas
          </button>
          <button
            className={`config-tab ${configActiveTab === 'logo' ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setConfigActiveTab('logo'); }}
          >
            🏢 Logo
          </button>
        </div>

        {/* Contenido de las pestañas */}
        <div className="tab-content" key={configActiveTab}>
          
          {/* PESTAÑA: Ubicación */}
          {configActiveTab === 'location' && (
            <div className="config-section" key="location-content">
              <h3>📍 Ubicación de Trabajo</h3>
          <div className="form-group">
            <label>Latitud:</label>
            <input
              type="number"
              step="0.000001"
              value={tempConfig.workLocation.latitude}
              onChange={(e) => setTempConfig({
                ...tempConfig,
                workLocation: { ...tempConfig.workLocation, latitude: parseFloat(e.target.value) }
              })}
            />
          </div>
          <div className="form-group">
            <label>Longitud:</label>
            <input
              type="number"
              step="0.000001"
              value={tempConfig.workLocation.longitude}
              onChange={(e) => setTempConfig({
                ...tempConfig,
                workLocation: { ...tempConfig.workLocation, longitude: parseFloat(e.target.value) }
              })}
            />
          </div>
          <div className="form-group">
            <label>Radio permitido (metros):</label>
            <input
              type="number"
              value={tempConfig.workLocation.radius}
              onChange={(e) => setTempConfig({
                ...tempConfig,
                workLocation: { ...tempConfig.workLocation, radius: parseInt(e.target.value) }
              })}
            />
          </div>
          {currentLocation && (
            <div className="help-text">
              📍 Tu ubicación actual: {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
              <button 
                className="btn-small"
                onClick={() => setTempConfig({
                  ...tempConfig,
                  workLocation: {
                    ...tempConfig.workLocation,
                    latitude: currentLocation.latitude,
                    longitude: currentLocation.longitude
                  }
                })}
              >
                Usar mi ubicación actual
              </button>
            </div>
          )}
            </div>
          )}

          {/* PESTAÑA: Horarios */}
          {configActiveTab === 'schedules' && (
            <div className="config-section" key="schedules-content">
              <h3>⏰ Horarios Laborales</h3>
          
          <div className="schedule-subsection">
            <label className="schedule-label">Hora de Entrada:</label>
            <div className="form-row">
              <div className="form-group">
                <label>Hora:</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={tempConfig.schedules.checkIn.hour}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, checkIn: { ...tempConfig.schedules.checkIn, hour: parseInt(e.target.value) }}
                  })}
                />
              </div>
              <div className="form-group">
                <label>Minutos:</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={tempConfig.schedules.checkIn.minute}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, checkIn: { ...tempConfig.schedules.checkIn, minute: parseInt(e.target.value) }}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="schedule-subsection">
            <label className="schedule-label">Inicio Colación:</label>
            <div className="form-row">
              <div className="form-group">
                <label>Hora:</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={tempConfig.schedules.breakStart.hour}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, breakStart: { ...tempConfig.schedules.breakStart, hour: parseInt(e.target.value) }}
                  })}
                />
              </div>
              <div className="form-group">
                <label>Minutos:</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={tempConfig.schedules.breakStart.minute}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, breakStart: { ...tempConfig.schedules.breakStart, minute: parseInt(e.target.value) }}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="schedule-subsection">
            <label className="schedule-label">Fin Colación:</label>
            <div className="form-row">
              <div className="form-group">
                <label>Hora:</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={tempConfig.schedules.breakEnd.hour}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, breakEnd: { ...tempConfig.schedules.breakEnd, hour: parseInt(e.target.value) }}
                  })}
                />
              </div>
              <div className="form-group">
                <label>Minutos:</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={tempConfig.schedules.breakEnd.minute}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, breakEnd: { ...tempConfig.schedules.breakEnd, minute: parseInt(e.target.value) }}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="schedule-subsection">
            <label className="schedule-label">Hora de Salida:</label>
            <div className="form-row">
              <div className="form-group">
                <label>Hora:</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={tempConfig.schedules.checkOut.hour}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, checkOut: { ...tempConfig.schedules.checkOut, hour: parseInt(e.target.value) }}
                  })}
                />
              </div>
              <div className="form-group">
                <label>Minutos:</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={tempConfig.schedules.checkOut.minute}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    schedules: { ...tempConfig.schedules, checkOut: { ...tempConfig.schedules.checkOut, minute: parseInt(e.target.value) }}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="help-text">
            📅 Horario configurado:<br/>
            Entrada: {String(tempConfig.schedules.checkIn.hour).padStart(2, '0')}:{String(tempConfig.schedules.checkIn.minute).padStart(2, '0')} |
            Colación: {String(tempConfig.schedules.breakStart.hour).padStart(2, '0')}:{String(tempConfig.schedules.breakStart.minute).padStart(2, '0')} - {String(tempConfig.schedules.breakEnd.hour).padStart(2, '0')}:{String(tempConfig.schedules.breakEnd.minute).padStart(2, '0')} |
            Salida: {String(tempConfig.schedules.checkOut.hour).padStart(2, '0')}:{String(tempConfig.schedules.checkOut.minute).padStart(2, '0')}
          </div>
            </div>
          )}

          {/* PESTAÑA: Tolerancias */}
          {configActiveTab === 'tolerances' && (
            <div className="config-section" key="tolerances-content">
              <h3>⏱️ Tolerancias de Horario</h3>
          <p className="section-hint">Define los minutos de gracia para evitar atrasos por filas o imprevistos menores</p>
          
          <div className="form-group">
            <label>Tolerancia de Entrada:</label>
            <div className="tolerance-input-group">
              <input
                type="number"
                min="0"
                max="15"
                value={tempConfig.tolerances?.checkIn || 0}
                onChange={(e) => setTempConfig({
                  ...tempConfig,
                  tolerances: { ...tempConfig.tolerances, checkIn: parseInt(e.target.value) || 0 }
                })}
              />
              <span className="tolerance-label">minutos</span>
            </div>
            <p className="tolerance-example">
              Ejemplo: Con 5 min, marcar hasta 5 min después = "A tiempo"
            </p>
          </div>

          <div className="form-group">
            <label>Tolerancia Regreso de Colación:</label>
            <div className="tolerance-input-group">
              <input
                type="number"
                min="0"
                max="15"
                value={tempConfig.tolerances?.breakReturn || 0}
                onChange={(e) => setTempConfig({
                  ...tempConfig,
                  tolerances: { ...tempConfig.tolerances, breakReturn: parseInt(e.target.value) || 0 }
                })}
              />
              <span className="tolerance-label">minutos</span>
            </div>
            <p className="tolerance-example">
              Ejemplo: Con 3 min, regresar hasta 3 min tarde = "A tiempo"
            </p>
          </div>

          <div className="form-group">
            <label>Tolerancia Salida Temprana:</label>
            <div className="tolerance-input-group">
              <input
                type="number"
                min="0"
                max="15"
                value={tempConfig.tolerances?.earlyCheckOut || 0}
                onChange={(e) => setTempConfig({
                  ...tempConfig,
                  tolerances: { ...tempConfig.tolerances, earlyCheckOut: parseInt(e.target.value) || 0 }
                })}
              />
              <span className="tolerance-label">minutos</span>
            </div>
            <p className="tolerance-example">
              Ejemplo: Con 0 min, salir 1 min antes = "Salida temprana"
            </p>
          </div>
          <div className="form-group" style={{marginTop:'20px', paddingTop:'16px', borderTop:'1px solid var(--border)'}}>
            <h4 style={{fontSize:'13px', color:'var(--text-secondary)', marginBottom:'8px'}}>Mantenimiento</h4>
            <button type="button" onClick={recalculateLateMinutes} style={{padding:'10px 16px', background:'#0891b2', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600'}}>
              🔄 Recalcular Atrasos de Todos los Registros
            </button>
            <p style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'6px'}}>
              Vuelve a calcular los atrasos usando las tolerancias actuales. Util si cambiaste estos valores y quieres aplicarlos a registros anteriores.
            </p>
          </div>
            </div>
          )}

          {/* PESTAÑA: Alertas y Códigos */}
          {configActiveTab === 'alerts' && (
            <div className="config-section" key="alerts-content">
              <h3>🔔 Alertas y Códigos de Acceso</h3>
              
              <div className="form-group">
                <label>Código del Gerente:</label>
                <input
                  type="text"
                  value={tempConfig.codes.manager}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    codes: { ...tempConfig.codes, manager: e.target.value.toUpperCase() }
                  })}
                  placeholder="Ej: GERENTE2026"
                />
              </div>

              <div className="form-group">
                <label>Código del Administrador:</label>
                <input
                  type="text"
                  value={tempConfig.codes.admin}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    codes: { ...tempConfig.codes, admin: e.target.value.toUpperCase() }
                  })}
                  placeholder="Ej: ADMIN2026"
                />
              </div>

              <h4 style={{marginTop: '24px', marginBottom: '12px'}}>Mensajes de Alerta</h4>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={tempConfig.alerts.lateArrival.enabled}
                    onChange={(e) => setTempConfig({
                      ...tempConfig,
                      alerts: {
                        ...tempConfig.alerts,
                        lateArrival: { ...tempConfig.alerts.lateArrival, enabled: e.target.checked }
                      }
                    })}
                  />
                  <span>Alerta de entrada tarde</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={tempConfig.alerts.lateBreakReturn.enabled}
                    onChange={(e) => setTempConfig({
                      ...tempConfig,
                      alerts: {
                        ...tempConfig.alerts,
                        lateBreakReturn: { ...tempConfig.alerts.lateBreakReturn, enabled: e.target.checked }
                      }
                    })}
                  />
                  <span>Alerta de regreso tarde de colación</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={tempConfig.alerts.earlyDeparture.enabled}
                    onChange={(e) => setTempConfig({
                      ...tempConfig,
                      alerts: {
                        ...tempConfig.alerts,
                        earlyDeparture: { ...tempConfig.alerts.earlyDeparture, enabled: e.target.checked }
                      }
                    })}
                  />
                  <span>Alerta de salida temprana</span>
                </label>
              </div>
            </div>
          )}

          {/* PESTAÑA: Logo */}
          {configActiveTab === 'logo' && (
            <div className="config-section" key="logo-content">
              <h3>🏢 Logo de la Empresa</h3>
          {tempConfig.companyLogo ? (
            <div className="logo-preview-container">
              <div className="logo-preview">
                <img src={tempConfig.companyLogo} alt="Logo de la empresa" />
              </div>
              <div className="logo-actions">
                <label className="btn-upload">
                  📷 Cambiar Logo
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) {
                          alert('⚠️ La imagen debe ser menor a 2MB');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setTempConfig({
                            ...tempConfig,
                            companyLogo: reader.result
                          });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
                <button 
                  className="btn-remove-logo"
                  onClick={() => setTempConfig({
                    ...tempConfig,
                    companyLogo: null
                  })}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div className="logo-upload-area">
              <label className="upload-box">
                <div className="upload-icon">📷</div>
                <p className="upload-text">Haz clic para subir el logo</p>
                <p className="upload-hint">PNG, JPG o SVG (máx. 2MB)</p>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 2 * 1024 * 1024) {
                        alert('⚠️ La imagen debe ser menor a 2MB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setTempConfig({
                          ...tempConfig,
                          companyLogo: reader.result
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            </div>
          )}
          <div className="help-text">
            El logo aparecerá en la pantalla de login y en el header de la aplicación.
          </div>
            </div>
          )}

        </div>
        {/* Fin del contenedor de pestañas */}

        <div className="modal-actions">
          <button className="btn-cancel" onClick={() => setShowConfigModal(false)}>
            Cancelar
          </button>
          <button 
            className="btn-save"
            onClick={() => {
              saveConfig(tempConfig);
              // Actualizar horario de empleados que usan el horario estándar
              const updatedEmps = employees.map(emp => ({
                ...emp,
                schedules: {
                  checkIn: { hour: tempConfig.schedules.checkIn.hour, minute: tempConfig.schedules.checkIn.minute },
                  breakStart: { hour: tempConfig.schedules.breakStart.hour, minute: tempConfig.schedules.breakStart.minute },
                  breakEnd: { hour: tempConfig.schedules.breakEnd.hour, minute: tempConfig.schedules.breakEnd.minute },
                  checkOut: { hour: tempConfig.schedules.checkOut.hour, minute: tempConfig.schedules.checkOut.minute }
                }
              }));
              saveEmployees(updatedEmps);
              setShowConfigModal(false);
              alert('✅ Configuración guardada exitosamente');
            }}
          >
            <Save size={18} />
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  );
};




const CentroIAView = () => {
  const {
    employees, attendanceRecords, tasks, missingProducts, currentUser, userRole,
    absences, setCurrentView, setShowChat, setChatAdminId, setShowChatGerente,
  } = React.useContext(AppContext);

  const [analisisMes, setAnalisisMes] = React.useState('');
  const [analisisLoading, setAnalisisLoading] = React.useState(false);
  const hoy = new Date();
  const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}`;
  const cacheKeyMes = `centroIA_mes_${mesActual}`;

  React.useEffect(() => {
    const cached = localStorage.getItem(cacheKeyMes);
    if (cached) setAnalisisMes(cached);
  }, []);

  const generarAnalisisMes = async () => {
    if (analisisLoading) return;
    setAnalisisLoading(true);
    try {
      const lsRecs = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
      const recsDelMes = lsRecs.filter(r => r.date?.startsWith(mesActual));
      const tareasDelMes = lsTasks.filter(t => (t.dueDate||'').startsWith(mesActual));
      const empsActivos = (Array.isArray(employees)?employees:[]).filter(e => e.status === 'active' && e.id !== 1000);

      const resumenEquipo = empsActivos.map(emp => {
        const recs = recsDelMes.filter(r => r.employeeId === emp.id);
        const atrasos = recs.filter(r => r.isLate).length;
        const minAtraso = recs.reduce((s,r) => s+(r.lateMinutes||0), 0);
        const tareasComp = tareasDelMes.filter(t => String(t.assignedTo) === String(emp.id) && t.status === 'done').length;
        const tareasVenc = tareasDelMes.filter(t => String(t.assignedTo) === String(emp.id) && t.status === 'overdue').length;
        const antiguedad = emp.hireDate ? (() => {
          const diff = new Date() - new Date(emp.hireDate);
          const meses = Math.floor(diff / (30.44*24*60*60*1000));
          return meses < 3 ? `nuevo empleado (${meses} mes${meses!==1?'es':''})` : `${Math.floor(meses/12) > 0 ? Math.floor(meses/12)+' año(s) y ':''} ${meses%12} mes(es)`;
        })() : 'antigüedad desconocida';
        return `${emp.name} (${antiguedad}): ${atrasos} atrasos (${minAtraso} min), ${tareasComp} tareas completadas, ${tareasVenc} vencidas`;
      }).join('\n');

      const res = await fetch('https://ocrfrjlehptgyudfxdah.supabase.co/functions/v1/employee-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-',
          'Authorization': 'Bearer sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-'
        },
        body: JSON.stringify({
          employee: {name: 'Equipo Multimarket GyC', position: 'Supermercado', vacationDays: 0},
          atrasosMes: recsDelMes.filter(r => r.isLate).length,
          tareasComp: tareasDelMes.filter(t => t.status === 'done').length,
          tareasVenc: tareasDelMes.filter(t => t.status === 'overdue').length,
          diasVacUsados: 0,
          faltasAnio: 0,
          diasLicencia: 0,
          promptOverride: `Eres el asistente IA de Multimarket GyC. Genera un análisis mensual del equipo en máximo 4 oraciones, directo y útil para el gerente. Incluye puntualidad, tareas y recomendación. Datos del equipo en ${mesActual}:\n${resumenEquipo}`
        })
      });
      const data = await res.json();
      const texto = data.diagnostico?.trim() || '';
      if (texto) { localStorage.setItem(cacheKeyMes, texto); setAnalisisMes(texto); }
    } catch(e) { console.error('Error análisis mes:', e); }
    setAnalisisLoading(false);
  };

  // Calcular semáforo por empleado
  const lsRecs = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
  const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
  const recsDelMes = lsRecs.filter(r => r.date?.startsWith(mesActual));
  const tareasDelMes = lsTasks.filter(t => (t.dueDate||'').startsWith(mesActual));

  // Calcular tareas sin revisar por el admin
  const _hoyIA = new Date();
  const _ayerIA = new Date(_hoyIA); _ayerIA.setDate(_ayerIA.getDate()-1);
  const ayerStrIA = `${_ayerIA.getFullYear()}-${String(_ayerIA.getMonth()+1).padStart(2,'0')}-${String(_ayerIA.getDate()).padStart(2,'0')}`;
  const hoyStrIA = `${_hoyIA.getFullYear()}-${String(_hoyIA.getMonth()+1).padStart(2,'0')}-${String(_hoyIA.getDate()).padStart(2,'0')}`;
  const tareasSinRevisarAdmin = lsTasks.filter(t => 
    t?.status === 'done' && !t?.reviewStatus && 
    t?.dueDate && (t.dueDate === ayerStrIA || t.dueDate === hoyStrIA)
  ).length;

  const semaforo = (Array.isArray(employees)?employees:[])
    .filter(e => e.status === 'active' && e.id !== 1000)
    .map(emp => {
      const recs = recsDelMes.filter(r => r.employeeId === emp.id);
      const atrasos = recs.filter(r => r.isLate).length;
      const tareasVenc = tareasDelMes.filter(t => String(t.assignedTo) === String(emp.id) && t.status === 'overdue').length;
      const tareasComp = tareasDelMes.filter(t => String(t.assignedTo) === String(emp.id) && t.status === 'done').length;
      
      // Patrones detectados
      const patronesDetectados = [];
      const recsSemana = recs.filter(r => {
        const d = new Date(r.date);
        const hoy2 = new Date();
        return (hoy2 - d) < 7*24*60*60*1000;
      });
      if (recsSemana.filter(r => r.isLate).length >= 2) patronesDetectados.push('⚠️ 2+ atrasos esta semana');
      if (tareasVenc >= 3) patronesDetectados.push(`⚠️ ${tareasVenc} tareas vencidas`);
      if (atrasos === 0 && tareasComp >= 5) patronesDetectados.push('⭐ Excelente rendimiento');
      // Alerta especial para el admin si no esta revisando tareas
      if (emp.id === 999 && tareasSinRevisarAdmin >= 5) patronesDetectados.push(`⚠️ ${tareasSinRevisarAdmin} tareas sin revisar`);

      let color = 'green';
      if (atrasos >= 3 || tareasVenc >= 3) color = 'red';
      else if (atrasos >= 1 || tareasVenc >= 1) color = 'yellow';
      // Admin en rojo si tiene muchas tareas sin revisar
      if (emp.id === 999 && tareasSinRevisarAdmin >= 5) color = 'red';

      return { emp, atrasos, tareasVenc, tareasComp, color, patronesDetectados };
    });

  const colores = { green: '#10b981', yellow: '#f59e0b', red: '#ef4444' };
  const emojis = { green: '🟢', yellow: '🟡', red: '🔴' };
  const bgColores = { green: 'rgba(16,185,129,0.08)', yellow: 'rgba(245,158,11,0.08)', red: 'rgba(239,68,68,0.08)' };

  // Alertas predictivas
  const alertasPredictivas = [];
  semaforo.forEach(({emp, atrasos, tareasVenc, color}) => {
    if (color === 'red') alertasPredictivas.push({
      tipo: 'critico', emp: emp.name,
      msg: `${emp.name} requiere atención urgente — ${atrasos} atrasos y ${tareasVenc} tareas vencidas este mes`
    });
  });

  const mesNombre = hoy.toLocaleDateString('es-CL', {month:'long', year:'numeric'});

  return (
    <div style={{padding:'20px', maxWidth:'900px', margin:'0 auto'}}>
      <h2 style={{fontSize:'20px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 4px'}}>🧠 Centro de Inteligencia IA</h2>
      <p style={{color:'var(--text-secondary)', fontSize:'13px', margin:'0 0 20px', textTransform:'capitalize'}}>{mesNombre} · Solo visible para el gerente</p>

      {/* Semaforo del equipo */}
      <div style={{marginBottom:'20px'}}>
        <p style={{fontSize:'14px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 10px'}}>🚦 Estado del equipo hoy</p>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'10px'}}>
          {semaforo.map(({emp, atrasos, tareasVenc, tareasComp, color, patronesDetectados}) => (
            <div key={emp.id} style={{background:bgColores[color], borderRadius:'10px', padding:'12px', border:`1px solid ${colores[color]}33`, cursor:'pointer'}}
              onClick={() => setCurrentView('employees')}>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px'}}>
                <span style={{fontSize:'20px'}}>{emp.photo}</span>
                <div style={{flex:1, minWidth:0}}>
                  <p style={{margin:0, fontSize:'12px', fontWeight:'700', color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{emp.name}</p>
                  <p style={{margin:0, fontSize:'10px', color:'var(--text-secondary)'}}>{emp.position}</p>
                </div>
                <span style={{fontSize:'18px'}}>{emojis[color]}</span>
              </div>
              <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                {atrasos > 0 && <span style={{fontSize:'10px', background:'rgba(239,68,68,0.15)', color:'#ef4444', borderRadius:'4px', padding:'2px 6px'}}>{atrasos} atrasos</span>}
                {tareasVenc > 0 && <span style={{fontSize:'10px', background:'rgba(245,158,11,0.15)', color:'#f59e0b', borderRadius:'4px', padding:'2px 6px'}}>{tareasVenc} vencidas</span>}
                {tareasComp > 0 && <span style={{fontSize:'10px', background:'rgba(16,185,129,0.15)', color:'#10b981', borderRadius:'4px', padding:'2px 6px'}}>{tareasComp} completadas</span>}
              </div>
              {patronesDetectados.length > 0 && (
                <div style={{marginTop:'6px'}}>
                  {patronesDetectados.map((p,i) => <p key={i} style={{margin:'2px 0', fontSize:'10px', color:colores[color]}}>{p}</p>)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Alertas predictivas */}
      {alertasPredictivas.length > 0 && (
        <div style={{marginBottom:'20px'}}>
          <p style={{fontSize:'14px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 10px'}}>⚡ Alertas predictivas</p>
          <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
            {alertasPredictivas.map((a,i) => (
              <div key={i} style={{background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'8px', padding:'10px 14px', display:'flex', alignItems:'center', gap:'10px'}}>
                <span style={{fontSize:'20px'}}>🚨</span>
                <p style={{margin:0, fontSize:'13px', color:'#ef4444', fontWeight:'600'}}>{a.msg}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Herramientas IA disponibles */}
      <div style={{marginBottom:'20px'}}>
        <p style={{fontSize:'14px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 10px'}}>🤖 Herramientas IA disponibles</p>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'10px'}}>
          {[
            {icon:'📊', titulo:'Informe diario', desc:'Resumen IA del día con asistencia y tareas', accion:'dashboard'},
            {icon:'👥', titulo:'Diagnóstico empleados', desc:'Análisis IA individual por empleado en su ficha', accion:'employees'},
            {icon:'💬', titulo:'Chat IA', desc:'Consulta al asistente IA cualquier situación del equipo', accion:'chat'},
            {icon:'📋', titulo:'Tareas IA', desc:'Genera tareas automáticas por empleado y pasillo', accion:'tasks'},
          ].map((h,i) => (
            <div key={i} onClick={() => {
                if (!h.accion) return;
                if (h.accion === 'chat') {
                  setShowChatGerente(true);
                } else {
                  setCurrentView(h.accion);
                }
              }}
              style={{background:'var(--bg-elevated)', borderRadius:'10px', padding:'12px', border:'1px solid var(--border)', cursor:'pointer', transition:'all 0.2s'}}>
              <p style={{margin:'0 0 4px', fontSize:'24px'}}>{h.icon}</p>
              <p style={{margin:'0 0 2px', fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>{h.titulo}</p>
              <p style={{margin:0, fontSize:'11px', color:'var(--text-secondary)'}}>{h.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Análisis IA mensual */}
      <div style={{background:'rgba(99,102,241,0.06)', borderRadius:'12px', padding:'16px', border:'1px solid rgba(99,102,241,0.2)'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
          <p style={{margin:0, fontSize:'14px', fontWeight:'700', color:'var(--text-primary)'}}>📝 Análisis IA del mes</p>
          <button onClick={generarAnalisisMes} disabled={analisisLoading}
            style={{padding:'6px 14px', background:'linear-gradient(135deg,#6366f1,#4f46e5)', color:'white', border:'none', borderRadius:'6px', cursor:analisisLoading?'wait':'pointer', fontSize:'11px', fontWeight:'700', fontFamily:'inherit'}}>
            {analisisLoading ? '⏳ Analizando...' : analisisMes ? '🔄 Regenerar' : '🤖 Analizar mes'}
          </button>
        </div>
        {analisisMes ? (
          <p style={{margin:0, fontSize:'13px', color:'var(--text-primary)', lineHeight:'1.6'}}>{analisisMes}</p>
        ) : (
          <p style={{margin:0, fontSize:'12px', color:'var(--text-secondary)', fontStyle:'italic'}}>
            Haz clic en "Analizar mes" para obtener un resumen inteligente del desempeño del equipo en {mesNombre}. Se cachea por día.
          </p>
        )}
      </div>
    </div>
  );
};

const DiagnosticoIA = ({ employee, diasVacUsados, faltasAnio, diasLicencia }) => {
  const cacheKey = `iaDiagnostico_${employee.id}_${getLocalDateStr()}`;
  const [diagTexto, setDiagTexto] = React.useState(() => localStorage.getItem(cacheKey) || '');
  const [diagLoading, setDiagLoading] = React.useState(false);

  const generarDiagnostico = async () => {
    if (diagLoading) return;
    setDiagLoading(true);
    try {
      const lsRecs = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
      const mesActual = getLocalDateStr().substring(0,7);
      const atrasosMes = lsRecs.filter(r => String(r.employeeId) === String(employee.id) && r.date?.startsWith(mesActual) && r.isLate).length;
      const tareasComp = lsTasks.filter(t => String(t.assignedTo) === String(employee.id) && t.status === 'done').length;
      const tareasVenc = lsTasks.filter(t => String(t.assignedTo) === String(employee.id) && t.status === 'overdue').length;
      const res = await fetch('https://ocrfrjlehptgyudfxdah.supabase.co/functions/v1/employee-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-',
          'Authorization': 'Bearer sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-'
        },
        body: JSON.stringify({ 
          employee, atrasosMes, tareasComp, tareasVenc, diasVacUsados, faltasAnio, diasLicencia,
          antiguedad: employee.hireDate ? (() => {
            const diff = new Date() - new Date(employee.hireDate);
            const meses = Math.floor(diff / (30.44*24*60*60*1000));
            return meses < 3 ? `nuevo empleado (${meses} mes${meses!==1?'es':''})` : `${Math.floor(meses/12) > 0 ? Math.floor(meses/12)+' año(s) y ':''} ${meses%12} mes(es)`;
          })() : null
        })
      });
      const data = await res.json();
      const texto = data.diagnostico?.trim() || '';
      if (texto) { localStorage.setItem(cacheKey, texto); setDiagTexto(texto); }
      else { console.error('[DiagIA] Respuesta sin diagnostico:', data); }
    } catch(e) { console.error('[DiagIA] Error:', e.message); }
    setDiagLoading(false);
  };

  return (
    <div style={{background:'rgba(99,102,241,0.08)', borderRadius:'8px', padding:'8px 10px', marginBottom:'10px', border:'1px solid rgba(99,102,241,0.15)', display:'flex', alignItems:'center', gap:'8px'}}>
      <span style={{fontSize:'16px'}}>🤖</span>
      {diagTexto ? (
        <p style={{margin:0, fontSize:'12px', color:'var(--text-primary)', flex:1}}>{diagTexto}</p>
      ) : (
        <p style={{margin:0, fontSize:'12px', color:'var(--text-secondary)', flex:1, fontStyle:'italic'}}>Sin diagnóstico — haz clic en 🤖 Analizar</p>
      )}
      <button onClick={generarDiagnostico} disabled={diagLoading}
        style={{padding:'4px 8px', background:'rgba(99,102,241,0.15)', color:'#6366f1', border:'1px solid rgba(99,102,241,0.3)', borderRadius:'6px', cursor:diagLoading?'wait':'pointer', fontSize:'10px', fontWeight:'700', whiteSpace:'nowrap', fontFamily:'inherit'}}>
        {diagLoading ? '⏳' : diagTexto ? '🔄' : '🤖 Analizar'}
      </button>
    </div>
  );
};


const PagosModal = ({ employee, employees, currentUser, saveEmployees, onClose, onUpdate }) => {
  const tiposMovimiento = [
    {value:'anticipo', label:'💰 Anticipo/Adelanto', color:'#f59e0b'},
    {value:'sueldo', label:'✅ Sueldo pagado', color:'#10b981'},
    {value:'bono', label:'⭐ Bono/Incentivo', color:'#6366f1'},
    {value:'descuento', label:'➖ Descuento', color:'#ef4444'},
  ];
  const [tipoNuevo, setTipoNuevo] = React.useState('anticipo');
  const [montoNuevo, setMontoNuevo] = React.useState('');
  const [descNuevo, setDescNuevo] = React.useState('');
  const [fechaNueva, setFechaNueva] = React.useState(getLocalDateStr());
  // IMPORTANTE: mesFiltro debe declararse ANTES de periodoNuevo ya que lo usa como valor inicial
  const [mesFiltro, setMesFiltro] = React.useState(getLocalDateStr().substring(0,7));
  const [periodoNuevo, setPeriodoNuevo] = React.useState(getLocalDateStr().substring(0,7));

  const movimientos = (Array.isArray(employee.advances) ? employee.advances : []).sort((a,b) => (b.date||'').localeCompare(a.date||''));
  const movimientosMes = movimientos.filter(m => {
    const periodoMov = m.periodo || (m.date||'').substring(0,7);
    return periodoMov === mesFiltro;
  });

  const guardarEnFirebase = (empActualizado) => {
    // Leer empleados frescos desde localStorage para evitar perder datos
    const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
    const baseEmps = lsEmps.length > 0 ? lsEmps : (Array.isArray(employees) ? employees : []);
    const empsActualizados = baseEmps.map(e => e.id === empActualizado.id ? empActualizado : e);
    saveEmployees(empsActualizados);
    onUpdate(empActualizado);
  };

  const guardarMovimiento = () => {
    if (!montoNuevo || isNaN(parseFloat(montoNuevo)) || parseFloat(montoNuevo) <= 0) {
      alert('Ingresa un monto válido');
      return;
    }
    const nuevo = {
      id: Date.now(),
      date: fechaNueva,
      periodo: periodoNuevo,
      amount: parseFloat(montoNuevo),
      type: tipoNuevo,
      description: descNuevo || tiposMovimiento.find(t=>t.value===tipoNuevo)?.label || tipoNuevo,
      status: tipoNuevo === 'anticipo' ? 'pendiente' : 'pagado',
      notes: descNuevo,
      registeredBy: currentUser?.name || 'Gerente',
      registeredAt: new Date().toISOString()
    };
    const empFrescoGuardar = getEmpFresco();
    const empActualizado = {...empFrescoGuardar, advances: [nuevo, ...(Array.isArray(empFrescoGuardar.advances)?empFrescoGuardar.advances:[])]};
    guardarEnFirebase(empActualizado);
    setMontoNuevo('');
    setDescNuevo('');
  };

  const getEmpFresco = () => {
    try {
      const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
      return lsEmps.find(e => e.id === employee.id) || employee;
    } catch(e) { return employee; }
  };

  const marcarPagado = (movId) => {
    const empFresco = getEmpFresco();
    const updated = (Array.isArray(empFresco.advances)?empFresco.advances:[]).map(m => m.id === movId ? {...m, status:'pagado', paidAt: new Date().toISOString()} : m);
    guardarEnFirebase({...empFresco, advances: updated});
  };

  const eliminarMovimiento = (movId) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    const empFresco = getEmpFresco();
    const updated = (Array.isArray(empFresco.advances)?empFresco.advances:[]).filter(m => m.id !== movId);
    guardarEnFirebase({...empFresco, advances: updated});
  };

  const totalPendiente = movimientosMes.filter(m => m.type === 'anticipo').reduce((s,m) => s+(m.amount||0), 0);
  const totalSueldoPagado = movimientosMes.filter(m => m.type === 'sueldo').reduce((s,m) => s+(m.amount||0), 0);
  const totalBonos = movimientosMes.filter(m => m.type === 'bono').reduce((s,m) => s+(m.amount||0), 0);
  const totalDescuentos = movimientosMes.filter(m => m.type === 'descuento').reduce((s,m) => s+(m.amount||0), 0);
  const mesNombre = new Date(mesFiltro + '-01T12:00:00').toLocaleDateString('es-CL', {month:'long', year:'numeric'});

  const exportarPDF = async () => {
    try {
      if (!window.jspdf) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          s.onload = res; s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210;
      const now = new Date();

      // Header
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, W, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Multimarket GyC', 14, 11);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Reporte de Pagos', 14, 18);
      doc.setFontSize(9);
      doc.text(`Generado: ${now.toLocaleDateString('es-CL')} ${now.toLocaleTimeString('es-CL', {hour:'2-digit', minute:'2-digit'})}`, W - 14, 18, {align:'right'});

      let y = 36;

      // Info empleado
      doc.setFillColor(30, 41, 59);
      doc.rect(10, y - 4, W - 20, 20, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(employee.name, 16, y + 4);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(employee.position || '', 16, y + 10);
      doc.text(`Período: ${mesNombre}`, W - 16, y + 4, {align:'right'});
      y += 26;

      // Resumen del mes
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(`Resumen — ${mesNombre}`, 14, y);
      y += 5;
      doc.setFontSize(8); doc.setFont('helvetica','normal'); doc.setTextColor(120,120,120);
      doc.text('* El historial muestra todos los movimientos. Los sueldos pueden aparecer en el mes siguiente.', 14, y);
      y += 7;

      const totalAPagar = totalPendiente + totalSueldoPagado + totalBonos - totalDescuentos;
      const resumen = [
        {label:'Anticipo dia 15', monto: totalPendiente, color:[245,158,11]},
        {label:'Sueldo total mes', monto: totalSueldoPagado, color:[16,185,129]},
        {label:'Bonos/Incentivos', monto: totalBonos, color:[99,102,241]},
        {label:'Descuentos', monto: totalDescuentos, color:[239,68,68]},
      ];
      resumen.forEach((r, i) => {
        const x = 14 + (i % 2) * 94;
        const yy = y + Math.floor(i / 2) * 16;
        doc.setFillColor(...r.color, 20);
        doc.rect(x, yy - 4, 88, 14, 'F');
        doc.setDrawColor(...r.color);
        doc.rect(x, yy - 4, 88, 14, 'S');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text(r.label, x + 4, yy + 2);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...r.color);
        doc.text('$' + r.monto.toLocaleString('es-CL'), x + 4, yy + 8);
      });
      y += 38;

      // Historial completo
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Historial de movimientos', 14, y);
      y += 6;

      // Encabezados tabla
      doc.setFillColor(15, 23, 42);
      doc.rect(10, y - 4, W - 20, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Fecha', 14, y + 1);
      doc.text('Tipo', 45, y + 1);
      doc.text('Descripción', 85, y + 1);
      doc.text('Monto', 155, y + 1);
      doc.text('Estado', 180, y + 1);
      y += 10;

      // Total a pagar
      if (totalSueldoPagado > 0 || totalPendiente > 0) {
        y += 4;
        doc.setFillColor(99,102,241);
        doc.rect(10, y-4, W-20, 14, 'F');
        doc.setTextColor(255,255,255);
        doc.setFontSize(10); doc.setFont('helvetica','bold');
        doc.text('SUELDO TOTAL DEL MES', 16, y+4);
        doc.setFontSize(13);
        doc.text('$' + totalAPagar.toLocaleString('es-CL'), W-16, y+4, {align:'right'});
        doc.setFontSize(7); doc.setFont('helvetica','normal');
        doc.text('Anticipo + Sueldo + Bonos - Descuentos', 16, y+10);
        doc.setTextColor(15,23,42);
        y += 22;
      }

      const tiposMap = {anticipo:'Anticipo', sueldo:'Sueldo', bono:'Bono', descuento:'Descuento'};
      movimientos.forEach((mov, idx) => {
        if (y > 270) { doc.addPage(); y = 20; }
        if (idx % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(10, y - 4, W - 20, 8, 'F');
        }
        doc.setTextColor(15, 23, 42);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(mov.date || '-', 14, y + 1);
        doc.text(tiposMap[mov.type] || mov.type || '-', 45, y + 1);
        const desc = (mov.description || '-').replace(/[^ -]/g, '').trim().substring(0, 30) || (mov.type || '-');
        doc.text(desc, 85, y + 1);
        doc.text('$' + (mov.amount||0).toLocaleString('es-CL'), 155, y + 1);
        doc.text(mov.status === 'pagado' ? 'Pagado' : 'Pendiente', 180, y + 1);
        y += 8;
      });

      // Footer
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 287, W, 10, 'F');
      doc.setTextColor(180, 180, 180);
      doc.setFontSize(7);
      doc.text('Multimarket GyC — Control de Asistencia', W/2, 293, {align:'center'});

      doc.save(`Pagos_${employee.name.replace(/ /g,'_')}_${mesFiltro}.pdf`);
    } catch(e) { alert('Error al generar PDF: ' + e.message); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{maxWidth:'600px', maxHeight:'90vh', overflow:'auto'}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h2>💰 Pagos — {employee.name}</h2>
          <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
            <button onClick={exportarPDF} style={{padding:'5px 12px', background:'linear-gradient(135deg, #6366f1, #4f46e5)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'700', fontFamily:'inherit'}}>
              📄 PDF
            </button>
            <button className="btn-close" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px'}}>
            <p style={{margin:0, fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>📊 Resumen: <span style={{color:'#6366f1', textTransform:'capitalize'}}>{mesNombre}</span></p>
            <input type="month" value={mesFiltro} onChange={e=>setMesFiltro(e.target.value)} style={{padding:'4px 8px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'6px', color:'var(--text-primary)', fontSize:'12px', fontFamily:'inherit'}} />
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'8px', marginBottom:'8px'}}>
            <div style={{background:'rgba(245,158,11,0.1)', borderRadius:'8px', padding:'10px', border:'1px solid rgba(245,158,11,0.2)'}}>
              <p style={{margin:'0 0 2px', fontSize:'11px', color:'#f59e0b', fontWeight:'700'}}>💰 Anticipo día 15</p>
              <p style={{margin:0, fontSize:'18px', fontWeight:'700', color:'#f59e0b'}}>${totalPendiente.toLocaleString('es-CL')}</p>
            </div>
            <div style={{background:'rgba(16,185,129,0.1)', borderRadius:'8px', padding:'10px', border:'1px solid rgba(16,185,129,0.2)'}}>
              <p style={{margin:'0 0 2px', fontSize:'11px', color:'#10b981', fontWeight:'700'}}>✅ Sueldo total del mes</p>
              <p style={{margin:0, fontSize:'18px', fontWeight:'700', color:'#10b981'}}>${totalSueldoPagado.toLocaleString('es-CL')}</p>
            </div>
            <div style={{background:'rgba(99,102,241,0.1)', borderRadius:'8px', padding:'10px', border:'1px solid rgba(99,102,241,0.2)'}}>
              <p style={{margin:'0 0 2px', fontSize:'11px', color:'#6366f1', fontWeight:'700'}}>⭐ Bonos</p>
              <p style={{margin:0, fontSize:'18px', fontWeight:'700', color:'#6366f1'}}>${totalBonos.toLocaleString('es-CL')}</p>
            </div>
            <div style={{background:'rgba(239,68,68,0.1)', borderRadius:'8px', padding:'10px', border:'1px solid rgba(239,68,68,0.2)'}}>
              <p style={{margin:'0 0 2px', fontSize:'11px', color:'#ef4444', fontWeight:'700'}}>➖ Descuentos</p>
              <p style={{margin:0, fontSize:'18px', fontWeight:'700', color:'#ef4444'}}>${totalDescuentos.toLocaleString('es-CL')}</p>
            </div>
          </div>
          {/* Sueldo total del mes */}
          {(totalSueldoPagado > 0 || totalPendiente > 0) && (
            <div style={{background:'rgba(99,102,241,0.1)', borderRadius:'10px', padding:'12px 14px', marginBottom:'16px', border:'2px solid rgba(99,102,241,0.3)', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <div>
                <p style={{margin:'0 0 2px', fontSize:'12px', color:'#6366f1', fontWeight:'700'}}>💰 SUELDO TOTAL DEL MES</p>
                <p style={{margin:0, fontSize:'11px', color:'var(--text-secondary)'}}>Anticipo + Sueldo + Bonos - Descuentos</p>
              </div>
              <p style={{margin:0, fontSize:'22px', fontWeight:'700', color:'#6366f1'}}>
                ${(totalPendiente + totalSueldoPagado + totalBonos - totalDescuentos).toLocaleString('es-CL')}
              </p>
            </div>
          )}
          <div style={{background:'var(--bg-elevated)', borderRadius:'10px', padding:'14px', marginBottom:'16px', border:'1px solid var(--border)'}}>
            <p style={{margin:'0 0 10px', fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>➕ Registrar movimiento</p>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px'}}>
              <select value={tipoNuevo} onChange={e=>setTipoNuevo(e.target.value)} style={{padding:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'6px', color:'var(--text-primary)', fontSize:'12px', fontFamily:'inherit'}}>
                {tiposMovimiento.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input type="date" value={fechaNueva} onChange={e=>setFechaNueva(e.target.value)} style={{padding:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'6px', color:'var(--text-primary)', fontSize:'12px', fontFamily:'inherit'}} />
            </div>
            <div style={{marginBottom:'8px'}}>
              <label style={{fontSize:'11px', color:'var(--text-secondary)', fontWeight:'600', display:'block', marginBottom:'4px'}}>Período que corresponde</label>
              <input type="month" value={periodoNuevo} onChange={e=>setPeriodoNuevo(e.target.value)} style={{width:'100%', padding:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'6px', color:'var(--text-primary)', fontSize:'12px', fontFamily:'inherit'}} />
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px'}}>
              <input type="number" value={montoNuevo} onChange={e=>setMontoNuevo(e.target.value)} placeholder="Monto ($)" style={{padding:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'6px', color:'var(--text-primary)', fontSize:'12px', fontFamily:'inherit'}} />
              <input type="text" value={descNuevo} onChange={e=>setDescNuevo(e.target.value)} placeholder="Descripción (opcional)" style={{padding:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'6px', color:'var(--text-primary)', fontSize:'12px', fontFamily:'inherit'}} />
            </div>
            <button onClick={guardarMovimiento} style={{width:'100%', padding:'8px', background:'linear-gradient(135deg, #10b981, #059669)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px', fontWeight:'700', fontFamily:'inherit'}}>
              Registrar
            </button>
          </div>
          <p style={{margin:'0 0 8px', fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>📋 Historial</p>
          {movimientos.length === 0 ? (
            <p style={{textAlign:'center', color:'var(--text-secondary)', fontSize:'12px', padding:'20px 0'}}>Sin movimientos registrados</p>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
              {movimientos.map(mov => {
                const tipo = tiposMovimiento.find(t => t.value === mov.type) || {label: mov.type || 'Anticipo', color:'#f59e0b'};
                return (
                  <div key={mov.id} style={{background:'var(--bg-elevated)', borderRadius:'8px', padding:'10px 12px', border:`1px solid ${tipo.color}33`, display:'flex', alignItems:'center', gap:'10px'}}>
                    <div style={{flex:1}}>
                      <div style={{display:'flex', alignItems:'center', gap:'6px', marginBottom:'2px', flexWrap:'wrap'}}>
                        <span style={{fontSize:'11px', color:tipo.color, fontWeight:'700'}}>{tipo.label}</span>
                        {mov.status === 'pendiente' && <span style={{fontSize:'10px', background:'rgba(245,158,11,0.2)', color:'#f59e0b', borderRadius:'4px', padding:'1px 6px'}}>Pendiente</span>}
                        {mov.status === 'pagado' && <span style={{fontSize:'10px', background:'rgba(16,185,129,0.2)', color:'#10b981', borderRadius:'4px', padding:'1px 6px'}}>✅ Pagado</span>}
                      </div>
                      <p style={{margin:0, fontSize:'16px', fontWeight:'700', color:'var(--text-primary)'}}>${(mov.amount||0).toLocaleString('es-CL')}</p>
                      {mov.description && mov.description !== tipo.label && <p style={{margin:'2px 0 0', fontSize:'11px', color:'var(--text-secondary)'}}>{mov.description}</p>}
                      <p style={{margin:'2px 0 0', fontSize:'10px', color:'var(--text-secondary)'}}>{mov.date} · {mov.registeredBy || 'Gerente'}</p>
                    </div>
                    <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
                      {mov.status === 'pendiente' && (
                        <button onClick={()=>marcarPagado(mov.id)} style={{padding:'4px 8px', background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)', borderRadius:'4px', cursor:'pointer', fontSize:'10px', fontWeight:'600', fontFamily:'inherit'}}>
                          ✅ Pagado
                        </button>
                      )}
                      <button onClick={()=>eliminarMovimiento(mov.id)} style={{padding:'4px 8px', background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'4px', cursor:'pointer', fontSize:'10px', fontWeight:'600', fontFamily:'inherit'}}>
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EmployeeModal = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const formData = Object.keys(employeeFormData).length > 0 ? employeeFormData : (editingEmployee || {
    // default - 
    name: '',
    position: '',
    photo: '👤',
    schedules: {
      checkIn: { hour: config.schedules.checkIn.hour, minute: config.schedules.checkIn.minute },
      breakStart: { hour: config.schedules.breakStart.hour, minute: config.schedules.breakStart.minute },
      breakEnd: { hour: config.schedules.breakEnd.hour, minute: config.schedules.breakEnd.minute },
      checkOut: { hour: config.schedules.checkOut.hour, minute: config.schedules.checkOut.minute }
    }
  });
  const setFormData = setEmployeeFormData;

  const emojis = ['👨‍💻', '👩‍💻', '👨‍💼', '👩‍💼', '👨‍🎨', '👩‍🎨', '👨‍🔧', '👩‍🔧', '👨‍⚕️', '👩‍⚕️', '👤'];
  
  const useStandardSchedule = () => {
    setFormData({
      ...formData,
      schedules: {
        checkIn: { hour: config.schedules.checkIn.hour, minute: config.schedules.checkIn.minute },
        breakStart: { hour: config.schedules.breakStart.hour, minute: config.schedules.breakStart.minute },
        breakEnd: { hour: config.schedules.breakEnd.hour, minute: config.schedules.breakEnd.minute },
        checkOut: { hour: config.schedules.checkOut.hour, minute: config.schedules.checkOut.minute }
      }
    });
  };

  
  // Bloquear auto-edición del admin
  const isAdminEditingSelf = currentUser?.id === 999 && editingEmployee?.id === 999;
  
  if (isAdminEditingSelf) {
    return (
      <div className="modal-overlay" onClick={() => { setShowEmployeeModal(false); setEditingEmployee(null); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth:'500px'}}>
          <div className="modal-header">
            <h2>⚠️ Restricción</h2>
            <button onClick={() => { setShowEmployeeModal(false); setEditingEmployee(null); }} className="btn-close">✕</button>
          </div>
          <div className="modal-body" style={{textAlign:'center', padding:'40px'}}>
            <div style={{fontSize:'60px', marginBottom:'20px'}}>🔒</div>
            <h3>No puedes editar tu propio perfil</h3>
            <p style={{color:'var(--text-muted)', marginTop:'12px'}}>
              Como administrador, solicita a un gerente<br/>
              que realice cambios en tu horario.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="modal-overlay" onClick={() => { setShowEmployeeModal(false); setEditingEmployee(null); }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingEmployee ? '✏️ Editar Empleado' : '➕ Nuevo Empleado'}</h2>
          <button className="modal-close" onClick={() => { setShowEmployeeModal(false); setEditingEmployee(null); }}>
            <X size={24} />
          </button>
        </div>

        <div className="form-group">
          <label>Nombre completo:</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Juan Pérez"
          />
        </div>

        <div className="form-group">
          <label>Cargo:</label>
          <input
            type="text"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            placeholder="Ej: Desarrollador"
          />
        </div>

        <div className="form-group">
          <label>Avatar:</label>
          <div className="emoji-picker">
            {emojis.map(emoji => (
              <button
                key={emoji}
                className={`emoji-btn ${formData.photo === emoji ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, photo: emoji })}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>


        {/* Sección de Contrato */}
        <div style={{marginTop: '24px', padding: '16px', background: 'var(--bg-card)', borderRadius: '8px'}}>
          <h3 style={{margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)'}}>

        {/* 📅 Horarios por Día de la Semana */}
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: 'var(--bg-elevated)',
          borderRadius: '8px',
          border: (() => {
              const hasW = formData.useFlexibleSchedule && formData.weeklySchedule && Object.values(formData.weeklySchedule).some(d => d.enabled && d.start);
              const hasM = formData.monthlySchedule && Object.values(formData.monthlySchedule).some(d => d.enabled && d.start);
              return (hasW || hasM) ? '1px solid rgba(59,130,246,0.4)' : '2px solid rgba(239,68,68,0.5)';
            })()
        }}>
          <h3 style={{
            margin: '0 0 4px 0',
            color: 'var(--blue)',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            📅 Horarios del Empleado
            <span style={{fontSize:'11px', background:'#ef4444', color:'white', padding:'2px 8px', borderRadius:'4px', fontWeight:'700'}}>OBLIGATORIO</span>
          </h3>
          <p style={{fontSize:'12px', color:'var(--text-secondary)', margin:'0 0 16px 0'}}>
            Define el horario base del empleado. Sin esto el sistema no puede calcular atrasos.
          </p>
          
          <button
            type="button"
            onClick={() => {
              setEmployeeFormData(formData);
              setShowCalendar(true);
              setCalendarEmployee(formData);
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: 'var(--blue)',
              color: '#0a0f1e',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              marginTop: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            📅 Abrir Calendario Mensual
          </button>


          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '20px',
            flexWrap: 'wrap'
          }}>
            <button
              type="button"
              onClick={() => {
                const useFlexible = !formData.useFlexibleSchedule;
                setFormData({
                  ...formData,
                  useFlexibleSchedule: useFlexible,
                  weeklySchedule: useFlexible ? {
                    monday: { enabled: true, start: '09:00', end: '18:00', lunchStart: '13:00', lunchEnd: '14:00' },
                    tuesday: { enabled: true, start: '09:00', end: '18:00', lunchStart: '13:00', lunchEnd: '14:00' },
                    wednesday: { enabled: true, start: '09:00', end: '18:00', lunchStart: '13:00', lunchEnd: '14:00' },
                    thursday: { enabled: true, start: '09:00', end: '18:00', lunchStart: '13:00', lunchEnd: '14:00' },
                    friday: { enabled: true, start: '09:00', end: '18:00', lunchStart: '13:00', lunchEnd: '14:00' },
                    saturday: { enabled: false, start: '09:00', end: '13:00', lunchStart: '', lunchEnd: '' },
                    sunday: { enabled: false, start: '', end: '', lunchStart: '', lunchEnd: '' }
                  } : undefined
                });
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: formData.useFlexibleSchedule ? 'var(--accent)' : 'var(--bg-elevated)',
                color: formData.useFlexibleSchedule ? '#0a0f1e' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {formData.useFlexibleSchedule ? '✅ Horarios por Día ACTIVADO' : '⚪ Activar Horarios por Día'}
            </button>

            {formData.useFlexibleSchedule && (
              <button
                type="button"
                onClick={() => {
                  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                  const newSchedule = { ...formData.weeklySchedule };
                  days.forEach(day => {
                    if (newSchedule[day]) {
                      newSchedule[day] = {
                        ...newSchedule[day],
                        start: formData.startTime || '09:00',
                        end: formData.endTime || '18:00',
                        lunchStart: formData.lunchStart || '13:00',
                        lunchEnd: formData.lunchEnd || '14:00'
                      };
                    }
                  });
                  setFormData({ ...formData, weeklySchedule: newSchedule });
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'var(--blue)',
                  color: '#0a0f1e',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                📋 Copiar Horario Estándar a Todos los Días
              </button>
            )}
          </div>

          {formData.useFlexibleSchedule && formData.weeklySchedule && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {[
                { key: 'monday', label: 'Lunes', emoji: '📅' },
                { key: 'tuesday', label: 'Martes', emoji: '📅' },
                { key: 'wednesday', label: 'Miércoles', emoji: '📅' },
                { key: 'thursday', label: 'Jueves', emoji: '📅' },
                { key: 'friday', label: 'Viernes', emoji: '📅' },
                { key: 'saturday', label: 'Sábado', emoji: '📅' },
                { key: 'sunday', label: 'Domingo', emoji: '📅' }
              ].map(day => (
                <div key={day.key} style={{
                  padding: '15px',
                  backgroundColor: formData.weeklySchedule[day.key]?.enabled ? 'var(--bg-card)' : 'var(--bg-elevated)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      checked={formData.weeklySchedule[day.key]?.enabled || false}
                      onChange={(e) => {
                        const newSchedule = { ...formData.weeklySchedule };
                        newSchedule[day.key] = {
                          ...newSchedule[day.key],
                          enabled: e.target.checked
                        };
                        setFormData({ ...formData, weeklySchedule: newSchedule });
                      }}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <strong style={{ fontSize: '16px', color: 'var(--text-primary)' }}>
                      {day.emoji} {day.label}
                    </strong>
                  </div>

                  {formData.weeklySchedule[day.key]?.enabled && (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                      gap: '10px',
                      marginLeft: '35px'
                    }}>
                      <div>
                        <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Entrada:</label>
                        <input
                          type="time"
                          value={formData.weeklySchedule[day.key]?.start || ''}
                          onChange={(e) => {
                            const newSchedule = { ...formData.weeklySchedule };
                            newSchedule[day.key] = {
                              ...newSchedule[day.key],
                              start: e.target.value
                            };
                            setFormData({ ...formData, weeklySchedule: newSchedule });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid var(--border)'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Salida:</label>
                        <input
                          type="time"
                          value={formData.weeklySchedule[day.key]?.end || ''}
                          onChange={(e) => {
                            const newSchedule = { ...formData.weeklySchedule };
                            newSchedule[day.key] = {
                              ...newSchedule[day.key],
                              end: e.target.value
                            };
                            setFormData({ ...formData, weeklySchedule: newSchedule });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid var(--border)'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Inicio Colación:</label>
                        <input
                          type="time"
                          value={formData.weeklySchedule[day.key]?.lunchStart || ''}
                          onChange={(e) => {
                            const newSchedule = { ...formData.weeklySchedule };
                            newSchedule[day.key] = {
                              ...newSchedule[day.key],
                              lunchStart: e.target.value
                            };
                            setFormData({ ...formData, weeklySchedule: newSchedule });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid var(--border)'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Fin Colación:</label>
                        <input
                          type="time"
                          value={formData.weeklySchedule[day.key]?.lunchEnd || ''}
                          onChange={(e) => {
                            const newSchedule = { ...formData.weeklySchedule };
                            newSchedule[day.key] = {
                              ...newSchedule[day.key],
                              lunchEnd: e.target.value
                            };
                            setFormData({ ...formData, weeklySchedule: newSchedule });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid var(--border)'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!formData.useFlexibleSchedule && (
            <p style={{ 
              textAlign: 'center', 
              color: 'var(--text-muted)', 
              fontStyle: 'italic',
              margin: '20px 0'
            }}>
              💡 Activa "Horarios por Día" para configurar horarios diferentes cada día de la semana
            </p>
          )}
        </div>

            📄 Información del Contrato
          </h3>
          
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
            <div>
              <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569'}}>
                Tipo de Contrato
              </label>
              <select
                value={formData.contractType || 'indefinido'}
                onChange={(e) => setFormData({...formData, contractType: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="indefinido">Plazo Indefinido</option>
                <option value="plazo_fijo">Plazo Fijo</option>
              </select>
            </div>

            <div>
              <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569'}}>
                Fecha de Inicio
              </label>
              <input
                type="date"
                value={formData.contractStartDate || ''}
                onChange={(e) => setFormData({...formData, contractStartDate: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{marginBottom:'16px'}}>
              <label style={{display:'block', marginBottom:'8px', fontSize:'14px', fontWeight:'500', color:'#475569'}}>
                Fecha Inicio Contrato
              </label>
              <input
                type="date"
                value={formData.hireDate || ''}
                onChange={(e) => setFormData({...formData, hireDate: e.target.value})}
                style={{width:'100%', padding:'10px', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'14px'}}
              />
            </div>
            {formData.contractType === 'plazo_fijo' && (
              <div>
                <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569'}}>
                  Fecha de Término
                </label>
                <input
                  type="date"
                  value={formData.contractEndDate || ''}
                  onChange={(e) => setFormData({...formData, contractEndDate: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Sección de Anticipos */}
        <div style={{marginTop: '24px', padding: '16px', background: 'var(--bg-card)', borderRadius: '8px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
            <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)'}}>
              💰 Anticipos de Sueldo
            </h3>
            <button
              type="button"
              onClick={() => {
                const amount = prompt('Monto del anticipo:');
                if (!amount) return;
                const parsedAmount = parseFloat(amount);
                if (isNaN(parsedAmount) || parsedAmount <= 0) {
                  alert('⚠️ El monto debe ser mayor a $0');
                  return;
                }
                const notes = prompt('Notas (opcional):') || '';
                
                const newAdvance = {
                  id: Date.now(),
                  date: getLocalDateStr(),
                  amount: parsedAmount,
                  status: 'pendiente',
                  notes: notes
                };
                
                const updatedAdvances = [...(formData.advances || []), newAdvance];
                setFormData({...formData, advances: updatedAdvances});
              }}
              style={{
                padding: '8px 16px',
                background: '#10b981',
                color: '#0a0f1e',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              + Registrar Anticipo
            </button>
          </div>

        {/* 🔔 Notificaciones */}
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: 'rgba(245,158,11,0.08)',
          borderRadius: '8px',
          border: '1px solid rgba(245,158,11,0.4)'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={formData.notificationsEnabled || false}
              onChange={(e) => setFormData({
                ...formData,
                notificationsEnabled: e.target.checked
              })}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <div>
              <strong style={{ fontSize: '16px', color: 'var(--orange)' }}>
                🔔 Activar Notificaciones Automáticas
              </strong>
              <p style={{ 
                margin: '5px 0 0 0', 
                fontSize: '13px', 
                color: 'var(--text-muted)' 
              }}>
                Recordatorios para marcar entrada/salida según el horario configurado
              </p>
            </div>
          </label>
        </div>


          {(!formData.advances || formData.advances.length === 0) ? (
            <p style={{textAlign: 'center', color: '#94a3b8', fontSize: '14px', margin: '20px 0'}}>
              No hay anticipos registrados
            </p>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px'}}>
                <thead>
                  <tr style={{borderBottom: '1px solid var(--border)'}}>
                    <th style={{padding: '12px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: '600'}}>Fecha</th>
                    <th style={{padding: '12px', textAlign: 'right', color: 'var(--text-muted)', fontWeight: '600'}}>Monto</th>
                    <th style={{padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: '600'}}>Estado</th>
                    <th style={{padding: '12px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: '600'}}>Notas</th>
                    <th style={{padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: '600'}}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.advances.map((advance) => (
                    <tr key={advance.id} style={{borderBottom: '1px solid var(--border)'}}>
                      <td style={{padding: '12px'}}>{advance.date}</td>
                      <td style={{padding: '12px', textAlign: 'right', fontWeight: '500'}}>
                        ${(advance.amount ?? 0).toLocaleString('es-CL')}
                      </td>
                      <td style={{padding: '12px', textAlign: 'center'}}>
                        <select
                          value={advance.status}
                          onChange={(e) => {
                            const updatedAdvances = formData.advances.map(a =>
                              a.id === advance.id ? {...a, status: e.target.value} : a
                            );
                            setFormData({...formData, advances: updatedAdvances});
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid var(--border)',
                            fontSize: '13px',
                            background: advance.status === 'pagado' ? 'rgba(0,229,160,0.12)' : 
                                       advance.status === 'aprobado' ? 'rgba(59,130,246,0.12)' : 'rgba(245,158,11,0.1)',
                            color: advance.status === 'pagado' ? 'var(--accent)' :
                                   advance.status === 'aprobado' ? 'var(--blue)' : 'var(--orange)'
                          }}
                        >
                          <option value="pendiente">Pendiente</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="pagado">Pagado</option>
                        </select>
                      </td>
                      <td style={{padding: '12px', fontSize: '13px', color: 'var(--text-muted)'}}>
                        {advance.notes || '-'}
                      </td>
                      <td style={{padding: '12px', textAlign: 'center'}}>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('¿Eliminar este anticipo?')) {
                              const updatedAdvances = formData.advances.filter(a => a.id !== advance.id);
                              setFormData({...formData, advances: updatedAdvances});
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(239,68,68,0.1)',
                            color: 'var(--red)',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>


        <div className="modal-actions">
          <button className="btn-cancel" onClick={() => { setShowEmployeeModal(false); setEditingEmployee(null); }}>
            Cancelar
          </button>
          <button 
            className="btn-save"
            onClick={() => {
              if (!formData.name || !formData.position) {
                alert('⚠️ Por favor completa el nombre y cargo del empleado.');
                return;
              }
              // Horario opcional — se puede configurar después
              if (editingEmployee) {
                handleEditEmployee(formData);
              } else {
                handleAddEmployee(formData);
              }
            }}
          >
            <div style={{marginBottom:'16px'}}>
              <label style={{display:'block', marginBottom:'8px', fontSize:'14px', fontWeight:'500', color:'#475569'}}>
                Observaciones
              </label>
              <textarea
                value={formData.observations || ''}
                onChange={(e) => setFormData({...formData, observations: e.target.value})}
                placeholder="Notas internas del empleado (solo visible para gerente/admin)"
                rows="3"
                style={{width:'100%', padding:'10px', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'13px', resize:'vertical', background:'var(--bg-input)', color:'var(--text-primary)'}}
              />
            </div>
                        <Save size={18} />
            {editingEmployee ? 'Actualizar' : 'Agregar'}
          </button>
        </div>
      </div>
    </div>
  );
};


const AbsenceRequestsManager = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const pendingRequests = absenceRequests.filter(r => r.status === 'pending');
  const approvedRequests = absenceRequests.filter(r => r.status === 'approved');
  const rejectedRequests = absenceRequests.filter(r => r.status === 'rejected');
  
  const handleApprove = (requestId) => {
    const updated = absenceRequests.map(r => 
      r.id === requestId ? {...r, status: 'approved'} : r
    );
    setAbsenceRequests(updated);
    localStorage.setItem('absenceRequests', JSON.stringify(updated));
    alert('✅ Solicitud aprobada');
  };
  
  const handleReject = (requestId) => {
    const updated = absenceRequests.map(r => 
      r.id === requestId ? {...r, status: 'rejected'} : r
    );
    setAbsenceRequests(updated);
    localStorage.setItem('absenceRequests', JSON.stringify(updated));
    alert('❌ Solicitud rechazada');
  };
  
  return (
    <div className="modal-overlay" onClick={() => setShowAbsenceRequestsManager(false)}>
      <div className="modal-content" style={{maxWidth: '800px'}} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📋 Gestión de Solicitudes</h2>
          <button onClick={() => setShowAbsenceRequestsManager(false)} className="btn-close">✕</button>
        </div>
        <div className="modal-body">
          {/* HORAS EXTRAS PENDIENTES */}
          {overtimeRequests?.filter(r => r.status === 'pending').length > 0 && (
            <div style={{marginBottom:'24px'}}>
              <h3 style={{color:'var(--orange)', marginBottom:'12px'}}>⏰ Horas Extras Pendientes ({overtimeRequests.filter(r => r.status === 'pending').length})</h3>
              {overtimeRequests.filter(r => r.status === 'pending').map(req => (
                <div key={req.id} style={{border:'1px solid rgba(245,158,11,0.4)', borderRadius:'8px', padding:'16px', marginBottom:'10px', background:'rgba(245,158,11,0.06)'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <div>
                      <h4 style={{margin:'0 0 6px 0', color:'var(--text-primary)'}}>{req.employeeName}</h4>
                      <p style={{margin:'2px 0', fontSize:'13px', color:'var(--text-secondary)'}}>📅 {req.date} — Salida: {req.checkOutTime || '--'}</p>
                      <p style={{margin:'2px 0', fontSize:'13px', color:'var(--orange)', fontWeight:'600'}}>⏱ {req.overtimeMinutes} minutos de horas extras</p>
                    </div>
                    <div style={{display:'flex', gap:'8px'}}>
                      <button
                        onClick={() => {
                          const updated = overtimeRequests.map(r => r.id === req.id ? {...r, status: 'approved'} : r);
                          setOvertimeRequests(updated);
                          localStorage.setItem('overtimeRequests', JSON.stringify(updated));
                          if (isOnline) { const rRef = ref(database, 'overtimeRequests'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); }
                          alert(`✅ Horas extras de ${req.employeeName} aprobadas.`);
                        }}
                        style={{padding:'8px 16px', background:'var(--green)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'600'}}
                      >✅ Aprobar</button>
                      <button
                        onClick={() => {
                          const updated = overtimeRequests.map(r => r.id === req.id ? {...r, status: 'rejected'} : r);
                          setOvertimeRequests(updated);
                          localStorage.setItem('overtimeRequests', JSON.stringify(updated));
                          if (isOnline) { const rRef = ref(database, 'overtimeRequests'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); }
                        }}
                        style={{padding:'8px 16px', background:'rgba(239,68,68,0.15)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'6px', cursor:'pointer', fontWeight:'600'}}
                      >❌ Rechazar</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <h3>⏳ Solicitudes de Ausencia Pendientes ({pendingRequests.length})</h3>
          {pendingRequests.length === 0 ? (
            <p style={{textAlign:'center', color:'var(--text-muted)', padding:'20px'}}>No hay solicitudes pendientes</p>
          ) : (
            <div style={{marginBottom:'30px'}}>
              {pendingRequests.map(req => (
                <div key={req.id} style={{border:'1px solid var(--border)', borderRadius:'8px', padding:'16px', marginBottom:'12px'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'start'}}>
                    <div>
                      <h4 style={{margin:'0 0 8px 0'}}>{req.employeeName}</h4>
                      <p style={{margin:'4px 0'}}>
                        {req.type === 'sick' && '🤒 Licencia Médica'}
                        {req.type === 'vacation' && '🏖️ Vacaciones'}
                        {req.type === 'personal' && '👤 Permiso Personal'}
                        {req.type === 'other' && '📋 Otro'}
                      </p>
                      <p style={{margin:'4px 0'}}>
                  📅 {req.startDate} al {req.endDate}
                  {req.totalDays && (
                    <span style={{marginLeft:'8px', fontWeight:'bold', color:'var(--accent)'}}>
                      ({req.totalDays} días{req.type === 'vacation' ? ' hábiles' : ''})
                    </span>
                  )}
                </p>
                      <p style={{margin:'4px 0', fontStyle:'italic'}}>"{req.reason}"</p>
                    </div>
                    <div style={{display:'flex', gap:'8px'}}>
                      <button 
                        onClick={() => handleApprove(req.id)}
                        style={{padding:'8px 16px', background:'var(--accent)', color:'#0a0f1e', border:'none', borderRadius:'6px', cursor:'pointer'}}
                      >
                        ✅ Aprobar
                      </button>
                      <button 
                        onClick={() => handleReject(req.id)}
                        style={{padding:'8px 16px', background:'rgba(239,68,68,0.15)', color:'var(--red)', border:'1px solid rgba(239,68,68,0.3)', border:'none', borderRadius:'6px', cursor:'pointer'}}
                      >
                        ❌ Rechazar
                      </button>
                      <button 
                        onClick={() => handleDeleteAbsenceRequest(req.id)}
                        style={{padding:'8px 16px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', border:'none', borderRadius:'6px', cursor:'pointer'}}
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <h3>✅ Aprobadas ({approvedRequests.length})</h3>
          {approvedRequests.length === 0 ? (
            <p style={{textAlign:'center', color:'var(--text-muted)', padding:'12px'}}>No hay solicitudes aprobadas</p>
          ) : (
            approvedRequests.map(req => (
              <div key={req.id} style={{border:'1px solid rgba(0,229,160,0.35)', borderRadius:'8px', padding:'12px', marginBottom:'8px', background:'rgba(0,229,160,0.08)', display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <strong>{req.employeeName}</strong>
                  <p style={{margin:'4px 0'}}>
                    {req.type === 'sick' && '🤒 Licencia Médica'}
                    {req.type === 'vacation' && '🏖️ Vacaciones'}
                    {req.type === 'personal' && '👤 Permiso Personal'}
                    {req.type === 'other' && '📋 Otro'}
                  </p>
                  <p style={{margin:'4px 0'}}>
                    📅 {req.startDate} al {req.endDate}
                    {req.totalDays && (
                      <span style={{marginLeft:'8px', fontWeight:'bold', color:'var(--accent)'}}>
                        ({req.totalDays} días{req.type === 'vacation' ? ' hábiles' : ''})
                      </span>
                    )}
                  </p>
                  <p style={{margin:'4px 0', fontSize:'12px', color:'var(--text-muted)'}}>Motivo: {req.reason}</p>
                </div>
                <button 
                  onClick={() => handleDeleteAbsenceRequest(req.id)}
                  style={{padding:'8px 12px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'14px'}}
                >
                  🗑️
                </button>
              </div>
            ))
          )}
          
          <h3 style={{marginTop:'24px'}}>❌ Rechazadas ({rejectedRequests.length})</h3>
          {rejectedRequests.length === 0 ? (
            <p style={{textAlign:'center', color:'var(--text-muted)', padding:'12px'}}>No hay solicitudes rechazadas</p>
          ) : (
            rejectedRequests.map(req => (
              <div key={req.id} style={{border:'1px solid rgba(239,68,68,0.4)', borderRadius:'8px', padding:'12px', marginBottom:'8px', background:'rgba(239,68,68,0.1)', display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <strong>{req.employeeName}</strong>
                  <p style={{margin:'4px 0'}}>
                    {req.type === 'sick' && '🤒 Licencia Médica'}
                    {req.type === 'vacation' && '🏖️ Vacaciones'}
                    {req.type === 'personal' && '👤 Permiso Personal'}
                    {req.type === 'other' && '📋 Otro'}
                  </p>
                  <p style={{margin:'4px 0'}}>
                    📅 {req.startDate} al {req.endDate}
                    {req.totalDays && (
                      <span style={{marginLeft:'8px', fontWeight:'bold', color:'var(--red)'}}>
                        ({req.totalDays} días{req.type === 'vacation' ? ' hábiles' : ''})
                      </span>
                    )}
                  </p>
                  <p style={{margin:'4px 0', fontSize:'12px', color:'var(--text-muted)'}}>Motivo: {req.reason}</p>
                </div>
                <button 
                  onClick={() => handleDeleteAbsenceRequest(req.id)}
                  style={{padding:'8px 12px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'14px'}}
                >
                  🗑️
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};


const PermissionsModal = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const tempPermissions = tempPermissionsState || config.adminPermissions;
  const setTempPermissions = (v) => setTempPermissionsState(typeof v === 'function' ? v(tempPermissionsState || config.adminPermissions) : v);

  return (
    <div className="modal-overlay" onClick={() => setShowPermissionsModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👥 Permisos del Administrador</h2>
          <button className="modal-close" onClick={() => setShowPermissionsModal(false)}>
            <X size={24} />
          </button>
        </div>

        <div className="help-text" style={{marginBottom: '20px'}}>
          Configura qué acciones puede realizar el administrador en el sistema.
        </div>

        {/* Administradores adicionales */}
        <div style={{background:'var(--bg-elevated)', borderRadius:'10px', padding:'14px', marginBottom:'16px', border:'1px solid var(--border)'}}>
          <h4 style={{margin:'0 0 10px', fontSize:'13px', color:'var(--text-primary)', fontWeight:'700'}}>👥 Administradores Adicionales</h4>
          <p style={{margin:'0 0 10px', fontSize:'12px', color:'var(--text-secondary)'}}>Empleados con acceso de administrador temporal (ej: durante vacaciones)</p>
          <div style={{display:'flex', flexDirection:'column', gap:'6px', marginBottom:'10px'}}>
            {(Array.isArray(employees)?employees:[]).filter(e => e.status === 'active' && e.id !== 999 && e.id !== 1000).map(emp => {
              const isExtraAdmin = (config.extraAdmins||[]).includes(`EMP${emp.id}`);
              return (
                <div key={emp.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', background:'var(--bg-card)', borderRadius:'8px'}}>
                  <span style={{fontSize:'13px', color:'var(--text-primary)'}}>{emp.photo} {emp.name} <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>— {emp.position}</span></span>
                  <button
                    onClick={() => {
                      const code = `EMP${emp.id}`;
                      const current = config.extraAdmins || [];
                      const updated = isExtraAdmin ? current.filter(c => c !== code) : [...current, code];
                      saveConfig({...config, extraAdmins: updated});
                      addAuditEntry(isExtraAdmin?'QUITAR_ADMIN_EXTRA':'ASIGNAR_ADMIN_EXTRA', `${isExtraAdmin?'Quitó':'Asignó'} rol admin extra a ${emp.name}`);
                    }}
                    style={{padding:'4px 12px', background:isExtraAdmin?'rgba(239,68,68,0.15)':'rgba(34,197,94,0.15)', color:isExtraAdmin?'#ef4444':'var(--green)', border:`1px solid ${isExtraAdmin?'rgba(239,68,68,0.4)':'rgba(34,197,94,0.4)'}`, borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}
                  >
                    {isExtraAdmin ? '🔴 Quitar Admin' : '🟢 Dar Admin'}
                  </button>
                </div>
              );
            })}
          </div>
          {(config.extraAdmins||[]).length > 0 && (
            <p style={{margin:0, fontSize:'11px', color:'var(--orange)'}}>⚠️ Los admins adicionales usan su código EMP para entrar</p>
          )}
        </div>

        <div className="permissions-grid">
          <label className="permission-item">
            <input
              type="checkbox"
              checked={tempPermissions.canAddEmployees}
              onChange={(e) => setTempPermissions({
                ...tempPermissions,
                canAddEmployees: e.target.checked
              })}
            />
            <div className="permission-info">
              <strong>Agregar Empleados</strong>
              <p>Permite crear nuevos empleados</p>
            </div>
          </label>

          <label className="permission-item">
            <input
              type="checkbox"
              checked={tempPermissions.canEditEmployees}
              onChange={(e) => setTempPermissions({
                ...tempPermissions,
                canEditEmployees: e.target.checked
              })}
            />
            <div className="permission-info">
              <strong>Editar Empleados</strong>
              <p>Permite modificar datos de empleados</p>
            </div>
          </label>

          <label className="permission-item">
            <input
              type="checkbox"
              checked={tempPermissions.canDeleteEmployees}
              onChange={(e) => setTempPermissions({
                ...tempPermissions,
                canDeleteEmployees: e.target.checked
              })}
            />
            <div className="permission-info">
              <strong>Eliminar Empleados</strong>
              <p>Permite eliminar empleados del sistema</p>
            </div>
          </label>

          <label className="permission-item">
            <input
              type="checkbox"
              checked={tempPermissions.canExportData}
              onChange={(e) => setTempPermissions({
                ...tempPermissions,
                canExportData: e.target.checked
              })}
            />
            <div className="permission-info">
              <strong>Exportar Datos</strong>
              <p>Permite exportar registros a CSV</p>
            </div>
          </label>

          <label className="permission-item permission-critical">
            <input
              type="checkbox"
              checked={tempPermissions.canChangeSchedules}
              onChange={(e) => setTempPermissions({
                ...tempPermissions,
                canChangeSchedules: e.target.checked
              })}
            />
            <div className="permission-info">
              <strong>⚠️ Cambiar Horarios</strong>
              <p>Permite modificar horarios laborales</p>
            </div>
          </label>

          <label className="permission-item permission-critical">
            <input
              type="checkbox"
              checked={tempPermissions.canChangeGPS}
              onChange={(e) => setTempPermissions({
                ...tempPermissions,
                canChangeGPS: e.target.checked
              })}
            />
            <div className="permission-info">
              <strong>⚠️ Cambiar Ubicación GPS</strong>
              <p>Permite modificar coordenadas y radio</p>
            </div>
          </label>
        </div>

        <div className="modal-actions">
          <button className="btn-cancel" onClick={() => setShowPermissionsModal(false)}>
            Cancelar
          </button>
          <button 
            className="btn-save"
            onClick={() => {
              const newConfig = { ...config, adminPermissions: tempPermissions };
              saveConfig(newConfig);
              setShowPermissionsModal(false);
              alert('✅ Permisos actualizados exitosamente');
            }}
          >
            <Save size={18} />
            Guardar Permisos
          </button>
        </div>
      </div>
    </div>
  );
};


const AdminRegistrationModal = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const adminTodayRecord = attendanceRecords.find(
    r => r.employeeId === currentUser.id && r.date === getLocalDateStr()
  );
  
  const adminCheckedIn = adminRegCheckedIn || !!adminTodayRecord?.checkIn;
  const setAdminCheckedIn = setAdminRegCheckedIn;
  const adminOnBreak = adminRegOnBreak || (!!adminTodayRecord?.breakStart && !adminTodayRecord?.breakEnd);
  const setAdminOnBreak = setAdminRegOnBreak;

  const handleAdminCheckIn = () => {
    // Validación GPS desactivada temporalmente
    
    const now = new Date();
    // Formato compatible con todos los navegadores incluyendo Safari/Chrome iOS
    const _nowH = now.getHours(), _nowM = String(now.getMinutes()).padStart(2,'0');
    const timeString = `${String(_nowH%12||12).padStart(2,'0')}:${_nowM} ${_nowH<12?'a. m.':'p. m.'}`;
    const scheduledTime = new Date();
    // Usar horario mensual del empleado si existe, sino usar config global
    const todayStr2 = getLocalDateStr(now);
    const empScheduleHoy = currentUser?.monthlySchedule?.[todayStr2];
    if (empScheduleHoy?.start && empScheduleHoy?.enabled) {
      const [startH, startM] = empScheduleHoy.start.split(':').map(Number);
      scheduledTime.setHours(startH, startM, 0, 0);
    } else {
      scheduledTime.setHours(config.schedules.checkIn.hour, config.schedules.checkIn.minute, 0, 0);
    }
    // Margen de 1 minuto para evitar falsos positivos por segundos
    const isLate = now > new Date(scheduledTime.getTime() + 60000);
    
    const newRecord = {
      id: attendanceRecords.length + 1,
      employeeId: currentUser.id,
      employeeName: currentUser.name,
      date: getLocalDateStr(now),
      checkIn: timeString,
      checkOut: "",
      breakStart: "",
      breakEnd: "",
      status: isLate ? 'late' : 'on_time',
      location: 'verified',
      lateBreakReturn: false,
      earlyCheckOut: false
    };
    
    const updatedRecords = [newRecord, ...attendanceRecords];
    setAttendanceRecords(updatedRecords);
    localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
    setAdminCheckedIn(true);
    
    if (isLate && config.alerts.lateArrival.enabled) {
      alert(config.alerts.lateArrival.message);
    } else {
      alert('✅ Entrada registrada exitosamente');
    }
  };

  const handleAdminCheckOut = () => {
    if (!adminTodayRecord) return;
    
    const now = new Date();
    // Formato compatible con todos los navegadores incluyendo Safari/Chrome iOS
    const _nowH = now.getHours(), _nowM = String(now.getMinutes()).padStart(2,'0');
    const timeString = `${String(_nowH%12||12).padStart(2,'0')}:${_nowM} ${_nowH<12?'a. m.':'p. m.'}`;
    
    const scheduledCheckOut = new Date();
    scheduledCheckOut.setHours(config.schedules.checkOut.hour, config.schedules.checkOut.minute, 0);
    const earlyCheckOut = now < scheduledCheckOut;
    
    const updatedRecords = attendanceRecords.map(record => 
      record.id === adminTodayRecord.id 
        ? { ...record, checkOut: timeString, earlyCheckOut }
        : record
    );
    
    setAttendanceRecords(updatedRecords);
    localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
    setAdminCheckedIn(false);
    
    if (earlyCheckOut && config.alerts.earlyDeparture.enabled) {
      alert(config.alerts.earlyDeparture.message);
    } else {
      alert('✅ Salida registrada exitosamente');
    }
  };

  const handleAdminBreakToggle = () => {
    if (!adminTodayRecord) return;
    
    const now = new Date();
    // Formato compatible con todos los navegadores incluyendo Safari/Chrome iOS
    const _nowH = now.getHours(), _nowM = String(now.getMinutes()).padStart(2,'0');
    const timeString = `${String(_nowH%12||12).padStart(2,'0')}:${_nowM} ${_nowH<12?'a. m.':'p. m.'}`;
    
    let lateBreakReturn = false;
    if (adminOnBreak) {
      const scheduledBreakEnd = new Date();
      scheduledBreakEnd.setHours(config.schedules.breakEnd.hour, config.schedules.breakEnd.minute, 0);
      if (now > scheduledBreakEnd) {
        lateBreakReturn = true;
      }
    }
    
    const updatedRecords = attendanceRecords.map(record => 
      record.id === adminTodayRecord.id 
        ? { 
            ...record, 
            breakStart: !adminOnBreak ? timeString : record.breakStart,
            breakEnd: adminOnBreak ? timeString : record.breakEnd,
            lateBreakReturn: lateBreakReturn || record.lateBreakReturn
          }
        : record
    );
    
    setAttendanceRecords(updatedRecords);
    localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
    setAdminOnBreak(!adminOnBreak);
    
    if (!adminOnBreak) {
      alert('☕ Colación iniciada');
    } else {
      if (lateBreakReturn && config.alerts.lateBreakReturn.enabled) {
        alert(config.alerts.lateBreakReturn.message);
      } else {
        alert('✅ Colación finalizada');
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={() => setShowAdminRegistration(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⏰ Mi Registro de Asistencia</h2>
          <button className="modal-close" onClick={() => setShowAdminRegistration(false)}>
            <X size={24} />
          </button>
        </div>

        <div className="admin-reg-content">
          <div className="location-card">
            <div className="location-header">
              <MapPin size={20} />
              <span>Verificación de Ubicación GPS</span>
            </div>
            <div className="location-status">
              {isInWorkZone ? (
                <>
                  <CheckCircle size={24} color="#10b981" />
                  <span className="location-text verified">En zona de trabajo ✓</span>
                </>
              ) : (
                <>
                  <AlertCircle size={24} color="#f59e0b" />
                  <span className="location-text warning">Fuera de zona ⚠️</span>
                </>
              )}
            </div>
            <p className="location-details">Radio permitido: {config.workLocation.radius} metros</p>
          </div>

          <div className="action-buttons">
            {!adminCheckedIn ? (
              <button className="btn-primary" onClick={handleAdminCheckIn}>
                <Clock size={24} />
                <span>Registrar Entrada</span>
              </button>
            ) : (
              <>
                <button 
                  className={`btn-break ${adminOnBreak ? 'active' : ''}`}
                  onClick={handleAdminBreakToggle}
                >
                  <Coffee size={24} />
                  <span>{adminOnBreak ? 'Terminar Colación' : 'Iniciar Colación'}</span>
                </button>
                
                <button className="btn-checkout" onClick={handleAdminCheckOut}>
                  <LogOut size={24} />
                  <span>Registrar Salida</span>
                </button>
              </>
            )}
          </div>

          {adminTodayRecord && (
            <div className="today-summary">
              <h3>Resumen de Hoy</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <Clock size={18} />
                  <div>
                    <p className="label">Entrada</p>
                    <p className="value">{adminTodayRecord.checkIn || '--:--'}</p>
                  </div>
                </div>
                <div className="summary-item">
                  <LogOut size={18} />
                  <div>
                    <p className="label">Salida</p>
                    <p className="value">{adminTodayRecord.checkOut || '--:--'}</p>
                  </div>
                </div>
              </div>
              {adminTodayRecord.breakStart && (
                <div className="break-info">
                  ☕ Colación: {adminTodayRecord.breakStart} - {adminTodayRecord.breakEnd || 'En curso'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const EmployeeHomeView = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  return (
    <div className="employee-home">
      {/* Reloj en vivo */}
      <LiveClock />
      
      {/* Notificación de alarma flotante */}
      {showAlarmNotification && (
        <div className={`alarm-notification ${minutesUntilCheckout !== null && minutesUntilCheckout <= 0 ? 'urgent' : ''}`}>
          <div className="alarm-icon">🔔</div>
          <div className="alarm-content">
            <div className="alarm-message">{alarmMessage}</div>
            {minutesUntilCheckout !== null && minutesUntilCheckout > 0 && (
              <div className="alarm-countdown">
                Tiempo restante: <strong>{minutesUntilCheckout} minuto{minutesUntilCheckout !== 1 ? 's' : ''}</strong>
              </div>
            )}
          </div>
          <button 
            className="alarm-close" 
            onClick={(e) => { 
              e.preventDefault();
              e.stopPropagation(); 
              setShowAlarmNotification(false); 
              setAlarmMessage(""); 
              console.log("✕ Alerta cerrada"); 
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowAlarmNotification(false);
              setAlarmMessage("");
              console.log("✕ Alerta cerrada (touch)");
            }}
            style={{
              cursor:'pointer', 
              zIndex:9999, 
              pointerEvents:'auto',
              minWidth:'44px',
              minHeight:'44px',
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              fontSize:'24px',
              background:'rgba(255,255,255,0.2)',
              border:'none',
              borderRadius:'50%',
              padding:'0'
            }}
          >
            ✕
          </button>
      </div>
    )}
    
    {(() => {
      try {
        const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
        const misTareas = lsTasks.filter(t => String(t?.assignedTo) === String(currentUser?.id));
        const vencidas = misTareas.filter(t => t?.status === 'overdue').length;
        const hoy = new Date();
        const dateStr = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
        const pendientesHoy = misTareas.filter(t => t?.status === 'pending' && t?.dueDate === dateStr).length;

        let mensaje = null;
        let color = 'var(--blue)';
        let bg = 'rgba(59,130,246,0.1)';

        if (vencidas > 0) {
          mensaje = `Tienes ${vencidas} tarea${vencidas > 1 ? 's' : ''} vencida${vencidas > 1 ? 's' : ''} sin completar`;
          color = '#ef4444'; bg = 'rgba(239,68,68,0.1)';
        } else if (pendientesHoy > 0) {
          mensaje = `Tienes ${pendientesHoy} tarea${pendientesHoy > 1 ? 's' : ''} pendiente${pendientesHoy > 1 ? 's' : ''} para hoy`;
          color = 'var(--orange)'; bg = 'rgba(245,158,11,0.1)';
        } else if (misTareas.length > 0) {
          mensaje = 'No tienes tareas pendientes hoy. Buen trabajo';
          color = 'var(--green)'; bg = 'rgba(34,197,94,0.1)';
        }

        if (!mensaje) return null;
        return (
          <div style={{background: bg, border: `1px solid ${color}33`, borderRadius:'10px', padding:'10px 14px', marginBottom:'12px', fontSize:'13px', fontWeight:'600', color: color}}>
            {mensaje}
          </div>
        );
      } catch(e) { return null; }
    })()}
    <div className="status-card">
      <div className="status-header">
        <div className="user-info">
          <div className="avatar">{currentUser.photo || '👤'}</div>
          <div>
            <h2>{currentUser.name}</h2>
            <p className="position">{currentUser.position}</p>
          </div>
        </div>
        <div className={`status-badge ${isCheckedIn ? 'active' : 'inactive'}`}>
          {isCheckedIn ? '🟢 Activo' : '⚪ Inactivo'}
        </div>
        
        {/* Badge de atraso integrado */}
        {todayRecord?.isLate && (
          <div style={{
            background: '#ef4444',
            color: '#0a0f1e',
            padding: '6px 14px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
            marginLeft: '10px'
          }}>
            🔴 Atraso: {todayRecord.lateMinutes} min
          </div>
        )}
        {todayRecord && !todayRecord.isLate && isCheckedIn && (
          <div style={{
            background: '#10b981',
            color: '#0a0f1e',
            padding: '6px 14px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
            marginLeft: '10px'
          }}>
            ✅ A tiempo
          </div>
        )}
      </div>
      
      <div className="date-display">
        <Calendar size={18} />
        <span>{new Date().toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
      </div>
    </div>

    <div className="location-card">
      <div className="location-header">
        <MapPin size={20} />
        <span>Verificación de Ubicación GPS</span>
      </div>
      <div className="location-status">
        {isInWorkZone ? (
          <>
            <CheckCircle size={24} color="#10b981" />
            <span className="location-text verified">En zona de trabajo ✓</span>
          </>
        ) : (
          <>
            <AlertCircle size={24} color="#f59e0b" />
            <span className="location-text warning">Fuera de zona ⚠️</span>
          </>
        )}
      </div>
      <p className="location-details">Radio permitido: {config.workLocation.radius} metros</p>
      {currentLocation && (
        <p className="location-coords">
          📍 Tu ubicación: {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
        </p>
      )}
    </div>

    {/* ── BANNER DE ANUNCIOS ── */}
    {(() => {
      const now = new Date();
      const activeAnns = (Array.isArray(announcements)?announcements:[]).filter(a => !a.expiresAt || new Date(a.expiresAt) > now);
      if (activeAnns.length === 0) return null;
      const colors = {
        warning: {bg:'rgba(239,68,68,0.12)', border:'rgba(239,68,68,0.4)', icon:'⚠️', color:'#ef4444'},
        reminder: {bg:'rgba(245,158,11,0.12)', border:'rgba(245,158,11,0.4)', icon:'📋', color:'#f59e0b'},
        success: {bg:'rgba(34,197,94,0.12)', border:'rgba(34,197,94,0.4)', icon:'🎉', color:'#22c55e'},
        info: {bg:'rgba(59,130,246,0.12)', border:'rgba(59,130,246,0.4)', icon:'📢', color:'#3b82f6'},
      };
      return (
        <div style={{marginBottom:'16px', display:'flex', flexDirection:'column', gap:'8px'}}>
          {activeAnns.map(ann => {
            const c = colors[ann.type] || colors.info;
            const daysLeft = Math.ceil((new Date(ann.expiresAt) - now) / 86400000);
            return (
              <div key={ann.id} style={{background:c.bg, border:`1px solid ${c.border}`, borderRadius:'12px', padding:'12px 16px'}}>
                <div style={{display:'flex', alignItems:'flex-start', gap:'10px'}}>
                  <span style={{fontSize:'20px', lineHeight:1}}>{c.icon}</span>
                  <div style={{flex:1}}>
                    <p style={{margin:0, fontSize:'14px', color:'var(--text-primary)', fontWeight:'600', lineHeight:1.4}}>{ann.text}</p>
                    <p style={{margin:'4px 0 0', fontSize:'11px', color:'var(--text-secondary)'}}>
                      {ann.createdBy} · {daysLeft === 1 ? 'Expira hoy' : `${daysLeft} días restantes`}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    })()}

    <div className="action-buttons">
      {!isCheckedIn ? (
        <button className="btn-primary" onClick={handleCheckIn}>
          <Clock size={24} />
          <span>Registrar Entrada</span>
        </button>
      ) : (
        <>
          {todayRecord?.breakStart && todayRecord?.breakEnd ? (
            // Colación completada - mostrar resumen
            <div style={{background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'12px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'10px'}}>
              <Coffee size={20} color="var(--green)" />
              <div>
                <p style={{margin:0, fontSize:'12px', color:'var(--text-secondary)', fontWeight:'600'}}>COLACIÓN COMPLETADA</p>
                <p style={{margin:0, fontSize:'14px', color:'var(--green)', fontWeight:'700'}}>
                  {todayRecord.breakStart} — {todayRecord.breakEnd}
                  {(() => {
                    const parseT = (str) => {
                      if (!str) return null;
                      const t2 = str.replace(/[^0-9:]/g,'').trim();
                      const [h2] = t2.split(':').map(Number);
                      const isPM = /p[\s.]*m/i.test(str) && h2 !== 12;
                      const isAM12 = /a[\s.]*m/i.test(str) && h2 === 12;
                      const t = str.replace(/[^0-9:]/g,'');
                      const [h,m] = t.split(':').map(Number);
                      if(isNaN(h)||isNaN(m)) return null;
                      return (isPM ? h+12 : h)*60+m;
                    };
                    const start = parseT(todayRecord.breakStart);
                    const end = parseT(todayRecord.breakEnd);
                    if(start && end) {
                      const diff = end - start;
                      const h = Math.floor(diff/60);
                      const m = diff%60;
                      return ` (${h>0?h+'h ':''}${m}min)`;
                    }
                    return '';
                  })()}
                </p>
              </div>
            </div>
          ) : (
            <button 
              className={`btn-break ${isOnBreak ? 'active' : ''}`}
              onClick={handleBreakToggle}
            >
              <Coffee size={24} />
              <span>{isOnBreak ? 'Terminar Colación' : 'Iniciar Colación'}</span>
            </button>
          )}
          
          <button className="btn-checkout" onClick={handleCheckOut}>
            <LogOut size={24} />
            <span>Registrar Salida</span>
          </button>
        </>
      )}
    </div>

    {todayRecord && (
      <div className="today-summary">
        <h3>Resumen de Hoy</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <Clock size={18} />
            <div>
              <p className="label">Entrada</p>
              <p className="value">{todayRecord.checkIn}</p>
            </div>
          </div>
          <div className="summary-item">
            <LogOut size={18} />
            <div>
              <p className="label">Salida</p>
              <p className="value">{todayRecord.checkOut || '--:--'}</p>
            </div>
          </div>
        </div>

        {/* Horas trabajadas hoy + contador regresivo */}
        {(() => {
          const parseT = (str) => {
            if (!str) return null;
            const _t = str.replace(/[^0-9:]/g,'').trim();
            const _parts = _t.split(':').map(Number);
            const _h = _parts[0], _m = _parts[1];
            if(isNaN(_h)||isNaN(_m)) return null;
            const _isPM = /p[\s.]*m/i.test(str) && _h !== 12;
            const _isMid = /a[\s.]*m/i.test(str) && _h === 12;
            const _hour = _isPM ? _h+12 : _isMid ? 0 : _h;
            return _hour*60+_m;
          };

          const now = new Date();
          const nowMin = now.getHours()*60 + now.getMinutes();
          const inMin = parseT(todayRecord.checkIn);
          if (!inMin) return null;

          // Horas trabajadas: nunca puede exceder las horas desde entrada hasta ahora
          const endMin = todayRecord.checkOut ? parseT(todayRecord.checkOut) : nowMin;
          // Validar que endMin sea mayor que inMin (misma sesión del día)
          let workedMin = 0;
          if (endMin >= inMin) {
            workedMin = endMin - inMin;
          } else {
            // Caso raro: cruce de medianoche — limitar a 16h máximo
            workedMin = Math.min(endMin + 1440 - inMin, 960);
          }
          // Limitar a máximo 16 horas para detectar datos corruptos
          workedMin = Math.min(workedMin, 960);

          // Restar colación si está completa
          if (todayRecord.breakStart && todayRecord.breakEnd) {
            const bs = parseT(todayRecord.breakStart);
            const be = parseT(todayRecord.breakEnd);
            if (bs && be) workedMin -= Math.max(0, be > bs ? be - bs : be + 1440 - bs);
          }
          workedMin = Math.max(0, workedMin);
          const workedH = Math.floor(workedMin/60);
          const workedM = workedMin % 60;

          // Contador regresivo hasta salida
          const schedule = getTodaySchedule(currentUser?.id);
          const exitMin = schedule ? schedule.checkOut.hour*60 + schedule.checkOut.minute : null;
          const remainingMin = exitMin ? Math.max(0, exitMin - nowMin) : null;
          const remainH = remainingMin !== null ? Math.floor(remainingMin/60) : null;
          const remainM = remainingMin !== null ? remainingMin % 60 : null;
          const isOvertime = exitMin && nowMin > exitMin && !todayRecord.checkOut;

          return (
            <div style={{marginTop:'12px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
              {/* Horas trabajadas */}
              <div style={{background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'10px', padding:'12px', textAlign:'center'}}>
                <div style={{fontSize:'11px', color:'var(--green)', fontWeight:'700', marginBottom:'4px', textTransform:'uppercase'}}>⏱ Horas Trabajadas</div>
                <div style={{fontSize:'24px', fontWeight:'900', color:'var(--green)'}}>{workedH}h {String(workedM).padStart(2,'0')}m</div>
                <div style={{fontSize:'10px', color:'var(--text-secondary)', marginTop:'2px'}}>{todayRecord.checkOut ? 'jornada completada' : 'hasta ahora'}</div>
              </div>

              {/* Contador regresivo */}
              {!todayRecord.checkOut && exitMin && (
                <div style={{background: isOvertime ? 'rgba(239,68,68,0.1)' : remainingMin && remainingMin <= 30 ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)', border:`1px solid ${isOvertime ? 'rgba(239,68,68,0.3)' : remainingMin && remainingMin <= 30 ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)'}`, borderRadius:'10px', padding:'12px', textAlign:'center'}}>
                  <div style={{fontSize:'11px', color: isOvertime ? '#ef4444' : remainingMin && remainingMin <= 30 ? 'var(--orange)' : 'var(--blue)', fontWeight:'700', marginBottom:'4px', textTransform:'uppercase'}}>
                    {isOvertime ? '🔴 Hora Extra' : '⏳ Para Salida'}
                  </div>
                  <div style={{fontSize:'24px', fontWeight:'900', color: isOvertime ? '#ef4444' : remainingMin && remainingMin <= 30 ? 'var(--orange)' : 'var(--blue)'}}>
                    {isOvertime ? `+${Math.floor((nowMin-exitMin)/60)}h ${String((nowMin-exitMin)%60).padStart(2,'0')}m` : `${remainH}h ${String(remainM).padStart(2,'0')}m`}
                  </div>
                  <div style={{fontSize:'10px', color:'var(--text-secondary)', marginTop:'2px'}}>
                    {isOvertime ? 'pasada tu hora de salida' : `salida: ${String(Math.floor(exitMin/60)).padStart(2,'0')}:${String(exitMin%60).padStart(2,'0')}`}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    )}
    
    {/* ── SCORE DE DESEMPEÑO PERSONAL ── */}
    {(() => {
      const _now = new Date();
      const monthStr = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}`;
      const empTasks = (Array.isArray(tasks)?tasks:[]).filter(t => String(t.assignedTo) === String(currentUser?.id) && (t.dueDate||'').startsWith(monthStr));
      const done = empTasks.filter(t => t.status === 'done').length;
      const total = empTasks.length;
      const approved = empTasks.filter(t => t.reviewStatus === 'approved').length;
      const taskRate = total > 0 ? Math.round((done/total)*100) : 100;
      const approvalRate = done > 0 ? Math.round((approved/done)*100) : 100;
      const empRecs = (Array.isArray(attendanceRecords)?attendanceRecords:[]).filter(r => r.employeeId === currentUser?.id && r.date?.startsWith(monthStr));
      const lateC = empRecs.filter(r => r.isLate).length;
      const punctRate = empRecs.length > 0 ? Math.round(((empRecs.length-lateC)/empRecs.length)*100) : 100;
      const prodsRep = (Array.isArray(missingProducts)?missingProducts:[]).filter(p => String(p.reportedById) === String(currentUser?.id)).length;
      const score = Math.round((taskRate*0.4)+(punctRate*0.3)+(approvalRate*0.2)+(Math.min(prodsRep*10,100)*0.1));
      const prevScore = parseInt(localStorage.getItem(`prevScore_${currentUser?.id}_${monthStr}`) || '0');
      const scoreLabel = score>=90?'Excelente 🌟':score>=75?'Bueno 👍':score>=60?'Regular 📈':'Necesita mejorar 💪';
      const scoreColor = score>=90?'var(--green)':score>=75?'var(--blue)':score>=60?'var(--orange)':'#ef4444';
      const trend = score > prevScore ? '▲' : score < prevScore ? '▼' : '●';
      const trendColor = score > prevScore ? 'var(--green)' : score < prevScore ? '#ef4444' : 'var(--text-muted)';
      if (total === 0 && empRecs.length === 0) return null;
      return (
        <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'16px', marginBottom:'16px', border:`1px solid ${scoreColor}30`}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px'}}>
            <div>
              <p style={{margin:0, fontSize:'11px', color:'var(--text-secondary)', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.5px'}}>Mi Desempeño — {new Date(monthStr+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'})}</p>
              <p style={{margin:'2px 0 0', fontSize:'13px', color:scoreColor, fontWeight:'700'}}>{scoreLabel}</p>
            </div>
            <div style={{textAlign:'right'}}>
              <span style={{fontSize:'32px', fontWeight:'900', color:scoreColor}}>{score}</span>
              <span style={{fontSize:'12px', color:'var(--text-muted)'}}>/100</span>
              {prevScore > 0 && <div style={{fontSize:'11px', color:trendColor, fontWeight:'700'}}>{trend} {Math.abs(score-prevScore)}pts vs mes anterior</div>}
            </div>
          </div>
          {/* Barra de progreso */}
          <div style={{height:'6px', background:'var(--bg-elevated)', borderRadius:'3px', overflow:'hidden', marginBottom:'10px'}}>
            <div style={{height:'100%', width:`${score}%`, background:scoreColor, borderRadius:'3px', transition:'width 0.6s ease'}} />
          </div>
          {/* Desglose de métricas */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'6px'}}>
            {[
              {label:'Tareas completadas', value:`${taskRate}%`, weight:'40%', color:'var(--green)'},
              {label:'Puntualidad', value:`${punctRate}%`, weight:'30%', color:'var(--blue)'},
              {label:'Calidad (aprobadas)', value:`${approvalRate}%`, weight:'20%', color:'var(--orange)'},
              {label:'Productos reportados', value:prodsRep, weight:'10%', color:'#a78bfa'},
            ].map((m,i)=>(
              <div key={i} style={{background:'var(--bg-elevated)', borderRadius:'8px', padding:'8px 10px'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>{m.label}</span>
                  <span style={{fontSize:'12px', fontWeight:'700', color:m.color}}>{m.value}</span>
                </div>
                <div style={{fontSize:'9px', color:'var(--text-muted)', marginTop:'2px'}}>Peso: {m.weight}</div>
              </div>
            ))}
          </div>
          <p style={{margin:'10px 0 0', fontSize:'11px', color:'var(--text-muted)', textAlign:'center'}}>
            {score>=90?'¡Excelente trabajo este mes! Sigue así 🎉':score>=75?'Buen desempeño, puedes seguir mejorando 💪':score>=60?'Hay oportunidades de mejora, enfócate en la puntualidad y tareas':'Comunícate con tu gerente para mejorar tu desempeño'}
          </p>
        </div>
      );
    })()}

    {/* Notificaciones de incidencias clasificadas */}
    {(() => {
      const myIssues = (Array.isArray(attendanceIssues)?attendanceIssues:[]).filter(i => i.employeeId === currentUser?.id && i.notifiedEmployee && i.status !== 'pending' && i.status !== 'dismissed');
      if (myIssues.length === 0) return null;
      return (
        <div style={{marginBottom:'16px', display:'flex', flexDirection:'column', gap:'8px'}}>
          {myIssues.slice(0,3).map(issue => (
            <div key={issue.id} style={{background: issue.status==='unjustified'?'rgba(239,68,68,0.1)':issue.status==='justified'?'rgba(34,197,94,0.1)':'rgba(59,130,246,0.1)', border:`1px solid ${issue.status==='unjustified'?'rgba(239,68,68,0.3)':issue.status==='justified'?'rgba(34,197,94,0.3)':'rgba(59,130,246,0.3)'}`, borderRadius:'10px', padding:'12px'}}>
              <p style={{margin:0, fontSize:'13px', fontWeight:'700', color:issue.status==='unjustified'?'#ef4444':issue.status==='justified'?'var(--green)':'var(--blue)'}}>
                {issue.status==='unjustified'?'🔴 Falta Injustificada':issue.status==='justified'?'🟢 Falta Justificada':'⚙️ Error de Horario'}
              </p>
              <p style={{margin:'4px 0 0', fontSize:'12px', color:'var(--text-secondary)'}}>
                📅 {issue.date} — {issue.type==='absence'?'No registraste entrada':'Registro incompleto: '+issue.missing?.join(', ')}
              </p>
              {issue.reason && <p style={{margin:'4px 0 0', fontSize:'12px', color:'var(--text-secondary)'}}>💬 {issue.reason}</p>}
              <p style={{margin:'4px 0 0', fontSize:'11px', color:'var(--text-muted)'}}>Clasificado por {issue.classifiedBy}</p>
            </div>
          ))}
        </div>
      );
    })()}

    {/* Botón de solicitar ausencia */}
    <div style={{display:'flex', gap:'12px', flexWrap:'wrap'}}>
      <button 
        className="btn-request-absence"
        onClick={() => setShowAbsenceModal(true)}
      >
        📝 Solicitar Permiso/Vacaciones
      </button>
      <button 
        className="btn-request-absence"
        style={{background:'#2196F3'}}
        onClick={() => setShowEmployeeRequestsModal(true)}
      >
        📋 Ver Mis Solicitudes
      </button>
    </div>
    
    {/* Mis solicitudes pendientes */}
    {absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'pending').length > 0 && (
      <div className="pending-requests-alert">
        ⏳ Tienes {absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'pending').length} solicitud(es) pendiente(s) de aprobación
      </div>
    )}
  </div>
  );
};


const EmployeeHistoryView = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const [expandedHistoryMonths, setExpandedHistoryMonths] = React.useState({});

  const allUserRecords = (Array.isArray(attendanceRecords) ? attendanceRecords : [])
    .filter(r => r.employeeId === currentUser.id)
    .sort((a,b) => (b.date||'').localeCompare(a.date||''));

  const userRecords = allUserRecords.filter(r => r.date?.startsWith(currentMonthStr));

  // Calcular horas trabajadas del día
  const calcHours = (r) => {
    if (!r.checkIn || !r.checkOut) return '--';
    const parseT = (str) => {
      if (!str) return null;
      const _t = str.replace(/[^0-9:]/g,'').trim();
      const _parts = _t.split(':').map(Number);
      const _h2 = _parts[0];
      const isPM = /p[\s.]*m/i.test(str) && _h2 !== 12;
      const isNoon = _h2 === 12 && /p[\s.]*m/i.test(str);
      const isMidnight = _h2 === 12 && /a[\s.]*m/i.test(str);
      const t = str.replace(/[^0-9:]/g,'').trim();
      const [h,m] = t.split(':').map(Number);
      if(isNaN(h)||isNaN(m)) return null;
      let hour = h;
      if(isPM||isNoon) hour = h===12?12:h+12;
      return hour*60+m;
    };
    const inMin = parseT(r.checkIn), outMin = parseT(r.checkOut);
    if(!inMin||!outMin) return '--';
    let gross = outMin>inMin ? outMin-inMin : outMin+1440-inMin;
    if(r.breakStart&&r.breakEnd){const bs=parseT(r.breakStart),be=parseT(r.breakEnd);if(bs&&be)gross-=(be>bs?be-bs:be+1440-bs);}
    gross -= (r.lateMinutes||0);
    gross = Math.max(0,gross);
    return `${Math.floor(gross/60)}h ${String(gross%60).padStart(2,'0')}min`;
  };

  // Stats del mes
  const currentMonthRecords = allUserRecords.filter(r => r.date?.startsWith(currentMonthStr));
  const totalDays = currentMonthRecords.length;
  const lateDays = currentMonthRecords.filter(r => r.isLate).length;
  const totalLateMin = currentMonthRecords.reduce((s,r) => s+(r.lateMinutes||0), 0);
  const punctuality = totalDays > 0 ? Math.round(((totalDays-lateDays)/totalDays)*100) : 100;

  return (
    <div className="history-view">
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'8px'}}>
        <h2 style={{margin:0}}>📅 Mi Historial de Asistencia</h2>
        <span style={{fontSize:'13px', color:'var(--text-secondary)', fontWeight:'600'}}>
          {new Date(currentMonthStr+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'})}
        </span>
      </div>

      {/* Stats del mes */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'20px'}}>
        {[
          {label:'Días trabajados', value:totalDays, color:'var(--blue)'},
          {label:'Días con atraso', value:lateDays, color:'#ef4444'},
          {label:'Min. atraso total', value:`${totalLateMin}`, color:'var(--orange)'},
          {label:'Puntualidad', value:`${punctuality}%`, color: punctuality>=90?'var(--green)':punctuality>=70?'var(--orange)':'#ef4444'},
        ].map((s,i) => (
          <div key={i} style={{background:'var(--bg-card)', borderRadius:'10px', padding:'12px', border:`1px solid ${s.color}30`, textAlign:'center'}}>
            <div style={{fontSize:'22px', fontWeight:'900', color:s.color}}>{s.value}</div>
            <div style={{fontSize:'10px', color:'var(--text-secondary)', marginTop:'4px'}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Lista de registros - mes actual */}
      {allUserRecords.filter(r => r.date?.startsWith(currentMonthStr)).length === 0 ? (
        <div style={{textAlign:'center', padding:'40px', color:'var(--text-muted)'}}>Sin registros este mes</div>
      ) : (
        <div className="records-list">
          {allUserRecords.filter(r => r.date?.startsWith(currentMonthStr)).map(record => (
            <div key={record.id} className="record-card" style={{borderLeft:`3px solid ${record.isLate ? '#ef4444' : 'var(--green)'}`}}>
              <div className="record-header">
                <div>
                  <span className="record-date" style={{fontWeight:'700'}}>{record.date}</span>
                  <span style={{fontSize:'11px', color:'var(--text-secondary)', marginLeft:'8px'}}>
                    {new Date(record.date+'T12:00:00').toLocaleDateString('es-CL',{weekday:'long'})}
                  </span>
                </div>
                <span className={`status-tag ${record.status}`}>
                  {record.isLate ? `⏰ Atraso ${record.lateMinutes||0}min` : '✓ A tiempo'}
                </span>
              </div>
              <div className="record-times">
                <div className="time-item"><Clock size={16} /><span>Entrada: {record.checkIn || '--'}</span></div>
                <div className="time-item"><LogOut size={16} /><span>Salida: {record.checkOut || '--'}</span></div>
                {record.breakStart && (
                  <div className="time-item"><Coffee size={16} /><span>Colación: {record.breakStart} - {record.breakEnd || '...'}</span></div>
                )}
                <div className="time-item">
                  <span style={{fontSize:'13px', color:'var(--green)', fontWeight:'700'}}>⏱ {calcHours(record)}</span>
                </div>
              </div>
              {record.lateBreakMinutes > 0 && (
                <div style={{fontSize:'11px', color:'var(--orange)', marginTop:'4px'}}>⚠️ Atraso colación: {record.lateBreakMinutes} min</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Meses anteriores - agrupados y colapsables */}
      {(() => {
        const olderRecords = allUserRecords.filter(r => !r.date?.startsWith(currentMonthStr));
        const byMonth = {};
        olderRecords.forEach(r => {
          const m = r.date?.substring(0,7);
          if (!m) return;
          if (!byMonth[m]) byMonth[m] = [];
          byMonth[m].push(r);
        });
        const months = Object.keys(byMonth).sort().reverse();
        if (months.length === 0) return null;
        return (
          <div style={{marginTop:'16px'}}>
            <p style={{fontSize:'12px', color:'var(--text-secondary)', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.5px', margin:'0 0 8px'}}>Meses anteriores</p>
            {months.map(m => {
              const recs = byMonth[m];
              const late = recs.filter(r=>r.isLate).length;
              const onTime = recs.length - late;
              const totalLateM = recs.reduce((s,r)=>s+(r.lateMinutes||0),0);
              const monthName = new Date(m+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'});
              const isExp = expandedHistoryMonths[m];
              return (
                <div key={m} style={{marginBottom:'8px'}}>
                  <button onClick={()=>setExpandedHistoryMonths({...expandedHistoryMonths,[m]:!isExp})} style={{width:'100%', padding:'10px 16px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'10px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', color:'var(--text-primary)', fontSize:'13px'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <span style={{fontWeight:'700', textTransform:'capitalize'}}>{monthName}</span>
                      <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>{recs.length} días</span>
                      {onTime > 0 && <span style={{fontSize:'11px', color:'var(--green)', fontWeight:'600'}}>✅{onTime}</span>}
                      {late > 0 && <span style={{fontSize:'11px', color:'#ef4444', fontWeight:'600'}}>⏰{late}</span>}
                      {totalLateM > 0 && <span style={{fontSize:'11px', color:'var(--orange)'}}>{totalLateM}min</span>}
                    </div>
                    <span style={{color:'var(--text-secondary)'}}>{isExp?'▲':'▼'}</span>
                  </button>
                  {isExp && (
                    <div className="records-list" style={{marginTop:'8px'}}>
                      {recs.map(record => (
                        <div key={record.id} className="record-card" style={{borderLeft:`3px solid ${record.isLate?'#ef4444':'var(--green)'}`}}>
                          <div className="record-header">
                            <div>
                              <span className="record-date" style={{fontWeight:'700'}}>{record.date}</span>
                              <span style={{fontSize:'11px', color:'var(--text-secondary)', marginLeft:'8px'}}>{new Date(record.date+'T12:00:00').toLocaleDateString('es-CL',{weekday:'long'})}</span>
                            </div>
                            <span className={`status-tag ${record.status}`}>{record.isLate?`⏰ ${record.lateMinutes||0}min`:'✓ A tiempo'}</span>
                          </div>
                          <div className="record-times">
                            <div className="time-item"><Clock size={16}/><span>Entrada: {record.checkIn||'--'}</span></div>
                            <div className="time-item"><LogOut size={16}/><span>Salida: {record.checkOut||'--'}</span></div>
                            {record.breakStart && <div className="time-item"><Coffee size={16}/><span>Colación: {record.breakStart}-{record.breakEnd||'...'}</span></div>}
                            <div className="time-item"><span style={{fontSize:'13px',color:'var(--green)',fontWeight:'700'}}>⏱ {calcHours(record)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── RESUMEN MENSUAL DEL EMPLEADO ── */}
      {(() => {
        try {
          const mesActual = getLocalDateStr().substring(0,7);
          const mesNombre = new Date(mesActual+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'});
          const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
          const lsRecs = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
          const misId = currentUser?.id;
          const tareasDelMes = lsTasks.filter(t => String(t.assignedTo) === String(misId) && (t.dueDate||'').startsWith(mesActual));
          const tareasComp = tareasDelMes.filter(t => t.status === 'done' || t.reviewStatus === 'approved').length;
          const tareasVenc = tareasDelMes.filter(t => t.status === 'overdue').length;
          const recsDelMes = lsRecs.filter(r => String(r.employeeId) === String(misId) && (r.date||'').startsWith(mesActual));
          const diasTrabajados = recsDelMes.filter(r => r.checkIn).length;
          const atrasos = recsDelMes.filter(r => r.isLate).length;
          const minAtraso = recsDelMes.reduce((s,r) => s+(r.lateMinutes||0), 0);
          const puntualidad = diasTrabajados > 0 ? Math.round(((diasTrabajados - atrasos) / diasTrabajados) * 100) : 100;

          return (
            <div style={{margin:'16px', background:'var(--bg-card)', borderRadius:'12px', padding:'14px', border:'1px solid var(--border)'}}>
              <p style={{margin:'0 0 12px', fontSize:'13px', fontWeight:'700', color:'var(--text-primary)', textTransform:'capitalize'}}>📊 Mi resumen — {mesNombre}</p>
              <div style={{display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'8px', marginBottom:'10px'}}>
                <div style={{background:'rgba(34,197,94,0.08)', borderRadius:'8px', padding:'10px', border:'1px solid rgba(34,197,94,0.2)', textAlign:'center'}}>
                  <p style={{margin:'0 0 2px', fontSize:'11px', color:'var(--green)', fontWeight:'700'}}>✅ Tareas completadas</p>
                  <p style={{margin:0, fontSize:'22px', fontWeight:'700', color:'var(--green)'}}>{tareasComp}</p>
                </div>
                <div style={{background: atrasos > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)', borderRadius:'8px', padding:'10px', border:`1px solid ${atrasos > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`, textAlign:'center'}}>
                  <p style={{margin:'0 0 2px', fontSize:'11px', color: atrasos > 0 ? '#ef4444' : 'var(--green)', fontWeight:'700'}}>⏱ Puntualidad</p>
                  <p style={{margin:0, fontSize:'22px', fontWeight:'700', color: atrasos > 0 ? '#ef4444' : 'var(--green)'}}>{puntualidad}%</p>
                </div>
                <div style={{background:'rgba(59,130,246,0.08)', borderRadius:'8px', padding:'10px', border:'1px solid rgba(59,130,246,0.2)', textAlign:'center'}}>
                  <p style={{margin:'0 0 2px', fontSize:'11px', color:'var(--blue)', fontWeight:'700'}}>📅 Días trabajados</p>
                  <p style={{margin:0, fontSize:'22px', fontWeight:'700', color:'var(--blue)'}}>{diasTrabajados}</p>
                </div>
                <div style={{background: atrasos > 0 ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)', borderRadius:'8px', padding:'10px', border:`1px solid ${atrasos > 0 ? 'rgba(245,158,11,0.2)' : 'rgba(34,197,94,0.2)'}`, textAlign:'center'}}>
                  <p style={{margin:'0 0 2px', fontSize:'11px', color: atrasos > 0 ? 'var(--orange)' : 'var(--green)', fontWeight:'700'}}>🕐 Atrasos</p>
                  <p style={{margin:0, fontSize:'22px', fontWeight:'700', color: atrasos > 0 ? 'var(--orange)' : 'var(--green)'}}>{atrasos > 0 ? `${atrasos} (${minAtraso} min)` : '0'}</p>
                </div>
              </div>
              {tareasVenc > 0 && (
                <div style={{background:'rgba(239,68,68,0.08)', borderRadius:'8px', padding:'8px 12px', border:'1px solid rgba(239,68,68,0.2)'}}>
                  <p style={{margin:0, fontSize:'12px', color:'#ef4444', fontWeight:'600'}}>⚠️ Tienes {tareasVenc} tarea(s) vencidas sin completar este mes</p>
                </div>
              )}
              {atrasos === 0 && tareasComp >= 5 && (
                <div style={{background:'rgba(34,197,94,0.08)', borderRadius:'8px', padding:'8px 12px', border:'1px solid rgba(34,197,94,0.2)'}}>
                  <p style={{margin:0, fontSize:'12px', color:'var(--green)', fontWeight:'600'}}>⭐ ¡Excelente desempeño este mes!</p>
                </div>
              )}
            </div>
          );
        } catch(e) { return null; }
      })()}
    </div>
  );
};


const EmployeeScheduleView = () => {
  const {
    attendanceRecords, currentUser, employees, tasks, missingProducts, absences,
    overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage,
    auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues,
    classifyIssue, dismissIssue, generatePDF, generateTasksPDF, recalculateLateMinutes,
    generatePerformancePDF, userRole, getTodaySchedule,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask,
    handleToggleStep, newStepText, setNewStepText,
  } = React.useContext(AppContext);

  const [viewMonth, setViewMonth] = React.useState(new Date());
  // Usar el usuario logueado para el calendario personal
  const emp = (Array.isArray(employees) ? employees : []).find(e => e.id === currentUser?.id);

  if (!emp) return null;

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const dayNames = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

  const daysInMonth = new Date(year, month+1, 0).getDate();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7; // 0=Lun

  const getDateStr = (d) => `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  const getDaySchedule = (d) => {
    const dateStr = getDateStr(d);
    const ms = emp.monthlySchedule?.[dateStr];
    if (ms !== undefined) return ms; // puede ser {enabled:false} = libre
    if (emp.useFlexibleSchedule && emp.weeklySchedule) {
      const dow = new Date(year, month, d).getDay();
      const dayNamesEn = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
      return emp.weeklySchedule[dayNamesEn[dow]] || null;
    }
    return null;
  };

  const hasAnySchedule = emp.monthlySchedule && Object.values(emp.monthlySchedule).some(d => d?.enabled && d?.start)
    || (emp.useFlexibleSchedule && emp.weeklySchedule && Object.values(emp.weeklySchedule).some(d => d?.enabled));

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  // Registros reales del empleado para este mes
  const myRecordsThisMonth = (Array.isArray(attendanceRecords) ? attendanceRecords : [])
    .filter(r => r.employeeId === currentUser?.id && r.date?.startsWith(getDateStr(1).substring(0,7)));
  const recordByDate = {};
  myRecordsThisMonth.forEach(r => { recordByDate[r.date] = r; });

  // Count working days this month
  let workDays = 0, freeDays = 0, unconfigured = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const s = getDaySchedule(d);
    if (s?.enabled && s?.start) workDays++;
    else if (s !== null && s?.enabled === false) freeDays++;
    else unconfigured++;
  }

  return (
    <div style={{padding:'16px', maxWidth:'600px', margin:'0 auto'}}>
      <h2 style={{color:'var(--text-primary)', marginBottom:'4px'}}>📅 Mi Horario</h2>
      <p style={{color:'var(--text-secondary)', fontSize:'13px', marginBottom:'16px'}}>{emp.name} — {emp.position}</p>

      {!hasAnySchedule && (
        <div style={{background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'12px', padding:'20px', marginBottom:'16px', textAlign:'center'}}>
          <div style={{fontSize:'40px', marginBottom:'8px'}}>⚠️</div>
          <p style={{color:'var(--orange)', fontWeight:'700', margin:'0 0 8px', fontSize:'16px'}}>Tu horario no está configurado</p>
          <p style={{color:'var(--text-secondary)', fontSize:'13px', margin:0}}>Contacta al administrador o gerente para que configuren tu horario</p>
        </div>
      )}

      {/* Month navigation */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', background:'var(--bg-card)', borderRadius:'10px', padding:'12px 16px'}}>
        <button onClick={()=>setViewMonth(new Date(year,month-1,1))} style={{background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', padding:'6px 14px', cursor:'pointer', fontWeight:'700'}}>←</button>
        <span style={{fontWeight:'700', color:'var(--text-primary)', fontSize:'16px'}}>{monthNames[month]} {year}</span>
        <button onClick={()=>setViewMonth(new Date(year,month+1,1))} style={{background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', padding:'6px 14px', cursor:'pointer', fontWeight:'700'}}>→</button>
      </div>

      {/* Stats */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'16px'}}>
        {[
          {label:'Días trabajo', value:workDays, color:'var(--green)'},
          {label:'Días libre', value:freeDays, color:'var(--text-secondary)'},
          {label:'Sin configurar', value:unconfigured, color:unconfigured>0?'var(--orange)':'var(--text-muted)'},
        ].map((s,i)=>(
          <div key={i} style={{background:'var(--bg-card)', borderRadius:'10px', padding:'12px', textAlign:'center', border:`1px solid ${s.color}30`}}>
            <div style={{fontSize:'22px', fontWeight:'900', color:s.color}}>{s.value}</div>
            <div style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'2px'}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'12px', border:'1px solid var(--border)'}}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'4px', marginBottom:'8px'}}>
          {dayNames.map(d=>(
            <div key={d} style={{textAlign:'center', fontSize:'11px', fontWeight:'700', color:'var(--text-secondary)', padding:'4px'}}>{d}</div>
          ))}
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'4px'}}>
          {Array.from({length:firstDayOfWeek}).map((_,i)=><div key={`e${i}`} />)}
          {Array.from({length:daysInMonth}).map((_,i)=>{
            const d = i+1;
            const dateStr = getDateStr(d);
            const s = getDaySchedule(d);
            const isToday = dateStr === todayStr;
            const hasWork = s?.enabled && s?.start;
            const isFree = s !== null && s?.enabled === false;
            const isUnconfigured = s === null;
            const isPast = new Date(year,month,d) < today && dateStr !== todayStr;
            const record = recordByDate[dateStr];

            // Verificar si hay ausencia aprobada para este día
            const _allAbsences = (Array.isArray(absences) && absences.length > 0) ? absences : JSON.parse(localStorage.getItem('employeeAbsences') || '[]');
            const _empId = emp?.id || currentUser?.id;
            const myAbsences = _allAbsences.filter(a => a.employeeId === _empId || String(a.employeeId) === String(_empId));
            const dayAbsence = myAbsences.find(a => a.startDate && a.endDate && dateStr >= a.startDate && dateStr <= a.endDate);
            const isVacation = dayAbsence?.type === 'vacation';
            const isPermission = dayAbsence?.type === 'permission' || dayAbsence?.type === 'sick';

            // Determinar estado real del día
            const marked = !!record?.checkIn;
            const isLate = record?.isLate;
            const incomplete = marked && !record?.checkOut;
            const absent = hasWork && isPast && !marked && !isToday;

            // Colores según estado real
            let bg, border, statusIcon = null;
            if (isToday) { bg='rgba(59,130,246,0.2)'; border='2px solid var(--blue)'; }
            else if (absent) { bg='rgba(239,68,68,0.1)'; border='1px solid rgba(239,68,68,0.4)'; statusIcon='🔴'; }
            else if (marked && isLate) { bg='rgba(245,158,11,0.1)'; border='1px solid rgba(245,158,11,0.4)'; statusIcon='⏰'; }
            else if (incomplete) { bg='rgba(245,158,11,0.08)'; border='1px dashed rgba(245,158,11,0.4)'; statusIcon='⚠️'; }
            else if (marked) { bg='rgba(34,197,94,0.15)'; border='1px solid rgba(34,197,94,0.4)'; statusIcon='✅'; }
            else if (hasWork) { bg='rgba(34,197,94,0.05)'; border='1px solid rgba(34,197,94,0.2)'; }
            else if (isVacation) { bg='rgba(167,139,250,0.12)'; border='1px solid rgba(167,139,250,0.4)'; }
            else if (isPermission) { bg='rgba(59,130,246,0.1)'; border='1px solid rgba(59,130,246,0.3)'; }
            else if (isFree) { bg='var(--bg-elevated)'; border='1px solid var(--border)'; }
            else { bg='rgba(245,158,11,0.05)'; border='1px dashed rgba(245,158,11,0.3)'; }

            return (
              <div key={d} style={{borderRadius:'8px', padding:'6px 4px', textAlign:'center', minHeight:'52px', background:bg, border, opacity: isPast && isUnconfigured ? 0.4 : 1}}>
                <div style={{fontSize:'12px', fontWeight:isToday?'900':'600', color:isToday?'var(--blue)':absent?'#ef4444':marked?'var(--text-primary)':'var(--text-secondary)'}}>
                  {d}{statusIcon && <span style={{fontSize:'8px', display:'block', lineHeight:1}}>{statusIcon}</span>}
                </div>
                {marked ? (
                  <>
                    <div style={{fontSize:'8px', color:'var(--green)', fontWeight:'700', lineHeight:1.3}}>{record.checkIn?.replace(' a. m.','a').replace(' p. m.','p')}</div>
                    {record.checkOut && <div style={{fontSize:'8px', color:'var(--text-secondary)', lineHeight:1.3}}>{record.checkOut?.replace(' a. m.','a').replace(' p. m.','p')}</div>}
                    {!record.checkOut && <div style={{fontSize:'8px', color:'var(--orange)', lineHeight:1.3}}>sin salida</div>}
                  </>
                ) : hasWork ? (
                  <>
                    <div style={{fontSize:'8px', color: absent?'rgba(239,68,68,0.7)':'var(--green)', lineHeight:1.3}}>{s.start}</div>
                    <div style={{fontSize:'8px', color:'var(--text-muted)', lineHeight:1.3}}>{s.end}</div>
                  </>
                ) : isVacation ? (
                  <div style={{fontSize:'8px', color:'#a78bfa', fontWeight:'700', lineHeight:1.2}}>🏖️<br/>Vacac.</div>
                ) : isPermission ? (
                  <div style={{fontSize:'8px', color:'var(--blue)', fontWeight:'700', lineHeight:1.2}}>📋<br/>Permiso</div>
                ) : isFree ? (
                  <div style={{fontSize:'9px', color:'var(--text-muted)', fontWeight:'600'}}>Libre</div>
                ) : (
                  <div style={{fontSize:'8px', color:'var(--orange)', lineHeight:1.2}}>Sin<br/>config</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{display:'flex', gap:'12px', marginTop:'12px', flexWrap:'wrap'}}>
        {[
          {color:'rgba(34,197,94,0.4)', label:'✅ A tiempo'},
          {color:'rgba(245,158,11,0.4)', label:'⏰ Atraso'},
          {color:'rgba(239,68,68,0.4)', label:'🔴 Ausente'},
          {color:'rgba(167,139,250,0.4)', label:'🏖️ Vacaciones'},
          {color:'rgba(59,130,246,0.3)', label:'📋 Permiso'},
          {color:'var(--border)', label:'Libre'},
          {color:'rgba(59,130,246,0.4)', label:'Hoy'},
        ].map((l,i)=>(
          <div key={i} style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <div style={{width:'12px', height:'12px', borderRadius:'3px', border:`2px solid ${l.color}`, background:l.color+'40'}} />
            <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Horario de hoy */}
      {hasAnySchedule && (() => {
        const todayDay = new Date().getDate();
        const todayMonth = new Date().getMonth();
        const todayYear = new Date().getFullYear();
        const isCurrentMonth = todayMonth === month && todayYear === year;
        if (isCurrentMonth) {
          const s = getDaySchedule(todayDay);
          if (s?.enabled && s?.start) {
            return (
              <div style={{marginTop:'16px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'10px', padding:'12px'}}>
                <p style={{margin:0, fontSize:'13px', color:'var(--blue)', fontWeight:'700'}}>
                  📅 Hoy — {new Date(todayYear,todayMonth,todayDay).toLocaleDateString('es-CL',{weekday:'long',day:'numeric',month:'long'})}
                </p>
                <p style={{margin:'4px 0 0', fontSize:'13px', color:'var(--text-secondary)'}}>
                  ⏰ {s.start} — {s.end}
                  {s.lunchStart && ` · Colación: ${s.lunchStart}-${s.lunchEnd}`}
                </p>
              </div>
            );
          }
        }
        return null;
      })()}

      {/* Next working day */}
      {hasAnySchedule && (() => {
        for (let d = 1; d <= daysInMonth; d++) {
          const dateStr = getDateStr(d);
          if (dateStr <= todayStr) continue;
          const s = getDaySchedule(d);
          if (s?.enabled && s?.start) {
            return (
              <div style={{marginTop:'16px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'10px', padding:'12px'}}>
                <p style={{margin:0, fontSize:'13px', color:'var(--green)', fontWeight:'700'}}>
                  📅 Próximo día de trabajo: {new Date(year,month,d).toLocaleDateString('es-CL',{weekday:'long',day:'numeric',month:'long'})}
                </p>
                <p style={{margin:'4px 0 0', fontSize:'13px', color:'var(--text-secondary)'}}>
                  ⏰ {s.start} — {s.end}
                  {s.lunchStart && ` · Colación: ${s.lunchStart}-${s.lunchEnd}`}
                </p>
              </div>
            );
          }
        }
        return null;
      })()}
    </div>
  );
};

const TasksView = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus, tasksFilterDays, setTasksFilterDays,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const activeTab = tasksActiveTab;
  const setActiveTab = setTasksActiveTab;
  const filterEmp = tasksFilterEmp;
  const setFilterEmp = setTasksFilterEmp;
  const filterStatus = tasksFilterStatus;
  const setFilterStatus = setTasksFilterStatus;
  const isManager = userRole === 'manager' || userRole === 'admin';

  const [expandedMonths, setExpandedMonths] = React.useState({});
  const [showCompletedToday, setShowCompletedToday] = React.useState(false);
  const [expandedProducts, setExpandedProducts] = React.useState({});
  const [productDeleteMonth, setProductDeleteMonth] = React.useState(() => {
    const saved = localStorage.getItem('productFilter');
    if (saved) { localStorage.removeItem('productFilter'); return saved; }
    return 'all';
  });
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const [expandedPerfEmps, setExpandedPerfEmps] = React.useState({});
  const [tareasIA, setTareasIA] = React.useState([]);
  const [loadingTareasIA, setLoadingTareasIA] = React.useState(false);
  const [showTareasIA, setShowTareasIA] = React.useState(false);
  const [tareasIAEditadas, setTareasIAEditadas] = React.useState({});
  const [progresoIA, setProgresoIA] = React.useState({actual: 0, total: 0});
  const [showPasillosModal, setShowPasillosModal] = React.useState(false);

  const [pasillosAsignados, setPasillosAsignados] = React.useState({});
  const [empleadosIncluidos, setEmpleadosIncluidos] = React.useState({});

  const filterDays = tasksFilterDays;
  const setFilterDays = setTasksFilterDays;
  const fechaLimite = (() => {
    if (filterDays === 'all') return null;
    const d = new Date();
    d.setDate(d.getDate() - parseInt(filterDays));
    return d.toISOString();
  })();

  const myTasks = (isManager
    ? tasks.filter(t => {
        const empOk = filterEmp === 'all' || String(t.assignedTo) === filterEmp;
        const stOk = filterStatus === 'all' || t.status === filterStatus || (filterStatus === 'not_done' && (t.status === 'overdue' || t.status === 'not_done' || t.reviewStatus === 'rejected')) || (filterStatus === 'done' && t.status === 'done');
        const dateOk = !fechaLimite || (t.createdAt && t.createdAt >= fechaLimite) || (t.dueDate && t.dueDate >= fechaLimite.substring(0,10));
        return empOk && stOk && dateOk;
      })
    : tasks.filter(t => {
        const isAssigned = String(t.assignedTo) === String(currentUser?.id);
        const dateOk = !fechaLimite || (t.createdAt && t.createdAt >= fechaLimite) || (t.dueDate && t.dueDate >= fechaLimite.substring(0,10));
        return isAssigned && dateOk;
      })
  ).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const myProducts = isManager
    ? missingProducts
    : missingProducts.filter(p => String(p.reportedById) === String(currentUser?.id));

  const pendingTasksCount = (isManager ? tasks : tasks.filter(t => String(t.assignedTo) === String(currentUser?.id))).filter(t => t.status === 'pending').length;
  const pendingProductsCount = isManager ? missingProducts.filter(p => p.status === 'pending').length : 0;

  const priorityLabel = { high: 'Alta', medium: 'Media', low: 'Baja' };
  const statusLabel = { pending: 'Pendiente', done: 'Completada', not_done: 'No completada', overdue: '🔴 Vencida' };
  const productStatusLabel = { pending: 'Pendiente', requested: 'Solicitado', resolved: 'Resuelto' };

  const formatDate = (iso) => {
    if (!iso) return '--';
    return new Date(iso).toLocaleDateString('es-CL', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  return (
    <div className="tasks-view">
      <div className="tasks-header">
        <h2>Tareas y Productos</h2>
        <div style={{display:'flex', gap:'10px'}}>
          {isManager && (
            <button className="btn-task-new" onClick={() => { setEditingTask(null); setTaskForm({ title:'', description:'', assignedTo:'', priority:'medium', steps:[], dueDate:'', dueTime:'' }); setNewStepText(''); setShowTaskModal(true); }}>
              + Nueva Tarea
            </button>
          )}
          <button className="btn-task-report" onClick={() => setShowProductModal(true)}>
            + Reportar Producto
          </button>
          {isManager && (
            <button onClick={() => {
                const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
                const hoy = getLocalDateStr();
                const aEliminar = lsTasks.filter(t => t.status === 'overdue' && !t.completedAt && (t.dueDate||'') < hoy);
                if (aEliminar.length === 0) { alert('No hay tareas vencidas de días anteriores.'); return; }
                if (!window.confirm(`¿Eliminar ${aEliminar.length} tarea(s) vencidas de días anteriores? Esta acción no se puede deshacer.`)) return;
                const ids = new Set(aEliminar.map(t => t.id));
                const updated = lsTasks.filter(t => !ids.has(t.id));
                saveTasksToStorage(updated);
                try {
                  const deleted = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
                  aEliminar.forEach(t => { if (!deleted.includes(t.id)) deleted.push(t.id); });
                  localStorage.setItem('deletedTaskIds', JSON.stringify(deleted.slice(-500)));
                } catch(e) {}
                aEliminar.forEach(t => {
                  fetch('https://control--asistencia-default-rtdb.firebaseio.com/tasks/' + t.id + '.json', {method:'DELETE'}).catch(()=>{});
                });
                alert(`✅ ${aEliminar.length} tarea(s) vencidas eliminadas.`);
              }} style={{padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'600', fontFamily:'inherit'}}>
              🗑️ Limpiar vencidas
            </button>
          )}
          {(userRole === 'manager') && (
            <button onClick={() => {
              const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
              const activeEmps = lsEmps.filter(e => e?.status === 'active' && e?.id !== 1000);
              const hoyStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
              // Inicializar pasillos por defecto segun cargo
              const defaults = {};
              const incluidos = {};
              activeEmps.forEach(e => {
                if (e?.position?.toLowerCase().includes('multifuncional')) defaults[e.id] = 'Pasillo 1';
                else if (e?.position?.toLowerCase().includes('rotiseria') || e?.position?.toLowerCase().includes('rotisería')) defaults[e.id] = 'Rotisería';
                else if (e?.position?.toLowerCase().includes('admin')) defaults[e.id] = 'Administración';
                else if (e?.position?.toLowerCase().includes('cajero') || e?.position?.toLowerCase().includes('ventas')) defaults[e.id] = 'Caja y Ventas Online';
                else defaults[e.id] = 'General';
                // Incluir por defecto solo los que tienen turno hoy
                const tieneTurno = e?.monthlySchedule?.[hoyStr]?.enabled && e?.monthlySchedule?.[hoyStr]?.start;
                incluidos[e.id] = !!tieneTurno;
              });
              setPasillosAsignados(defaults);
              setEmpleadosIncluidos(incluidos);
              setShowPasillosModal(true);
            }} style={{padding:'8px 14px', background:'linear-gradient(135deg, #6366f1, #8b5cf6)', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}>
              🤖 Generar Tareas IA
            </button>
          )}

          {showPasillosModal && (
            <div className="modal-overlay" onClick={() => setShowPasillosModal(false)}>
              <div className="modal-content" style={{maxWidth:'420px'}} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>🤖 Asignar pasillos antes de generar</h2>
                  <button className="btn-close" onClick={() => setShowPasillosModal(false)}>X</button>
                </div>
                <div className="modal-body" style={{padding:'12px 0'}}>
                  <p style={{fontSize:'12px', color:'var(--text-secondary)', marginBottom:'12px'}}>Asigna el área de trabajo de cada empleado para que la IA genere tareas específicas.</p>
                  {(() => {
                    const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
                    const activeEmps = lsEmps.filter(e => e?.status === 'active' && e?.id !== 1000);
                    return activeEmps.map(emp => (
                      <div key={emp.id} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                          <input type="checkbox" checked={empleadosIncluidos[emp.id] !== false} onChange={e => setEmpleadosIncluidos({...empleadosIncluidos, [emp.id]: e.target.checked})} style={{width:'16px', height:'16px', cursor:'pointer', accentColor:'var(--blue)'}} />
                          <div>
                            <p style={{fontSize:'13px', fontWeight:'600', color:'var(--text-primary)', margin:0}}>{emp.name}</p>
                            <p style={{fontSize:'11px', color:'var(--text-secondary)', margin:0}}>{emp.position}</p>
                          </div>
                        </div>
                        <div style={{display:'flex', flexDirection:'column', gap:'4px', alignItems:'flex-end'}}>
                        <select id={`pasillo_${emp.id}`} defaultValue={pasillosAsignados[emp.id] || 'General'}
                          style={{padding:'6px 8px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px', color:'var(--text-primary)'}}>
                          <option value="Pasillo 1">Pasillo 1 (Perfumería+Abarrotes)</option>
                          <option value="Pasillo 2">Pasillo 2 (Dulces+Conservas)</option>
                          <option value="Pasillo 3">Pasillo 3 (Bebidas+Vinos)</option>
                          <option value="Rotisería">Rotisería</option>
                          <option value="Administración">Administración</option>
                          <option value="Caja">Caja</option>
                          <option value="Caja y Ventas Online">Caja y Ventas Online</option>
                          <option value="Apoyo general">Apoyo general</option>
                        </select>
                        <input id={`pasillo_extra_${emp.id}`} type="text" placeholder="+ Área adicional (opcional)" style={{padding:'4px 8px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'11px', color:'var(--text-secondary)', width:'100%'}} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={() => setShowPasillosModal(false)}>Cancelar</button>
                  <button className="btn-save" disabled={loadingTareasIA} onClick={async () => {
                    try {
                      const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
                      const activeEmps = lsEmps.filter(e => e?.status === 'active' && e?.id !== 1000);
                      const hoy = new Date();
                      const diasSemana = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
                      const fecha = hoy.toLocaleDateString('es-CL', {day:'numeric', month:'long', year:'numeric'});
                      const diaSemana = diasSemana[hoy.getDay()];
                      // Leer pasillos directamente del DOM para evitar problema de closure con estado React
                      const empleadosActivos = activeEmps.filter(e => empleadosIncluidos[e.id] !== false);
                      const empleados = empleadosActivos.map(e => {
                        const sel = document.getElementById(`pasillo_${e.id}`);
                        const selExtra = document.getElementById(`pasillo_extra_${e.id}`);
                        const areaExtra = selExtra?.value?.trim() || '';
                        const pasilloFinal = sel ? sel.value : (pasillosAsignados[e.id] || 'General');
                        return {id: e.id, nombre: e.name, cargo: e.position, pasilloHoy: areaExtra ? `${pasilloFinal} + ${areaExtra}` : pasilloFinal};
                      });
                      setTareasIA([]);
                      setLoadingTareasIA(true);
                      setShowPasillosModal(false);
                      setShowTareasIA(true);

                      // Llamadas paralelas por empleado para evitar truncamiento
                      const promesas = empleados.map(emp => 
                        fetch('https://ocrfrjlehptgyudfxdah.supabase.co/functions/v1/generate-tasks', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'apikey': 'sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-',
                            'Authorization': 'Bearer sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-'
                          },
                          body: JSON.stringify({ 
                            empleado: emp, empleados: [emp], fecha, diaSemana,
                            horarioApertura: `${String(config?.schedules?.checkIn?.hour || 10).padStart(2,'0')}:${String(config?.schedules?.checkIn?.minute || 0).padStart(2,'0')}`,
                            horarioCierre: `${String(config?.schedules?.checkOut?.hour || 21).padStart(2,'0')}:${String(config?.schedules?.checkOut?.minute || 0).padStart(2,'0')}`
                          })
                        }).then(r => r.json()).catch(() => ({ tareas: [] }))
                      );
                      // Procesar con progreso
                      setProgresoIA({actual: 0, total: empleados.length});
                      const todasTareas = [];
                      for (let i = 0; i < promesas.length; i++) {
                        setProgresoIA({actual: i + 1, total: empleados.length});
                        try { const r = await promesas[i]; if (r.tareas) todasTareas.push(...r.tareas); } catch(e) {}
                      }
                      setTareasIA(todasTareas);
                      setProgresoIA({actual: 0, total: 0});
                    } catch(e) {
                      alert('Error al generar tareas con IA');
                    } finally {
                      setLoadingTareasIA(false);
                    }
                  }}>
                    {loadingTareasIA ? (
                      progresoIA.total > 0 ? (
                        <span style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', width:'100%'}}>
                          <span style={{fontSize:'12px'}}>⏳ Generando {progresoIA.actual} de {progresoIA.total}...</span>
                          <div style={{width:'100%', height:'6px', background:'rgba(255,255,255,0.3)', borderRadius:'3px', overflow:'hidden'}}>
                            <div style={{width:`${Math.round((progresoIA.actual/progresoIA.total)*100)}%`, height:'100%', background:'white', borderRadius:'3px', transition:'width 0.3s'}}></div>
                          </div>
                        </span>
                      ) : '⏳ Iniciando...'
                    ) : '✅ Generar tareas'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      <div className="tasks-tabs">
        <button className={"tasks-tab" + (activeTab==='tasks' ? ' active' : '')} onClick={() => setActiveTab('tasks')}>
          Tareas {pendingTasksCount > 0 && <span className="tasks-badge">{pendingTasksCount}</span>}
        </button>
        <button className={"tasks-tab" + (activeTab==='products' ? ' active' : '')} onClick={() => setActiveTab('products')}>
          Productos Faltantes {pendingProductsCount > 0 && <span className="tasks-badge">{pendingProductsCount}</span>}
        </button>
        {isManager && (
          <button className={"tasks-tab" + (activeTab==='performance' ? ' active' : '')} onClick={() => setActiveTab('performance')}>
            Desempeno
          </button>
        )}
      </div>

      {activeTab === 'tasks' && (
        <div>
          {isManager && (
            <div className="tasks-filters">
              <select className="tasks-select" value={filterEmp} onChange={e => setFilterEmp(e.target.value)}>
                <option value="all">Todos los empleados</option>
                {employees.map(e => <option key={e.id} value={String(e.id)}>{e.name}</option>)}
              </select>
              <select className="tasks-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="all">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="done">Completada</option>
                <option value="not_done">No completada</option>
                <option value="overdue">Vencida</option>
              </select>
              <select className="tasks-select" value={filterDays} onChange={e => setFilterDays(e.target.value)}>
                <option value="7">Últimos 7 días</option>
                <option value="30">Últimos 30 días</option>
                <option value="60">Últimos 60 días</option>
                <option value="all">Todas las fechas</option>
              </select>
            </div>
          )}
          {myTasks.length === 0 ? (
            <div className="tasks-empty"><p>No hay tareas{isManager ? '' : ' asignadas'}</p></div>
          ) : (() => {
            const now = new Date();
            const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;

            // Split: current month vs older
            const currentMonthTasks = myTasks.filter(t => (t.createdAt||'').startsWith(currentMonthStr));
            const olderTasks = myTasks.filter(t => !(t.createdAt||'').startsWith(currentMonthStr));

            // Current month: active (pending/overdue) vs completed
            const pendientesRevision = isManager && filterStatus === 'all' ? myTasks.filter(t => t.status === 'done' && !t.reviewStatus && t.reviewStatus !== 'approved' && t.reviewStatus !== 'rejected') : [];
            const vencidasSinCompletar = filterStatus === 'all' ? myTasks.filter(t => (t.status === 'overdue' || t.status === 'not_done') && !t.completedAt && t.reviewStatus !== 'approved' && t.reviewStatus !== 'rejected') : [];
            const activeTasks = filterStatus !== 'all' ? myTasks : myTasks.filter(t => t.status === 'pending' || t.status === 'overdue' || t.status === 'not_done' || (t.status === 'done' && userRole === 'manager' && t.reviewStatus !== 'approved' && t.reviewStatus !== 'rejected'));
            // Reordenar: primero pendientes de revision, luego vencidas, luego el resto
            const activeTasksOrdenadas = isManager && filterStatus === 'all'
              ? [...activeTasks].sort((a, b) => {
                  const aPrioridad = (a.status === 'done' && !a.reviewStatus) ? 0 : ((a.status === 'overdue' || a.status === 'not_done') && !a.completedAt) ? 1 : 2;
                  const bPrioridad = (b.status === 'done' && !b.reviewStatus) ? 0 : ((b.status === 'overdue' || b.status === 'not_done') && !b.completedAt) ? 1 : 2;
                  return aPrioridad - bPrioridad;
                })
              : activeTasks;
            const completedTasks = filterStatus !== 'all' ? [] : myTasks.filter(t => t.status === 'done' && (userRole !== 'manager' || t.reviewStatus === 'approved' || t.reviewStatus === 'rejected'));
            const olderByMonth = {};
            if (filterStatus === 'all') {
              olderTasks.forEach(t => {
                // Solo incluir tareas activas en secciones antiguas — las done van a completedTasks
                if (t.status === 'done' && (userRole !== 'manager' || t.reviewStatus === 'approved' || t.reviewStatus === 'rejected')) return;
                const m = (t.createdAt||'').substring(0,7);
                if (!olderByMonth[m]) olderByMonth[m] = [];
                olderByMonth[m].push(t);
              });
            }
            const sortedMonths = Object.keys(olderByMonth).sort().reverse();

            return (
            <div>
              {isManager && filterStatus === 'all' && pendientesRevision.length > 0 && (
                <div onClick={() => setFilterStatus('done')} style={{padding:'6px 12px', background:'rgba(59,130,246,0.08)', borderRadius:'8px', border:'1px solid rgba(59,130,246,0.2)', marginBottom:'10px', display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                  <span style={{fontSize:'13px', fontWeight:'700', color:'var(--blue)'}}>⏳ Pendientes de revisión</span>
                  <span style={{fontSize:'11px', background:'var(--blue)', color:'white', borderRadius:'12px', padding:'2px 8px', fontWeight:'700'}}>{pendientesRevision.length}</span>
                  <span style={{fontSize:'11px', color:'var(--blue)', marginLeft:'auto'}}>Ver todas →</span>
                </div>
              )}
              {isManager && filterStatus === 'all' && vencidasSinCompletar.length > 0 && (
                <div onClick={() => setFilterStatus('overdue')} style={{padding:'6px 12px', background:'rgba(239,68,68,0.08)', borderRadius:'8px', border:'1px solid rgba(239,68,68,0.2)', marginBottom:'10px', display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
                  <span style={{fontSize:'13px', fontWeight:'700', color:'#ef4444'}}>🔴 Vencidas sin completar</span>
                  <span style={{fontSize:'11px', background:'#ef4444', color:'white', borderRadius:'12px', padding:'2px 8px', fontWeight:'700'}}>{vencidasSinCompletar.length}</span>
                  <span style={{fontSize:'11px', color:'#ef4444', marginLeft:'auto'}}>Ver todas →</span>
                </div>
              )}
              {activeTasksOrdenadas.length > 0 && (
                <div className="tasks-list" style={{marginBottom:'12px'}}>
                  {activeTasksOrdenadas.map(task => (
                <div key={task.id} className={"task-card priority-" + task.priority}>
                  <div className="task-card-header">
                    <div style={{display:'flex', alignItems:'center', gap:'8px', flex:1, flexWrap:'wrap'}}>
                      <span className={"priority-badge " + task.priority}>{priorityLabel[task.priority]}</span>
                      <span className={"task-status-badge " + task.status}>{statusLabel[task.status]}</span>
                      {task.reviewStatus === 'reviewed' && <span className="task-status-badge reviewed">Revisado</span>}
                    </div>
                    {isManager && (
                      <div style={{display:'flex', gap:'6px'}}>
                        <button className="task-action-btn edit" title="Editar" onClick={() => { setEditingTask(task); setTaskForm({ title: task.title, description: task.description, assignedTo: String(task.assignedTo), priority: task.priority, steps: task.steps || [], dueDate: task.dueDate || '', dueTime: task.dueTime || '' }); setNewStepText(''); setShowTaskModal(true); }}>
                          <Edit2 size={14} />
                        </button>
                        <button className="task-action-btn delete" title="Eliminar" onClick={() => handleDeleteTask(task.id)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  {task.dueDate && (() => {
                    try {
                      const [dy, dm, dd] = task.dueDate.split('-');
                      return <p style={{fontSize:'11px', color:'var(--text-secondary)', fontWeight:'600', margin:'0 0 4px'}}>{dd}/{dm}/{dy}</p>;
                    } catch(e) { return null; }
                  })()}
                  <h4 className="task-title">{task.title}</h4>
                  {task.dueDate && (
                    <div style={{display:'flex', alignItems:'center', gap:'4px', marginTop:'4px'}}>
                      {(() => {
                        // Si ya esta aprobada, rechazada o completada no mostrar badge de vencimiento
                        if (task.reviewStatus === 'approved' || task.reviewStatus === 'rejected' || task.status === 'done') return null;
                        const now = new Date();
                        const [dy, dm, dd] = task.dueDate.split('-').map(Number);
                        const [dh, dmin] = (task.dueTime || '23:59').split(':').map(Number);
                        const due = new Date(dy, dm-1, dd, dh, dmin, 0);
                        const diffMs = due - now;
                        const diffH = Math.floor(diffMs / 3600000);
                        const diffMin = Math.floor(diffMs / 60000);
                        const isOverdue = diffMs < 0;
                        const isUrgent = diffMs > 0 && diffH <= 24;
                        const venceLabel = diffH >= 1 ? `⚠️ Vence en ${diffH}h` : diffMin > 0 ? `⚠️ Vence en ${diffMin}min` : '⚠️ Vence pronto';
                        return (
                          <span style={{
                            fontSize:'11px', padding:'2px 8px', borderRadius:'4px', fontWeight:'700',
                            background: isOverdue ? 'rgba(239,68,68,0.2)' : isUrgent ? 'rgba(245,158,11,0.2)' : 'rgba(59,130,246,0.15)',
                            color: isOverdue ? '#ef4444' : isUrgent ? '#f59e0b' : 'var(--blue)'
                          }}>
                            {isOverdue ? '🔴 VENCIDA' : isUrgent ? venceLabel : `📅 ${task.dueDate} ${task.dueTime || ''}`}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                  {task.description && <p className="task-desc">{task.description}</p>}
                  <div className="task-meta">
                    <span style={{color:'var(--text-primary)', fontWeight:'700'}}>👤 {task.assignedName}</span>
                    <span>{formatDate(task.createdAt)}</span>
                    <span style={{color:'var(--text-secondary)', fontSize:'11px'}}>Creado por: {task.createdBy}</span>
                  </div>
                  {/* Pasos / subtareas */}
                  {(task.steps||[]).length > 0 && (
                    <div style={{margin:'10px 0', display:'flex', flexDirection:'column', gap:'6px'}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px'}}>
                        <span style={{fontSize:'12px', color:'var(--text-secondary)', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.5px'}}>Pasos</span>
                        <span style={{fontSize:'12px', color:'var(--blue)', fontWeight:'700'}}>
                          {(task.steps||[]).filter(s=>s.done).length}/{(task.steps||[]).length} completados
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div style={{height:'4px', background:'var(--bg-secondary)', borderRadius:'4px', marginBottom:'6px'}}>
                        <div style={{height:'100%', background:'var(--green)', borderRadius:'4px', width: `${(task.steps||[]).length > 0 ? ((task.steps||[]).filter(s=>s.done).length / (task.steps||[]).length * 100) : 0}%`, transition:'width 0.3s'}} />
                      </div>
                      {(task.steps||[]).map((step, idx) => (
                        <div key={step.id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'7px 10px', background:'var(--bg-secondary)', borderRadius:'8px', opacity: step.done ? 0.7 : 1}}>
                          <input
                            type="checkbox"
                            checked={step.done}
                            onChange={() => !isManager && task.status === 'pending' && handleToggleStep(task.id, step.id)}
                            style={{width:'16px', height:'16px', cursor: !isManager && task.status === 'pending' ? 'pointer' : 'default', accentColor:'var(--green)'}}
                            disabled={isManager || task.status !== 'pending'}
                          />
                          <span style={{fontSize:'13px', color:'var(--text-primary)', flex:1, textDecoration: step.done ? 'line-through' : 'none'}}>
                            <span style={{color:'var(--text-secondary)', fontWeight:'700', marginRight:'6px'}}>{idx+1}.</span>
                            {step.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Rechazo del gerente visible para el empleado */}
                  {task.reviewStatus === 'rejected' && (
                    <div style={{background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'8px', padding:'10px', marginBottom:'8px'}}>
                      <span style={{fontSize:'12px', color:'#ef4444', fontWeight:'700'}}>⚠️ Rechazada por {task.reviewedBy}</span>
                      {task.reviewComment && <p style={{fontSize:'13px', color:'var(--text-primary)', margin:'4px 0 0'}}>{task.reviewComment}</p>}
                    </div>
                  )}
                  {task.reviewStatus === 'approved' && (
                    <div style={{background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'8px', padding:'10px', marginBottom:'8px'}}>
                      <span style={{fontSize:'12px', color:'var(--green)', fontWeight:'700'}}>✅ Aprobada por {task.reviewedBy} — {formatDate(task.reviewedAt)}</span>
                    </div>
                  )}
                  {/* Botón reactivar para tareas vencidas */}
                  {isManager && task.status === 'overdue' && (
                    <div style={{marginTop:'8px', padding:'10px', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'8px'}}>
                      <p style={{margin:'0 0 8px', fontSize:'12px', color:'var(--orange)', fontWeight:'700'}}>🔴 Tarea Vencida — ¿Reactivar?</p>
                      {task.reactivationRequest && task.reactivationRequest.status === 'pending' ? (
                        // Admin ya solicitó — gerente puede aprobar/rechazar
                        userRole === 'manager' ? (
                          <div>
                            <p style={{margin:'0 0 6px', fontSize:'11px', color:'var(--text-secondary)'}}>Solicitado por {task.reactivationRequest.requestedBy} — Nueva fecha: {task.reactivationRequest.newDate} {task.reactivationRequest.newTime}</p>
                            <div style={{display:'flex', gap:'6px'}}>
                              <button onClick={() => {
                                const req = task.reactivationRequest;
                                const updated = (Array.isArray(tasks)?tasks:[]).map(t => t.id === task.id ? {...t, status:'pending', isOverdue:false, dueDate:req.newDate, dueTime:req.newTime||'23:00', reactivationRequest:null} : t);
                                saveTasksToStorage(updated);
                                addAuditEntry('APROBAR_REACTIVACION', `Aprobó reactivación de "${task.title}" — nueva fecha: ${req.newDate}`);
                                alert('✅ Reactivación aprobada');
                              }} style={{padding:'5px 12px', background:'rgba(34,197,94,0.2)', color:'var(--green)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>✅ Aprobar</button>
                              <button onClick={() => {
                                const updated = (Array.isArray(tasks)?tasks:[]).map(t => t.id === task.id ? {...t, reactivationRequest:{...t.reactivationRequest, status:'rejected'}} : t);
                                saveTasksToStorage(updated);
                                addAuditEntry('RECHAZAR_REACTIVACION', `Rechazó reactivación de "${task.title}"`);
                                alert('❌ Reactivación rechazada');
                              }} style={{padding:'5px 12px', background:'rgba(239,68,68,0.2)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.4)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>❌ Rechazar</button>
                            </div>
                          </div>
                        ) : (
                          <p style={{margin:0, fontSize:'11px', color:'var(--orange)'}}>⏳ Solicitud pendiente de aprobación del gerente</p>
                        )
                      ) : task.reactivationRequest?.status === 'rejected' ? (
                        <p style={{margin:0, fontSize:'11px', color:'#ef4444'}}>❌ Reactivación rechazada por el gerente</p>
                      ) : (
                        // Formulario para solicitar/reactivar
                        <div style={{display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap'}}>
                          <input type="date" defaultValue={new Date(new Date().setDate(new Date().getDate()+1)).toISOString().split('T')[0]} id={`reactivate-date-${task.id}`} style={{padding:'6px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}} />
                          <input type="time" defaultValue={task.dueTime || '23:00'} id={`reactivate-time-${task.id}`} style={{padding:'6px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}} />
                          {userRole === 'manager' ? (
                            <button onClick={() => {
                              const newDate = document.getElementById(`reactivate-date-${task.id}`)?.value;
                              const newTime = document.getElementById(`reactivate-time-${task.id}`)?.value;
                              if (!newDate) { alert('Selecciona una nueva fecha'); return; }
                              const updated = (Array.isArray(tasks)?tasks:[]).map(t => t.id === task.id ? {...t, status:'pending', isOverdue:false, dueDate:newDate, dueTime:newTime||'23:00', reactivationRequest:null, reviewStatus:null, reviewComment:'', completedAt:null, employeeObservation:''} : t);
                              saveTasksToStorage(updated);
                              addAuditEntry('REACTIVAR_TAREA', `Reactivó tarea "${task.title}" — nueva fecha: ${newDate} ${newTime||''}`);
                              alert('✅ Tarea reactivada');
                            }} style={{padding:'6px 14px', background:'var(--orange)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>🔄 Reactivar</button>
                          ) : (
                            <button onClick={() => {
                              const newDate = document.getElementById(`reactivate-date-${task.id}`)?.value;
                              const newTime = document.getElementById(`reactivate-time-${task.id}`)?.value;
                              if (!newDate) { alert('Selecciona una nueva fecha'); return; }
                              const updated = (Array.isArray(tasks)?tasks:[]).map(t => t.id === task.id ? {...t, reactivationRequest:{status:'pending', requestedBy: currentUser?.name, newDate, newTime:newTime||'23:00', requestedAt: new Date().toISOString()}} : t);
                              saveTasksToStorage(updated);
                              addAuditEntry('SOLICITAR_REACTIVACION', `Solicitó reactivación de "${task.title}" — fecha propuesta: ${newDate}`);
                              alert('📨 Solicitud enviada al gerente');
                            }} style={{padding:'6px 14px', background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>📨 Solicitar Reactivación</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {task.status !== 'pending' && task.reviewStatus !== 'approved' && (
                    <div className="task-employee-response">
                      <span className="task-response-label">Respuesta: {task.employeeObservation || 'Sin observacion'}</span>
                      <span className="task-response-date">{formatDate(task.completedAt)}</span>
                    </div>
                  )}
                  {/* Observación del gerente visible para empleado */}
                  {task.managerNote && (
                    <div style={{background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'8px', padding:'10px', marginBottom:'8px'}}>
                      <span style={{fontSize:'11px', color:'var(--blue)', fontWeight:'700'}}>💬 Nota del Gerente ({task.managerNoteBy || 'Gerente'}):</span>
                      <p style={{fontSize:'13px', color:'var(--text-primary)', margin:'4px 0 0'}}>{task.managerNote}</p>
                      <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>{formatDate(task.managerNoteAt)}</span>
                    </div>
                  )}

                  {/* Acciones empleado - solo si tarea pendiente y es el mismo día */}
                  {!isManager && (task.status === 'pending' || task.status === 'overdue' || task.reviewStatus === 'rejected') && (
                    <div className="task-employee-actions">
                      <div style={{position:'relative'}}>
                        <textarea
                          className="task-obs-input"
                          placeholder="Observación (se guarda automáticamente como borrador)"
                          value={taskObs[task.id] || ''}
                          onChange={e => {
                            const val = e.target.value;
                            setTaskObs({...taskObs, [task.id]: val});
                            // Guardar borrador en localStorage por empleado y tarea
                            const draftKey = `taskDraft_${currentUser?.id}_${task.id}`;
                            localStorage.setItem(draftKey, val);
                          }}
                          rows={3}
                        />
                        {taskObs[task.id] && (
                          <span style={{position:'absolute', bottom:'6px', right:'8px', fontSize:'10px', color:'var(--text-muted)'}}>✓ Borrador guardado</span>
                        )}
                      </div>
                      <div style={{display:'flex', gap:'8px', marginTop:'8px'}}>
                        <button className="btn-task-done" onClick={() => {
                          handleEmployeeCompleteTask(task.id, true, taskObs[task.id] || '');
                          // Limpiar borrador al completar
                          localStorage.removeItem(`taskDraft_${currentUser?.id}_${task.id}`);
                          setTaskObs({...taskObs, [task.id]: ''});
                        }}>Completada</button>
                        <button className="btn-task-notdone" onClick={() => {
                          if (!(taskObs[task.id]||'').trim()) { alert('Escribe una observacion para marcar como no completada'); return; }
                          handleEmployeeCompleteTask(task.id, false, taskObs[task.id]);
                          localStorage.removeItem(`taskDraft_${currentUser?.id}_${task.id}`);
                          setTaskObs({...taskObs, [task.id]: ''});
                        }}>No completada</button>
                      </div>
                    </div>
                  )}

                  {/* Acciones gerente: aprobar/rechazar con un clic, comentario opcional */}
                  {isManager && task.status !== 'pending' && !['approved','rejected'].includes(task.reviewStatus) && (
                    <div style={{marginTop:'12px', display:'flex', flexDirection:'column', gap:'8px'}}>
                      <div style={{display:'flex', gap:'8px'}}>
                        <button className="btn-task-done" style={{flex:1, fontSize:'14px', padding:'10px'}} onClick={() => { handleManagerReviewTask(task.id, true, taskObs[task.id]||''); setTaskObs({...taskObs, [task.id]: ''}); }}>✅ Aprobar</button>
                        <button className="btn-task-notdone" style={{flex:1, fontSize:'14px', padding:'10px'}} onClick={() => { handleManagerReviewTask(task.id, false, taskObs[task.id]||'Rechazada por el gerente'); setTaskObs({...taskObs, [task.id]: ''}); }}>❌ Rechazar</button>
                      </div>
                      {userRole === 'manager' && (
                        <details style={{fontSize:'11px'}}>
                          <summary style={{color:'var(--text-secondary)', cursor:'pointer'}}>+ Agregar comentario (opcional)</summary>
                          <textarea
                            placeholder="Comentario para el empleado..."
                            value={taskObs[task.id] || ''}
                            onChange={e => setTaskObs({...taskObs, [task.id]: e.target.value})}
                            rows={2}
                            className="task-obs-input"
                            style={{marginTop:'6px'}}
                          />
                        </details>
                      )}
                    </div>
                  )}

                  {/* Gerente puede dejar nota en cualquier tarea + editar observación */}
                  {isManager && (
                    <div style={{marginTop:'8px'}}>
                      {userRole === 'manager' ? (
                        // Gerente sin límite de tiempo
                        <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                          <textarea
                            className="task-obs-input"
                            placeholder="💬 Dejar nota para el empleado (visible para él)"
                            value={taskObs['note_'+task.id] || task.managerNote || ''}
                            onChange={e => setTaskObs({...taskObs, ['note_'+task.id]: e.target.value})}
                            rows={2}
                          />
                          <button
                            style={{padding:'6px 12px', background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600', alignSelf:'flex-end'}}
                            onClick={() => {
                              const note = taskObs['note_'+task.id] || '';
                              const updated = tasks.map(t => t.id === task.id ? {...t, managerNote: note, managerNoteBy: currentUser?.name || 'Gerente', managerNoteAt: new Date().toISOString()} : t);
                              saveTasksToStorage(updated);
                              setTaskObs({...taskObs, ['note_'+task.id]: ''});
                            }}
                          >💬 Guardar Nota</button>
                        </div>
                      ) : (
                        // Admin solo puede actuar hoy
                        (() => {
                          const today = getLocalDateStr();
                          const taskDate = task.createdAt ? task.createdAt.split('T')[0] : today;
                          if (taskDate !== today) return <p style={{fontSize:'11px', color:'var(--text-secondary)', margin:'4px 0'}}>🔒 Solo el gerente puede editar tareas de días anteriores</p>;
                          return (
                            <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                              <textarea
                                className="task-obs-input"
                                placeholder="💬 Dejar nota para el empleado"
                                value={taskObs['note_'+task.id] || task.managerNote || ''}
                                onChange={e => setTaskObs({...taskObs, ['note_'+task.id]: e.target.value})}
                                rows={2}
                              />
                              <button
                                style={{padding:'6px 12px', background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600', alignSelf:'flex-end'}}
                                onClick={() => {
                                  const note = taskObs['note_'+task.id] || '';
                                  const updated = tasks.map(t => t.id === task.id ? {...t, managerNote: note, managerNoteBy: currentUser?.name || 'Admin', managerNoteAt: new Date().toISOString()} : t);
                                  saveTasksToStorage(updated);
                                  setTaskObs({...taskObs, ['note_'+task.id]: ''});
                                }}
                              >💬 Guardar Nota</button>
                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}

                  {/* Gerente puede marcar como completada tarea antigua */}
                  {userRole === 'manager' && task.status === 'pending' && task.createdAt && task.createdAt.split('T')[0] !== getLocalDateStr() && (
                    <div style={{marginTop:'8px', padding:'8px', background:'rgba(245,158,11,0.1)', borderRadius:'8px', border:'1px solid rgba(245,158,11,0.3)'}}>
                      <p style={{fontSize:'11px', color:'var(--orange)', margin:'0 0 6px', fontWeight:'600'}}>⚡ Acción del Gerente — Tarea de día anterior</p>
                      <textarea
                        className="task-obs-input"
                        placeholder="Observación del gerente al completar"
                        value={taskObs['mgr_'+task.id] || ''}
                        onChange={e => setTaskObs({...taskObs, ['mgr_'+task.id]: e.target.value})}
                        rows={2}
                      />
                      <div style={{display:'flex', gap:'8px', marginTop:'6px'}}>
                        <button className="btn-task-done" style={{flex:1}} onClick={() => { handleEmployeeCompleteTask(task.id, true, taskObs['mgr_'+task.id]||'Completada por gerente'); setTaskObs({...taskObs, ['mgr_'+task.id]: ''}); }}>✅ Marcar Completada</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
                </div>
              )}

              {/* COMPLETADAS DEL MES - colapsables */}
              {completedTasks.length > 0 && (
                <div style={{marginBottom:'12px'}}>
                  <button onClick={()=>setShowCompletedToday(!showCompletedToday)} style={{width:'100%', padding:'10px 16px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'10px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', color:'var(--green)', fontWeight:'700', fontSize:'13px'}}>
                    <span>✅ {completedTasks.length} tarea(s) completada(s) este mes</span>
                    <span>{showCompletedToday ? '▲' : '▼'}</span>
                  </button>
                  {showCompletedToday && (
                    <div className="tasks-list" style={{marginTop:'8px'}}>
                      {completedTasks.map(task => (
                        <div key={task.id} className={"task-card priority-" + task.priority} style={{opacity:0.8}}>
                          <div className="task-card-header">
                            <div style={{display:'flex', alignItems:'center', gap:'8px', flex:1, flexWrap:'wrap'}}>
                              <span className={"priority-badge " + task.priority}>{priorityLabel[task.priority]}</span>
                              <span className={"task-status-badge " + task.status}>{statusLabel[task.status]}</span>
                              {task.reviewStatus === 'approved' && <span className="task-status-badge approved">✅ Aprobada</span>}
                              {task.reviewStatus === 'rejected' && <span className="task-status-badge rejected">❌ Rechazada</span>}
                            </div>
                            {isManager && (
                              <button className="task-action-btn delete" onClick={() => handleDeleteTask(task.id)}><Trash2 size={14} /></button>
                            )}
                          </div>
                          <h4 className="task-title">{task.title}</h4>
                          <div className="task-meta">
                            <span>Asignado a: {task.assignedName}</span>
                            <span>{formatDate(task.completedAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* MESES ANTERIORES - agrupados */}
              {sortedMonths.length > 0 && (
                <div>
                  <p style={{fontSize:'12px', color:'var(--text-secondary)', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.5px', margin:'8px 0'}}>Historial</p>
                  {sortedMonths.map(monthKey => {
                    const monthTasks = olderByMonth[monthKey];
                    const done = monthTasks.filter(t=>t.status==='done').length;
                    const notDone = monthTasks.filter(t=>t.status==='not_done').length;
                    const overdue = monthTasks.filter(t=>t.status==='overdue').length;
                    const pending = monthTasks.filter(t=>t.status==='pending').length;
                    const monthName = new Date(monthKey+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'});
                    const isExpanded = expandedMonths[monthKey];
                    return (
                      <div key={monthKey} style={{marginBottom:'8px'}}>
                        <button onClick={()=>setExpandedMonths({...expandedMonths,[monthKey]:!isExpanded})} style={{width:'100%', padding:'10px 16px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'10px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', color:'var(--text-primary)', fontSize:'13px'}}>
                          <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                            <span style={{fontWeight:'700', textTransform:'capitalize'}}>{monthName}</span>
                            <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>{monthTasks.length} tareas</span>
                            {done > 0 && <span style={{fontSize:'11px', color:'var(--green)', fontWeight:'600'}}>✅{done}</span>}
                            {notDone > 0 && <span style={{fontSize:'11px', color:'#ef4444', fontWeight:'600'}}>❌{notDone}</span>}
                            {overdue > 0 && <span style={{fontSize:'11px', color:'#ef4444', fontWeight:'600'}}>🔴{overdue}</span>}
                            {pending > 0 && <span style={{fontSize:'11px', color:'var(--orange)', fontWeight:'600'}}>⏳{pending}</span>}
                          </div>
                          <span style={{color:'var(--text-secondary)'}}>{isExpanded?'▲':'▼'}</span>
                        </button>
                        {isExpanded && (
                          <div className="tasks-list" style={{marginTop:'8px'}}>
                            {monthTasks.map(task => (
                              <div key={task.id} className={"task-card priority-" + task.priority} style={{opacity:0.85}}>
                                <div className="task-card-header">
                                  <div style={{display:'flex', alignItems:'center', gap:'8px', flex:1, flexWrap:'wrap'}}>
                                    <span className={"priority-badge " + task.priority}>{priorityLabel[task.priority]}</span>
                                    <span className={"task-status-badge " + task.status}>{statusLabel[task.status]}</span>
                                  </div>
                                  {isManager && (
                                    <button className="task-action-btn delete" onClick={() => handleDeleteTask(task.id)}><Trash2 size={14} /></button>
                                  )}
                                </div>
                                <h4 className="task-title">{task.title}</h4>
                                <div className="task-meta">
                                  <span>Asignado a: {task.assignedName}</span>
                                  <span>{formatDate(task.createdAt)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'products' && (
        <div>
          {/* Filtros */}
          {isManager && (
            <div style={{display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap', alignItems:'center', justifyContent:'space-between'}}>
              <select value={productDeleteMonth} onChange={e=>setProductDeleteMonth(e.target.value)} style={{padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}}>
                <option value="all">Todos los estados</option>
                <option value="pending">Solo Pendientes</option>
                <option value="requested">Solo Solicitados</option>
                <option value="resolved">Solo Resueltos</option>
              </select>
              {productDeleteMonth === 'resolved' && (
                <button onClick={()=>{
                  const resolved = (Array.isArray(myProducts)?myProducts:[]).filter(p=>p.status==='resolved');
                  if(!window.confirm(`¿Eliminar ${resolved.length} producto(s) resuelto(s)?`)) return;
                  resolved.forEach(p => handleDeleteProduct(p.id));
                  setProductDeleteMonth('all');
                }} style={{padding:'6px 14px', background:'#ef4444', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>
                  🗑️ Eliminar todos los resueltos
                </button>
              )}
            </div>
          )}
          {myProducts.length === 0 ? (
            <div className="tasks-empty"><p>No hay reportes de productos</p></div>
          ) : (
            <div className="tasks-list">
              {[...(Array.isArray(myProducts)?myProducts:[])].filter(p => {
                if (productDeleteMonth === 'all') return true;
                return p.status === productDeleteMonth;
              }).sort((a,b)=>(b.reportedAt||'').localeCompare(a.reportedAt||'')).map(p => (
                <div key={p.id} className={"task-card product-card status-prod-" + p.status}>
                  <div className="task-card-header">
                    <div style={{display:'flex', alignItems:'center', gap:'8px', flex:1}}>
                      <span className={"product-status-badge " + p.status}>{productStatusLabel[p.status]}</span>
                    </div>
                    <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                      {isManager && p.status === 'pending' && (
                        <button className="btn-task-review" onClick={() => handleMarkProductRequested(p.id)}>Marcar Solicitado</button>
                      )}
                      {isManager && p.status === 'requested' && (
                        <button className="btn-task-done" onClick={() => handleMarkProductResolved(p.id)}>Resuelto</button>
                      )}
                      {isManager && (
                        <button className="task-action-btn delete" onClick={() => handleDeleteProduct(p.id)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <h4 className="task-title">{p.name}</h4>
                  <div className="task-meta">
                    {p.quantity && <span>Cantidad: {p.quantity} {p.unit}</span>}
                    <span>Reportado por: {p.reportedBy}</span>
                    <span>{formatDate(p.reportedAt)}</span>
                  </div>
                  {p.notes && <p className="task-desc">{p.notes}</p>}
                  {p.requestedBy && <div className="task-review-info">Solicitado por {p.requestedBy} — {formatDate(p.requestedAt)}</div>}
                  {p.resolvedBy && <div className="task-review-info">Resuelto por {p.resolvedBy} — {formatDate(p.resolvedAt)}</div>}
                  {/* Comentario del gerente/admin */}
                  {p.managerComment && (
                    <div style={{background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'8px', padding:'8px 10px', marginTop:'6px'}}>
                      <p style={{margin:0, fontSize:'12px', color:'var(--blue)'}}>💬 <strong>{p.commentBy}:</strong> {p.managerComment}</p>
                    </div>
                  )}
                  {isManager && (
                    <div style={{marginTop:'8px', display:'flex', gap:'6px'}}>
                      <input
                        placeholder="💬 Dejar comentario sobre este reporte..."
                        defaultValue={p.managerComment||''}
                        id={`prod-comment-${p.id}`}
                        style={{flex:1, padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}}
                      />
                      <button onClick={()=>{
                        const comment = document.getElementById(`prod-comment-${p.id}`)?.value;
                        const updated = (Array.isArray(missingProducts)?missingProducts:[]).map(mp => mp.id===p.id ? {...mp, managerComment:comment, commentBy:currentUser?.name, commentAt:new Date().toISOString()} : mp);
                        setMissingProducts(updated);
                        localStorage.setItem('missingProducts', JSON.stringify(updated));
                        if(navigator.onLine){const r=ref(database,'missingProducts');const obj={};updated.forEach(m=>{if(m&&m.id)obj[m.id]=m;});set(r,sanitizeForFirebase(obj));}
                      }} style={{padding:'6px 12px', background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}>💾</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'performance' && isManager && (
        <div>
          <h3 style={{color:'var(--text-primary)', marginBottom:'16px', fontSize:'16px', fontWeight:'700'}}>Desempeño por Empleado</h3>
          <div className="tasks-list">
            {(() => {
              // Calcular scores para ranking con fórmula completa
              const monthStr = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`;
              const empScores = (Array.isArray(employees)?employees:[]).map(emp => {
                const isAdminRole = emp.id === 999 || (config.extraAdmins||[]).includes(`EMP${emp.id}`);
                if (isAdminRole) {
                  // Fórmula especial para admin: gestión
                  const createdTasks = (Array.isArray(tasks)?tasks:[]).filter(t => t.createdBy === emp.name || t.createdByRole === 'admin').length;
                  const reviewedTasks = (Array.isArray(tasks)?tasks:[]).filter(t => (t.reviewStatus==='approved'||t.reviewStatus==='rejected') && (Array.isArray(employees)?employees:[]).find(e=>e.id===emp.id)?.name === t.reviewedBy).length;
                  const totalTasks = (Array.isArray(tasks)?tasks:[]).filter(t => t.status !== 'pending').length;
                  const taskMgmt = totalTasks > 0 ? Math.min(100, Math.round((createdTasks/Math.max(totalTasks,1))*100 + (reviewedTasks/Math.max(totalTasks,1))*50)) : 50;
                  const prodsManaged = (Array.isArray(missingProducts)?missingProducts:[]).filter(p => p.status === 'requested' || p.status === 'resolved').length;
                  const totalProds = (Array.isArray(missingProducts)?missingProducts:[]).length;
                  const prodMgmt = totalProds > 0 ? Math.round((prodsManaged/totalProds)*100) : 100;
                  const resolvedIssues = (Array.isArray(attendanceIssues)?attendanceIssues:[]).filter(i => i.status !== 'pending').length;
                  const totalIssues = (Array.isArray(attendanceIssues)?attendanceIssues:[]).length;
                  const issueMgmt = totalIssues > 0 ? Math.round((resolvedIssues/totalIssues)*100) : 100;
                  const score = Math.round((taskMgmt*0.35)+(prodMgmt*0.3)+(issueMgmt*0.2)+50*0.15);
                  return {id: emp.id, score, isAdmin: true};
                }
                const empTasks = (Array.isArray(tasks)?tasks:[]).filter(t => String(t.assignedTo) === String(emp.id));
                const done = empTasks.filter(t => t.status === 'done').length;
                const total = empTasks.length;
                const approved = empTasks.filter(t => t.reviewStatus === 'approved').length;
                const taskRate = total > 0 ? Math.round((done/total)*100) : 100;
                const approvalRate = done > 0 ? Math.round((approved/done)*100) : 100;
                const empRecs = (Array.isArray(attendanceRecords)?attendanceRecords:[]).filter(r => r.employeeId === emp.id && r.date?.startsWith(monthStr));
                const lateC = empRecs.filter(r => r.isLate).length;
                const punctRate = empRecs.length > 0 ? Math.round(((empRecs.length-lateC)/empRecs.length)*100) : 100;
                const prodsRep = (Array.isArray(missingProducts)?missingProducts:[]).filter(p => String(p.reportedById) === String(emp.id)).length;
                const score = Math.round((taskRate*0.4)+(punctRate*0.3)+(approvalRate*0.2)+(Math.min(prodsRep*10,100)*0.1));
                return {id: emp.id, score};
              });
              const getRank = (empId) => {
                const sorted = [...empScores].sort((a,b)=>b.score-a.score);
                return sorted.findIndex(e=>e.id===empId)+1;
              };
              const rankEmojis = {1:'🥇',2:'🥈',3:'🥉'};
              // Ordenar por score — excluir gerente (1000)
              const sortedEmps = [...(Array.isArray(employees)?employees:[])].filter(emp => {
                if (emp.id === 1000) return false; // Gerente fuera del ranking
                const empT = (Array.isArray(tasks)?tasks:[]).filter(t => String(t.assignedTo) === String(emp.id));
                const empP = (Array.isArray(missingProducts)?missingProducts:[]).filter(p => String(p.reportedById) === String(emp.id));
                const isAdminEmp = emp.id === 999 || (config.extraAdmins||[]).includes(`EMP${emp.id}`);
                if (isAdminEmp) return true; // Admin siempre aparece
                return empT.length > 0 || empP.length > 0;
              }).sort((a,b) => (empScores.find(e=>e.id===b.id)?.score||0) - (empScores.find(e=>e.id===a.id)?.score||0));

              return sortedEmps.map((emp, sortIdx) => {
              const empTasks = (Array.isArray(tasks)?tasks:[]).filter(t => String(t.assignedTo) === String(emp.id));
              const done = empTasks.filter(t => t.status === 'done').length;
              const notDone = empTasks.filter(t => t.status === 'not_done').length;
              const pending = empTasks.filter(t => t.status === 'pending').length;
              const total = empTasks.length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const empProducts = (Array.isArray(missingProducts)?missingProducts:[]).filter(p => String(p.reportedById) === String(emp.id));
              const reviewed = empTasks.filter(t => t.reviewStatus === 'reviewed' || t.reviewStatus === 'approved').length;

              // ── ANÁLISIS DE COMPORTAMIENTO ──
              const completedTasks = empTasks.filter(t => t.status === 'done' && t.completedAt).sort((a,b) => new Date(a.completedAt)-new Date(b.completedAt));
              let suspiciousGroups = [];
              if (completedTasks.length >= 2) {
                let group = [completedTasks[0]];
                for (let i = 1; i < completedTasks.length; i++) {
                  const diff = (new Date(completedTasks[i].completedAt) - new Date(completedTasks[i-1].completedAt)) / 1000;
                  if (diff < 60) { // menos de 60 segundos entre marcaciones
                    group.push(completedTasks[i]);
                  } else {
                    if (group.length >= 3) suspiciousGroups.push([...group]);
                    group = [completedTasks[i]];
                  }
                }
                if (group.length >= 3) suspiciousGroups.push(group);
              }
              const isSuspicious = suspiciousGroups.length > 0;
              const maxGroupSize = suspiciousGroups.length > 0 ? Math.max(...suspiciousGroups.map(g=>g.length)) : 0;

              const rank = sortIdx + 1;
              const rankEmoji = rankEmojis[rank] || '';
              const empScore = empScores.find(e=>e.id===emp.id)?.score || 0;
              const isExpanded = expandedPerfEmps[emp.id];
              const top3Colors = {1:'rgba(255,215,0,0.15)', 2:'rgba(192,192,192,0.12)', 3:'rgba(205,127,50,0.12)'};
              const top3Borders = {1:'rgba(255,215,0,0.5)', 2:'rgba(192,192,192,0.4)', 3:'rgba(205,127,50,0.4)'};
              if (total === 0 && empProducts.length === 0) return null;
              return (
                <div key={emp.id} className="task-card" style={{
                  border: rank<=3 ? `2px solid ${top3Borders[rank]}` : isSuspicious ? '1px solid rgba(245,158,11,0.5)' : undefined,
                  background: rank<=3 ? top3Colors[rank] : undefined,
                  transform: rank===1 ? 'scale(1.01)' : undefined
                }}>
                  {/* Header compacto siempre visible */}
                  <div style={{display:'flex', alignItems:'center', gap:'10px', cursor:'pointer'}} onClick={()=>setExpandedPerfEmps({...expandedPerfEmps,[emp.id]:!isExpanded})}>
                    <span style={{fontSize: rank<=3?'28px':'20px'}}>{rankEmoji||emp.photo}</span>
                    <div style={{flex:1}}>
                      <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                        <span style={{fontWeight:'700', color:'var(--text-primary)', fontSize:'14px'}}>{emp.name}</span>
                        {isSuspicious && <span style={{background:'rgba(245,158,11,0.15)', color:'#f59e0b', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'4px', padding:'1px 6px', fontSize:'10px', fontWeight:'700'}}>⚡ Marcado rápido</span>}
                      </div>
                      <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>{emp.position} · #{rank} ranking</span>
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize: rank<=3?'22px':'18px', fontWeight:'900', color: empScore>=80?'var(--green)':empScore>=50?'var(--orange)':'#ef4444'}}>{empScore}pts</div>
                        <div style={{fontSize:'10px', color:'var(--text-muted)'}}>completado: {pct}%</div>
                      </div>
                      <span style={{color:'var(--text-muted)', fontSize:'12px'}}>{isExpanded?'▲':'▼'}</span>
                    </div>
                  </div>
                  {/* Barra de progreso siempre visible */}
                  <div style={{height:'4px', background:'var(--bg-elevated)', borderRadius:'2px', overflow:'hidden', marginTop:'8px'}}>
                    <div style={{height:'100%', width:pct+'%', background:pct>=80?'var(--green)':pct>=50?'var(--orange)':'#ef4444', borderRadius:'2px'}} />
                  </div>
                  {/* Detalle expandible */}
                  {isExpanded && (
                  <div style={{marginTop:'10px', paddingTop:'10px', borderTop:'1px solid var(--border)'}}>
                  <div className="task-meta">
                    <span>Total: {total}</span>
                    <span style={{color:'var(--accent)'}}>✅ {done}</span>
                    <span style={{color:'var(--red)'}}>❌ {notDone}</span>
                    <span style={{color:'var(--orange)'}}>⏳ {pending}</span>
                    <span style={{color:'var(--blue)'}}>👁 Revisadas: {reviewed}</span>
                    <span>📦 Productos: {empProducts.length}</span>
                  </div>
                  <div style={{marginTop:'10px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'4px'}}>
                      <span style={{fontSize:'11px', color:'var(--text-muted)'}}>Tasa de completado</span>
                      <span style={{fontSize:'12px', fontWeight:'700', color: pct >= 80 ? 'var(--accent)' : pct >= 50 ? 'var(--orange)' : 'var(--red)'}}>{pct}%</span>
                    </div>
                    <div style={{height:'6px', background:'var(--bg-elevated)', borderRadius:'3px', overflow:'hidden'}}>
                      <div style={{height:'100%', width: pct + '%', background: pct >= 80 ? 'var(--accent)' : pct >= 50 ? 'var(--orange)' : 'var(--red)', borderRadius:'3px', transition:'width 0.4s'}}></div>
                    </div>
                  </div>
                  {/* Detalle de grupos sospechosos */}
                  {isSuspicious && (
                    <div style={{marginTop:'10px', background:'rgba(245,158,11,0.08)', borderRadius:'8px', padding:'10px', border:'1px solid rgba(245,158,11,0.2)'}}>
                      <p style={{margin:'0 0 6px', fontSize:'12px', color:'#f59e0b', fontWeight:'700'}}>⚡ Grupos de marcación rápida detectados:</p>
                      {suspiciousGroups.map((group, gi) => (
                        <div key={gi} style={{marginBottom:'6px'}}>
                          <p style={{margin:'0 0 2px', fontSize:'11px', color:'var(--text-secondary)'}}>
                            {group.length} tareas marcadas el {new Date(group[0].completedAt).toLocaleDateString('es-CL')} entre {new Date(group[0].completedAt).toLocaleTimeString('es-CL',{hour:'2-digit',minute:'2-digit',second:'2-digit'})} y {new Date(group[group.length-1].completedAt).toLocaleTimeString('es-CL',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                            {' '}({Math.round((new Date(group[group.length-1].completedAt)-new Date(group[0].completedAt))/1000)}s total)
                          </p>
                          {group.map(t => (
                            <div key={t.id} style={{fontSize:'11px', color:'var(--text-muted)', paddingLeft:'8px'}}>
                              • {t.title?.substring(0,40)} — {new Date(t.completedAt).toLocaleTimeString('es-CL',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  </div>)}
                </div>
              );
            });
            })()}
          </div>
        </div>
      )}

      {showTareasIA && tareasIA.length > 0 && (
        <div className="modal-overlay" onClick={() => setShowTareasIA(false)}>
          <div className="modal-content" style={{maxWidth:'600px', maxHeight:'80vh', overflowY:'auto'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🤖 Tareas propuestas por IA</h2>
              <button className="btn-close" onClick={() => setShowTareasIA(false)}>X</button>
            </div>
            <div className="modal-body" style={{padding:'16px 0'}}>
              <p style={{fontSize:'12px', color:'var(--text-secondary)', marginBottom:'16px'}}>
                Revisa y edita las tareas antes de aprobarlas. Solo se crearán las que apruebes.
              </p>
              {tareasIA.map((empTareas, empIdx) => (
                <div key={empIdx} style={{marginBottom:'16px', background:'var(--bg-elevated)', borderRadius:'10px', padding:'12px'}}>
                  <p style={{fontSize:'13px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 10px'}}>
                    {empTareas.empleadoNombre}
                  </p>
                  {(empTareas.tareas || []).map((tarea, tareaIdx) => {
                    const key = `${empIdx}-${tareaIdx}`;
                    const editada = tareasIAEditadas[key] || tarea;
                    const aprobada = editada.aprobada !== false;
                    const asignadoId = editada.empleadoIdOverride || empTareas.empleadoId;
                    const lsEmpsSelect = JSON.parse(localStorage.getItem('employees') || '[]').filter(e => e?.status === 'active' && e?.id !== 999);
                    return (
                      <div key={tareaIdx} style={{background: aprobada ? 'rgba(34,197,94,0.06)' : 'var(--bg-card)', borderRadius:'8px', padding:'10px', marginBottom:'8px', border:`1px solid ${aprobada ? 'rgba(34,197,94,0.2)' : 'var(--border)'}`}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px', gap:'6px'}}>
                          <input
                            value={editada.titulo || tarea.titulo}
                            onChange={e => setTareasIAEditadas({...tareasIAEditadas, [key]: {...editada, titulo: e.target.value}})}
                            style={{flex:1, background:'transparent', border:'none', fontSize:'13px', fontWeight:'600', color:'var(--text-primary)', outline:'none', minWidth:0}}
                          />
                          <button onClick={() => setTareasIAEditadas({...tareasIAEditadas, [key]: {...editada, aprobada: !aprobada}})}
                            style={{padding:'4px 10px', background: aprobada ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)', color: aprobada ? 'var(--green)' : '#ef4444', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'700', flexShrink:0}}>
                            {aprobada ? '✅' : '❌'}
                          </button>
                        </div>
                        <textarea
                          value={editada.descripcion || tarea.descripcion}
                          onChange={e => setTareasIAEditadas({...tareasIAEditadas, [key]: {...editada, descripcion: e.target.value}})}
                          rows={2}
                          style={{width:'100%', background:'transparent', border:'none', fontSize:'12px', color:'var(--text-secondary)', outline:'none', resize:'none', fontFamily:'inherit', marginBottom:'6px'}}
                        />
                        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                          <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>👤 Asignar a:</span>
                          <select
                            value={asignadoId}
                            onChange={e => {
                              const nuevoId = parseInt(e.target.value);
                              const nuevoEmp = lsEmpsSelect.find(emp => emp?.id === nuevoId);
                              setTareasIAEditadas({...tareasIAEditadas, [key]: {...editada, empleadoIdOverride: nuevoId, empleadoNombreOverride: nuevoEmp?.name}});
                            }}
                            style={{padding:'3px 6px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'11px', color:'var(--text-primary)'}}
                          >
                            {lsEmpsSelect.map(emp => (
                              <option key={emp.id} value={emp.id}>{emp.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {userRole === 'manager' && (
                <button onClick={async () => {
                  try {
                    const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
                    const activeEmps = lsEmps.filter(e => e?.status === 'active' && e?.id !== 999);
                    const hoy = new Date();
                    const diasSemana = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
                    const fecha = hoy.toLocaleDateString('es-CL', {day:'numeric', month:'long', year:'numeric'});
                    const diaSemana = diasSemana[hoy.getDay()];
                    const empleados = activeEmps.map(e => ({id: e.id, nombre: e.name, cargo: e.position}));

                    setLoadingTareasIA(true);
                    const response = await fetch('https://ocrfrjlehptgyudfxdah.supabase.co/functions/v1/generate-tasks', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'apikey': 'sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-',
                        'Authorization': 'Bearer sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-'
                      },
                      body: JSON.stringify({ 
                        empleados, fecha, diaSemana,
                        horarioApertura: `${String(config?.schedules?.checkIn?.hour || 10).padStart(2,'0')}:${String(config?.schedules?.checkIn?.minute || 0).padStart(2,'0')}`,
                        horarioCierre: `${String(config?.schedules?.checkOut?.hour || 21).padStart(2,'0')}:${String(config?.schedules?.checkOut?.minute || 0).padStart(2,'0')}`
                      })
                    });
                    const result = await response.json();
                    if (result.tareas) {
                      // Combinar tareas nuevas con las existentes, evitando perder ediciones previas
                      const combinadas = tareasIA.map((empT, idx) => {
                        const nuevasDelEmp = result.tareas.find(r => r.empleadoId === empT.empleadoId);
                        if (!nuevasDelEmp) return empT;
                        return { ...empT, tareas: [...(empT.tareas || []), ...(nuevasDelEmp.tareas || [])] };
                      });
                      setTareasIA(combinadas);
                    }
                  } catch(e) {
                    alert('Error al generar más tareas');
                  } finally {
                    setLoadingTareasIA(false);
                  }
                }} style={{width:'100%', padding:'10px', background:'rgba(99,102,241,0.1)', color:'#6366f1', border:'1px dashed rgba(99,102,241,0.4)', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'600', marginBottom:'12px'}}>
                  {loadingTareasIA ? '⏳ Generando más...' : '🤖 + Generar más tareas para todos'}
                </button>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowTareasIA(false)}>Cancelar</button>
              <button className="btn-save" onClick={() => {
                try {
                  const hoy = new Date();
                  const dateStr = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
                  let creadas = 0;
                  const nuevasTareas = [];
                  tareasIA.forEach((empTareas, empIdx) => {
                    (empTareas.tareas || []).forEach((tarea, tareaIdx) => {
                      const key = `${empIdx}-${tareaIdx}`;
                      const editada = tareasIAEditadas[key] || tarea;
                      if (editada.aprobada === false) return;
                      const nuevaTarea = {
                        id: Date.now() + creadas,
                        title: editada.titulo || tarea.titulo,
                        description: editada.descripcion || tarea.descripcion,
                        assignedTo: editada.empleadoIdOverride || empTareas.empleadoId,
                        assignedName: editada.empleadoNombreOverride || empTareas.empleadoNombre,
                        priority: 'medium',
                        dueDate: dateStr,
                        dueTime: '21:00',
                        createdBy: currentUser?.name || 'Gerente',
                        createdByRole: userRole,
                        createdAt: new Date().toISOString(),
                        status: 'pending',
                        steps: [],
                        employeeObservation: '',
                        completedAt: null,
                        reviewedBy: null,
                        reviewedAt: null,
                        reviewStatus: null,
                        reviewComment: '',
                        managerNote: '',
                        generadaPorIA: true,
                      };
                      nuevasTareas.push(nuevaTarea);
                      creadas++;
                    });
                  });
                  // Guardar todas las tareas en una sola operacion batch
                  if (nuevasTareas.length > 0) {
                    const updated = [...nuevasTareas, ...(Array.isArray(tasks) ? tasks : [])];
                    saveTasksToStorage(updated);
                  }
                  alert(`✅ ${creadas} tarea(s) creadas exitosamente`);
                  setShowTareasIA(false);
                  setTareasIA([]);
                  setTareasIAEditadas({});
                } catch(e) {
                  alert('Error al crear tareas');
                }
              }}>
                ✅ Crear tareas aprobadas
              </button>
            </div>
          </div>
        </div>
      )}

      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal-content" style={{maxWidth:'480px'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTask ? 'Editar Tarea' : 'Nueva Tarea'}</h2>
              <button className="btn-close" onClick={() => setShowTaskModal(false)}>X</button>
            </div>
            <div className="modal-body" style={{padding:'20px 0'}}>
              <div className="form-group">
                <label>Titulo *</label>
                <input className="input-field" value={taskForm.title} onChange={e => setTaskForm({...taskForm, title: e.target.value})} placeholder="Ej: Limpieza de estantes" />
              </div>
              <div className="form-group">
                <label>Descripcion</label>
                <textarea className="input-field" rows={3} value={taskForm.description} onChange={e => setTaskForm({...taskForm, description: e.target.value})} placeholder="Detalle de la tarea..." />
              </div>
              <div className="form-group">
                <label>Asignar a *</label>
                <select className="input-field" value={taskForm.assignedTo} onChange={e => setTaskForm({...taskForm, assignedTo: e.target.value})}>
                  <option value="">Seleccionar empleado...</option>
                  {employees.filter(e => e.status === 'active' && e.role !== 'manager' && e.id !== currentUser?.id).filter((e,i,arr)=>arr.findIndex(x=>x.id===e.id)===i).map(e => <option key={e.id} value={String(e.id)}>{e.name} — {e.position}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Prioridad</label>
                <select className="input-field" value={taskForm.priority} onChange={e => setTaskForm({...taskForm, priority: e.target.value})}>
                  <option value="high">Alta</option>
                  <option value="medium">Media</option>
                  <option value="low">Baja</option>
                </select>
              </div>
              <div className="form-group">
                <label>📅 Fecha y Hora Límite</label>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px'}}>
                  <input
                    type="date"
                    className="input-field"
                    value={taskForm.dueDate || ''}
                    onChange={e => setTaskForm({...taskForm, dueDate: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <input
                    type="time"
                    className="input-field"
                    value={taskForm.dueTime || ''}
                    onChange={e => setTaskForm({...taskForm, dueTime: e.target.value})}
                  />
                </div>
                {taskForm.dueDate && (
                  <p style={{fontSize:'11px', color:'var(--orange)', margin:'4px 0 0'}}>
                    ⏰ Vence: {taskForm.dueDate} {taskForm.dueTime && `a las ${taskForm.dueTime}`}
                  </p>
                )}
              </div>
              <div className="form-group">
                <label>Pasos / Subtareas</label>
                <div style={{display:'flex', gap:'8px', marginBottom:'8px'}}>
                  <input
                    className="input-field"
                    value={newStepText}
                    onChange={e => setNewStepText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && newStepText.trim()) { setTaskForm({...taskForm, steps: [...(taskForm.steps||[]), {id: Date.now(), text: newStepText.trim(), done: false}]}); setNewStepText(''); e.preventDefault(); }}}
                    placeholder="Ej: Limpiar estantes superiores"
                    style={{flex:1}}
                  />
                  <button
                    onClick={() => { if (newStepText.trim()) { setTaskForm({...taskForm, steps: [...(taskForm.steps||[]), {id: Date.now(), text: newStepText.trim(), done: false}]}); setNewStepText(''); }}}
                    style={{padding:'8px 14px', background:'var(--blue)', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', whiteSpace:'nowrap'}}
                  >+ Agregar</button>
                </div>
                {(taskForm.steps||[]).length > 0 && (
                  <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                    {(taskForm.steps||[]).map((step, idx) => (
                      <div key={step.id} style={{display:'flex', alignItems:'center', gap:'8px', background:'var(--bg-secondary)', padding:'8px 10px', borderRadius:'8px'}}>
                        <span style={{color:'var(--text-secondary)', fontSize:'13px', minWidth:'20px', fontWeight:'700'}}>{idx+1}.</span>
                        <span style={{flex:1, fontSize:'14px', color:'var(--text-primary)'}}>{step.text}</span>
                        <button onClick={() => setTaskForm({...taskForm, steps: (taskForm.steps||[]).filter(s => s.id !== step.id)})} style={{background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:'16px', lineHeight:1}}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{display:'flex', gap:'10px', marginTop:'8px'}}>
                <button className="btn-cancel" onClick={() => { setShowTaskModal(false); setNewStepText(''); }} style={{flex:1}}>Cancelar</button>
                <button className="btn-save" onClick={editingTask ? handleEditTask : handleCreateTask} style={{flex:2}}>{editingTask ? 'Actualizar' : 'Crear Tarea'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProductModal && (
        <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="modal-content" style={{maxWidth:'440px'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reportar Producto Faltante</h2>
              <button className="btn-close" onClick={() => setShowProductModal(false)}>X</button>
            </div>
            <div className="modal-body" style={{padding:'20px 0'}}>
              <div className="form-group">
                <label>Nombre del producto *</label>
                <input className="input-field" value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} placeholder="Ej: Detergente Omo 1kg" />
                {(() => {
                  // Algoritmo de similitud Jaccard por tokens
                  const similitud = (a, b) => {
                    if (!a || !b) return 0;
                    const tokenizar = s => new Set(s.toLowerCase().replace(/[.,]/g,'').split(/\s+/).filter(t => t.length > 1));
                    const ta = tokenizar(a), tb = tokenizar(b);
                    const interseccion = [...ta].filter(t => tb.has(t)).length;
                    const union = new Set([...ta, ...tb]).size;
                    return union === 0 ? 0 : interseccion / union;
                  };
                  const nombreActual = productForm.name?.trim();
                  if (!nombreActual || nombreActual.length < 3) return null;
                  const pendientes = (Array.isArray(missingProducts)?missingProducts:[]).filter(p => p.status === 'pending');
                  const similares = pendientes.map(p => ({...p, sim: similitud(nombreActual, p.name)}))
                    .filter(p => p.sim >= 0.6)
                    .sort((a,b) => b.sim - a.sim)
                    .slice(0, 3);
                  if (similares.length === 0) return null;
                  return (
                    <div style={{marginTop:'6px', borderRadius:'8px', overflow:'hidden', border:'1px solid rgba(245,158,11,0.4)'}}>
                      {similares.map((p, i) => (
                        <div key={p.id} style={{padding:'8px 10px', background: i%2===0 ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.04)', display:'flex', alignItems:'center', gap:'8px'}}>
                          <span style={{fontSize:'14px'}}>{p.sim >= 0.85 ? '🔴' : '🟡'}</span>
                          <div style={{flex:1}}>
                            <p style={{margin:0, fontSize:'12px', fontWeight:'700', color: p.sim >= 0.85 ? '#ef4444' : 'var(--orange)'}}>
                              {p.sim >= 0.85 ? 'Ya reportado:' : 'Producto similar:'}
                            </p>
                            <p style={{margin:'1px 0 0', fontSize:'12px', color:'var(--text-primary)'}}>
                              <strong>{p.name}</strong> — {p.quantity} {p.unit}
                            </p>
                            <p style={{margin:'1px 0 0', fontSize:'11px', color:'var(--text-secondary)'}}>
                              Reportado por {p.reportedBy} · {p.sim >= 0.85 ? 'Verifica antes de reportar nuevamente' : '¿Es el mismo producto?'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
                <div className="form-group">
                  <label>Cantidad</label>
                  <input className="input-field" value={productForm.quantity} onChange={e => setProductForm({...productForm, quantity: e.target.value})} placeholder="Ej: 5" />
                </div>
                <div className="form-group">
                  <label>Unidad</label>
                  <select className="input-field" value={productForm.unit} onChange={e => setProductForm({...productForm, unit: e.target.value})}>
                    <option>unidades</option>
                    <option>kg</option>
                    <option>litros</option>
                    <option>cajas</option>
                    <option>bolsas</option>
                    <option>paquetes</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Observaciones</label>
                <textarea className="input-field" rows={2} value={productForm.notes} onChange={e => setProductForm({...productForm, notes: e.target.value})} placeholder="Motivo o urgencia..." />
              </div>
              <div style={{display:'flex', gap:'10px', marginTop:'8px'}}>
                <button className="btn-cancel" onClick={() => setShowProductModal(false)} style={{flex:1}}>Cancelar</button>
                <button className="btn-save" onClick={handleReportProduct} style={{flex:2}}>Reportar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const AdminDashboardView = () => {
  const {
    chatUnread, setChatUnread, showChat, setShowChat, chatAdminId, setChatAdminId,
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setCurrentView, setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const todayRecords = attendanceRecords.filter(r => r.date === getLocalDateStr());
  const onTimeCount = todayRecords.filter(r => r.status === 'on_time').length;
  const lateCount = todayRecords.filter(r => r.status === 'late').length;
  const activeCount = employees.filter(e => e.status === 'active').length;
  const isManager = userRole === 'manager' || userRole === 'admin';
  const [showIssues, setShowIssues] = React.useState(false);
  const [selectedDayDetail, setSelectedDayDetail] = React.useState(null);
  const [issueComment, setIssueComment] = React.useState({});
  const [issueOrder, setIssueOrder] = React.useState('newest');
  const [annText, setAnnText] = React.useState('');
  const [annType, setAnnType] = React.useState('info');
  const [annDays, setAnnDays] = React.useState('1');
  const [expandedCard, setExpandedCard] = React.useState(null);
  const [markOnTimeSelected, setMarkOnTimeSelected] = React.useState([]);
  const [showMarkOnTime, setShowMarkOnTime] = React.useState(false);
  
  const [informeIA, setInformeIA] = React.useState('');
  const [loadingIA, setLoadingIA] = React.useState(false);
  const [showInformeIA, setShowInformeIA] = React.useState(false);

  const generarInformeIA = async () => {
    try {
      setLoadingIA(true);
      setShowInformeIA(true);
      setInformeIA('');

      const hoy = new Date();
      const dateStr = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
      
      const lsRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
      const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
      const lsProds = JSON.parse(localStorage.getItem('missingProducts') || '[]');

      const todayRecords = lsRecords.filter(r => r?.date === dateStr);
      const activeEmps = lsEmps.filter(e => e?.status === 'active');
      const tareasHoy = lsTasks.filter(t => t?.dueDate === dateStr);
      const prodsPendientes = lsProds.filter(p => p?.status === 'pending');

      // Datos adicionales para evaluación completa
      const lsAbsenceRequests = JSON.parse(localStorage.getItem('absenceRequests') || '[]');
      const lsAbsences = JSON.parse(localStorage.getItem('employeeAbsences') || '[]');
      
      // Registros del admin este mes
      const mesActual = dateStr.substring(0, 7);
      const adminEmps = lsEmps.filter(e => e?.id === 999 || e?.role === 'admin');
      const adminRecordsHoy = todayRecords.filter(r => adminEmps.find(a => a?.id === r?.employeeId));
      const adminRecordsMes = lsRecords.filter(r => r?.date?.startsWith(mesActual) && adminEmps.find(a => a?.id === r?.employeeId));
      const adminAtrasosMes = adminRecordsMes.filter(r => r?.isLate);

      // Tareas sin revisar por más de 24 horas
      const ahora = new Date();
      const tareasSinRevisarMas24h = lsTasks.filter(t => {
        if (t?.status !== 'done' || t?.reviewStatus) return false;
        if (!t?.completedAt) return false;
        const horas = (ahora - new Date(t.completedAt)) / (1000 * 60 * 60);
        return horas > 24;
      });

      // Solicitudes de ausencia pendientes
      const solicitudesPendientes = lsAbsenceRequests.filter(r => r?.status === 'pending');

      const empleadosConTurnoHoy = activeEmps.filter(e => e?.monthlySchedule?.[dateStr]?.enabled && e?.monthlySchedule?.[dateStr]?.start);
      const empleadosLibresHoy = activeEmps.filter(e => !e?.monthlySchedule?.[dateStr]?.enabled || !e?.monthlySchedule?.[dateStr]?.start);

      const data = {
        fecha: hoy.toLocaleDateString('es-CL', {weekday:'long', day:'numeric', month:'long'}),
        asistencia: {
          totalActivosEnPlantilla: activeEmps.length,
          totalConTurnoHoy: empleadosConTurnoHoy.length,
          libresHoy: empleadosLibresHoy.map(e => e?.name),
          horaActual: `${hoy.getHours()}:${String(hoy.getMinutes()).padStart(2,'0')}`,
          empleadosConTurnoDetalle: empleadosConTurnoHoy.map(e => ({
            nombre: e?.name,
            horaEntrada: e?.monthlySchedule?.[dateStr]?.start,
            horaSalida: e?.monthlySchedule?.[dateStr]?.end,
            debioHaberMarcado: (() => {
              try {
                const [h, m] = (e?.monthlySchedule?.[dateStr]?.start || '23:59').split(':').map(Number);
                return hoy.getHours() > h || (hoy.getHours() === h && hoy.getMinutes() > m + 15);
              } catch(err) { return false; }
            })()
          })),
          notaHorarios: "Evaluar ausencias SOLO para empleados donde debioHaberMarcado=true segun su hora de entrada del calendario. Si debioHaberMarcado=false el turno aun no comienza y es completamente normal que no hayan marcado.",
          marcaronEntradaDeLosConTurnoHoy: todayRecords.length,
          completaronTurno: todayRecords.filter(r => r?.checkIn && r?.checkOut).length,
          enTurno: todayRecords.filter(r => r?.checkIn && !r?.checkOut).length,
          conAtraso: todayRecords.filter(r => r?.isLate).map(r => ({nombre: r?.employeeName, minutos: r?.lateMinutes})),
          sinMarcar: activeEmps.filter(e => !todayRecords.find(r => r?.employeeId === e?.id) && e?.monthlySchedule?.[dateStr]?.enabled).map(e => e?.name),
          nota: "totalConTurnoHoy es el numero real de empleados que deben trabajar hoy segun el calendario. totalActivosEnPlantilla es el total general de empleados de la empresa, NO todos trabajan el mismo dia. Usar siempre totalConTurnoHoy para evaluar asistencia del dia, nunca totalActivosEnPlantilla.",
        },
        tareas: {
          total: tareasHoy.length,
          completadas: tareasHoy.filter(t => t?.status === 'done').length,
          pendientes: tareasHoy.filter(t => t?.status === 'pending').length,
          vencidas: lsTasks.filter(t => t?.status === 'overdue').length,
          sinRevisar: lsTasks.filter(t => t?.status === 'done' && !t?.reviewStatus).length,
          sinRevisarMas24h: tareasSinRevisarMas24h.length,
          rechazadas: lsTasks.filter(t => t?.reviewStatus === 'rejected').length,
        },
        productos: {
          pendientes: prodsPendientes.length,
          masde7dias: prodsPendientes.filter(p => p?.reportedAt && (new Date() - new Date(p.reportedAt)) > 7*24*60*60*1000).length,
          solicitados: lsProds.filter(p => p?.status === 'requested').length,
          resueltos: lsProds.filter(p => p?.status === 'resolved').length,
          todosLosProductos: lsProds.map(p => ({nombre: p?.name, estado: p?.status, reportadoPor: p?.reportedBy, fecha: p?.reportedAt})),
          analisisCategoriasNota: "Analiza los nombres de todosLosProductos para identificar categorias con mas faltantes (ej: galletas, bebidas, cecinas, conservas, lacteos, etc) y menciona las top 3 categorias con mas productos pendientes o resueltos historicamente.",
        },
        evaluacionAdmin: (() => {
          const lsAbsences = JSON.parse(localStorage.getItem('employeeAbsences') || '[]');
          const adminAusenciaHoy = lsAbsences.find(a => {
            const esAdmin = adminEmps.find(emp => emp?.id === a?.employeeId);
            return esAdmin && a?.startDate <= dateStr && a?.endDate >= dateStr;
          });
          const tipoAusencia = adminAusenciaHoy?.type === 'vacation' ? 'Vacaciones' : adminAusenciaHoy?.type === 'medical' ? 'Licencia médica' : adminAusenciaHoy ? 'Permiso' : null;
          return {
            estaDeAusenciaHoy: !!adminAusenciaHoy,
            tipoAusenciaHoy: tipoAusencia,
            notaAusencia: tipoAusencia ? `El admin esta de ${tipoAusencia} hoy - NO evaluar puntualidad ni presencia del dia, es esperado que no marque asistencia` : null,
            marcoCorrecto: adminRecordsHoy.length > 0,
            atrasoHoy: adminAusenciaHoy ? false : adminRecordsHoy.some(r => r?.isLate),
            atrasosMes: adminAtrasosMes.length,
            diasTrabajadasMes: adminRecordsMes.length,
            tareassinRevisarMas24h: tareasSinRevisarMas24h.length,
            solicitudesAusenciaPendientes: solicitudesPendientes.length,
            empleadosSinHorario: activeEmps.filter(e => !e?.monthlySchedule || Object.keys(e?.monthlySchedule || {}).length < 15).map(e => e?.name),
            productosPendientesSinGestionar: prodsPendientes.filter(p => p?.reportedAt && (new Date() - new Date(p.reportedAt)) > 3*24*60*60*1000).length,
          };
        })(),
        empleados: activeEmps.map(emp => {
          const empRecords = lsRecords.filter(r => r?.employeeId === emp?.id && r?.date?.startsWith(mesActual));
          const empTasks = lsTasks.filter(t => String(t?.assignedTo) === String(emp?.id));
          const empAtrasos = empRecords.filter(r => r?.isLate).length;
          const empTareasCompletadas = empTasks.filter(t => t?.status === 'done').length;
          const empTareasVencidas = empTasks.filter(t => t?.status === 'overdue').length;
          return {
            nombre: emp?.name,
            cargo: emp?.position,
            atrasosMes: empAtrasos,
            diasTrabajadasMes: empRecords.length,
            tareasCompletadas: empTareasCompletadas,
            tareasVencidas: empTareasVencidas,
            puntualidad: empRecords.length > 0 ? Math.round(((empRecords.length - empAtrasos) / empRecords.length) * 100) : 100,
          };
        }),
      };

      const response = await fetch('https://ocrfrjlehptgyudfxdah.supabase.co/functions/v1/attendance-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-',
          'Authorization': 'Bearer sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-'
        },
        body: JSON.stringify({ data })
      });

      const result = await response.json();
      setInformeIA(result.informe || result.error || 'Error al generar informe');
    } catch(e) {
      setInformeIA('Error al conectar con el servicio de IA. Intenta nuevamente.');
    } finally {
      setLoadingIA(false);
    }
  };

  return (
    <div className="admin-dashboard">

      {/* ── ALERTAS CRITICAS AUTOMATICAS PARA EL GERENTE ── */}
      {userRole === 'manager' && (() => {
        try {
          const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
          const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
          const lsProdsLS = JSON.parse(localStorage.getItem('missingProducts') || '[]');
          const lsProds = (Array.isArray(missingProducts) && missingProducts.length > 0) ? missingProducts : lsProdsLS;
          const lsRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
          const hoy = new Date();
          const ds = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
          const mesActual = ds.substring(0,7);
          const hace24h = new Date(hoy - 24*60*60*1000).toISOString();

          const alertas = [];

          // 1. Tareas completadas sin revisar hace mas de 24h
          const sinRevisar24h = lsTasks.filter(t => t?.status === 'done' && !t?.reviewStatus && t?.completedAt && t.completedAt < hace24h).length;
          if (sinRevisar24h > 3) alertas.push({ color:'#ef4444', bg:'rgba(239,68,68,0.1)', border:'rgba(239,68,68,0.3)', texto:`${sinRevisar24h} tareas llevan más de 24 horas esperando tu revisión`, accion:'tasks' });

          // 1b. Alerta si admin no esta revisando tareas
          const lsTasksAlert = JSON.parse(localStorage.getItem('tasks') || '[]');
          const _hoyAlert = new Date();
          const _ayerAlert = new Date(_hoyAlert); _ayerAlert.setDate(_ayerAlert.getDate()-1);
          const ayerStrAlert = `${_ayerAlert.getFullYear()}-${String(_ayerAlert.getMonth()+1).padStart(2,'0')}-${String(_ayerAlert.getDate()).padStart(2,'0')}`;
          const hoyStrAlert = `${_hoyAlert.getFullYear()}-${String(_hoyAlert.getMonth()+1).padStart(2,'0')}-${String(_hoyAlert.getDate()).padStart(2,'0')}`;
          const tareasSinRevisar = lsTasksAlert.filter(t => 
            t?.status === 'done' && 
            !t?.reviewStatus && 
            t?.dueDate && 
            (t.dueDate === ayerStrAlert || t.dueDate === hoyStrAlert)
          ).length;
          if (tareasSinRevisar >= 5) alertas.push({ 
            color:'#ef4444', bg:'rgba(239,68,68,0.1)', border:'rgba(239,68,68,0.3)', 
            texto:`⚠️ El admin tiene ${tareasSinRevisar} tareas sin revisar — requiere atención`, 
            accion:'tasks' 
          });

          // 2. Empleados con mas de 3 atrasos este mes
          const activeEmps = lsEmps.filter(e => e?.status === 'active' && e?.id !== 1000);
          activeEmps.forEach(emp => {
            const atrasosMes = lsRecords.filter(r => r?.employeeId === emp?.id && r?.date?.startsWith(mesActual) && r?.isLate).length;
            if (atrasosMes >= 3) alertas.push({ color:'var(--orange)', bg:'rgba(245,158,11,0.1)', border:'rgba(245,158,11,0.3)', texto:`${emp?.name} tiene ${atrasosMes} atrasos este mes`, accion:'reports' });
          });

          // 3. Productos sin gestionar hace mas de 10 dias
          const prodsViejos = lsProds.filter(p => {
            if (p?.status !== 'pending' || !p?.reportedAt) return false;
            return (hoy - new Date(p.reportedAt)) > 10*24*60*60*1000;
          }).length;
          if (prodsViejos > 3) alertas.push({ color:'var(--orange)', bg:'rgba(245,158,11,0.1)', border:'rgba(245,158,11,0.3)', texto:`${prodsViejos} productos llevan más de 10 días sin resolverse`, accion:'tasks' });

          // 4. Empleados sin horario para proxima semana
          const proxSemana = [];
          for (let i = 1; i <= 7; i++) {
            const d = new Date(hoy); d.setDate(hoy.getDate() + i);
            proxSemana.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
          }
          const sinHorario = activeEmps.filter(e => !proxSemana.some(d => e?.monthlySchedule?.[d]?.enabled && e?.monthlySchedule?.[d]?.start));
          if (sinHorario.length > 0) alertas.push({ color:'#6366f1', bg:'rgba(99,102,241,0.1)', border:'rgba(99,102,241,0.3)', texto:`${sinHorario.length} empleado(s) sin horario para la próxima semana: ${sinHorario.slice(0,2).map(e=>e?.name?.split(' ')[0]).join(', ')}`, accion:'employees' });

          if (alertas.length === 0) return null;
          return (
            <div style={{marginBottom:'16px', display:'flex', flexDirection:'column', gap:'8px'}}>
              <p style={{fontSize:'12px', fontWeight:'700', color:'var(--text-secondary)', margin:0}}>⚡ Alertas automáticas</p>
              {alertas.map((a, i) => (
                <div key={i} onClick={() => setCurrentView(a.accion)} style={{background:a.bg, border:`1px solid ${a.border}`, borderRadius:'10px', padding:'10px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:'10px'}}>
                  <span style={{fontSize:'16px'}}>🔔</span>
                  <span style={{fontSize:'13px', color:a.color, fontWeight:'600', flex:1}}>{a.texto}</span>
                  <span style={{fontSize:'11px', color:a.color, opacity:0.7}}>Ver →</span>
                </div>
              ))}
            </div>
          );
        } catch(e) { return null; }
      })()}

      {/* ── ALERTA MENSAJES SIN LEER ── */}
      {userRole === 'admin' && (() => {
        try {
          const totalUnread = Object.values(chatUnread||{}).reduce((a,b)=>a+(b||0),0);
          if (totalUnread === 0) return null;
          return (
            <div onClick={() => { setShowChat(true); setChatAdminId(currentUser?.id); setChatUnread(u=>({...u,[currentUser?.id]:0})); }}
              style={{background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'12px', padding:'12px 16px', marginBottom:'12px', cursor:'pointer', display:'flex', alignItems:'center', gap:'12px'}}>
              <span style={{fontSize:'20px'}}>🔔</span>
              <div style={{flex:1}}>
                <p style={{fontSize:'13px', fontWeight:'700', color:'#ef4444', margin:0}}>El gerente te envió {totalUnread} mensaje(s) nuevo(s)</p>
                <p style={{fontSize:'11px', color:'#ef4444', margin:'2px 0 0', opacity:0.8}}>Toca para ver el chat</p>
              </div>
              <span style={{fontSize:'11px', color:'white', background:'#ef4444', borderRadius:'12px', padding:'3px 8px', fontWeight:'700'}}>{totalUnread}</span>
            </div>
          );
        } catch(e) { return null; }
      })()}

      {/* ── RECORDATORIOS ADMIN ── */}
      {userRole === 'admin' && (() => {
        try {
          const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
          const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
          const lsProds = JSON.parse(localStorage.getItem('missingProducts') || '[]');
          const lsAbsenceReq = JSON.parse(localStorage.getItem('absenceRequests') || '[]');
          const hoy = new Date();
          const dateStr = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
          const semanaStr = [];
          for (let i = 0; i < 7; i++) {
            const d = new Date(hoy);
            d.setDate(hoy.getDate() + i);
            semanaStr.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
          }

          const recordatorios = [];

          // Tareas completadas sin revisar
          const sinRevisar = lsTasks.filter(t => t?.status === 'done' && !t?.reviewStatus).length;
          if (sinRevisar > 0) recordatorios.push({
            color: '#ef4444', bg: 'rgba(239,68,68,0.08)',
            texto: `${sinRevisar} tarea${sinRevisar > 1 ? 's' : ''} completada${sinRevisar > 1 ? 's' : ''} esperando tu revisión`
          });

          // Empleados sin horario esta semana
          const activeEmps = lsEmps.filter(e => e?.status === 'active' && e?.id !== 1000);
          const sinHorario = activeEmps.filter(e => !semanaStr.some(d => e?.monthlySchedule?.[d]?.enabled && e?.monthlySchedule?.[d]?.start));
          if (sinHorario.length > 0) recordatorios.push({
            color: 'var(--orange)', bg: 'rgba(245,158,11,0.08)',
            texto: `${sinHorario.length} empleado${sinHorario.length > 1 ? 's' : ''} sin horario configurado esta semana: ${sinHorario.slice(0,2).map(e => e?.name?.split(' ')[0]).join(', ')}${sinHorario.length > 2 ? ' y más' : ''}`
          });

          // Productos pendientes hace más de 3 días
          const prodsViejos = lsProds.filter(p => p?.status === 'pending' && p?.reportedAt && (new Date() - new Date(p.reportedAt)) > 3*24*60*60*1000).length;
          if (prodsViejos > 0) recordatorios.push({
            color: 'var(--orange)', bg: 'rgba(245,158,11,0.08)',
            texto: `${prodsViejos} producto${prodsViejos > 1 ? 's' : ''} pendiente${prodsViejos > 1 ? 's' : ''} sin gestionar hace más de 3 días`
          });

          // Solicitudes de ausencia sin responder
          const solicPendientes = lsAbsenceReq.filter(r => r?.status === 'pending').length;
          if (solicPendientes > 0) recordatorios.push({
            color: '#a855f7', bg: 'rgba(168,85,247,0.08)',
            texto: `${solicPendientes} solicitud${solicPendientes > 1 ? 'es' : ''} de permiso/vacaciones sin responder`
          });

          if (recordatorios.length === 0) return null;

          return (
            <div style={{marginBottom:'16px', display:'flex', flexDirection:'column', gap:'8px'}}>
              {recordatorios.map((r, i) => (
                <div key={i} style={{background: r.bg, borderRadius:'10px', padding:'10px 14px', fontSize:'13px', fontWeight:'600', color: r.color, border:`1px solid ${r.color}33`}}>
                  ⚡ {r.texto}
                </div>
              ))}
            </div>
          );
        } catch(e) { return null; }
      })()}

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
        <h2 style={{margin:0}}>Panel de Control</h2>
        {userRole === 'manager' && (
          <button onClick={generarInformeIA} disabled={loadingIA}
            style={{display:'flex', alignItems:'center', gap:'6px', padding:'8px 16px', background:'transparent', color:'#7c3aed', border:'1.5px solid #7c3aed', borderRadius:'8px', cursor: loadingIA ? 'wait' : 'pointer', fontSize:'13px', fontWeight:'600', opacity: loadingIA ? 0.7 : 1, fontFamily:'inherit'}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/><path d="M20 3v4"/><path d="M22 5h-4"/></svg>
            {loadingIA ? 'Analizando...' : 'Informe IA'}
          </button>
        )}
      </div>

      {showInformeIA && userRole === 'manager' && (
        <div style={{background:'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))', border:'1px solid rgba(99,102,241,0.3)', borderRadius:'12px', padding:'16px', marginBottom:'20px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
            <span style={{fontSize:'13px', fontWeight:'700', color:'#6366f1'}}>🤖 Informe IA — Análisis ejecutivo</span>
            <button onClick={() => setShowInformeIA(false)} style={{background:'transparent', border:'none', cursor:'pointer', color:'var(--text-secondary)', fontSize:'16px'}}>✕</button>
          </div>
          {loadingIA ? (
            <p style={{fontSize:'13px', color:'var(--text-secondary)', margin:0}}>Analizando datos de la operación...</p>
          ) : (
            <p style={{fontSize:'13px', color:'var(--text-primary)', margin:0, lineHeight:'1.6', whiteSpace:'pre-wrap'}}>{informeIA}</p>
          )}
        </div>
      )}

        {/* ── MENSAJE IA PROACTIVO GERENTE (1 vez por dia) ── */}
        {userRole === 'manager' && (() => {
          try {
            const _hoy = new Date();
            const _ds = `${_hoy.getFullYear()}-${String(_hoy.getMonth()+1).padStart(2,'0')}-${String(_hoy.getDate()).padStart(2,'0')}`;
            const _cacheKey = `iaProactivaGerente_${_ds}`;
            const _cached = localStorage.getItem(_cacheKey);
            if (_cached) {
              return (
                <div style={{background:'linear-gradient(135deg,rgba(99,102,241,0.08),rgba(139,92,246,0.08))', border:'1px solid rgba(99,102,241,0.2)', borderRadius:'12px', padding:'12px 16px', marginBottom:'16px'}}>
                  <p style={{fontSize:'12px', fontWeight:'700', color:'#6366f1', margin:'0 0 6px'}}>🤖 Resumen del día</p>
                  <p style={{fontSize:'13px', color:'var(--text-primary)', margin:0, lineHeight:'1.6', whiteSpace:'pre-wrap'}}>{_cached}</p>
                </div>
              );
            }
            // Generar solo si no existe cache de hoy
            const lsRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
            const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
            const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
            const lsProds = JSON.parse(localStorage.getItem('missingProducts') || '[]');
            const lsAbsReq = JSON.parse(localStorage.getItem('absenceRequests') || '[]');
            const activeEmps = lsEmps.filter(e => e?.status === 'active');
            const conTurnoHoy = activeEmps.filter(e => e?.monthlySchedule?.[_ds]?.enabled && e?.monthlySchedule?.[_ds]?.start);
            const todayRecs = lsRecords.filter(r => r?.date === _ds);
            const sinMarcar = conTurnoHoy.filter(e => !todayRecs.find(r => r?.employeeId === e?.id));
            const conAtraso = todayRecs.filter(r => r?.isLate);
            const sinRevisar = lsTasks.filter(t => t?.status === 'done' && !t?.reviewStatus).length;
            const prodsViejos = lsProds.filter(p => p?.status === 'pending' && p?.reportedAt && (new Date() - new Date(p.reportedAt)) > 7*24*60*60*1000).length;
            const solicPend = lsAbsReq.filter(r => r?.status === 'pending').length;

            const datos = {
              fecha: _hoy.toLocaleDateString('es-CL', {weekday:'long', day:'numeric', month:'long'}),
              turnoHoy: { total: conTurnoHoy.length, marcaron: todayRecs.length, sinMarcar: sinMarcar.map(e => e?.name), conAtraso: conAtraso.map(r => ({nombre: r?.employeeName, minutos: r?.lateMinutes})) },
              pendientes: { tareassinRevisar: sinRevisar, productosViejos7dias: prodsViejos, solicitudesAusencia: solicPend }
            };

            fetch('https://ocrfrjlehptgyudfxdah.supabase.co/functions/v1/attendance-report', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'apikey': 'sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-', 'Authorization': 'Bearer sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-' },
              body: JSON.stringify({ data: datos, modo: 'proactivo_gerente' })
            }).then(r => r.json()).then(result => {
              if (result.informe) {
                localStorage.setItem(_cacheKey, result.informe);
                // Forzar re-render guardando en sessionStorage para trigger
                sessionStorage.setItem('iaProactivaReady', '1');
                window.dispatchEvent(new Event('storage'));
              }
            }).catch(() => {});
            return null;
          } catch(e) { return null; }
        })()}

        {/* Banner de alertas de atrasos */}
        {(userRole === 'manager' || userRole === 'admin') && showLateAlert && lateEmployeesAlerts.filter(a => {
          const alertDate = new Date(a.timestamp).toDateString();
          const today = new Date().toDateString();
          const isToday = alertDate === today;
          
          // Gerente/Admin: ver TODAS las alertas
          if (userRole === 'manager' || userRole === 'admin') {
            return isToday;
          }
          
          // Empleado: solo ver SU alerta
          return isToday && a.employeeId === currentUser.id;
        }).length > 0 && (
          <div style={{background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'8px', padding:'16px', marginBottom:'24px', display:'flex', alignItems:'center', gap:'12px'}}>
            <div style={{fontSize:'32px'}}>⚠️</div>
            <div style={{flex:1}}>
              <strong style={{color:'var(--orange)'}}>Atrasos Detectados Hoy</strong>
              <p style={{margin:'4px 0 0 0', color:'var(--orange)'}}>
                {userRole === 'manager' || userRole === 'admin' 
                  ? `${lateEmployeesAlerts.filter(a => new Date(a.timestamp).toDateString() === new Date().toDateString()).length} empleado(s) llegaron tarde`
                  : `Llegaste ${lateEmployeesAlerts.find(a => a.employeeId === currentUser.id && new Date(a.timestamp).toDateString() === new Date().toDateString())?.minutesLate || 0} minutos tarde`}
              </p>
            </div>
      {/* Solicitudes de Horas Extras */}
      {overtimeRequests?.filter(r => r.status === 'pending').length > 0 && (
        <div style={{background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '12px', padding: '20px', marginBottom: '30px'}}>
          <h3 style={{margin: '0 0 15px 0', color: 'var(--orange)'}}>🔔 Solicitudes de Horas Extras ({overtimeRequests.filter(r => r.status === 'pending').length})</h3>
          {overtimeRequests.filter(r => r.status === 'pending').map(request => (
            <div key={request.id} style={{background: 'var(--bg-card)', padding: '15px', borderRadius: '8px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div>
                <p style={{margin: '0 0 5px 0', fontWeight: '600'}}>{request.employeeName}</p>
                <p style={{margin: 0, fontSize: '14px', color: 'var(--text-muted)'}}>Solicita {request.overtimeMinutes} minutos de horas extras</p>
              </div>
              <div style={{display: 'flex', gap: '10px'}}>
                <button onClick={() => {
                  const updated = overtimeRequests.map(r => r.id === request.id ? {...r, status: 'approved'} : r);
                  setOvertimeRequests(updated);
                  localStorage.setItem('overtimeRequests', JSON.stringify(updated));
                  const updatedRecords = attendanceRecords.map(rec => rec.employeeId === request.employeeId && rec.date === getLocalDateStr() ? {...rec, overtimeAuthorized: true} : rec);
                  setAttendanceRecords(updatedRecords);
                  localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
                  alert('✅ Aprobado');
                }} style={{padding: '8px 16px', background: '#10b981', color: '#0a0f1e', border: 'none', borderRadius: '6px', cursor: 'pointer'}}>✅ Aprobar</button>
                <button onClick={() => {
                  const updated = overtimeRequests.map(r => r.id === request.id ? {...r, status: 'rejected'} : r);
                  setOvertimeRequests(updated);
                  localStorage.setItem('overtimeRequests', JSON.stringify(updated));
                  alert('❌ Rechazado');
                }} style={{padding: '8px 16px', background: '#ef4444', color: '#0a0f1e', border: 'none', borderRadius: '6px', cursor: 'pointer'}}>❌ Rechazar</button>
              </div>
            </div>
          ))}
        </div>
      )}
            <button onClick={() => setShowLateAlert(false)} style={{background:'transparent', border:'none', fontSize:'24px', cursor:'pointer', color:'var(--orange)', padding:'8px'}}>✕</button>
          </div>
        )}
      
      <div className="stats-grid">
        <div className="stat-card">
          <Users size={32} />
          <div>
            <p className="stat-value">{activeCount}</p>
            <p className="stat-label">Empleados Activos</p>
          </div>
        </div>
        
        <div className="stat-card">
          <CheckCircle size={32} color="#10b981" />
          <div>
            <p className="stat-value">{onTimeCount}</p>
            <p className="stat-label">A Tiempo Hoy</p>
          </div>
        </div>
        
        <div className="stat-card">
          <AlertCircle size={32} color="#f59e0b" />
          <div>
            <p className="stat-value">{lateCount}</p>
            <p className="stat-label">Atrasos Hoy</p>
          </div>
        </div>
        
        <div className="stat-card">
          <Clock size={32} color="#3b82f6" />
          <div>
            <p className="stat-value">{(() => {
              const schedule = getTodaySchedule(currentUser.id);
              if (!schedule || !schedule.checkIn) return '--:--';
              return `${String(schedule.checkIn.hour).padStart(2, '0')}:${String(schedule.checkIn.minute).padStart(2, '0')}`;
            })()}</p>
            <p className="stat-label">Hora de Entrada</p>
          </div>
        </div>
      </div>

      {/* Accesos rápidos cuando hay atrasos hoy */}
      {lateCount > 0 && (userRole === 'manager' || userRole === 'admin') && (
        <div style={{background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'12px', padding:'12px 16px', marginBottom:'20px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px', marginBottom: showMarkOnTime ? '12px' : '0'}}>
            <div>
              <span style={{fontSize:'13px', fontWeight:'700', color:'#ef4444'}}>⏰ {lateCount} registro(s) con atraso hoy</span>
              <span style={{fontSize:'12px', color:'var(--text-secondary)', marginLeft:'8px'}}>¿Fueron a tiempo pero el sistema falló?</span>
            </div>
            <button onClick={() => { setShowMarkOnTime(!showMarkOnTime); setMarkOnTimeSelected([]); }}
              style={{padding:'6px 14px', background:'rgba(239,68,68,0.15)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.4)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>
              {showMarkOnTime ? 'Cancelar' : 'Seleccionar empleados'}
            </button>
          </div>
          {showMarkOnTime && (() => {
            const lateToday = todayRecords.filter(r => r.isLate);
            return (
              <div>
                <div style={{display:'flex', flexDirection:'column', gap:'6px', marginBottom:'10px'}}>
                  {lateToday.map(rec => {
                    const isSel = markOnTimeSelected.includes(rec.employeeId);
                    return (
                      <div key={rec.id} onClick={() => setMarkOnTimeSelected(prev => isSel ? prev.filter(id => id !== rec.employeeId) : [...prev, rec.employeeId])}
                        style={{display:'flex', alignItems:'center', gap:'10px', padding:'8px 12px', background: isSel ? 'rgba(34,197,94,0.1)' : 'var(--bg-elevated)', borderRadius:'8px', cursor:'pointer', border:`1px solid ${isSel ? 'rgba(34,197,94,0.4)' : 'var(--border)'}`}}>
                        <input type="checkbox" checked={isSel} onChange={() => {}} style={{width:'16px', height:'16px', accentColor:'var(--green)'}} />
                        <div style={{flex:1}}>
                          <div style={{fontSize:'13px', fontWeight:'600', color:'var(--text-primary)'}}>{rec.employeeName}</div>
                          <div style={{fontSize:'11px', color:'var(--text-secondary)'}}>Entrada: {rec.checkIn} — Atraso: {rec.lateMinutes} min</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {markOnTimeSelected.length > 0 && (
                  <button onClick={() => {
                    const today = getLocalDateStr();
                    const selected = lateToday.filter(r => markOnTimeSelected.includes(r.employeeId));
                    const names = selected.map(r => r.employeeName).join(', ');
                    if (!window.confirm(`¿Corregir a tiempo: ${names}?`)) return;
                    const updated = (Array.isArray(attendanceRecords)?attendanceRecords:[]).map(rec => {
                      if (rec.date !== today || !markOnTimeSelected.includes(rec.employeeId) || !rec.isLate) return rec;
                      return {...rec, isLate: false, lateMinutes: 0, status: 'on_time'};
                    });
                    setAttendanceRecords(updated);
                    localStorage.setItem('attendanceRecords', JSON.stringify(updated));
                    try { const rRef = ref(database, 'attendanceRecords'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); } catch(e) { console.warn('Sync pendiente',e); }
                    addAuditEntry('CORREGIR_ATRASOS_HOY', `Corrigió ${selected.length} registro(s): ${names}`);
                    alert(`✅ ${selected.length} registro(s) corregidos a tiempo`);
                    setShowMarkOnTime(false); setMarkOnTimeSelected([]);
                  }} style={{padding:'8px 16px', background:'#22c55e', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'700', width:'100%'}}>
                    ✅ Corregir seleccionados ({markOnTimeSelected.length})
                  </button>
                )}
                {markOnTimeSelected.length === 0 && <p style={{fontSize:'12px', color:'var(--text-muted)', margin:0}}>Selecciona los empleados a corregir</p>}
              </div>
            );
          })()}
          <div style={{display: showMarkOnTime ? 'none' : 'flex', justifyContent:'flex-end'}}>
          <button
            onClick={() => {
              const lateToday = todayRecords.filter(r => r.isLate);
              const names = lateToday.map(r => r.employeeName).join(', ');
              if (!window.confirm(`¿Corregir a tiempo los siguientes registros de hoy?

${names}

Se usará el horario programado de cada empleado.`)) return;
              const today = getLocalDateStr();
              const updated = (Array.isArray(attendanceRecords)?attendanceRecords:[]).map(rec => {
                if (rec.date !== today || !rec.isLate) return rec;
                const emp = (Array.isArray(employees)?employees:[]).find(e => e.id === rec.employeeId);
                const ms = emp?.monthlySchedule?.[today];
                if (!ms?.start) return rec;
                const [sh, sm] = ms.start.split(':').map(Number);
                const newCheckIn = `${String(sh%12||12).padStart(2,'0')}:${String(sm).padStart(2,'0')} ${sh<12?'a. m.':'p. m.'}`;
                return {...rec, checkIn: newCheckIn, isLate: false, lateMinutes: 0, status: 'on_time'};
              });
              setAttendanceRecords(updated);
              localStorage.setItem('attendanceRecords', JSON.stringify(updated));
              try { const rRef = ref(database, 'attendanceRecords'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); } catch(e) { console.warn('Sync pendiente',e); }
              addAuditEntry('CORREGIR_ATRASOS_HOY', `Corrigió ${lateToday.length} registro(s) con atraso: ${names}`);
              alert(`✅ ${lateToday.length} registro(s) corregidos a tiempo`);
            }}
            style={{padding:'8px 16px', background:'#22c55e', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'700'}}
          >✅ Corregir todos a tiempo</button>
          </div>
        </div>
      )}

      {/* ── ALERTA PRODUCTOS SIN RESOLVER ── */}
      {(() => {
        try {
          const lsProds = JSON.parse(localStorage.getItem('missingProducts') || '[]');
          const allProds = lsProds.length > 0 ? lsProds : (Array.isArray(missingProducts) ? missingProducts : []);
          const now = new Date();
          const viejos = allProds.filter(p => {
            if (p?.status !== 'pending') return false;
            if (!p?.reportedAt) return false;
            const dias = (now - new Date(p.reportedAt)) / (1000 * 60 * 60 * 24);
            return dias >= 3;
          });
          if (viejos.length === 0) return null;
          return (
            <div onClick={() => { setCurrentView('tasks'); setTasksActiveTab && setTasksActiveTab('products'); localStorage.setItem('productFilter', 'pending'); }} style={{background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'12px', padding:'12px 16px', marginBottom:'16px', cursor:'pointer'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
                <p style={{fontSize:'13px', fontWeight:'600', color:'var(--orange)', margin:0}}>⚠️ {viejos.length} producto(s) sin resolver hace más de 3 días</p>
                <span style={{fontSize:'11px', color:'var(--orange)'}}>Ver todos →</span>
              </div>
              {viejos.slice(0,3).map(p => {
                const dias = Math.floor((now - new Date(p.reportedAt)) / (1000 * 60 * 60 * 24));
                return (
                  <div key={p.id} style={{fontSize:'12px', color:'var(--text-secondary)', padding:'2px 0'}}>
                    • {p.name} — {dias} días — reportado por {p.reportedBy}
                  </div>
                );
              })}
              {viejos.length > 3 && <p style={{fontSize:'11px', color:'var(--text-secondary)', margin:'4px 0 0'}}>y {viejos.length - 3} más...</p>}
            </div>
          );
        } catch(e) { return null; }
      })()}

      {/* ── RESUMEN SEMANAL TAREAS ── */}
      {(() => {
        try {
          const _d = new Date();
          const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
          const allTasks = lsTasks.length > 0 ? lsTasks : (Array.isArray(tasks) ? tasks : []);
          
          // Últimos 7 días
          const hace7 = new Date(_d);
          hace7.setDate(_d.getDate() - 7);
          const hace7str = `${hace7.getFullYear()}-${String(hace7.getMonth()+1).padStart(2,'0')}-${String(hace7.getDate()).padStart(2,'0')}`;
          
          const semana = allTasks.filter(t => t?.dueDate >= hace7str);
          if (semana.length === 0) return null;
          
          const completadas = semana.filter(t => t?.status === 'done' && t?.reviewStatus === 'approved').length;
          const rechazadas = semana.filter(t => t?.reviewStatus === 'rejected').length;
          const vencidas = semana.filter(t => t?.status === 'overdue').length;
          const pendientes = semana.filter(t => t?.status === 'pending').length;
          
          return (
            <div onClick={() => { setCurrentView('tasks'); setTasksActiveTab && setTasksActiveTab('tasks'); }} 
              style={{background:'var(--bg-card)', borderRadius:'12px', padding:'12px 16px', marginBottom:'16px', border:'1px solid var(--border)', cursor:'pointer'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                <p style={{fontSize:'13px', fontWeight:'600', color:'var(--text-primary)', margin:0}}>📊 Tareas — últimos 7 días</p>
                <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>Ver todas →</span>
              </div>
              <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                <div style={{display:'flex', justifyContent:'space-between', padding:'6px 10px', background:'rgba(34,197,94,0.08)', borderRadius:'6px'}}>
                  <span style={{fontSize:'12px', color:'var(--green)'}}>✅ Completadas y aprobadas</span>
                  <span style={{fontSize:'13px', fontWeight:'600', color:'var(--green)'}}>{completadas}</span>
                </div>
                {rechazadas > 0 && (
                  <div style={{display:'flex', justifyContent:'space-between', padding:'6px 10px', background:'rgba(239,68,68,0.08)', borderRadius:'6px'}}>
                    <span style={{fontSize:'12px', color:'#ef4444'}}>❌ Rechazadas</span>
                    <span style={{fontSize:'13px', fontWeight:'600', color:'#ef4444'}}>{rechazadas}</span>
                  </div>
                )}
                {vencidas > 0 && (
                  <div style={{display:'flex', justifyContent:'space-between', padding:'6px 10px', background:'rgba(239,68,68,0.06)', borderRadius:'6px'}}>
                    <span style={{fontSize:'12px', color:'var(--orange)'}}>🔴 Vencidas sin completar</span>
                    <span style={{fontSize:'13px', fontWeight:'600', color:'var(--orange)'}}>{vencidas}</span>
                  </div>
                )}
                <div style={{display:'flex', justifyContent:'space-between', padding:'6px 10px', background:'var(--bg-secondary)', borderRadius:'6px'}}>
                  <span style={{fontSize:'12px', color:'var(--text-secondary)'}}>⏳ Pendientes</span>
                  <span style={{fontSize:'13px', fontWeight:'600', color:'var(--text-secondary)'}}>{pendientes}</span>
                </div>
              </div>
            </div>
          );
        } catch(e) { return null; }
      })()}

      {/* ── RESUMEN DIARIO ── */}
      {(() => {
        try {
          const _d = new Date();
          const today = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;

          const lsRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
          const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
          const allRecs = lsRecords.length > 0 ? lsRecords : (Array.isArray(attendanceRecords) ? attendanceRecords : []);
          const allEmps = lsEmps.length > 0 ? lsEmps : (Array.isArray(employees) ? employees : []);
          const todayRecs = allRecs.filter(r => r?.date === today);
          const activeEmps = allEmps.filter(e => e?.status === 'active');
          if (activeEmps.length === 0) return null;

          const cumplieron = todayRecs.filter(r => r?.checkIn && r?.checkOut);
          const sinSalida = todayRecs.filter(r => r?.checkIn && !r?.checkOut);
          const conAtraso = todayRecs.filter(r => r?.isLate);
          
          // Solo mostrar como "sin marcar" empleados que tienen turno HOY
          const sinMarcar = activeEmps.filter(e => {
            if (todayRecs.find(r => r?.employeeId === e?.id)) return false;
            const ms = e?.monthlySchedule?.[today];
            return ms?.enabled && ms?.start;
          });

          const todayTasks = (Array.isArray(tasks) ? tasks : []).filter(t => t?.dueDate === today);
          const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
          const allTasks = lsTasks.length > 0 ? lsTasks : todayTasks;
          const tareasHoy = allTasks.filter(t => t?.dueDate === today);
          const tareasCompletas = tareasHoy.filter(t => t?.status === 'done');
          const tareasPendientes = tareasHoy.filter(t => t?.status === 'pending' || t?.status === 'overdue');

          const formatNames = (arr, key='name') => {
            if (arr.length === 0) return '';
            if (arr.length <= 2) return arr.map(e => e?.[key]?.split(' ')[0]).join(', ');
            return arr.slice(0,2).map(e => e?.[key]?.split(' ')[0]).join(', ') + ' y ' + (arr.length - 2) + ' más';
          };

          return (
            <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'16px', marginBottom:'20px', border:'1px solid var(--border)'}}>
              <p style={{fontSize:'13px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 12px'}}>📋 Resumen del día</p>
              <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                {(cumplieron.length > 0 || new Date().getHours() >= 14) && (
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background: cumplieron.length > 0 ? 'rgba(34,197,94,0.1)' : 'var(--bg-secondary)', borderRadius:'10px', border: cumplieron.length > 0 ? '1px solid rgba(34,197,94,0.2)' : '1px solid var(--border)'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <span style={{fontSize:'18px'}}>✅</span>
                      <span style={{fontSize:'13px', color: cumplieron.length > 0 ? 'var(--green)' : 'var(--text-secondary)', fontWeight:'500'}}>Completaron turno</span>
                    </div>
                    <span style={{fontSize:'18px', fontWeight:'700', color: cumplieron.length > 0 ? 'var(--green)' : 'var(--text-secondary)'}}>{cumplieron.length}</span>
                  </div>
                )}
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background: sinSalida.length > 0 ? 'rgba(59,130,246,0.1)' : 'var(--bg-secondary)', borderRadius:'10px', border: sinSalida.length > 0 ? '1px solid rgba(59,130,246,0.2)' : '1px solid var(--border)'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span style={{fontSize:'18px'}}>🟢</span>
                    <span style={{fontSize:'13px', color: sinSalida.length > 0 ? 'var(--blue)' : 'var(--text-secondary)', fontWeight:'500'}}>En turno ahora</span>
                  </div>
                  <span style={{fontSize:'18px', fontWeight:'700', color: sinSalida.length > 0 ? 'var(--blue)' : 'var(--text-secondary)'}}>{sinSalida.length}</span>
                </div>
                {conAtraso.length > 0 && (
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'rgba(239,68,68,0.1)', borderRadius:'10px', border:'1px solid rgba(239,68,68,0.2)'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <span style={{fontSize:'18px'}}>⚠️</span>
                      <div>
                        <span style={{fontSize:'13px', color:'#ef4444', fontWeight:'500'}}>Con atraso</span>
                        <p style={{fontSize:'11px', color:'#ef4444', margin:'2px 0 0', opacity:0.8}}>{formatNames(conAtraso, 'employeeName')}</p>
                      </div>
                    </div>
                    <span style={{fontSize:'18px', fontWeight:'700', color:'#ef4444'}}>{conAtraso.length}</span>
                  </div>
                )}
                {sinMarcar.length > 0 && (
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'rgba(245,158,11,0.1)', borderRadius:'10px', border:'1px solid rgba(245,158,11,0.2)'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <span style={{fontSize:'18px'}}>🔔</span>
                      <div>
                        <span style={{fontSize:'13px', color:'var(--orange)', fontWeight:'500'}}>Sin marcar (con turno)</span>
                        <p style={{fontSize:'11px', color:'var(--orange)', margin:'2px 0 0', opacity:0.8}}>{formatNames(sinMarcar)}</p>
                      </div>
                    </div>
                    <span style={{fontSize:'18px', fontWeight:'700', color:'var(--orange)'}}>{sinMarcar.length}</span>
                  </div>
                )}
                {tareasHoy.length > 0 && (
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'var(--bg-secondary)', borderRadius:'10px', border:'1px solid var(--border)'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <span style={{fontSize:'18px'}}>📋</span>
                      <span style={{fontSize:'13px', color:'var(--text-secondary)', fontWeight:'500'}}>Tareas de hoy</span>
                    </div>
                    <span style={{fontSize:'13px', color:'var(--text-secondary)'}}>✅ {tareasCompletas.length} &nbsp; ⏳ {tareasPendientes.length}</span>
                  </div>
                )}
              </div>
            </div>
          );
        } catch(e) {
          return null;
        }
      })()}

      {/* ── DASHBOARD SEMANAL ── */}
      {(() => {
        // Últimos 7 días
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const dayName = d.toLocaleDateString('es-CL', {weekday:'short'});
          const dayNum = d.getDate();
          const recs = (Array.isArray(attendanceRecords) ? attendanceRecords : []).filter(r => r.date === dateStr);
          const onTime = recs.filter(r => !r.isLate).length;
          const late = recs.filter(r => r.isLate).length;
          const total = recs.length;
          const activeEmps = (Array.isArray(employees) ? employees : []).filter(e => e.status === 'active').length;
          const noShow = Math.max(0, activeEmps - total);
          days.push({dateStr, dayName, dayNum, onTime, late, noShow, total, isToday: i === 0});
        }
        const maxVal = Math.max(...days.map(d => d.onTime + d.late + d.noShow), 1);

        // Empleados sin marcar hoy
        const todayStr = getLocalDateStr();
        const markedToday = new Set((Array.isArray(attendanceRecords) ? attendanceRecords : []).filter(r => r.date === todayStr).map(r => r.employeeId));
        const notMarked = (Array.isArray(employees) ? employees : []).filter(e => {
          if (e.status !== 'active') return false;
          if (markedToday.has(e.id)) return false;
          // Verificar si trabaja hoy según su calendario
          const ms = e.monthlySchedule?.[todayStr];
          if (ms !== undefined) {
            // Tiene entrada en calendario mensual para hoy
            return ms.enabled === true && ms.start;
          }
          // Verificar weeklySchedule
          if (e.useFlexibleSchedule && e.weeklySchedule) {
            const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
            const dow = new Date().getDay();
            const ds = e.weeklySchedule[dayNames[dow]];
            return ds?.enabled === true;
          }
          // Sin horario configurado → no contar como ausente
          return false;
        });

        // Tareas vencidas/urgentes
        const now = new Date();
        const urgentTasks = (Array.isArray(tasks) ? tasks : []).filter(t => {
          if (!t.dueDate || t.status !== 'pending') return false;
          const [_dy, _dm, _dd] = t.dueDate.split('-').map(Number);
          const [_dh, _dmin] = (t.dueTime || '23:59').split(':').map(Number);
          const due = new Date(_dy, _dm-1, _dd, _dh, _dmin, 0);
          const diffH = (due - now) / 3600000;
          return diffH <= 24;
        });
        const overdueTasks = urgentTasks.filter(t => new Date(`${t.dueDate}T${t.dueTime || '23:59'}`) < now);

        // Atrasos semana
        const totalLateMin = days.reduce((s, d) => {
          const recs = (Array.isArray(attendanceRecords) ? attendanceRecords : []).filter(r => r.date === d.dateStr);
          return s + recs.reduce((ss, r) => ss + (r.lateMinutes || 0), 0);
        }, 0);
        const totalLateDays = days.reduce((s, d) => s + d.late, 0);

        return (
          <div style={{marginBottom:'24px'}}>
            <h3 style={{color:'var(--text-primary)', fontSize:'16px', fontWeight:'700', marginBottom:'16px'}}>📊 Resumen Últimos 7 Días</h3>

            {/* Gráfico de barras */}
            <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'20px', marginBottom:'16px', border:'1px solid var(--border)'}}>
              <div style={{display:'flex', alignItems:'flex-end', gap:'8px', height:'120px', marginBottom:'8px'}}>
                {days.map((day, i) => {
                  const barH = maxVal > 0 ? Math.round((day.onTime + day.late) / maxVal * 100) : 0;
                  const lateH = maxVal > 0 && (day.onTime + day.late) > 0 ? Math.round(day.late / (day.onTime + day.late) * barH) : 0;
                  const onTimeH = barH - lateH;
                  return (
                    <div key={i} onClick={()=>setSelectedDayDetail(selectedDayDetail?.dateStr===day.dateStr?null:day)} style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'2px', height:'100%', justifyContent:'flex-end', cursor:'pointer', opacity: selectedDayDetail && selectedDayDetail.dateStr!==day.dateStr ? 0.6 : 1, transition:'opacity 0.2s'}}>
                      <div style={{width:'100%', display:'flex', flexDirection:'column', gap:'1px'}}>
                        {lateH > 0 && <div style={{height:`${lateH}px`, background:'#ef4444', borderRadius:'3px 3px 0 0', minHeight:'4px', boxShadow: selectedDayDetail?.dateStr===day.dateStr?'0 0 8px #ef4444':undefined}} />}
                        {onTimeH > 0 && <div style={{height:`${onTimeH}px`, background:'#22c55e', borderRadius: lateH > 0 ? '0' : '3px 3px 0 0', minHeight:'4px', boxShadow: selectedDayDetail?.dateStr===day.dateStr?'0 0 8px #22c55e':undefined}} />}
                        {barH === 0 && <div style={{height:'4px', background:'var(--bg-elevated)', borderRadius:'3px'}} />}
                      </div>
                      <span style={{fontSize:'10px', color: selectedDayDetail?.dateStr===day.dateStr ? 'var(--blue)' : day.isToday ? 'var(--blue)' : 'var(--text-secondary)', fontWeight: day.isToday || selectedDayDetail?.dateStr===day.dateStr ? '700' : '400', marginTop:'4px'}}>{day.dayName}</span>
                      <span style={{fontSize:'9px', color:'var(--text-muted)'}}>{day.dayNum}</span>
                    </div>
                  );
                })}
              </div>
              {/* Legend */}
              <div style={{display:'flex', gap:'16px', marginTop:'8px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'4px'}}><div style={{width:'10px', height:'10px', background:'#22c55e', borderRadius:'2px'}} /><span style={{fontSize:'11px', color:'var(--text-secondary)'}}>A tiempo</span></div>
                <div style={{display:'flex', alignItems:'center', gap:'4px'}}><div style={{width:'10px', height:'10px', background:'#ef4444', borderRadius:'2px'}} /><span style={{fontSize:'11px', color:'var(--text-secondary)'}}>Con atraso</span></div>
                <span style={{fontSize:'11px', color:'var(--text-muted)', marginLeft:'auto'}}>Click en una barra para ver detalle</span>
              </div>
              {/* Detalle del día seleccionado */}
              {selectedDayDetail && (() => {
                const dayRecs = (Array.isArray(attendanceRecords)?attendanceRecords:[]).filter(r => r.date === selectedDayDetail.dateStr);
                const activeEmps = (Array.isArray(employees)?employees:[]).filter(e => e.status === 'active');
                const markedIds = new Set(dayRecs.map(r => r.employeeId));
                const notMarkedDay = activeEmps.filter(e => {
                  const ms = e.monthlySchedule?.[selectedDayDetail.dateStr];
                  if (ms !== undefined) return ms.enabled && ms.start && !markedIds.has(e.id);
                  return false;
                });
                return (
                  <div style={{marginTop:'12px', background:'var(--bg-elevated)', borderRadius:'10px', padding:'12px', border:'1px solid var(--blue)30'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                      <h4 style={{margin:0, fontSize:'14px', color:'var(--blue)', fontWeight:'700'}}>
                        📅 {new Date(selectedDayDetail.dateStr+'T12:00:00').toLocaleDateString('es-CL',{weekday:'long',day:'numeric',month:'long'})}
                      </h4>
                      <button onClick={()=>setSelectedDayDetail(null)} style={{background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'18px'}}>×</button>
                    </div>
                    <div style={{display:'flex', gap:'8px', marginBottom:'10px'}}>
                      <span style={{fontSize:'12px', color:'var(--green)', fontWeight:'600'}}>✅ {dayRecs.filter(r=>!r.isLate).length} a tiempo</span>
                      <span style={{fontSize:'12px', color:'#ef4444', fontWeight:'600'}}>⏰ {dayRecs.filter(r=>r.isLate).length} atrasos</span>
                      {notMarkedDay.length > 0 && <span style={{fontSize:'12px', color:'var(--orange)', fontWeight:'600'}}>❌ {notMarkedDay.length} sin marcar</span>}
                    </div>
                    <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                      {dayRecs.sort((a,b)=>(a.employeeName||'').localeCompare(b.employeeName||'')).map(r => (
                        <div key={r.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'var(--bg-card)', borderRadius:'8px', borderLeft:`3px solid ${r.isLate?'#ef4444':'#22c55e'}`}}>
                          <span style={{fontSize:'13px', fontWeight:'600', color:'var(--text-primary)'}}>{r.employeeName}</span>
                          <div style={{display:'flex', gap:'12px', fontSize:'12px', color:'var(--text-secondary)'}}>
                            <span>🕐 {r.checkIn}</span>
                            {r.checkOut && <span>🚪 {r.checkOut}</span>}
                            {r.isLate && <span style={{color:'#ef4444', fontWeight:'600'}}>+{r.lateMinutes}min</span>}
                          </div>
                        </div>
                      ))}
                      {notMarkedDay.map(e => (
                        <div key={e.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'var(--bg-card)', borderRadius:'8px', borderLeft:'3px solid var(--orange)', opacity:0.7}}>
                          <span style={{fontSize:'13px', fontWeight:'600', color:'var(--text-secondary)'}}>{e.photo} {e.name}</span>
                          <span style={{fontSize:'12px', color:'var(--orange)'}}>Sin registro</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Panel registrar a tiempo con selección */}
            {notMarked.length > 0 && (userRole === 'manager' || userRole === 'admin') && (
              <div style={{marginBottom:'12px', background:'var(--bg-card)', borderRadius:'12px', border:'1px solid rgba(34,197,94,0.3)', overflow:'hidden'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span style={{fontSize:'13px', fontWeight:'700', color:'var(--green)'}}>✅ Registrar a tiempo</span>
                    <span style={{fontSize:'11px', color:'var(--text-secondary)'}}>{notMarked.length} sin marcar hoy</span>
                  </div>
                  <button onClick={()=>{setShowMarkOnTime(!showMarkOnTime); setMarkOnTimeSelected([]);}} style={{padding:'5px 12px', background:'rgba(34,197,94,0.15)', color:'var(--green)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>
                    {showMarkOnTime ? 'Cancelar' : 'Seleccionar empleados'}
                  </button>
                </div>
                {showMarkOnTime && (
                  <div style={{padding:'0 16px 16px'}}>
                    {/* Lista de empleados con checkbox */}
                    <div style={{display:'flex', flexDirection:'column', gap:'6px', marginBottom:'12px'}}>
                      {notMarked.map(emp => {
                        const ms = emp.monthlySchedule?.[getLocalDateStr()];
                        const isSel = markOnTimeSelected.includes(emp.id);
                        return (
                          <div key={emp.id} onClick={()=>setMarkOnTimeSelected(prev=>isSel?prev.filter(id=>id!==emp.id):[...prev,emp.id])}
                            style={{display:'flex', alignItems:'center', gap:'10px', padding:'8px 12px', background:isSel?'rgba(34,197,94,0.1)':'var(--bg-elevated)', borderRadius:'8px', cursor:'pointer', border:`1px solid ${isSel?'rgba(34,197,94,0.4)':'var(--border)'}`}}>
                            <input type="checkbox" checked={isSel} onChange={()=>{}} style={{width:'16px',height:'16px',accentColor:'var(--green)'}} />
                            <span style={{fontSize:'20px'}}>{emp.photo}</span>
                            <div style={{flex:1}}>
                              <div style={{fontSize:'13px', fontWeight:'600', color:'var(--text-primary)'}}>{emp.name}</div>
                              <div style={{fontSize:'11px', color:'var(--text-secondary)'}}>⏰ {ms?.start||'--'} — {ms?.end||'--'}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Botones de acción */}
                    {markOnTimeSelected.length > 0 && (
                      <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                        {[
                          {label:'✅ Solo entrada', modo:'1'},
                          {label:'☕ Entrada + colación', modo:'2'},
                          {label:'🚪 Jornada completa', modo:'3'},
                        ].map(({label, modo}) => (
                          <button key={modo} onClick={() => {
                            const today = getLocalDateStr();
                            const makeTimeStr = (h, m) => {
                              const hh = parseInt(h), mm = String(parseInt(m)).padStart(2,'0');
                              return `${String(hh%12||12).padStart(2,'0')}:${mm} ${hh<12?'a. m.':'p. m.'}`;
                            };
                            const selected = notMarked.filter(e => markOnTimeSelected.includes(e.id));
                            const newRecords = selected.map(emp => {
                              const ms = emp.monthlySchedule?.[today];
                              const [sh,sm] = (ms?.start||'09:00').split(':');
                              const [lsh,lsm] = (ms?.lunchStart||'13:00').split(':');
                              const [leh,lem] = (ms?.lunchEnd||'14:00').split(':');
                              const [eh,em] = (ms?.end||'18:00').split(':');
                              return {
                                id: Date.now() + emp.id,
                                employeeId: emp.id, employeeName: emp.name, date: today,
                                checkIn: makeTimeStr(sh,sm),
                                checkOut: modo==='3' ? makeTimeStr(eh,em) : '',
                                breakStart: modo!=='1' ? makeTimeStr(lsh,lsm) : '',
                                breakEnd: modo!=='1' ? makeTimeStr(leh,lem) : '',
                                status:'on_time', isLate:false, lateMinutes:0, lateBreakMinutes:0,
                                earlyCheckOutMinutes:0, location:'manager_override',
                                lateBreakReturn:false, earlyCheckOut:false,
                                registeredBy:currentUser?.name, registeredByRole:userRole
                              };
                            });
                            const updated = [...(Array.isArray(attendanceRecords)?attendanceRecords:[]), ...newRecords];
                            setAttendanceRecords(updated);
                            localStorage.setItem('attendanceRecords', JSON.stringify(updated));
                            try { const rRef = ref(database, 'attendanceRecords'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); } catch(e) { console.warn('Sync pendiente',e); }
                            const modoLabel = modo==='1'?'entrada':modo==='2'?'entrada+colación':'jornada completa';
                            addAuditEntry('MARCAR_A_TIEMPO', `Registró ${newRecords.length} empleado(s) — ${modoLabel}: ${newRecords.map(r=>r.employeeName).join(', ')}`);
                            alert(`✅ ${newRecords.length} empleado(s) registrados (${modoLabel})`);
                            setShowMarkOnTime(false); setMarkOnTimeSelected([]);
                          }} style={{padding:'7px 14px', background:'rgba(34,197,94,0.15)', color:'var(--green)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>
                            {label} ({markOnTimeSelected.length})
                          </button>
                        ))}
                      </div>
                    )}
                    {markOnTimeSelected.length === 0 && <p style={{fontSize:'12px', color:'var(--text-muted)', margin:0}}>Selecciona los empleados que ya llegaron</p>}
                  </div>
                )}
              </div>
            )}

            {/* 3 cards: atrasos, sin marcar, tareas urgentes */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'12px'}}>

              {/* Resumen atrasos */}
              <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'16px', border:'1px solid rgba(239,68,68,0.3)', cursor: totalLateDays > 0 ? 'pointer' : 'default'}}
                onClick={() => totalLateDays > 0 && setExpandedCard(expandedCard === 'late' ? null : 'late')}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <div style={{fontSize:'11px', color:'#ef4444', fontWeight:'700', marginBottom:'8px', textTransform:'uppercase'}}>⏱ Atrasos Semana</div>
                  {totalLateDays > 0 && <span style={{fontSize:'11px', color:'var(--text-muted)'}}>{expandedCard==='late'?'▲':'▼'}</span>}
                </div>
                <div style={{fontSize:'28px', fontWeight:'900', color:'#ef4444', lineHeight:1}}>{totalLateDays}</div>
                <div style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'4px'}}>días con atraso</div>
                <div style={{fontSize:'13px', color:'var(--orange)', fontWeight:'600', marginTop:'6px'}}>{totalLateMin} min perdidos</div>
                {expandedCard === 'late' && (
                  <div style={{marginTop:'12px', borderTop:'1px solid var(--border)', paddingTop:'10px', display:'flex', flexDirection:'column', gap:'6px'}}>
                    {days.filter(d => d.late > 0).map(d => {
                      const dayRecs = (Array.isArray(attendanceRecords)?attendanceRecords:[]).filter(r => r.date === d.dateStr && r.isLate);
                      return (
                        <div key={d.dateStr}>
                          <div style={{fontSize:'11px', color:'var(--text-muted)', fontWeight:'700', marginBottom:'3px'}}>{d.dayName} {d.dayNum} — {d.late} atraso(s)</div>
                          {dayRecs.map(r => (
                            <div key={r.id} style={{fontSize:'11px', color:'var(--text-secondary)', paddingLeft:'8px'}}>
                              • {r.employeeName} <span style={{color:'#ef4444'}}>+{r.lateMinutes}min</span> · Entrada: {r.checkIn?.replace(' a. m.','a').replace(' p. m.','p')}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sin marcar hoy */}
              <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'16px', border:`1px solid ${notMarked.length > 0 ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`, cursor: notMarked.length > 0 ? 'pointer' : 'default'}}
                onClick={() => notMarked.length > 0 && setExpandedCard(expandedCard === 'unmarked' ? null : 'unmarked')}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <div style={{fontSize:'11px', color: notMarked.length > 0 ? 'var(--orange)' : 'var(--green)', fontWeight:'700', marginBottom:'8px', textTransform:'uppercase'}}>👥 Sin Marcar Hoy</div>
                  {notMarked.length > 0 && <span style={{fontSize:'11px', color:'var(--text-muted)'}}>{expandedCard==='unmarked'?'▲':'▼'}</span>}
                </div>
                <div style={{fontSize:'28px', fontWeight:'900', color: notMarked.length > 0 ? 'var(--orange)' : 'var(--green)', lineHeight:1}}>{notMarked.length}</div>
                <div style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'4px'}}>empleado(s)</div>
                {!expandedCard || expandedCard !== 'unmarked' ? (
                  notMarked.length > 0 && (
                    <div style={{marginTop:'6px', display:'flex', flexDirection:'column', gap:'2px'}}>
                      {notMarked.slice(0,3).map(e => <span key={e.id} style={{fontSize:'11px', color:'var(--text-secondary)'}}>{e.photo} {e.name}</span>)}
                      {notMarked.length > 3 && <span style={{fontSize:'11px', color:'var(--text-muted)'}}>+{notMarked.length-3} más</span>}
                    </div>
                  )
                ) : (
                  <div style={{marginTop:'12px', borderTop:'1px solid var(--border)', paddingTop:'10px', display:'flex', flexDirection:'column', gap:'6px'}}>
                    {notMarked.map(e => {
                      const ms = e.monthlySchedule?.[getLocalDateStr()];
                      return (
                        <div key={e.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 8px', background:'var(--bg-elevated)', borderRadius:'6px'}}>
                          <span style={{fontSize:'12px', color:'var(--text-primary)', fontWeight:'600'}}>{e.photo} {e.name}</span>
                          <span style={{fontSize:'11px', color:'var(--orange)'}}>⏰ {ms?.start||'--'} - {ms?.end||'--'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Tareas urgentes/vencidas */}
              <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'16px', border:`1px solid ${overdueTasks.length > 0 ? 'rgba(239,68,68,0.3)' : urgentTasks.length > 0 ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`, cursor: urgentTasks.length > 0 ? 'pointer' : 'default'}}
                onClick={() => urgentTasks.length > 0 && setExpandedCard(expandedCard === 'tasks' ? null : 'tasks')}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <div style={{fontSize:'11px', color: overdueTasks.length > 0 ? '#ef4444' : urgentTasks.length > 0 ? 'var(--orange)' : 'var(--green)', fontWeight:'700', marginBottom:'8px', textTransform:'uppercase'}}>📋 Tareas Urgentes</div>
                  {urgentTasks.length > 0 && <span style={{fontSize:'11px', color:'var(--text-muted)'}}>{expandedCard==='tasks'?'▲':'▼'}</span>}
                </div>
                <div style={{fontSize:'28px', fontWeight:'900', color: overdueTasks.length > 0 ? '#ef4444' : urgentTasks.length > 0 ? 'var(--orange)' : 'var(--green)', lineHeight:1}}>{urgentTasks.length}</div>
                <div style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'4px'}}>{overdueTasks.length > 0 ? `${overdueTasks.length} vencida(s)` : urgentTasks.length > 0 ? 'vencen en 24h' : 'todo al día'}</div>
                {expandedCard !== 'tasks' && urgentTasks.slice(0,2).map(t => <div key={t.id} style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'4px'}}>• {t.title?.substring(0,25)}</div>)}
                {expandedCard === 'tasks' && (
                  <div style={{marginTop:'12px', borderTop:'1px solid var(--border)', paddingTop:'10px', display:'flex', flexDirection:'column', gap:'6px'}}>
                    {urgentTasks.map(t => {
                      const isOverdue = t.status === 'overdue';
                      return (
                        <div key={t.id} style={{padding:'6px 8px', background:'var(--bg-elevated)', borderRadius:'6px', borderLeft:`3px solid ${isOverdue?'#ef4444':'var(--orange)'}`}}>
                          <div style={{fontSize:'12px', color:'var(--text-primary)', fontWeight:'600'}}>{t.title?.substring(0,35)}</div>
                          <div style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'2px'}}>
                            {t.assignedName} · {isOverdue ? <span style={{color:'#ef4444'}}>🔴 Vencida</span> : <span style={{color:'var(--orange)'}}>⚠️ Vence {t.dueDate} {t.dueTime}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── INCIDENCIAS DE ASISTENCIA ── */}
      {isManager && (() => {
        const pendingIssues = (Array.isArray(attendanceIssues)?attendanceIssues:[]).filter(i => i.status === 'pending');
        return (
          <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'16px', marginBottom:'20px', border:`1px solid ${pendingIssues.length>0?'rgba(245,158,11,0.4)':'var(--border)'}`}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                <h3 style={{margin:0, fontSize:'15px'}}>⚠️ Incidencias de Asistencia</h3>
                {pendingIssues.length > 0 && <span style={{background:'#ef4444', color:'white', borderRadius:'20px', padding:'2px 10px', fontSize:'12px', fontWeight:'700'}}>{pendingIssues.length} pendientes</span>}
              </div>
              <div style={{display:'flex', gap:'8px'}}>
                <button onClick={() => { const n = detectAttendanceIssues(); alert(n>0?`✅ Se detectaron ${n} incidencia(s) nuevas`:'✅ Sin incidencias nuevas'); }} style={{padding:'6px 12px', background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}>🔍 Detectar</button>
                {pendingIssues.length > 0 && (
                  <>
                    <select value={issueOrder} onChange={e=>setIssueOrder(e.target.value)} style={{padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px', cursor:'pointer'}}>
                      <option value="newest">Más recientes primero</option>
                      <option value="oldest">Más antiguas primero</option>
                      <option value="employee">Por empleado</option>
                    </select>
                    <button onClick={()=>setShowIssues(!showIssues)} style={{padding:'6px 12px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', cursor:'pointer', fontSize:'12px'}}>{showIssues?'Ocultar':'Ver todas'}</button>
                  </>
                )}
              </div>
            </div>
            {pendingIssues.length === 0 && <p style={{color:'var(--green)', fontSize:'13px', margin:'8px 0 0'}}>✅ Sin incidencias pendientes</p>}
            {showIssues && pendingIssues.length > 0 && (
              <div style={{marginTop:'12px', display:'flex', flexDirection:'column', gap:'10px', maxHeight:'400px', overflowY:'auto'}}>
                {[...pendingIssues].sort((a,b) => {
                    if (issueOrder === 'newest') return (b.date||'').localeCompare(a.date||'');
                    if (issueOrder === 'oldest') return (a.date||'').localeCompare(b.date||'');
                    return (a.employeeName||'').localeCompare(b.employeeName||'');
                  }).map(issue => (
                  <div key={issue.id} style={{background:'var(--bg-elevated)', borderRadius:'10px', padding:'12px', border:`1px solid ${issue.type==='absence'?'rgba(239,68,68,0.3)':'rgba(245,158,11,0.3)'}`}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'8px'}}>
                      <div>
                        <span style={{fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>{issue.employeePhoto} {issue.employeeName}</span>
                        <span style={{fontSize:'12px', color:'var(--text-secondary)', marginLeft:'8px'}}>📅 {issue.date}</span>
                        <div style={{marginTop:'4px'}}>
                          <span style={{fontSize:'11px', padding:'2px 8px', borderRadius:'4px', background:issue.type==='absence'?'rgba(239,68,68,0.15)':'rgba(245,158,11,0.15)', color:issue.type==='absence'?'#ef4444':'var(--orange)', fontWeight:'700'}}>
                            {issue.type==='absence'?'🔴 Ausencia':'⚠️ Registro incompleto'}
                          </span>
                          <span style={{fontSize:'11px', color:'var(--text-secondary)', marginLeft:'8px'}}>Falta: {issue.missing?.join(', ')}</span>
                        </div>
                      </div>
                      <button onClick={()=>dismissIssue(issue.id)} style={{padding:'4px 10px', background:'var(--bg-secondary)', color:'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:'6px', cursor:'pointer', fontSize:'11px'}} title="Eliminar (falso positivo)">🗑️ Eliminar</button>
                    </div>
                    {/* Modo: justify o adjust */}
                    {issueComment[issue.id+'_mode'] === 'adjust' ? (
                      // ── MODO AJUSTAR HORARIO ──
                      <div style={{background:'var(--bg-card)', borderRadius:'8px', padding:'12px', border:'1px solid rgba(59,130,246,0.3)'}}>
                        <p style={{margin:'0 0 8px', fontSize:'12px', color:'var(--blue)', fontWeight:'700'}}>⏰ Ajustar Horario del Día</p>
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px'}}>
                          <div>
                            <label style={{fontSize:'11px', color:'var(--text-secondary)', display:'block', marginBottom:'4px'}}>Hora entrada real:</label>
                            <input
                              type="time"
                              value={issueComment[issue.id+'_entry']||''}
                              onChange={e => {
                                const entry = e.target.value;
                                // Calcular salida automática: entrada + 8h + colación (2h por defecto)
                                const emp2 = employees.find(emp => emp.id === issue.employeeId);
                                const ms2 = emp2?.monthlySchedule?.[issue.date];
                                const lunchDur = ms2?.lunchStart && ms2?.lunchEnd ? (() => {
                                  const [lsh,lsm] = ms2.lunchStart.split(':').map(Number);
                                  const [leh,lem] = ms2.lunchEnd.split(':').map(Number);
                                  return (leh*60+lem)-(lsh*60+lsm);
                                })() : 120;
                                const [h,m] = entry.split(':').map(Number);
                                const exitMin = h*60+m+480+lunchDur; // 8h = 480min
                                const exitH = Math.floor(exitMin/60)%24;
                                const exitM = exitMin%60;
                                const exitStr = `${String(exitH).padStart(2,'0')}:${String(exitM).padStart(2,'0')}`;
                                setIssueComment({...issueComment,[issue.id+'_entry']:entry,[issue.id+'_exit']:exitStr,[issue.id+'_mode']:'adjust'});
                              }}
                              style={{width:'100%', padding:'6px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'13px'}}
                            />
                          </div>
                          <div>
                            <label style={{fontSize:'11px', color:'var(--text-secondary)', display:'block', marginBottom:'4px'}}>Hora salida (auto 8h):</label>
                            <input
                              type="time"
                              value={issueComment[issue.id+'_exit']||''}
                              onChange={e=>setIssueComment({...issueComment,[issue.id+'_exit']:e.target.value})}
                              style={{width:'100%', padding:'6px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'13px'}}
                            />
                          </div>
                        </div>
                        <input
                          placeholder="Motivo del ajuste"
                          value={issueComment[issue.id]||''}
                          onChange={e=>setIssueComment({...issueComment,[issue.id]:e.target.value})}
                          style={{width:'100%', padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px', marginBottom:'8px', boxSizing:'border-box'}}
                        />
                        <div style={{display:'flex', gap:'6px'}}>
                          <button
                            onClick={() => {
                              const entry = issueComment[issue.id+'_entry'];
                              const exit = issueComment[issue.id+'_exit'];
                              if (!entry || !exit) { alert('Ingresa hora de entrada y salida'); return; }
                              // Update monthlySchedule of employee
                              const empToUpdate = employees.find(e => e.id === issue.employeeId);
                              if (empToUpdate) {
                                const updatedEmp = {
                                  ...empToUpdate,
                                  monthlySchedule: {
                                    ...(empToUpdate.monthlySchedule||{}),
                                    [issue.date]: {
                                      ...(empToUpdate.monthlySchedule?.[issue.date]||{}),
                                      enabled: true,
                                      start: entry,
                                      end: exit
                                    }
                                  }
                                };
                                const updatedEmps = employees.map(e => e.id === issue.employeeId ? updatedEmp : e);
                                saveEmployees(updatedEmps);
                                classifyIssue(issue.id, 'justified', `Horario ajustado: ${entry}-${exit}. ${issueComment[issue.id]||''}`);
                                addAuditEntry('AJUSTAR_HORARIO', `Ajustó horario de ${issue.employeeName} del ${issue.date}: entrada ${entry}, salida ${exit}`);
                                setIssueComment({...issueComment,[issue.id+'_mode']:undefined});
                                alert('✅ Horario ajustado correctamente');
                              }
                            }}
                            style={{flex:1, padding:'6px 12px', background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}
                          >💾 Guardar Ajuste</button>
                          <button onClick={()=>setIssueComment({...issueComment,[issue.id+'_mode']:undefined})} style={{padding:'6px 12px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:'6px', cursor:'pointer', fontSize:'12px'}}>Cancelar</button>
                        </div>
                      </div>
                    ) : issueComment[issue.id+'_mode'] === 'justify' ? (
                      // ── MODO JUSTIFICAR ──
                      <div style={{background:'var(--bg-card)', borderRadius:'8px', padding:'12px', border:'1px solid rgba(34,197,94,0.3)'}}>
                        <p style={{margin:'0 0 8px', fontSize:'12px', color:'var(--green)', fontWeight:'700'}}>✅ Justificar Incidencia</p>
                        <input
                          placeholder="Motivo de la justificación (requerido)"
                          value={issueComment[issue.id]||''}
                          onChange={e=>setIssueComment({...issueComment,[issue.id]:e.target.value})}
                          style={{width:'100%', padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px', marginBottom:'8px', boxSizing:'border-box'}}
                        />
                        <div style={{display:'flex', gap:'6px'}}>
                          <button onClick={()=>{if(!(issueComment[issue.id]||'').trim()){alert('Escribe el motivo');return;}classifyIssue(issue.id,'justified',issueComment[issue.id]);setIssueComment({...issueComment,[issue.id]:'', [issue.id+'_mode']:undefined});}} style={{flex:1, padding:'6px 12px', background:'rgba(34,197,94,0.2)', color:'var(--green)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}>✅ Confirmar Justificación</button>
                          <button onClick={()=>setIssueComment({...issueComment,[issue.id+'_mode']:undefined})} style={{padding:'6px 12px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:'6px', cursor:'pointer', fontSize:'12px'}}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      // ── BOTONES PRINCIPALES ──
                      <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                        <button onClick={()=>setIssueComment({...issueComment,[issue.id+'_mode']:'justify'})} style={{padding:'5px 12px', background:'rgba(34,197,94,0.15)', color:'var(--green)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}>✅ Justificar</button>
                        <button onClick={()=>setIssueComment({...issueComment,[issue.id+'_mode']:'adjust'})} style={{padding:'5px 12px', background:'rgba(59,130,246,0.15)', color:'var(--blue)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}>⏰ Ajustar Horario</button>
                        <button onClick={()=>{classifyIssue(issue.id,'unjustified','');}} style={{padding:'5px 12px', background:'rgba(239,68,68,0.15)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600'}}>🔴 Injustificada</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── PANEL DE ANUNCIOS ── */}
      {isManager && (() => {
        const now = new Date();
        const activeAnns = (Array.isArray(announcements)?announcements:[]).filter(a => !a.expiresAt || new Date(a.expiresAt) > now);
        return (
          <div style={{background:'var(--bg-card)', borderRadius:'12px', padding:'16px', marginBottom:'20px', border:'1px solid var(--border)'}}>
            <h3 style={{margin:'0 0 12px', fontSize:'15px'}}>📢 Anuncios para Empleados</h3>

            {/* Crear nuevo anuncio */}
            {(() => {
              return (
                <div style={{background:'var(--bg-elevated)', borderRadius:'10px', padding:'12px', marginBottom:'12px'}}>
                  <textarea
                    value={annText}
                    onChange={e=>setAnnText(e.target.value)}
                    placeholder="Escribe el mensaje para todos los empleados..."
                    rows={2}
                    style={{width:'100%', padding:'8px 10px', background:'var(--bg-card)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'8px', fontSize:'13px', marginBottom:'8px', boxSizing:'border-box', resize:'vertical'}}
                  />
                  <div style={{display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center'}}>
                    <select value={annType} onChange={e=>setAnnType(e.target.value)} style={{padding:'6px 10px', background:'var(--bg-card)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}}>
                      <option value="info">📢 Anuncio</option>
                      <option value="warning">⚠️ Advertencia</option>
                      <option value="reminder">📋 Recordatorio</option>
                      <option value="success">🎉 Reconocimiento</option>
                    </select>
                    <select value={annDays} onChange={e=>setAnnDays(e.target.value)} style={{padding:'6px 10px', background:'var(--bg-card)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}}>
                      <option value="1">Expira hoy</option>
                      <option value="2">2 días</option>
                      <option value="3">3 días</option>
                      <option value="7">1 semana</option>
                      <option value="30">1 mes</option>
                    </select>
                    <button
                      onClick={()=>{ if(!annText.trim()){alert('Escribe un mensaje');return;} createAnnouncement(annText,annType,annDays); setAnnText(''); }}
                      style={{padding:'6px 16px', background:'var(--blue)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}
                    >📢 Publicar</button>
                  </div>
                </div>
              );
            })()}

            {/* Lista de anuncios activos */}
            {activeAnns.length === 0 ? (
              <p style={{color:'var(--text-muted)', fontSize:'13px', margin:0}}>Sin anuncios activos</p>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                {activeAnns.map(ann => {
                  const daysLeft = Math.ceil((new Date(ann.expiresAt) - now) / 86400000);
                  const icons = {info:'📢', warning:'⚠️', reminder:'📋', success:'🎉'};
                  return (
                    <div key={ann.id} style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', background:'var(--bg-elevated)', borderRadius:'8px', padding:'10px 12px'}}>
                      <div>
                        <span style={{fontSize:'13px'}}>{icons[ann.type]||'📢'} </span>
                        <span style={{fontSize:'13px', color:'var(--text-primary)'}}>{ann.text}</span>
                        <p style={{margin:'4px 0 0', fontSize:'11px', color:'var(--text-secondary)'}}>{ann.createdBy} · {daysLeft<=1?'Expira hoy':`${daysLeft} días`}</p>
                      </div>
                      <button onClick={()=>deleteAnnouncement(ann.id)} style={{background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'16px', padding:'0 4px', flexShrink:0}}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      <div className="schedule-display">
        <h3>⏰ Horario Laboral Configurado</h3>
        <div className="schedule-grid">
          <div className="schedule-item">
            <span className="schedule-icon">🕐</span>
            <div>
              <p className="schedule-time">{String(config.schedules.checkIn.hour).padStart(2, '0')}:{String(config.schedules.checkIn.minute).padStart(2, '0')}</p>
              <p className="schedule-desc">Entrada</p>
            </div>
          </div>
          <div className="schedule-item">
            <span className="schedule-icon">☕</span>
            <div>
              <p className="schedule-time">{String(config.schedules.breakStart.hour).padStart(2, '0')}:{String(config.schedules.breakStart.minute).padStart(2, '0')} - {String(config.schedules.breakEnd.hour).padStart(2, '0')}:{String(config.schedules.breakEnd.minute).padStart(2, '0')}</p>
              <p className="schedule-desc">Colación</p>
            </div>
          </div>
          <div className="schedule-item">
            <span className="schedule-icon">🏁</span>
            <div>
              <p className="schedule-time">{String(config.schedules.checkOut.hour).padStart(2, '0')}:{String(config.schedules.checkOut.minute).padStart(2, '0')}</p>
              <p className="schedule-desc">Salida</p>
            </div>
          </div>
        </div>
      </div>

      <div className="today-attendance">
        <h3>Asistencia de Hoy</h3>
        <div className="attendance-list">
          {todayRecords.length > 0 ? todayRecords.map(record => (
            <div key={record.id} className="attendance-item">
              <div className="attendance-avatar">
                {employees.find(e => e.id === record.employeeId)?.photo || '👤'}
              </div>
              <div className="attendance-info" style={{flex:1}}>
                <h4>{record.employeeName}</h4>
                <div style={{display:'flex', gap:'14px', flexWrap:'wrap', marginTop:'4px'}}>
                  <span style={{fontSize:'12px', color:'var(--accent)', fontFamily:'"DM Mono",monospace'}}>
                    ↗ {record.checkIn || '--'}
                  </span>
                  {record.breakStart && (
                    <span style={{fontSize:'12px', color:'var(--orange)', fontFamily:'"DM Mono",monospace'}}>
                      ☕ {record.breakStart}
                    </span>
                  )}
                  {record.breakEnd && (
                    <span style={{fontSize:'12px', color:'var(--blue)', fontFamily:'"DM Mono",monospace'}}>
                      ↩ {record.breakEnd}
                    </span>
                  )}
                  {record.checkOut && (
                    <span style={{fontSize:'12px', color:'var(--red)', fontFamily:'"DM Mono",monospace'}}>
                      ↙ {record.checkOut}
                    </span>
                  )}
                </div>
              </div>
              <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                <span className={"status-tag " + record.status}>
                  {record.status === 'on_time' ? 'OK' : 'Atraso'}
                </span>
                {(userRole === 'manager' || userRole === 'admin') && (
                  <button
                    onClick={() => handleDeleteAttendanceRecord(record.id)}
                    className="btn-sm danger"
                    title="Eliminar registro"
                  >✕</button>
                )}
              </div>
            </div>
          )) : (
            <p className="no-data">No hay registros de hoy</p>
          )}
        </div>
      </div>
    </div>
  );
};


const AdminEmployeesView = () => {
  const {
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    showPagosModal, setShowPagosModal, pagosEmployee, setPagosEmployee,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
  } = React.useContext(AppContext);

  const filter = adminFilterEmp;
  const setFilter = setAdminFilterEmp;
  
  const uniqueEmployees = employees.filter((e,i,arr)=>arr.findIndex(x=>x.id===e.id)===i);
  const filteredEmployees = filter === 'all' 
    ? uniqueEmployees 
    : uniqueEmployees.filter(e => e.status === filter);

  const [showScheduleSummary, setShowScheduleSummary] = React.useState(false);
  const [scheduleWeekOffset, setScheduleWeekOffset] = React.useState(0);
  
  return (
    <div className="employees-view">
      <div className="view-header">
        <h2>Gestión de Empleados</h2>
        <div style={{display:'flex', gap:'8px'}}>
          <button className="btn-add" onClick={() => setShowScheduleSummary(!showScheduleSummary)} style={{background: showScheduleSummary ? 'var(--blue)' : undefined}}>
            📅 Resumen Horarios
          </button>
          <button className="btn-add" onClick={() => setShowEmployeeModal(true)}>
            <Plus size={18} />
            Agregar Empleado
          </button>
        </div>
      </div>

      {showScheduleSummary && (() => {
        try {
          const activeEmps = uniqueEmployees.filter(e => e?.status === 'active');
          const baseDate = new Date();
          baseDate.setDate(baseDate.getDate() + (scheduleWeekOffset * 7));
          // Lunes de la semana
          const dayOfWeek = (baseDate.getDay() + 6) % 7;
          const monday = new Date(baseDate);
          monday.setDate(baseDate.getDate() - dayOfWeek);

          const days = [];
          for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            days.push({ date: d, dateStr, label: d.toLocaleDateString('es-CL', {weekday:'short'}) });
          }

          const sinConfigCount = activeEmps.filter(e => {
            return days.some(day => {
              const ms = e?.monthlySchedule?.[day.dateStr];
              return !ms || !ms?.enabled || !ms?.start;
            });
          }).length;

          return (
            <div style={{background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'16px', marginBottom:'16px'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
                <button onClick={() => setScheduleWeekOffset(scheduleWeekOffset - 1)} style={{padding:'6px 12px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'6px', cursor:'pointer', color:'var(--text-primary)'}}>← Semana anterior</button>
                <span style={{fontSize:'13px', fontWeight:'600', color:'var(--text-primary)'}}>
                  {days[0].date.toLocaleDateString('es-CL',{day:'numeric',month:'short'})} — {days[6].date.toLocaleDateString('es-CL',{day:'numeric',month:'short'})}
                </span>
                <button onClick={() => setScheduleWeekOffset(scheduleWeekOffset + 1)} style={{padding:'6px 12px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'6px', cursor:'pointer', color:'var(--text-primary)'}}>Semana siguiente →</button>
              </div>

              {sinConfigCount > 0 && (
                <p style={{fontSize:'12px', color:'var(--text-secondary)', margin:'0 0 12px', fontWeight:'500', background:'var(--bg-elevated)', padding:'6px 10px', borderRadius:'6px', border:'1px solid var(--border)'}}>
                  📋 {sinConfigCount} empleado(s) con días sin asignar — clic en el nombre para configurar
                </p>
              )}

              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'12px'}}>
                  <thead>
                    <tr>
                      <th style={{textAlign:'left', padding:'8px', color:'var(--text-secondary)', borderBottom:'1px solid var(--border)', minWidth:'100px'}}>Empleado</th>
                      {days.map(day => {
                        const _today = new Date();
                        const isToday = day.date.getFullYear() === _today.getFullYear() && day.date.getMonth() === _today.getMonth() && day.date.getDate() === _today.getDate();
                        return (
                          <th key={day.dateStr} style={{textAlign:'center', padding:'8px 4px', color: isToday ? 'var(--blue)' : 'var(--text-secondary)', borderBottom: isToday ? '2px solid var(--blue)' : '1px solid var(--border)', minWidth:'70px', textTransform:'capitalize', fontWeight: isToday ? '700' : '400'}}>
                            {day.label} {day.date.getDate()}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {activeEmps.map(emp => (
                      <tr key={emp.id}>
                        <td onClick={() => { setCalendarEmployee(emp); setShowCalendar(true); }} style={{padding:'8px 10px', borderBottom:'1px solid var(--border)', cursor:'pointer', minWidth:'140px'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                          <span style={{fontSize:'16px'}}>{emp.photo || '👤'}</span>
                          <span style={{fontSize:'12px', fontWeight:'600', color:'var(--text-primary)'}}>{emp.name.split(' ')[0]} {emp.name.split(' ')[1] || ''}</span>
                        </div>
                      </td>
                        {days.map(day => {
                          const ms = emp?.monthlySchedule?.[day.dateStr];
                          const hasSchedule = ms?.enabled && ms?.start;
                          const _today = new Date();
                          const isToday = day.date.getFullYear() === _today.getFullYear() && day.date.getMonth() === _today.getMonth() && day.date.getDate() === _today.getDate();
                          const isDayOff = ms?.enabled === false;
                          const isVacation = (() => { try { const lsAbs = JSON.parse(localStorage.getItem('employeeAbsences')||'[]'); return lsAbs.some(a => String(a.employeeId)===String(emp.id) && a.startDate<=day.dateStr && a.endDate>=day.dateStr); } catch(e) { return false; } })();
                          // Verificar ausencia registrada para este empleado y este día (usa LS como fallback si el estado aun no cargo)
                          const _absData = (Array.isArray(absences) && absences.length > 0) ? absences : JSON.parse(localStorage.getItem('employeeAbsences') || '[]');
                          const dayAbsence = _absData.find(a => (a?.employeeId === emp?.id || String(a?.employeeId) === String(emp?.id)) && a?.startDate <= day.dateStr && a?.endDate >= day.dateStr);
                          const absenceLabel = dayAbsence?.type === 'vacation' ? '🏖️ Vacaciones' : dayAbsence?.type === 'medical' ? '🏥 Licencia' : (dayAbsence?.type === 'permission' || dayAbsence?.type === 'sick') ? '📋 Permiso' : null;
                          return (
                            <td key={day.dateStr} 
                              onClick={() => { setCalendarEmployee(emp); setShowCalendar(true); }}
                              style={{textAlign:'center', padding:'6px 4px', borderBottom:'1px solid var(--border)', cursor:'pointer',
                                outline: isToday ? '1px solid var(--blue)' : 'none',
                                background: absenceLabel ? 'rgba(168,85,247,0.08)' : hasSchedule ? 'rgba(34,197,94,0.06)' : 'transparent'}}>
                              {absenceLabel ? (
                                <span style={{color:'#a855f7', fontWeight:'600', fontSize:'11px'}}>{absenceLabel}</span>
                              ) : hasSchedule ? (
                                <div>
                                  <div style={{color:'var(--green)', fontWeight:'700', fontSize:'12px'}}>{ms.start}–{ms.end}</div>
                                  {ms.lunchStart && (
                                    <div style={{color:'var(--text-secondary)', fontSize:'10px', marginTop:'2px'}}>☕ {ms.lunchStart}–{ms.lunchEnd}</div>
                                  )}
                                </div>
                              ) : ms?.enabled === false ? (
                                <span style={{color:'var(--text-secondary)', fontSize:'12px'}}>📅 Libre</span>
                              ) : (
                                <span style={{color:'var(--text-muted)', fontSize:'13px'}}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        } catch(e) { return null; }
      })()}
      
      {/* Filtros de estado */}
      <div className="status-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Todos ({employees.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          🟢 Activos ({employees.filter(e => e.status === 'active').length})
        </button>
        <button 
          className={`filter-btn ${filter === 'inactive' ? 'active' : ''}`}
          onClick={() => setFilter('inactive')}
        >
          ⚪ Inactivos ({employees.filter(e => e.status === 'inactive').length})
        </button>
        <button 
          className={`filter-btn ${filter === 'suspended' ? 'active' : ''}`}
          onClick={() => setFilter('suspended')}
        >
          🔒 Suspendidos ({employees.filter(e => e.status === 'suspended').length})
        </button>
      </div>
      
      <div className="employees-grid">
        {filteredEmployees.map(employee => (
          <div key={employee.id} style={{
              background:'var(--bg-card)', borderRadius:'14px',
              border:`1px solid ${employee.status==='active'?'rgba(34,197,94,0.2)':employee.status==='suspended'?'rgba(239,68,68,0.2)':'var(--border)'}`,
              overflow:'hidden', display:'flex', flexDirection:'column'
            }}>
            {/* Header con color según estado */}
            <div style={{
              background: employee.status==='active'?'rgba(34,197,94,0.08)':employee.status==='suspended'?'rgba(239,68,68,0.08)':'var(--bg-elevated)',
              padding:'12px 14px', display:'flex', justifyContent:'space-between', alignItems:'center',
              borderBottom:'1px solid var(--border)'
            }}>
              <span style={{fontSize:'11px', fontWeight:'700', color:employee.status==='active'?'var(--green)':employee.status==='suspended'?'#ef4444':'var(--text-secondary)'}}>
                {employee.status==='active'?'🟢 Activo':employee.status==='suspended'?'🔒 Suspendido':'⚪ Inactivo'}
              </span>
              <span style={{fontSize:'11px', color:'var(--text-muted)', fontWeight:'600'}}>EMP{String(employee.id).padStart(3,'0')}</span>
            </div>

            {/* Foto + info principal */}
            <div style={{padding:'14px', display:'flex', gap:'12px', alignItems:'flex-start'}}>
              <div style={{fontSize:'40px', lineHeight:1, flexShrink:0}}>{employee.photo}</div>
              <div style={{flex:1, minWidth:0}}>
                <h3 style={{margin:'0 0 2px', fontSize:'15px', fontWeight:'700', color:'var(--text-primary)'}}>{employee.name}</h3>
                <p style={{margin:'0 0 6px', fontSize:'12px', color:'var(--text-secondary)'}}>{employee.position}</p>
                {/* Horario hoy o ausencia */}
                {(() => {
                  const today = new Date();
                  const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
                  // Verificar ausencia activa hoy (solo lectura del sistema de ausencias existente)
                  const _absArr = (Array.isArray(absences) && absences.length > 0) ? absences : JSON.parse(localStorage.getItem('employeeAbsences') || '[]');
                  const todayAbsence = _absArr.find(a => (a?.employeeId === employee?.id || String(a?.employeeId) === String(employee?.id)) && a?.startDate <= dateStr && a?.endDate >= dateStr);
                  if (todayAbsence) {
                    const label = todayAbsence.type === 'vacation' ? '🏖️ Vacaciones' : todayAbsence.type === 'medical' ? '🏥 Licencia médica' : '📋 Permiso';
                    return <span style={{fontSize:'11px', color:'#a855f7', fontWeight:'700'}}>{label}</span>;
                  }
                  const hasWeekly = employee.useFlexibleSchedule && employee.weeklySchedule && Object.values(employee.weeklySchedule).some(d => d.enabled && d.start);
                  const hasMonthly = employee.monthlySchedule && Object.values(employee.monthlySchedule).some(d => d?.enabled && d?.start);
                  const ms = employee.monthlySchedule?.[dateStr];
                  if (!hasWeekly && !hasMonthly) return <span style={{fontSize:'11px', color:'#ef4444', fontWeight:'700'}}>⚠️ Sin horario</span>;
                  if (ms && !ms.enabled) return <span style={{fontSize:'11px', color:'var(--text-muted)'}}>🏖️ Libre hoy</span>;
                  if (ms?.enabled && ms?.start) return <span style={{fontSize:'11px', color:'var(--green)'}}>⏰ {ms.start} - {ms.end}</span>;
                  return <span style={{fontSize:'11px', color:'var(--text-muted)'}}>🏖️ Libre hoy</span>;
                })()}
              </div>
            </div>

            {/* Anticipos colapsados */}
            {employee.advances && employee.advances.filter(a => a.status !== 'pagado' && (a.amount??0) > 0).length > 0 && (
              <div style={{padding:'0 14px 10px'}}>
                <div onClick={()=>{setEditingEmployee(employee);setShowEmployeeModal(true);}} style={{background:'rgba(245,158,11,0.08)', borderRadius:'8px', padding:'8px 10px', cursor:'pointer', border:'1px solid rgba(245,158,11,0.2)'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px'}}>
                    <p style={{margin:0, fontSize:'11px', color:'var(--orange)', fontWeight:'700', textTransform:'uppercase'}}>💰 Anticipos pendientes</p>
                    <span style={{fontSize:'10px', color:'var(--orange)', opacity:0.8}}>Ver detalle →</span>
                  </div>
                  {employee.advances.filter(a => a.status !== 'pagado' && (a.amount??0) > 0).map(adv => (
                    <div key={adv.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:'12px', padding:'4px 0', borderBottom:'1px solid rgba(245,158,11,0.15)'}}>
                      <div>
                        <span style={{color:'var(--text-primary)', fontWeight:'700'}}>${(adv.amount??0).toLocaleString('es-CL')}</span>
                        {adv.description && <span style={{color:'var(--text-muted)', fontSize:'11px', marginLeft:'6px'}}>{adv.description}</span>}
                      </div>
                      <div style={{textAlign:'right'}}>
                        <span style={{color: adv.status==='aprobado'?'var(--green)':'var(--orange)', fontWeight:'600', fontSize:'11px'}}>{adv.status==='aprobado'?'✅ Aprobado':'⏳ Pendiente'}</span>
                        {adv.date && <p style={{margin:'1px 0 0', fontSize:'10px', color:'var(--text-secondary)', fontWeight:'600'}}>{new Date(adv.date).toLocaleDateString('es-CL')}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Acciones con etiquetas */}
            <div style={{padding:'10px 14px', borderTop:'1px solid var(--border)', display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'auto'}}>
              {!(userRole === 'admin' && employee.id === currentUser?.id) && (
              <>
              {userRole === 'manager' && (
                <button onClick={()=>{ setPagosEmployee(employee); setShowPagosModal(true); }} style={{padding:'5px 10px', background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px', fontFamily:'inherit'}}>
                  💰 Pagos
                </button>
              )}
              <button onClick={()=>{setSelectedEmployeeForAbsences(employee.id);setShowAbsenceManager(true);}} style={{flex:1, padding:'6px 4px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:'8px', cursor:'pointer', fontSize:'11px', display:'flex', flexDirection:'column', alignItems:'center', gap:'2px', minWidth:'44px'}}>
                📅 Ausencias
              </button>
              <button onClick={()=>{setCalendarEmployee(employee);setShowCalendar(true);}} style={{padding:'5px 10px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px', fontFamily:'inherit'}}>
                🗓️ Horario
              </button>
              <button onClick={()=>{setEditingEmployee(employee);setShowEmployeeModal(true);}} style={{padding:'5px 10px', background:'rgba(59,130,246,0.1)', color:'var(--blue)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px', fontFamily:'inherit'}}>
                ✏️ Editar
              </button>
              </>
              )}
              {employee.scheduleHistory && employee.scheduleHistory.length > 0 && (
                <div style={{width:'100%', padding:'4px 8px', fontSize:'10px', color:'var(--text-secondary)', borderTop:'1px solid var(--border)', marginTop:'4px'}}>
                  🕐 Último cambio horario: {new Date(employee.scheduleHistory[0]?.date).toLocaleDateString('es-CL')} — {employee.scheduleHistory[0]?.changedBy}
                </div>
              )}

              {/* ── FICHA RR.HH. — solo visible para el gerente ── */}
              {userRole === 'manager' && (() => {
                try {
                  const lsAbsences = JSON.parse(localStorage.getItem('employeeAbsences') || '[]');
                  const empAbsences = lsAbsences.filter(a => String(a.employeeId) === String(employee.id));
                  const vacaciones = empAbsences.filter(a => a.type === 'vacation');
                  const licencias = empAbsences.filter(a => a.type === 'medical');
                  const faltas = empAbsences.filter(a => a.type === 'absence');
                  const diasVacUsados = vacaciones.reduce((s, a) => s + (a.days || 0), 0);
                  const diasLicencia = licencias.reduce((s, a) => s + (a.days || 0), 0);
                  const anioActual = new Date().getFullYear();
                  const faltasAnio = faltas.filter(a => a.startDate?.startsWith(String(anioActual))).length;
                  const antiguedad = employee.hireDate ? (() => {
                    const diff = new Date() - new Date(employee.hireDate);
                    const years = Math.floor(diff / (365.25*24*60*60*1000));
                    const months = Math.floor((diff % (365.25*24*60*60*1000)) / (30.44*24*60*60*1000));
                    return years > 0 ? `${years} año(s) y ${months} mes(es)` : `${months} mes(es)`;
                  })() : null;
                  const feriadosIrrenunciables = [
                    {fecha:`${anioActual}-05-01`, nombre:'1 Mayo — Día del Trabajador'},
                    {fecha:`${anioActual}-09-18`, nombre:'18 Sep — Independencia'},
                    {fecha:`${anioActual}-09-19`, nombre:'19 Sep — Glorias del Ejército'},
                    {fecha:`${anioActual}-12-25`, nombre:'25 Dic — Navidad'},
                  ];
                  return (
                    <div style={{padding:'8px 14px 12px'}}>
                      <div style={{background:'var(--bg-elevated)', borderRadius:'10px', padding:'12px', border:'1px solid var(--border)'}}>
                        <p style={{margin:'0 0 12px', fontSize:'13px', color:'var(--text-primary)', fontWeight:'700'}}>📋 Ficha RR.HH.</p>
                        <DiagnosticoIA employee={employee} diasVacUsados={diasVacUsados} faltasAnio={faltasAnio} diasLicencia={diasLicencia} />
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:'8px', marginBottom:'10px'}}>
                          <div style={{background:'var(--bg-card)', borderRadius:'8px', padding:'8px'}}>
                            <p style={{margin:'0 0 3px', fontSize:'11px', color:'#e2e8f0', fontWeight:'600'}}>Tipo contrato</p>
                            <p style={{margin:0, fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>{employee.contractType === 'plazo_fijo' ? '⏳ Plazo fijo' : employee.contractType === 'part-time' ? '⏱ Part-time' : '📄 Indefinido'}</p>
                            {employee.contractEndDate && <p style={{margin:'3px 0 0', fontSize:'11px', color:'#ef4444', fontWeight:'600'}}>Vence: {new Date(employee.contractEndDate).toLocaleDateString('es-CL')}</p>}
                          </div>
                          <div style={{background:'var(--bg-card)', borderRadius:'8px', padding:'8px'}}>
                            <p style={{margin:'0 0 3px', fontSize:'11px', color:'#e2e8f0', fontWeight:'600'}}>Inicio contrato</p>
                            <p style={{margin:0, fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>{employee.hireDate ? new Date(employee.hireDate).toLocaleDateString('es-CL') : '—'}</p>
                            {antiguedad && <p style={{margin:'3px 0 0', fontSize:'11px', color:'#e2e8f0', fontWeight:'600'}}>{antiguedad}</p>}
                          </div>
                        </div>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'6px', marginBottom:'10px'}}>
                          <div style={{background:'rgba(34,197,94,0.1)', borderRadius:'8px', padding:'8px 6px', textAlign:'center', border:'1px solid rgba(34,197,94,0.2)'}}>
                            <p style={{margin:'0 0 2px', fontSize:'11px', color:'var(--green)', fontWeight:'700'}}>🏖️ Vacaciones</p>
                            <p style={{margin:0, fontSize:'20px', fontWeight:'700', color:'var(--green)'}}>{Math.max(0,(employee.vacationDays||15)-diasVacUsados)}</p>
                            <p style={{margin:0, fontSize:'11px', color:'var(--green)', opacity:0.8}}>días disp.</p>
                          </div>
                          <div style={{background:'rgba(239,68,68,0.1)', borderRadius:'8px', padding:'8px 6px', textAlign:'center', border:'1px solid rgba(239,68,68,0.2)'}}>
                            <p style={{margin:'0 0 2px', fontSize:'11px', color:'#ef4444', fontWeight:'700'}}>🏥 Licencias</p>
                            <p style={{margin:0, fontSize:'20px', fontWeight:'700', color:'#ef4444'}}>{diasLicencia}</p>
                            <p style={{margin:0, fontSize:'11px', color:'#ef4444', opacity:0.8}}>días año</p>
                          </div>
                          <div style={{background:'rgba(245,158,11,0.1)', borderRadius:'8px', padding:'8px 6px', textAlign:'center', border:'1px solid rgba(245,158,11,0.2)'}}>
                            <p style={{margin:'0 0 2px', fontSize:'11px', color:'var(--orange)', fontWeight:'700'}}>❌ Faltas</p>
                            <p style={{margin:0, fontSize:'20px', fontWeight:'700', color:'var(--orange)'}}>{faltasAnio}</p>
                            <p style={{margin:0, fontSize:'11px', color:'var(--orange)', opacity:0.8}}>año {anioActual}</p>
                          </div>
                        </div>
                        <div style={{marginBottom: employee.observations ? '10px' : '0'}}>
                          <p style={{margin:'0 0 6px', fontSize:'12px', color:'var(--text-primary)', fontWeight:'700'}}>🇨🇱 Feriados irrenunciables {anioActual}</p>
                          {feriadosIrrenunciables.map((f,i) => (
                            <div key={i} style={{display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'5px 0', borderBottom:'1px solid var(--border)'}}>
                              <span style={{color:'var(--text-primary)', fontWeight:'500'}}>{f.nombre}</span>
                              <span style={{color:'#e2e8f0', fontSize:'12px', fontWeight:'600'}}>{new Date(f.fecha).toLocaleDateString('es-CL',{day:'numeric',month:'short'})}</span>
                            </div>
                          ))}
                        </div>
                        {employee.observations && (
                          <div style={{background:'rgba(99,102,241,0.08)', borderRadius:'8px', padding:'8px', border:'1px solid rgba(99,102,241,0.15)', marginTop:'8px'}}>
                            <p style={{margin:'0 0 2px', fontSize:'12px', color:'#6366f1', fontWeight:'700'}}>📝 OBSERVACIONES</p>
                            <p style={{margin:0, fontSize:'12px', color:'var(--text-primary)'}}>{employee.observations}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } catch(e) { return null; }
              })()}
              {/* ── BOTONES ACCION EMPLEADO — mantener en este div flex, no separar ── */}
              {!(userRole === 'admin' && employee.id === currentUser?.id) && (
              <div style={{display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'4px'}}>
                {employee.status === 'active' && (
                  <button onClick={()=>handleChangeEmployeeStatus(employee.id,'inactive')} style={{padding:'5px 10px', background:'rgba(245,158,11,0.1)', color:'var(--orange)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px', fontFamily:'inherit'}}>
                    ⏸️ Pausar
                  </button>
                )}
                {(employee.status==='inactive'||employee.status==='suspended') && (
                  <button onClick={()=>handleChangeEmployeeStatus(employee.id,'active')} style={{padding:'5px 10px', background:'rgba(34,197,94,0.1)', color:'var(--green)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px', fontFamily:'inherit'}}>
                    ▶️ Activar
                  </button>
                )}
                {employee.status === 'active' && (
                  <button onClick={()=>handleChangeEmployeeStatus(employee.id,'suspended')} style={{padding:'5px 10px', background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px', fontFamily:'inherit'}}>
                    🔒 Suspender
                  </button>
                )}
                <button onClick={()=>handleDeleteEmployee(employee.id)} style={{padding:'5px 10px', background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px', fontFamily:'inherit'}}>
                  <Trash2 size={12}/> Borrar
                </button>
              </div>
              )}
            </div>
            
            {/* Horario del empleado */}
          </div>
        ))}
      </div>
    </div>
  );
};


const AdminReportsView = () => {
  const {
    setAuditLog,
    LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
    adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
    currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
    getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
    handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
    handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
    handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
    handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
    handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
    isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
    tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
    selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
    setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
    setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
    setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
    setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
    setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
    setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
    setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
    setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, setTempConfigState,
    setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
    showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
    tempConfigState, tempPermissionsState, todayRecord, userRole,
    ignoreNextFirebaseUpdate, // FIX BUG 3
  } = React.useContext(AppContext);

  // ── HOOKS para Registros Recientes ──
  const [recordsTab, setRecordsTab] = React.useState('resumen');
  const [filterEmp, setFilterEmp] = React.useState('all');
  const [auditLimit, setAuditLimit] = React.useState(20);
  const [filterMonth, setFilterMonth] = React.useState(getLocalDateStr().substring(0,7));
  const [filterMode, setFilterMode] = React.useState('month'); // 'month' | 'range'
  const [filterDateFrom, setFilterDateFrom] = React.useState(getLocalDateStr().substring(0,7) + '-01');
  const [filterDateTo, setFilterDateTo] = React.useState(getLocalDateStr());
  const [currentPage, setCurrentPage] = React.useState(1);
  const [expandedEmp, setExpandedEmp] = React.useState(null);

  // Obtener mes actual
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  // Filtrar registros según modo de filtro
  const currentMonthRecords = (Array.isArray(attendanceRecords)?attendanceRecords:[]).filter(r => {
    const empOk = filterEmp === 'all' || String(r.employeeId) === filterEmp;
    const dateOk = filterMode === 'month' ? r.date?.startsWith(filterMonth) : (r.date >= filterDateFrom && r.date <= filterDateTo);
    return empOk && dateOk;
  });
  
  // Calcular resumen por empleado
  const employeeStats = employees.map(emp => {
    const empRecords = currentMonthRecords.filter(r => r.employeeId === emp.id);
    
    const totalLateMinutes = empRecords.reduce((sum, r) => sum + (r.lateMinutes || 0), 0);
    const totalLateBreakMinutes = empRecords.reduce((sum, r) => sum + (r.lateBreakMinutes || 0), 0);
    const totalEarlyCheckOutMinutes = empRecords.reduce((sum, r) => sum + (r.earlyCheckOutMinutes || 0), 0);
    const totalMinutesLost = totalLateMinutes + totalLateBreakMinutes + totalEarlyCheckOutMinutes;
    
    const daysWithDelay = empRecords.filter(r => 
      (r.lateMinutes && r.lateMinutes > 0) || 
      (r.lateBreakMinutes && r.lateBreakMinutes > 0) || 
      (r.earlyCheckOutMinutes && r.earlyCheckOutMinutes > 0)
    ).length;
    
    const punctualityRate = empRecords.length > 0 
      ? ((empRecords.length - daysWithDelay) / empRecords.length * 100).toFixed(1)
      : 100;
    
    // Calcular horas trabajadas reales
    const parseTimeToMin = (str) => {
      if (!str || str.trim() === '') return null;
      const _t = str.replace(/[^0-9:]/g,'').trim();
      const _parts = _t.split(':').map(Number);
      const _h2 = _parts[0];
      const isPM = /p[\s.]*m/i.test(str) && _h2 !== 12;
      const isNoon = _h2 === 12 && /p[\s.]*m/i.test(str);
      const isMidnight = _h2 === 12 && /a[\s.]*m/i.test(str);
      const [h,m] = _parts;
      if (isNaN(h)||isNaN(m)) return null;
      let hour = isMidnight ? 0 : (isPM||isNoon) ? (h===12?12:h+12) : h;
      return hour * 60 + m;
    };

    const totalWorkedMinutes = empRecords.reduce((sum, r) => {
      if (!r.checkIn || !r.checkOut) return sum;
      const inMin = parseTimeToMin(r.checkIn);
      const outMin = parseTimeToMin(r.checkOut);
      if (inMin === null || outMin === null) return sum;
      // Handle overnight shifts
      let gross = outMin > inMin ? outMin - inMin : outMin + 1440 - inMin;
      // Subtract actual break duration
      if (r.breakStart && r.breakEnd) {
        const bsMin = parseTimeToMin(r.breakStart);
        const beMin = parseTimeToMin(r.breakEnd);
        if (bsMin !== null && beMin !== null) {
          const breakDur = beMin > bsMin ? beMin - bsMin : beMin + 1440 - bsMin;
          gross -= breakDur;
        }
      }
      // Subtract late minutes
      gross -= (r.lateMinutes || 0);
      gross -= (r.lateBreakMinutes || 0);
      return sum + Math.max(0, gross);
    }, 0);

    return {
      id: emp.id,
      name: emp.name,
      photo: emp.photo,
      totalMinutesLost,
      totalHoursLost: totalMinutesLost ? (totalMinutesLost / 60) : 0,
      totalLateMinutes,
      totalLateBreakMinutes,
      totalEarlyCheckOutMinutes,
      daysWithDelay,
      totalDays: empRecords.length,
      punctualityRate: parseFloat(punctualityRate),
      totalWorkedMinutes,
      totalWorkedHours: (totalWorkedMinutes / 60).toFixed(1)
    };
  }).sort((a, b) => b.punctualityRate - a.punctualityRate); // Ordenar por puntualidad

  // Encontrar máximo para la escala del gráfico
  const maxMinutes = Math.max(...employeeStats.map(s => s.totalMinutesLost), 1);

  return (
    <div className="reports-view">
      <div className="view-header">
        <h2>📊 Reportes y Estadísticas - {new Date().toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}</h2>
        <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
          <select
            style={{padding:'8px 12px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'8px', fontSize:'13px', cursor:'pointer', fontWeight:'600'}}
            onChange={e => {
              const v = e.target.value;
              if (v === 'csv') exportData();
              if (v === 'pdf-todos') generatePDF(null);
              if (v === 'pdf-tareas-todos') generateTasksPDF(null);
              e.target.value = '';
            }}
            defaultValue=""
          >
            <option value="" disabled>⬇️ Exportar...</option>
            <option value="csv">Exportar CSV</option>
            <option value="pdf-todos">PDF Asistencia - Todos</option>
            <option value="pdf-tareas-todos">PDF Tareas - Todos</option>
          </select>
          <select
            style={{padding:'8px 12px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'8px', fontSize:'13px', cursor:'pointer'}}
            onChange={e => { if (e.target.value) { generatePDF(parseInt(e.target.value)); e.target.value=''; }}}
            defaultValue=""
          >
            <option value="">📄 PDF Asistencia...</option>
            {employees.filter(e => e.status === 'active').map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <button className="btn-export" style={{background:'#7c3aed'}} onClick={() => generateTasksPDF(null)}>
            <Download size={18} />
            PDF Tareas Todos
          </button>
          <select
            style={{padding:'8px 12px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'8px', fontSize:'13px', cursor:'pointer'}}
            onChange={e => { if (e.target.value) { generateTasksPDF(parseInt(e.target.value)); e.target.value=''; }}}
            defaultValue=""
          >
            <option value="">📋 PDF Tareas Individual...</option>
            {employees.filter(e => e.status === 'active').map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Resumen General */}
      <div className="stats-grid">
        <div className="stat-card">
          <TrendingUp size={32} color="var(--accent)" />
          <div>
            <p className="stat-value">{employeeStats.length > 0 ? (employeeStats.reduce((sum, s) => sum + (s.totalHoursLost || 0), 0)).toFixed(2) : '0.00'}h</p>
            <p className="stat-label">Total Horas Perdidas</p>
          </div>
        </div>
        <div className="stat-card">
          <Clock size={32} color="#f59e0b" />
          <div>
            <p className="stat-value">{employeeStats.length > 0 ? employeeStats.reduce((sum, s) => sum + (s.daysWithDelay || 0), 0) : 0}</p>
            <p className="stat-label">Total Días con Atraso</p>
          </div>
        </div>
        <div className="stat-card">
          <Users size={32} color="#10b981" />
          <div>
            <p className="stat-value">{employeeStats.length > 0 ? ((employeeStats.reduce((sum, s) => sum + (s.punctualityRate || 0), 0) / employeeStats.length)).toFixed(1) : '0.0'}%</p>
            <p className="stat-label">Puntualidad Promedio</p>
          </div>
        </div>
      </div>

      {/* Tabla Detallada de Atrasos */}
      <div className="delay-details-table">
        <h3>📋 Detalle de Atrasos por Empleado</h3>
        <div style={{overflowX: 'auto'}}>
          <table>
            <thead>
              <tr>
                <th>Empleado</th>
                <th>Días Trabajados</th>
                <th>Atrasos Entrada</th>
                <th>Atrasos Colación</th>
                <th>Salidas Tempranas</th>
                <th>Total Minutos</th>
                <th>Total Horas</th>
                <th>Puntualidad</th>
              </tr>
            </thead>
            <tbody>
              {employeeStats.map(emp => (
                <tr key={emp.id}>
                  <td>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{fontSize: '24px'}}>{emp.photo}</span>
                      <span>{emp.name}</span>
                    </div>
                  </td>
                  <td>{emp.totalDays}</td>
                  <td className="delay-cell">{emp.totalLateMinutes} min</td>
                  <td className="delay-cell">{emp.totalLateBreakMinutes} min</td>
                  <td className="delay-cell">{emp.totalEarlyCheckOutMinutes} min</td>
                  <td><strong>{emp.totalMinutesLost} min</strong></td>
                  <td><strong className="hours-lost">{Number(emp.totalHoursLost).toFixed(2)} h</strong></td>
                  <td><strong style={{color:'var(--green)'}}>{emp.totalWorkedHours} h</strong></td>
                  <td>
                    <span className={`punctuality-badge ${emp.punctualityRate >= 95 ? 'excellent' : emp.punctualityRate >= 85 ? 'good' : 'warning'}`}>
                      {emp.punctualityRate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Registros Recientes — con tabs */}
      <div className="records-table">
        {(() => {
          const PAGE_SIZE = 15;

          const allRecs = (Array.isArray(attendanceRecords) ? attendanceRecords : [])
            .filter(r => (filterEmp === 'all' || String(r.employeeId) === filterEmp) && (filterMode === 'month' ? r.date?.startsWith(filterMonth) : (r.date >= filterDateFrom && r.date <= filterDateTo)))
            .sort((a,b) => (b.date||'').localeCompare(a.date||''));

          const totalPages = Math.ceil(allRecs.length / PAGE_SIZE);
          const pagedRecs = allRecs.slice((currentPage-1)*PAGE_SIZE, currentPage*PAGE_SIZE);

          const calcHours = (r) => {
            if (!r.checkIn || !r.checkOut) return '--';
            const parseT = (str) => {
              if (!str) return null;
              const _t = str.replace(/[^0-9:]/g,'').trim();
              const _parts = _t.split(':').map(Number);
              const _h2 = _parts[0];
              const isPM = /p[\s.]*m/i.test(str) && _h2 !== 12;
              const isNoon = _h2 === 12 && /p[\s.]*m/i.test(str);
              const isMidnight = _h2 === 12 && /a[\s.]*m/i.test(str);
              const t = str.replace(/[^0-9:]/g,'').trim();
              const [h,m] = t.split(':').map(Number);
              if(isNaN(h)||isNaN(m)) return null;
              let hour = isMidnight ? 0 : (isPM||isNoon) ? (h===12?12:h+12) : h;
              return hour*60+m;
            };
            const inMin=parseT(r.checkIn), outMin=parseT(r.checkOut);
            if(!inMin||!outMin) return '--';
            let gross = outMin>inMin?outMin-inMin:outMin+1440-inMin;
            if(r.breakStart&&r.breakEnd){const bs=parseT(r.breakStart),be=parseT(r.breakEnd);if(bs&&be)gross-=(be>bs?be-bs:be+1440-bs);}
            gross-=(r.lateMinutes||0); gross=Math.max(0,gross);
            return `${Math.floor(gross/60)}h ${String(gross%60).padStart(2,'0')}min`;
          };

          // Months available
          const availMonths = [...new Set((Array.isArray(attendanceRecords)?attendanceRecords:[]).map(r=>r.date?.substring(0,7)).filter(Boolean))].sort().reverse();

          return (
            <>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'8px'}}>
                <h3 style={{margin:0}}>📋 Registros de Asistencia</h3>
                <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                  <div style={{display:'flex', gap:'6px', alignItems:'center', flexWrap:'wrap'}}>
                    <select value={filterMode} onChange={e=>{setFilterMode(e.target.value);setCurrentPage(1);}} style={{padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}}>
                      <option value="month">Por mes</option>
                      <option value="range">Rango de fechas</option>
                    </select>
                    {filterMode === 'month' ? (
                      <select value={filterMonth} onChange={e=>{setFilterMonth(e.target.value);setCurrentPage(1);}} style={{padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}}>
                        {availMonths.map(m=><option key={m} value={m}>{new Date(m+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'})}</option>)}
                      </select>
                    ) : (
                      <div style={{display:'flex', gap:'4px', alignItems:'center'}}>
                        <input type="date" value={filterDateFrom} onChange={e=>{setFilterDateFrom(e.target.value);setCurrentPage(1);}} style={{padding:'6px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}} />
                        <span style={{color:'var(--text-secondary)', fontSize:'12px'}}>al</span>
                        <input type="date" value={filterDateTo} onChange={e=>{setFilterDateTo(e.target.value);setCurrentPage(1);}} style={{padding:'6px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}} />
                      </div>
                    )}
                  </div>
                  <select value={filterEmp} onChange={e=>{setFilterEmp(e.target.value);setCurrentPage(1);}} style={{padding:'6px 10px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', fontSize:'12px'}}>
                    <option value="all">Todos los empleados</option>
                    {(Array.isArray(employees)?employees:[]).filter(e=>e.status==='active').map(e=><option key={e.id} value={String(e.id)}>{e.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Tabs */}
              <div style={{display:'flex', gap:'4px', marginBottom:'16px', background:'var(--bg-elevated)', padding:'4px', borderRadius:'8px'}}>
                {[{id:'resumen',label:'📊 Resumen'},{id:'detalle',label:'📋 Detalle'},{id:'atrasos',label:'⏱️ Atrasos'}].map(tab=>(
                  <button key={tab.id} onClick={()=>setRecordsTab(tab.id)} style={{flex:1, padding:'8px', background:recordsTab===tab.id?'var(--bg-card)':'transparent', color:recordsTab===tab.id?'var(--text-primary)':'var(--text-secondary)', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:recordsTab===tab.id?'700':'400', fontSize:'13px', transition:'all 0.2s'}}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* TAB: RESUMEN */}
              {recordsTab === 'resumen' && (() => {
                const empStats = (Array.isArray(employees)?employees:[]).filter(e=>e.status==='active').map(emp => {
                  const recs = allRecs.filter(r=>r.employeeId===emp.id);
                  const late = recs.filter(r=>r.isLate).length;
                  const lateMin = recs.reduce((s,r)=>s+(r.lateMinutes||0),0);
                  const punct = recs.length>0?Math.round(((recs.length-late)/recs.length)*100):100;
                  const totalMin = recs.reduce((s,r)=>{
                    const parseT=(str)=>{if(!str)return null;const _t=str.replace(/[^0-9:]/g,'').trim();const[_h,_m]=_t.split(':').map(Number);if(isNaN(_h)||isNaN(_m))return null;const _pm=/p[\s.]*m/i.test(str)&&_h!==12;const _mid=/a[\s.]*m/i.test(str)&&_h===12;return(_pm?_h+12:_mid?0:_h)*60+_m;};
                    if(!r.checkIn||!r.checkOut)return s;const inM=parseT(r.checkIn),outM=parseT(r.checkOut);if(!inM||!outM)return s;
                    let g=outM>inM?outM-inM:outM+1440-inM;if(r.breakStart&&r.breakEnd){const bs=parseT(r.breakStart),be=parseT(r.breakEnd);if(bs&&be)g-=(be>bs?be-bs:be+1440-bs);}
                    return s+Math.max(0,g-(r.lateMinutes||0));
                  },0);
                  return {emp, days:recs.length, late, lateMin, punct, totalHours:(totalMin/60).toFixed(1)};
                });
                return (
                  <div style={{overflowX:'auto'}}>
                    <table>
                      <thead><tr><th>Empleado</th><th>Días</th><th>Horas Trabajadas</th><th>Atrasos</th><th>Min. Perdidos</th><th>Puntualidad</th></tr></thead>
                      <tbody>
                        {empStats.map(({emp,days,late,lateMin,punct,totalHours})=>(
                          <tr key={emp.id}>
                            <td><div style={{display:'flex',alignItems:'center',gap:'8px'}}><span style={{fontSize:'20px'}}>{emp.photo}</span><span>{emp.name}</span></div></td>
                            <td style={{fontWeight:'600'}}>{days}</td>
                            <td style={{color:'var(--green)',fontWeight:'600'}}>{totalHours}h</td>
                            <td className={late>0?'delay-cell':''}>{late>0?`${late} días`:'-'}</td>
                            <td className={lateMin>0?'delay-cell':''}>{lateMin>0?`${lateMin} min`:'-'}</td>
                            <td><span className={`punctuality-badge ${punct>=95?'excellent':punct>=85?'good':'warning'}`}>{punct}%</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* TAB: POR EMPLEADO */}
              {/* TAB: DETALLE con paginación */}
              {recordsTab === 'atrasos' && (() => {
                const mesAnterior = (() => {
                  const [y, m] = filterMonth.split('-').map(Number);
                  const d = new Date(y, m-2, 1);
                  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
                })();
                const mesNombreActual = new Date(filterMonth+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'});
                const mesNombreAnterior = new Date(mesAnterior+'-01T12:00:00').toLocaleDateString('es-CL',{month:'long',year:'numeric'});
                const allRecs2 = Array.isArray(attendanceRecords) ? attendanceRecords : [];
                const recsAnterior = allRecs2.filter(r => r.date?.startsWith(mesAnterior));

                const statsEmpleados = (Array.isArray(employees)?employees:[])
                  .filter(e => e.status === 'active' && e.id !== 999)
                  .map(emp => {
                    const recs = currentMonthRecords.filter(r => r.employeeId === emp.id && r.isLate);
                    const recsAnt = recsAnterior.filter(r => r.employeeId === emp.id && r.isLate);
                    const totalMinMes = recs.reduce((s,r) => s+(r.lateMinutes||0), 0);
                    const totalMinAnt = recsAnt.reduce((s,r) => s+(r.lateMinutes||0), 0);
                    return {
                      emp,
                      recs: recs.sort((a,b) => a.date.localeCompare(b.date)),
                      totalMinMes,
                      totalMinAnt,
                      diferencia: totalMinMes - totalMinAnt,
                      diasAtrasado: recs.length
                    };
                  })
                  .filter(s => s.totalMinMes > 0 || s.diasAtrasado > 0);

                const totalEquipo = statsEmpleados.reduce((s,e) => s+e.totalMinMes, 0);

                return (
                  <div>
                    {/* Header resumen equipo */}
                    <div style={{background:'var(--bg-elevated)', borderRadius:'10px', padding:'14px', marginBottom:'16px', border:'1px solid var(--border)'}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
                        <p style={{margin:0, fontSize:'14px', fontWeight:'700', color:'var(--text-primary)'}}>⏱️ Resumen de atrasos — <span style={{color:'#6366f1', textTransform:'capitalize'}}>{mesNombreActual}</span></p>
                        <button onClick={async () => {
                          try {
                            if (!window.jspdf) {
                              await new Promise((res,rej) => {
                                const s = document.createElement('script');
                                s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
                                s.onload = res; s.onerror = rej;
                                document.head.appendChild(s);
                              });
                            }
                            const {jsPDF} = window.jspdf;
                            const doc = new jsPDF({orientation:'portrait', unit:'mm', format:'a4'});
                            const W = 210;
                            doc.setFillColor(15,23,42); doc.rect(0,0,W,28,'F');
                            doc.setTextColor(255,255,255); doc.setFontSize(16); doc.setFont('helvetica','bold');
                            doc.text('Multimarket GyC', 14, 11);
                            doc.setFontSize(10); doc.setFont('helvetica','normal');
                            doc.text('Reporte de Atrasos', 14, 18);
                            doc.text(new Date().toLocaleDateString('es-CL'), W-14, 18, {align:'right'});
                            doc.setFontSize(12); doc.setTextColor(15,23,42); doc.setFont('helvetica','bold');
                            let y = 36;
                            doc.text(`Periodo: ${mesNombreActual}`, 14, y);
                            doc.setFontSize(9); doc.setFont('helvetica','normal');
                            doc.text(`Total equipo: ${totalEquipo} minutos de atraso`, 14, y+6);
                            y += 16;
                            statsEmpleados.forEach(({emp, recs, totalMinMes, totalMinAnt, diferencia, diasAtrasado}) => {
                              if (y > 250) { doc.addPage(); y = 20; }
                              doc.setFillColor(30,41,59); doc.rect(10, y-4, W-20, 10, 'F');
                              doc.setTextColor(255,255,255); doc.setFontSize(10); doc.setFont('helvetica','bold');
                              doc.text(emp.name, 14, y+2);
                              const hrs = Math.floor(totalMinMes/60);
                              const min = totalMinMes%60;
                              const resumen = hrs > 0 ? `${hrs}h ${min}min` : `${min} min`;
                              doc.text(`Total: ${resumen} en ${diasAtrasado} dia(s)`, W-16, y+2, {align:'right'});
                              y += 12;
                              doc.setTextColor(15,23,42); doc.setFontSize(8); doc.setFont('helvetica','normal');
                              doc.setFillColor(248,250,252); doc.rect(10, y-4, W-20, 8, 'F');
                              doc.setFont('helvetica','bold');
                              doc.text('Fecha', 14, y); doc.text('Llegada', 65, y); doc.text('Minutos atraso', 110, y); doc.text('vs mes anterior', 165, y);
                              y += 8;
                              doc.setFont('helvetica','normal');
                              recs.forEach((r, i) => {
                                if (y > 270) { doc.addPage(); y = 20; }
                                if (i%2===0) { doc.setFillColor(248,250,252); doc.rect(10,y-4,W-20,8,'F'); }
                                const fecha = new Date(r.date+'T12:00:00').toLocaleDateString('es-CL',{weekday:'short',day:'numeric',month:'short'});
                                doc.text(fecha, 14, y);
                                doc.text(r.checkIn || '-', 65, y);
                                doc.text(`${r.lateMinutes||0} min`, 110, y);
                                y += 8;
                              });
                              if (diferencia !== 0) {
                                doc.setTextColor(diferencia > 0 ? 200 : 0, diferencia > 0 ? 0 : 150, 0);
                                doc.text(`Comparativa vs ${mesNombreAnterior}: ${diferencia > 0 ? '+' : ''}${diferencia} min`, 14, y);
                                doc.setTextColor(15,23,42);
                                y += 8;
                              }
                              y += 4;
                            });
                            doc.setFillColor(15,23,42); doc.rect(0,287,W,10,'F');
                            doc.setTextColor(180,180,180); doc.setFontSize(7);
                            doc.text('Multimarket GyC — Control de Asistencia', W/2, 293, {align:'center'});
                            doc.save(`Atrasos_${filterMonth}.pdf`);
                          } catch(e) { alert('Error PDF: '+e.message); }
                        }} style={{padding:'6px 14px', background:'linear-gradient(135deg,#6366f1,#4f46e5)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'700', fontFamily:'inherit'}}>
                          📄 Exportar PDF
                        </button>
                      </div>
                      <p style={{margin:0, fontSize:'12px', color:'var(--text-secondary)'}}>
                        Total equipo: <strong style={{color:'#ef4444'}}>{totalEquipo} minutos</strong> ({Math.floor(totalEquipo/60)}h {totalEquipo%60}min) · Comparativa vs <span style={{textTransform:'capitalize'}}>{mesNombreAnterior}</span>
                      </p>
                    </div>

                    {statsEmpleados.length === 0 ? (
                      <div style={{textAlign:'center', padding:'40px', color:'var(--text-secondary)'}}>
                        <p style={{fontSize:'32px', margin:'0 0 8px'}}>✅</p>
                        <p style={{fontSize:'14px', fontWeight:'700', color:'var(--green)'}}>Sin atrasos en {mesNombreActual}</p>
                      </div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                        {statsEmpleados.map(({emp, recs, totalMinMes, totalMinAnt, diferencia, diasAtrasado}) => (
                          <div key={emp.id} style={{background:'var(--bg-elevated)', borderRadius:'10px', overflow:'hidden', border:'1px solid var(--border)'}}>
                            {/* Header empleado */}
                            <div style={{background:'rgba(239,68,68,0.08)', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                              <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                <span style={{fontSize:'20px'}}>{emp.photo}</span>
                                <div>
                                  <p style={{margin:0, fontSize:'13px', fontWeight:'700', color:'var(--text-primary)'}}>{emp.name}</p>
                                  <p style={{margin:0, fontSize:'11px', color:'var(--text-secondary)'}}>{emp.position}</p>
                                </div>
                              </div>
                              <div style={{textAlign:'right'}}>
                                <p style={{margin:0, fontSize:'16px', fontWeight:'700', color:'#ef4444'}}>
                                  {Math.floor(totalMinMes/60) > 0 ? `${Math.floor(totalMinMes/60)}h ` : ''}{totalMinMes%60}min
                                </p>
                                <p style={{margin:0, fontSize:'11px', color:'var(--text-secondary)'}}>{diasAtrasado} día(s) atrasado</p>
                                {diferencia !== 0 && (
                                  <p style={{margin:'2px 0 0', fontSize:'11px', color: diferencia > 0 ? '#ef4444' : '#10b981', fontWeight:'600'}}>
                                    {diferencia > 0 ? '▲' : '▼'} {Math.abs(diferencia)} min vs mes anterior
                                  </p>
                                )}
                              </div>
                            </div>
                            {/* Detalle días */}
                            <div style={{padding:'8px 14px'}}>
                              {recs.map((r, i) => (
                                <div key={r.id || i} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom: i < recs.length-1 ? '1px solid var(--border)' : 'none'}}>
                                  <div>
                                    <p style={{margin:0, fontSize:'12px', fontWeight:'600', color:'var(--text-primary)', textTransform:'capitalize'}}>
                                      {new Date(r.date+'T12:00:00').toLocaleDateString('es-CL',{weekday:'long', day:'numeric', month:'long'})}
                                    </p>
                                    <p style={{margin:0, fontSize:'11px', color:'var(--text-secondary)'}}>Llegó a las {r.checkIn}</p>
                                  </div>
                                  <span style={{background:'rgba(239,68,68,0.1)', color:'#ef4444', borderRadius:'6px', padding:'3px 10px', fontSize:'12px', fontWeight:'700'}}>
                                    {r.lateMinutes||0} min
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {recordsTab === 'detalle' && (
                <>
                  <div style={{overflowX:'auto'}}>
                    <table>
                      <thead>
              <tr>
                <th>Empleado</th>
                <th>Fecha</th>
                <th>Entrada</th>
                <th>Salida</th>
                <th>Col. Inicio</th>
                <th>Col. Fin</th>
                <th>Horas Trabajadas</th>
                <th>Atraso</th>
                <th>Estado</th>
                {userRole === 'manager' && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {pagedRecs.map(record => {
                const totalDelay = (record.lateMinutes || 0) + (record.lateBreakMinutes || 0) + (record.earlyCheckOutMinutes || 0);
                const calcDayHours = (r) => {
                  if (!r.checkIn || !r.checkOut) return '--';
                  const parseT = (str) => {
                    if (!str) return null;
                    const _t = str.replace(/[^0-9:]/g,'').trim();
                    const _parts = _t.split(':').map(Number);
                    const _h2 = _parts[0];
                    const isPM = /p[\s.]*m/i.test(str) && _h2 !== 12;
                    const isNoon = _h2 === 12 && /p[\s.]*m/i.test(str);
                    const isMidnight = _h2 === 12 && /a[\s.]*m/i.test(str);
                    const t = str.replace(/[^0-9:]/g,'').trim();
                    const [h,m] = t.split(':').map(Number);
                    if (isNaN(h)||isNaN(m)) return null;
                    let hour = h;
                    if (isPM || isNoon) hour = h === 12 ? 12 : h + 12;
                    return hour * 60 + m;
                  };
                  const inMin = parseT(r.checkIn);
                  const outMin = parseT(r.checkOut);
                  if (inMin === null || outMin === null) return '--';
                  let gross = outMin > inMin ? outMin - inMin : outMin + 1440 - inMin;
                  if (r.breakStart && r.breakEnd) {
                    const bs = parseT(r.breakStart); const be = parseT(r.breakEnd);
                    if (bs !== null && be !== null) gross -= (be > bs ? be - bs : be + 1440 - bs);
                  }
                  gross -= (r.lateMinutes || 0);
                  gross -= (r.lateBreakMinutes || 0);
                  gross = Math.max(0, gross);
                  return `${Math.floor(gross/60)}h ${String(gross%60).padStart(2,'0')}min`;
                };
                return (
                  <tr key={record.id}>
                    <td>{record.employeeName}</td>
                    <td>{record.date}</td>
                    <td>{record.checkIn}</td>
                    <td>{record.checkOut || '--'}</td>
                    <td>{record.breakStart || '--'}</td>
                    <td>{record.breakEnd || '--'}</td>
                    <td style={{color:'var(--green)', fontWeight:'600'}}>{calcDayHours(record)}</td>
                    <td className={totalDelay > 0 ? 'delay-cell-highlight' : ''}>
                      {totalDelay > 0 ? totalDelay + ' min' : '--'}
                    </td>
                    <td>
                      <span className={"status-tag " + record.status}>
                        {record.status === 'on_time' ? 'A tiempo' : 'Atraso'}
                      </span>
                    </td>
                    {userRole === 'manager' && (
                      <td style={{display:'flex', gap:'6px'}}>
                        <button
                          onClick={() => {
                            const r = record;
                            const newCheckIn = window.prompt('Entrada:', r.checkIn || '');
                            if (newCheckIn === null) return;
                            const newCheckOut = window.prompt('Salida:', r.checkOut || '');
                            if (newCheckOut === null) return;
                            const newBreakStart = window.prompt('Inicio Colacion:', r.breakStart || '');
                            if (newBreakStart === null) return;
                            const newBreakEnd = window.prompt('Fin Colacion:', r.breakEnd || '');
                            if (newBreakEnd === null) return;
                            const updated = attendanceRecords.map(rec =>
                              rec.id === r.id ? Object.assign({}, rec, {checkIn: newCheckIn, checkOut: newCheckOut, breakStart: newBreakStart, breakEnd: newBreakEnd}) : rec
                            );
                            setAttendanceRecords(updated);
                            localStorage.setItem('attendanceRecords', JSON.stringify(updated));
                            try { const rRef = ref(database, 'attendanceRecords'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); } catch(e) { console.warn('Sync pendiente',e); }
                            alert('Registro actualizado');
                          }}
                          style={{padding:'4px 10px', background:'#3b82f6', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px'}}
                        >Editar</button>
                        {/* Botón corregir a tiempo */}
                        {record.isLate && (() => {
                          const emp = (Array.isArray(employees)?employees:[]).find(e => e.id === record.employeeId);
                          const ms = emp?.monthlySchedule?.[record.date];
                          const schedStart = ms?.start;
                          if (!schedStart) return null;
                          return (
                            <button
                              onClick={() => {
                                if (!window.confirm(`¿Corregir entrada de ${record.employeeName} a su horario programado (${schedStart})?`)) return;
                                const [sh, sm] = schedStart.split(':').map(Number);
                                const _h = sh, _m = String(sm).padStart(2,'0');
                                const newCheckIn = `${String(_h%12||12).padStart(2,'0')}:${_m} ${_h<12?'a. m.':'p. m.'}`;
                                const updated = (Array.isArray(attendanceRecords)?attendanceRecords:[]).map(rec =>
                                  rec.id === record.id ? {...rec, checkIn: newCheckIn, isLate: false, lateMinutes: 0, status: 'on_time'} : rec
                                );
                                setAttendanceRecords(updated);
                                localStorage.setItem('attendanceRecords', JSON.stringify(updated));
                                try { const rRef = ref(database, 'attendanceRecords'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); } catch(e) { console.warn('Sync pendiente',e); }
                                addAuditEntry('CORREGIR_A_TIEMPO', `Corrigió entrada de ${record.employeeName} del ${record.date} a ${newCheckIn}`);
                                alert(`✅ Corregido — ${record.employeeName} marcado a tiempo (${newCheckIn})`);
                              }}
                              style={{padding:'4px 10px', background:'#22c55e', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px'}}
                            >✅ A tiempo</button>
                          );
                        })()}
                        <button
                          onClick={() => {
                            if (!window.confirm('Eliminar registro de ' + record.employeeName + ' del ' + record.date + '?')) return;
                            const updated = attendanceRecords.filter(rec => rec.id !== record.id);
                            setAttendanceRecords(updated);
                            localStorage.setItem('attendanceRecords', JSON.stringify(updated));
                            try { const rRef = ref(database, 'attendanceRecords'); const obj = {}; updated.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj)); } catch(e) { console.warn('Sync pendiente',e); }
                            alert('Registro eliminado');
                          }}
                          style={{padding:'4px 10px', background:'#ef4444', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px'}}
                        >Borrar</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Paginación */}
        {totalPages > 1 && (
          <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:'8px', marginTop:'16px', flexWrap:'wrap'}}>
            <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1} style={{padding:'6px 14px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', cursor:currentPage===1?'not-allowed':'pointer', opacity:currentPage===1?0.5:1}}>← Anterior</button>
            {Array.from({length:Math.min(totalPages,5)},(_,i)=>{
              let page = i+1;
              if(totalPages>5){
                if(currentPage<=3) page=i+1;
                else if(currentPage>=totalPages-2) page=totalPages-4+i;
                else page=currentPage-2+i;
              }
              return <button key={page} onClick={()=>setCurrentPage(page)} style={{padding:'6px 12px', background:currentPage===page?'var(--blue)':'var(--bg-elevated)', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:currentPage===page?'700':'400'}}>{page}</button>;
            })}
            <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages} style={{padding:'6px 14px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'6px', cursor:currentPage===totalPages?'not-allowed':'pointer', opacity:currentPage===totalPages?0.5:1}}>Siguiente →</button>
            <span style={{fontSize:'12px', color:'var(--text-secondary)'}}>Página {currentPage} de {totalPages} ({allRecs.length} registros)</span>
          </div>
        )}
                </>
              )}
            </>
          );
        })()}
      </div>

      {/* ── REGISTRO DE AUDITORÍA ── */}
      {userRole === 'manager' && (() => {
        const sortedAudit = [...(Array.isArray(auditLog)?auditLog:[])].sort((a,b)=>new Date(b.timestamp)-new Date(a.timestamp));
        const visibleAudit = sortedAudit.slice(0, auditLimit);
        return (
        <div className="records-table">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
            <h3 style={{margin:0}}>🔍 Registro de Auditoría</h3>
            {sortedAudit.length > 0 && (
              <button onClick={()=>{
                if(!window.confirm('¿Limpiar todo el registro de auditoría?')) return;
                const auditRef = ref(database, 'auditLog');
                set(auditRef, null);
                localStorage.removeItem('auditLog');
                setAuditLog([]);
                const nuevaEntrada = {
                  id: Date.now(),
                  timestamp: new Date().toISOString(),
                  user: currentUser?.name || 'Sistema',
                  userRole: userRole || 'unknown',
                  action: 'LIMPIAR_AUDITORIA',
                  details: 'Gerente limpió el registro de auditoría'
                };
                setAuditLog([nuevaEntrada]);
                localStorage.setItem('auditLog', JSON.stringify([nuevaEntrada]));
                if (navigator.onLine) {
                  set(auditRef, sanitizeForFirebase({[nuevaEntrada.id]: nuevaEntrada}));
                }
              }} style={{padding:'4px 12px', background:'rgba(239,68,68,0.15)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'700'}}>
                🗑️ Limpiar todo
              </button>
            )}
          </div>
          <p style={{fontSize:'12px', color:'var(--text-secondary)', marginBottom:'12px'}}>Mostrando {visibleAudit.length} de {sortedAudit.length} registros — más recientes primero</p>
          {sortedAudit.length === 0 ? (
            <p style={{color:'var(--text-muted)', textAlign:'center', padding:'20px'}}>Sin registros de auditoría aún</p>
          ) : (
            <>
            <div style={{overflowX:'auto'}}>
              <table>
                <thead>
                  <tr>
                    <th>Fecha/Hora</th>
                    <th>Usuario</th>
                    <th>Rol</th>
                    <th>Acción</th>
                    <th>Detalle</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAudit.map(entry => (
                    <tr key={entry.id}>
                      <td style={{fontSize:'12px', whiteSpace:'nowrap'}}>{entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('es-CL',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '--'}</td>
                      <td style={{fontWeight:'600'}}>{entry.user}</td>
                      <td><span style={{fontSize:'11px', padding:'2px 8px', borderRadius:'4px', background:entry.userRole==='manager'?'rgba(59,130,246,0.15)':'rgba(245,158,11,0.15)', color:entry.userRole==='manager'?'var(--blue)':'var(--orange)', fontWeight:'700'}}>{entry.userRole==='manager'?'Gerente':'Admin'}</span></td>
                      <td style={{fontSize:'12px', fontWeight:'600', color:entry.action.includes('RECHAZAR')?'#ef4444':entry.action.includes('APROBAR')?'var(--green)':'var(--text-primary)'}}>{entry.action.replace(/_/g,' ')}</td>
                      <td style={{fontSize:'12px', color:'var(--text-secondary)', maxWidth:'250px'}}>{entry.details}</td>
                      <td>
                        <button onClick={()=>{
                          const updated = (Array.isArray(auditLog)?auditLog:[]).filter(e=>e.id!==entry.id);
                          localStorage.setItem('auditLog', JSON.stringify(updated));
                          const aRef = ref(database,'auditLog');
                          const obj={};updated.forEach(e=>{if(e&&e.id)obj[e.id]=e;});
                          set(aRef,updated.length===0?null:sanitizeForFirebase(obj));
                        }} style={{background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', padding:'2px 6px'}} title="Eliminar entrada">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sortedAudit.length > auditLimit && (
              <button onClick={()=>setAuditLimit(l=>l+20)} style={{width:'100%', marginTop:'10px', padding:'8px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:'8px', cursor:'pointer', fontSize:'12px'}}>
                Ver 20 más ({sortedAudit.length - auditLimit} restantes)
              </button>
            )}
            </>
          )}
        </div>
        );
      })()}

      {/* ── EVALUACIÓN DE DESEMPEÑO ── */}
      <div className="records-table">
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'8px'}}>
          <h3 style={{margin:0}}>⭐ Evaluación de Desempeño Mensual</h3>
          <div style={{display:'flex', gap:'8px'}}>
            <button className="btn-export" style={{background:'#7c3aed'}} onClick={() => generatePerformancePDF(null)}>
              <Download size={18} />
              PDF Todos
            </button>
            <select
              style={{padding:'8px 12px', background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:'8px', fontSize:'13px', cursor:'pointer'}}
              onChange={e => { if (e.target.value) { generatePerformancePDF(parseInt(e.target.value)); e.target.value=''; }}}
              defaultValue=""
            >
              <option value="">⭐ PDF Individual...</option>
              {(Array.isArray(employees) ? employees : []).filter(e => e.status === 'active').map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
          {(Array.isArray(employees) ? employees : []).filter(e => e.status === 'active').map(emp => {
            const empTasks = (Array.isArray(tasks) ? tasks : []).filter(t => String(t.assignedTo) === String(emp.id));
            const totalTasks = empTasks.length;
            const completedTasks = empTasks.filter(t => t.status === 'done').length;
            const approvedTasks = empTasks.filter(t => t.reviewStatus === 'approved').length;
            const rejectedTasks = empTasks.filter(t => t.reviewStatus === 'rejected').length;
            const pendingTasks = empTasks.filter(t => t.status === 'pending').length;
            const taskComplianceRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 100;
            const approvalRate = completedTasks > 0 ? Math.round((approvedTasks / completedTasks) * 100) : 100;
            const empRecords = currentMonthRecords.filter(r => r.employeeId === emp.id);
            const lateCount = empRecords.filter(r => r.isLate).length;
            const punctualityRate = empRecords.length > 0 ? Math.round(((empRecords.length - lateCount) / empRecords.length) * 100) : 100;
            const empProducts = (Array.isArray(missingProducts) ? missingProducts : []).filter(p => p.reportedById === emp.id || p.reportedBy === emp.name);
            const productsReported = empProducts.length;
            // Score: 40% tareas completadas + 30% puntualidad + 20% aprobación gerente + 10% productos reportados
            const score = Math.round(
              (taskComplianceRate * 0.4) +
              (punctualityRate * 0.3) +
              (approvalRate * 0.2) +
              (Math.min(productsReported * 10, 100) * 0.1)
            );
            const badge = score >= 90 ? {label:'Excelente', color:'#22c55e'} : score >= 75 ? {label:'Bueno', color:'#3b82f6'} : score >= 60 ? {label:'Regular', color:'#f59e0b'} : {label:'Deficiente', color:'#ef4444'};
            return (
              <div key={emp.id} style={{background:'var(--bg-elevated)', borderRadius:'12px', padding:'16px', border:`1px solid ${badge.color}40`}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                    <span style={{fontSize:'24px'}}>{emp.photo}</span>
                    <div>
                      <p style={{margin:0, fontWeight:'700', color:'var(--text-primary)'}}>{emp.name}</p>
                      <p style={{margin:0, fontSize:'12px', color:'var(--text-secondary)'}}>{emp.position}</p>
                    </div>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontSize:'32px', fontWeight:'900', color:badge.color, lineHeight:1}}>{score}</div>
                    <span style={{fontSize:'11px', padding:'2px 10px', borderRadius:'20px', background:`${badge.color}20`, color:badge.color, fontWeight:'700'}}>{badge.label}</span>
                  </div>
                </div>
                {/* Score bar */}
                <div style={{height:'6px', background:'var(--bg-secondary)', borderRadius:'6px', marginBottom:'12px'}}>
                  <div style={{height:'100%', width:`${score}%`, background:badge.color, borderRadius:'6px', transition:'width 0.5s'}} />
                </div>
                {/* Detail stats */}
                <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'8px'}}>
                  {[
                    {label:'Tareas completadas', value:`${completedTasks}/${totalTasks}`, sub:`${taskComplianceRate}%`, color:'#22c55e'},
                    {label:'Aprobadas gerente', value:`${approvedTasks}/${completedTasks}`, sub:`${approvalRate}%`, color:'#3b82f6'},
                    {label:'Puntualidad', value:`${empRecords.length - lateCount}/${empRecords.length}`, sub:`${punctualityRate}%`, color:'#f59e0b'},
                    {label:'Productos reportados', value:String(productsReported), sub:'iniciativa', color:'#8b5cf6'},
                  ].map((stat,i) => (
                    <div key={i} style={{background:'var(--bg-card)', borderRadius:'8px', padding:'8px', textAlign:'center'}}>
                      <div style={{fontSize:'16px', fontWeight:'700', color:stat.color}}>{stat.value}</div>
                      <div style={{fontSize:'10px', color:stat.color, fontWeight:'600'}}>{stat.sub}</div>
                      <div style={{fontSize:'10px', color:'var(--text-secondary)', marginTop:'2px'}}>{stat.label}</div>
                    </div>
                  ))}
                </div>
                {(pendingTasks > 0 || rejectedTasks > 0) && (
                  <div style={{marginTop:'8px', fontSize:'12px', color:'var(--text-secondary)'}}>
                    {pendingTasks > 0 && <span style={{marginRight:'12px'}}>⏳ {pendingTasks} tarea(s) pendiente(s)</span>}
                    {rejectedTasks > 0 && <span style={{color:'#ef4444'}}>❌ {rejectedTasks} tarea(s) rechazada(s)</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};



const AttendanceControlApp = () => {
  React.useEffect(() => {
    const s = document.createElement("style");
    s.textContent = "@media screen and (max-width:9999px){.employees-grid-admin{grid-template-columns:1fr!important}}";
    document.head.appendChild(s);
  }, []);
  // Estado de conexión a internet
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // FIX BUG 3: Bandera para ignorar el próximo evento onValue de Firebase
  // cuando somos NOSOTROS quienes acaban de escribir (borrar/editar registro).
  // Evita que el registro borrado vuelva a aparecer al dispararse onValue.
  const ignoreNextFirebaseUpdate = useRef(false);


  // Configuración inicial (se carga/guarda en localStorage)
  const [config, setConfig] = useState({
    workLocation: { latitude: -33.57049194414672, longitude: -71.20044164472745, radius: 20 },
    schedules: {
      checkIn: { hour: 9, minute: 0 },      // Hora de entrada
      breakStart: { hour: 13, minute: 0 },  // Inicio colación
      breakEnd: { hour: 14, minute: 0 },    // Fin colación
      checkOut: { hour: 18, minute: 0 }     // Hora de salida
    },
    tolerances: {
      checkIn: 5,        // Tolerancia entrada en minutos (evita atrasos por fila)
      breakReturn: 3,    // Tolerancia regreso de colación en minutos
      earlyCheckOut: 0,  // Tolerancia salida temprana en minutos
      checkOut: 30,      // Minutos antes de la hora de salida que se puede registrar
      breakStart: 15,    // Minutos antes de colación que se puede iniciar
    },
    codes: {
      manager: 'GERENTE2026',  // Código del gerente (máximo control)
      admin: 'ADMIN2026'       // Código del administrador (control limitado)
    },
    companyLogo: null,  // Logo de la empresa en base64
    alerts: {
      lateArrival: {
        enabled: true,
        message: '⏰ Has llegado tarde. Por favor justifica tu atraso.'
      },
      lateBreakReturn: {
        enabled: true,
        message: '⏰ Has regresado tarde de colación. Por favor justifica.'
      },
      earlyDeparture: {
        enabled: true,
        message: '⏰ Estás saliendo antes de tiempo. Por favor justifica.'
      }
    },
    adminPermissions: {
      canAddEmployees: true,
      canEditEmployees: true,
      canDeleteEmployees: true,
      canViewReports: true,
      canExportData: true,
      canChangeSchedules: false,  // Solo gerente puede cambiar horarios
      canChangeGPS: false,  // Solo gerente puede cambiar ubicación GPS
      canManageAlerts: false  // Solo gerente puede configurar alertas
    }
  });
  
  const [userRole, setUserRole] = useState(() => localStorage.getItem('userRole') || null);
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [currentView, setCurrentView] = useState(() => {
    // Si hay usuario guardado, ir a la vista correcta; si no, mostrar login
    const saved = localStorage.getItem('currentUser');
    const role = localStorage.getItem('userRole');
    if (!saved || !role) return 'login';
    if (role === 'manager' || role === 'admin') return 'dashboard';
    return 'home';
  });
  const [isCheckedIn, setIsCheckedIn] = useState(() => {
    // Inicializar desde localStorage para evitar parpadeo al recargar
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (!savedUser) return false;
      const user = JSON.parse(savedUser);
      const today = getLocalDateStr();
      const lsRaw = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const lsArray = Array.isArray(lsRaw) ? lsRaw : Object.values(lsRaw || {});
      const rec = lsArray.find(r => r && r.employeeId === user.id && r.date === today);
      return !!(rec?.checkIn && !rec?.checkOut);
    } catch(e) { return false; }
  });
  const [isOnBreak, setIsOnBreak] = useState(() => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (!savedUser) return false;
      const user = JSON.parse(savedUser);
      const today = getLocalDateStr();
      const lsRaw = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const lsArray = Array.isArray(lsRaw) ? lsRaw : Object.values(lsRaw || {});
      const rec = lsArray.find(r => r && r.employeeId === user.id && r.date === today);
      return !!(rec?.breakStart && !rec?.breakEnd);
    } catch(e) { return false; }
  });
  const [todayRecord, setTodayRecord] = useState(() => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (!savedUser) return null;
      const user = JSON.parse(savedUser);
      const today = getLocalDateStr();
      const lsRaw = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const lsArray = Array.isArray(lsRaw) ? lsRaw : Object.values(lsRaw || {});
      return lsArray.find(r => r && r.employeeId === user.id && r.date === today) || null;
    } catch(e) { return null; }
  });
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loginCode, setLoginCode] = useState('');
  const [loginView, setLoginView] = useState('select'); // 'select' o 'password'
  const [selectedRole, setSelectedRole] = useState(null); // 'manager' o 'admin'
  const [password, setPassword] = useState('');
  
  // Estados para sistema de alarmas
  const [showAlarmNotification, setShowAlarmNotification] = useState(false) // Banner desactivado permanentemente;
  const [showChat, setShowChat] = useState(false);
  const [adminPresence, setAdminPresence] = useState({});
  const [chatAdminId, setChatAdminId] = useState(null);
  const [chatConversations, setChatConversations] = useState({});
  const [chatInput, setChatInput] = useState('');
  const [chatVolume, setChatVolume] = useState(() => parseFloat(localStorage.getItem('chatVolume') || '0.5'));
  const [chatUnread, setChatUnread] = useState({});
  const [titleBlink, setTitleBlink] = React.useState(false);
  React.useEffect(() => {
    try {
      const totalUnread = Object.values(chatUnread||{}).reduce((a,b)=>a+(b||0),0);
      if (totalUnread > 0) {
        let on = true;
        const interval = setInterval(() => {
          document.title = on ? `🔔 ${totalUnread} mensaje(s) nuevo(s)` : 'Control de Asistencia';
          on = !on;
        }, 1200);
        return () => { clearInterval(interval); document.title = 'Control de Asistencia'; };
      } else {
        document.title = 'Control de Asistencia';
      }
    } catch(e) {}
  }, [chatUnread]);
  const [alarmMessage, setAlarmMessage] = useState('');
  const [minutesUntilCheckout, setMinutesUntilCheckout] = useState(null);
  const [alarmSound] = useState(new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZTA0PVK3n77BdGAg+ltryxnMkBSp+zPDajzsIGGS57OihUBELTKXh8LZiHAU2jtXywn0qBSV7y/DdkEALFGCy6eysVhYKRp/g8rpvIQU1h9Lz0oMzBh1sv+7mnEcOEFKs5+6vYRcJPZXa8sV0JAYpfsrv2qc4CRhjtOzoo1IQC0il4O+zYBsGNY7U8sB+KQYke8rw3J…'));
  
  // Estados para horas extras
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [overtimeRequest, setOvertimeRequest] = useState(null);
  
  // Estados para verificación biométrica
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [pendingCheckInAction, setPendingCheckInAction] = useState(null); // 'checkIn', 'checkOut', 'breakToggle'
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  
  // Estados para gestión de ausencias
  const [absenceRequests, setAbsenceRequests] = useState([]);
  const [overtimeRequests, setOvertimeRequests] = useState(() => {
    const saved = localStorage.getItem('overtimeRequests');
    return saved ? JSON.parse(saved) : [];
  });
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [showLateAlert, setShowLateAlert] = useState(true);
  const [lateEmployeesAlerts, setLateEmployeesAlerts] = useState([]);
  const [showEmployeeRequestsModal, setShowEmployeeRequestsModal] = useState(false);
  const [absenceFormData, setAbsenceFormData] = useState({
    type: 'vacation', // vacation, medical, personal, sick_leave, shift_change, holiday_worked
    startDate: '',
    endDate: '',
    reason: '',
    status: 'pending' // pending, approved, rejected
  });
  
  // Estados para configuración
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configActiveTab, setConfigActiveTab] = useState('location'); // Pestaña activa del modal de configuración
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  // 📅 Estado del Calendario Mensual
  const [showCalendar, setShowCalendar] = useState(false);
  const [showPagosModal, setShowPagosModal] = useState(false);
  const [pagosEmployee, setPagosEmployee] = useState(null);
  const [calendarEmployee, setCalendarEmployee] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthlySchedule, setMonthlySchedule] = useState({});

  // 📅 Estado del Calendario Mensual

  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showChatGerente, setShowChatGerente] = useState(false);
  const [chatGerenteMensajes, setChatGerenteMensajes] = useState([]);
  const [chatGerenteLoading, setChatGerenteLoading] = useState(false);
  const [showAdminRegistration, setShowAdminRegistration] = useState(false);

  // Estados para gestión de ausencias
  const [showAbsenceManager, setShowAbsenceManager] = useState(false);
  const [showAbsenceRequestsManager, setShowAbsenceRequestsManager] = useState(false);
  const [selectedEmployeeForAbsences, setSelectedEmployeeForAbsences] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [announcements, setAnnouncements] = useState(() => {
    try { return JSON.parse(localStorage.getItem('announcements') || '[]'); } catch(e) { return []; }
  });
  const [attendanceIssues, setAttendanceIssues] = useState(() => {
    try { return JSON.parse(localStorage.getItem('attendanceIssues') || '[]'); } catch(e) { return []; }
  });
  const [absenceForm, setAbsenceForm] = useState({
    type: 'vacation',
    startDate: '',
    endDate: '',
    notes: ''
  });
  
  // Estados para tareas y productos
  const [auditLog, setAuditLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem('auditLog') || '[]'); } catch(e) { return []; }
  });

  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem('tasks');
    return saved ? JSON.parse(saved) : [];
  });
  const [missingProducts, setMissingProducts] = useState(() => {
    const saved = localStorage.getItem('missingProducts');
    return saved ? JSON.parse(saved) : [];
  });
  const [absenceManagerTime, setAbsenceManagerTime] = useState(new Date());
  const [employeeFormData, setEmployeeFormData] = useState({});
  const [tempConfigState, setTempConfigState] = useState(null);
  const [tempPermissionsState, setTempPermissionsState] = useState(null);
  const [adminRegCheckedIn, setAdminRegCheckedIn] = useState(false);
  const [adminRegOnBreak, setAdminRegOnBreak] = useState(false);
  const [adminFilterEmp, setAdminFilterEmp] = useState('all');
  const [tasksActiveTab, setTasksActiveTab] = useState('tasks');
  const [tasksFilterEmp, setTasksFilterEmp] = useState('all');
  const [tasksFilterStatus, setTasksFilterStatus] = useState('all');
  const [tasksFilterDays, setTasksFilterDays] = useState('30');
  const [taskObs, setTaskObs] = useState({});

  // Cargar borradores cuando el usuario hace login
  React.useEffect(() => {
    if (!currentUser?.id) return;
    try {
      const drafts = {};
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`taskDraft_${currentUser.id}_`)) {
          const taskId = parseInt(key.split('_').pop());
          if (!isNaN(taskId)) drafts[taskId] = localStorage.getItem(key) || '';
        }
      });
      if (Object.keys(drafts).length > 0) setTaskObs(prev => ({...prev, ...drafts}));
    } catch(e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assignedTo: '', priority: 'medium', steps: [], dueDate: '', dueTime: '' });
  const [newStepText, setNewStepText] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', quantity: '', unit: 'unidades', notes: '' });

  // Estados para admin
  // Función para limpiar undefined antes de Firebase
  const cleanForFirebase = (data) => {
    return JSON.parse(JSON.stringify(data, (key, value) => 
      value === undefined ? "" : value
    ));
  };

  const [employees, setEmployees] = useState(() => {
    // NO cargar desde localStorage, solo desde Firebase
    return [];
  });
  
  // Array de empleados por defecto (solo se usa si Firebase está vacío)
  const defaultEmployees = [
    { 
      id: 999,
      code: 'ADMIN001',
      name: 'Administrador',
      position: 'Administrator',
      status: 'active',
      contractType: 'full-time',
      workDays: [1, 2, 3, 4, 5],
      vacationDays: 0,
      vacationUsed: 0,
      startDate: '2024-01-01',
      photo: '👨‍💼',
      email: 'admin@empresa.com',
      phone: '+56900000000',
      schedules: {
        checkIn: { hour: 9, minute: 0 },
        breakStart: { hour: 13, minute: 0 },
        breakEnd: { hour: 14, minute: 0 },
        checkOut: { hour: 18, minute: 0 }
      },
      isAdmin: true
    },
    { 
      id: 1, 
      name: 'Juan Pérez', 
      position: 'Desarrollador', 
      status: 'active',
      contractType: 'full-time', // full-time, part-time
      workDays: [1, 2, 3, 4, 5], // 0=Domingo, 1=Lunes, ..., 6=Sábado
      vacationDays: 15,
      vacationUsed: 0,
      startDate: '2024-01-15',
      photo: '👨‍💻',
      schedules: {
        checkIn: { hour: 9, minute: 0 },
        breakStart: { hour: 13, minute: 0 },
        breakEnd: { hour: 14, minute: 0 },
        checkOut: { hour: 18, minute: 0 }
      }
    },
    { 
      id: 2, 
      name: 'María González', 
      position: 'Diseñadora', 
      status: 'active',
      contractType: 'full-time',
      workDays: [1, 2, 3, 4, 5],
      vacationDays: 15,
      vacationUsed: 0,
      startDate: '2024-01-15',
      photo: '👩‍🎨',
      schedules: {
        checkIn: { hour: 9, minute: 0 },
        breakStart: { hour: 13, minute: 0 },
        breakEnd: { hour: 14, minute: 0 },
        checkOut: { hour: 18, minute: 0 }
      }
    },
    { 
      id: 3, 
      name: 'Carlos Rodríguez', 
      position: 'Project Manager', 
      status: 'active',
      contractType: 'part-time',
      workDays: [1, 3, 5], // Lunes, Miércoles, Viernes
      vacationDays: 10,
      vacationUsed: 0,
      startDate: '2024-01-15',
      photo: '👨‍💼',
      schedules: {
        checkIn: { hour: 9, minute: 0 },
        breakStart: { hour: 13, minute: 0 },
        breakEnd: { hour: 14, minute: 0 },
        checkOut: { hour: 18, minute: 0 }
      }
    },
    { 
      id: 4, 
      name: 'Ana Martínez', 
      position: 'QA Tester', 
      status: 'active',
      contractType: 'full-time',
      workDays: [1, 2, 3, 4, 5],
      vacationDays: 15,
      vacationUsed: 0,
      startDate: '2024-01-15',
      photo: '👩‍💻',
      schedules: {
        checkIn: { hour: 9, minute: 0 },
        breakStart: { hour: 13, minute: 0 },
        breakEnd: { hour: 14, minute: 0 },
        checkOut: { hour: 18, minute: 0 }
      }
    },
  ];
  
  // NO usar defaultEmployees directamente, solo para inicializar Firebase
  
  const [attendanceRecords, setAttendanceRecords] = useState([

  ]);

  // 🔥 FIX 1 y 3: useEffect para actualizar estado por empleado
  
  // Sincronizar empleados - Modo híbrido
  
  
  // Detectar cambios de conexión
  useEffect(() => { return; // SYNC DESACTIVADA
    const handleOnline = () => {
      console.log('🟢 Conexión restaurada');
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      console.log('🔴 Sin conexión');
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Cargar desde localStorage primero (funciona offline)
    const localEmployees = JSON.parse(localStorage.getItem('employees') || '[]');
    if (localEmployees.length > 0) {
      console.log('💾 Cargando empleados desde localStorage:', localEmployees.length);
      setEmployees(localEmployees);
    }
    
    // Si hay internet, escuchar Firebase
    if (isOnline) {
      // CARGA INICIAL desde Firebase (sin listeners)
      console.log('📥 Cargando datos desde Firebase...');
      
      // Función para sincronizar sin causar loops
      let lastSync = Date.now();
      const syncData = () => {
        const now = Date.now();
        // Solo sincronizar si han pasado 30 segundos
        if (now - lastSync < 30000) return;
        
        lastSync = now;
        console.log('🔄 Sincronizando datos...');
        
        // Cargar empleados
        get(ref(database, 'employees')).then((snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            const dataArr = (Array.isArray(data) ? data : Object.values(data)).filter(e => e && e.id);
            const unique = dataArr.filter((e,i,arr)=>arr.findIndex(x=>x.id===e.id)===i);
            if (unique.length > 0) {
              setEmployees(sanitizeEmployees(unique));
              localStorage.setItem('employees', JSON.stringify(unique));
            }
          }
        });
        
        // Cargar registros
        get(ref(database, 'attendanceRecords')).then((snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            const arr = (Array.isArray(data) ? data : Object.values(data)).filter(r => r && r.id);
            setAttendanceRecords(arr);
            localStorage.setItem('attendanceRecords', JSON.stringify(arr));
          }
        });
        
        // Cargar solicitudes
        get(ref(database, 'overtimeRequests')).then((snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            const arr = Array.isArray(data) ? data : Object.values(data).filter(r => r && r.id);
            setOvertimeRequests(arr);
            localStorage.setItem('overtimeRequests', JSON.stringify(arr));
          }
        });
      };
      
      // Sincronizar cada 30 segundos - DESACTIVADO temporalmente
      // const syncInterval = setInterval(syncData, 30000);
      
      const employeesRef = ref(database, 'employees');
      get(employeesRef).then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          console.log('✅ Empleados cargados:', data.length);
          const dataArr = Array.isArray(data) ? data : Object.values(data).filter(e => e && e.id);
          setEmployees(sanitizeEmployees(dataArr));
          localStorage.setItem('employees', JSON.stringify(dataArr));
        }
      });
      
      const configRef = ref(database, 'config');
      get(configRef).then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setConfig(data);
          localStorage.setItem('config', JSON.stringify(data));
        }
      });
      
      const attendanceRef = ref(database, 'attendanceRecords');
      get(attendanceRef).then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const arr = (Array.isArray(data) ? data : Object.values(data)).filter(r => r && r.id);
          console.log('✅ Registros cargados desde Firebase:', arr.length);
          setAttendanceRecords(arr);
          localStorage.setItem('attendanceRecords', JSON.stringify(arr));
        }
      }).catch(err => console.error('Error cargando registros:', err));
      
      const employeesRefOld = ref(database, 'employees');
      return; // LISTENERS DESACTIVADOS - causan loop infinito
      
      const unsubscribe = onValue(employeesRef, (snapshot) => {
        const data = snapshot.val();
        if (data && Array.isArray(data)) {
          console.log('📥 Empleados recibidos de Firebase:', data.length);
          const dataArr = Array.isArray(data) ? data : Object.values(data).filter(e => e && e.id);
          setEmployees(sanitizeEmployees(dataArr));
          localStorage.setItem('employees', JSON.stringify(dataArr));
        } else if (data === null && localEmployees.length > 0) {
          // Subir datos locales a Firebase
          console.log('📤 Subiendo empleados locales a Firebase');
          set(employeesRef, sanitizeForFirebase(localEmployees));
        }
      });
      
      return () => unsubscribe();
    }
  }, [isOnline]);

  // Presencia del usuario — actualiza cada 30s
  React.useEffect(() => {
    if (!currentUser) return;
    const updatePresence = () => {
      fetch('https://control--asistencia-default-rtdb.firebaseio.com/presence/' + currentUser.id + '.json', {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({online: true, lastSeen: new Date().toISOString(), name: currentUser.name, role: userRole})
      });
    };
    updatePresence();
    const interval = setInterval(updatePresence, 30000);
    const handleUnload = () => {
      fetch('https://control--asistencia-default-rtdb.firebaseio.com/presence/' + currentUser.id + '.json', {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({online: false, lastSeen: new Date().toISOString(), name: currentUser.name, role: userRole})
      });
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => { clearInterval(interval); window.removeEventListener('beforeunload', handleUnload); };
  }, [currentUser, userRole]);

  // Listener chat en tiempo real — polling cada 5 segundos
  React.useEffect(() => {
    if (!currentUser || (userRole !== 'manager' && userRole !== 'admin')) return;
    const adminId = userRole === 'admin' ? currentUser.id : null;
    
    const loadMessages = (id) => {
      fetch('https://control--asistencia-default-rtdb.firebaseio.com/chat/admin_' + id + '.json')
        .then(r => r.json())
        .then(data => {
          if (data) {
            const arr = Object.values(data).filter(Boolean).sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
            // Marcar mensajes del otro como leidos si el chat esta abierto
            const chatOpen = showChat && (userRole==='admin' ? true : chatAdminId === id);
            if (chatOpen) {
              let needsUpdate = false;
              const updated = arr.map(m => {
                if (m.senderId !== currentUser.id && !(m.readBy||[]).includes(currentUser.id)) {
                  needsUpdate = true;
                  return {...m, readBy: [...(m.readBy||[]), currentUser.id]};
                }
                return m;
              });
              if (needsUpdate) {
                const obj = {};
                updated.forEach(m => { obj[m.id] = m; });
                fetch('https://control--asistencia-default-rtdb.firebaseio.com/chat/admin_' + id + '.json', {method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(obj)});
              }
            }
            setChatConversations(prev => {
              const prevMsgs = prev[id] || [];
              const prevIds = new Set(prevMsgs.map(m => m.id));
              const newMsgs = arr.filter(m => !prevIds.has(m.id) && m.senderId !== currentUser.id);
              // Si es primera carga (prevMsgs vacio), contar mensajes no leidos por readBy
              const isFirstLoad = prevMsgs.length === 0;
              const unreadCount = isFirstLoad
                ? arr.filter(m => m.senderId !== currentUser.id && !(m.readBy||[]).includes(currentUser.id)).length
                : newMsgs.length;
              if (unreadCount > 0 && !chatOpen) {
                setChatUnread(u => ({...u, [id]: unreadCount}));
                try {
                  const ctx = new (window.AudioContext || window.webkitAudioContext)();
                  const osc = ctx.createOscillator();
                  const gain = ctx.createGain();
                  osc.connect(gain); gain.connect(ctx.destination);
                  osc.frequency.setValueAtTime(880, ctx.currentTime);
                  gain.gain.setValueAtTime(chatVolume, ctx.currentTime);
                  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
                  osc.start(); osc.stop(ctx.currentTime + 0.4);
                } catch(e) {}
              }
              return {...prev, [id]: arr};
            });
          } else {
            setChatConversations(prev => ({...prev, [id]: []}));
          }
        }).catch(() => {});
    };

    if (adminId) {
      loadMessages(adminId);
      const interval = setInterval(() => loadMessages(adminId), 1000);
      return () => clearInterval(interval);
    } else {
      // Gerente — cargar todos los admins
      const loadAll = () => {
        const admins = (Array.isArray(employees) ? employees : []).filter(e => e.status === 'active' && (e.id === 999 || e.role === 'admin'));
        admins.forEach(a => loadMessages(a.id));
        if (chatAdminId) loadMessages(chatAdminId);
      };
      loadAll();
      const interval = setInterval(loadAll, 1000);
      return () => clearInterval(interval);
    }
  }, [currentUser, userRole, employees]);

  // Leer presencia de admins cada 30s
  React.useEffect(() => {
    if (userRole !== 'manager') return;
    const loadPresence = () => {
      fetch('https://control--asistencia-default-rtdb.firebaseio.com/presence.json')
        .then(r => r.json())
        .then(data => { if (data) setAdminPresence(data); })
        .catch(() => {});
    };
    loadPresence();
    const interval = setInterval(loadPresence, 30000);
    return () => clearInterval(interval);
  }, [userRole]);

  // Sincronización automática cada 30 segundos
  useEffect(() => {
    if (!isOnline) return; // Solo si hay internet
    
    /* SYNC DESACTIVADO - const syncInterval = setInterval(() => {
      console.log('🔄 Sincronizando datos desde Firebase...');
      
      // Forzar recarga desde Firebase
      const employeesRef = ref(database, 'employees');
      get(employeesRef).then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const cleanData = sanitizeForFirebase(data);
          const cleanDataArr = Array.isArray(cleanData) ? cleanData : Object.values(cleanData).filter(e => e && e.id);
          setEmployees(sanitizeEmployees(cleanDataArr));
          localStorage.setItem('employees', JSON.stringify(data));
          console.log('✅ Empleados sincronizados:', data.length);
        }
      });
      
      const attendanceRef = ref(database, 'attendanceRecords');
      get(attendanceRef).then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          // Limpiar undefined de datos sincronizados
          const rawData = JSON.parse(JSON.stringify(data, (k, v) => v === undefined ? null : v));
          const cleanData = (Array.isArray(rawData) ? rawData : Object.values(rawData)).filter(r => r && r.id);
          setAttendanceRecords(cleanData);
          localStorage.setItem('attendanceRecords', JSON.stringify(cleanData));
          console.log('✅ Registros sincronizados:', cleanData.length);
        }
      });
      
      const configRef = ref(database, 'config');
      get(configRef).then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const cleanData = sanitizeForFirebase(data);
          setConfig(cleanData);
          localStorage.setItem('config', JSON.stringify(data));
          console.log('✅ Configuración sincronizada');
        }
      });
      
    }, 30000); // DESACTIVADO */
    
    // return () => clearInterval(syncInterval); // DESACTIVADO
  }, [isOnline]);

  
  

  
  // Sincronizar asistencia con Firebase
  useEffect(() => {
    const attendanceRef = ref(database, 'attendanceRecords');
    
    const unsubscribe = onValue(attendanceRef, (snapshot) => {
      if (ignoreNextFirebaseUpdate.current) {
        ignoreNextFirebaseUpdate.current = false;
        return;
      }
      const data = snapshot.val();
      if (data) {
        const firebaseArray = (Array.isArray(data) ? data : Object.values(data)).filter(r => r && r.id);
        // Merge con localStorage para no perder registros recientes que aún no llegaron a Firebase
        const lsRaw = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
        const lsArray = (Array.isArray(lsRaw) ? lsRaw : Object.values(lsRaw)).filter(r => r && r.id);
        // Unión: Firebase es la fuente principal pero preservar registros locales más recientes
        const merged = [...firebaseArray];
        lsArray.forEach(lr => {
          const fbRecord = merged.find(fr => fr.id === lr.id);
          if (!fbRecord) {
            // Registro local no está en Firebase — puede ser muy reciente, preservarlo
            if (Date.now() - lr.id < 60000) merged.push(lr); // menos de 1 minuto
          }
        });
        const filtered = merged.filter(r => r && r.id);
        setAttendanceRecords(filtered);
        localStorage.setItem('attendanceRecords', JSON.stringify(filtered));
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // useEffect de sobreescritura Firebase deshabilitado (causaba loop)
  // Los registros se guardan en Firebase dentro de completeCheckIn directamente

  
  // Sincronizar solicitudes con Firebase
  useEffect(() => {
    const absencesRef = ref(database, 'absenceRequests');
    
    const unsubscribe = onValue(absencesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Convertir objeto Firebase a array
        const arr = Array.isArray(data) ? data : Object.values(data).filter(r => r && r.id);
        setAbsenceRequests(arr);
        localStorage.setItem('absenceRequests', JSON.stringify(arr));
      } else {
        // Si Firebase está vacío, limpiar también local
        setAbsenceRequests([]);
        localStorage.setItem('absenceRequests', JSON.stringify([]));
      }
    });
    
    return () => unsubscribe();
  }, []);
  // NOTA: Se eliminó el useEffect que re-subía absenceRequests a Firebase
  // porque causaba un loop que restauraba solicitudes eliminadas

  // Sincronizar tareas con Firebase
  useEffect(() => {
    const tasksRef = ref(database, 'tasks');
    const unsubscribe = onValue(tasksRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const firebaseArr = Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean);
        // Combinar con localStorage para no perder tareas nuevas no sincronizadas
        const lsArr = JSON.parse(localStorage.getItem('tasks') || '[]');
        const deletedIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
        const _nowHoyFB = new Date();
        const hoyFB = `${_nowHoyFB.getFullYear()}-${String(_nowHoyFB.getMonth()+1).padStart(2,'0')}-${String(_nowHoyFB.getDate()).padStart(2,'0')}`;
        // Filtrar tareas eliminadas de Firebase y limpiar inconsistencias automaticamente
        const filteredFirebase = firebaseArr.filter(t => {
          if (!t || !t.id) return false;
          if (deletedIds.includes(t.id)) return false;
          // Eliminar tareas overdue/pending de dias anteriores que reaparezcan
          if (t.status === 'overdue' && t.dueDate && t.dueDate < hoyFB) return false;
          // Auto-aprobar done sin reviewStatus de dias ANTERIORES (nunca hoy) - usar fecha local Chile
          const _nowFB = new Date();
          const hoyLocalFB = `${_nowFB.getFullYear()}-${String(_nowFB.getMonth()+1).padStart(2,'0')}-${String(_nowFB.getDate()).padStart(2,'0')}`;
          const _ayerFB = new Date(_nowFB); _ayerFB.setDate(_ayerFB.getDate()-1);
          const ayerFB = `${_ayerFB.getFullYear()}-${String(_ayerFB.getMonth()+1).padStart(2,'0')}-${String(_ayerFB.getDate()).padStart(2,'0')}`;
          // Auto-aprobar solo tareas de hace 3+ dias - admin/gerente tienen 3 dias para revisar
          const _hace3FB = new Date(); _hace3FB.setDate(_hace3FB.getDate()-3);
          const hace3FB = `${_hace3FB.getFullYear()}-${String(_hace3FB.getMonth()+1).padStart(2,'0')}-${String(_hace3FB.getDate()).padStart(2,'0')}`;
          if (t.status === 'done' && !t.reviewStatus && t.dueDate && t.dueDate <= hace3FB) {
            t.reviewStatus = 'approved';
            t.reviewedBy = 'Sistema';
            t.reviewedAt = new Date().toISOString();
          }
          return true;
        });
        const merged = [...filteredFirebase];
        lsArr.forEach(lt => {
          if (lt && lt.id && !deletedIds.includes(lt.id) && !merged.find(ft => ft.id === lt.id)) {
            merged.push(lt);
          }
        });
        setTasks(merged);
        localStorage.setItem('tasks', JSON.stringify(merged));
      }
    });
    return () => unsubscribe();
  }, []);

  // Sincronizar productos faltantes con Firebase
  useEffect(() => {
    const productsRef = ref(database, 'missingProducts');
    const unsubscribe = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const arr = Array.isArray(data) ? data : Object.values(data);
        setMissingProducts(arr);
        localStorage.setItem('missingProducts', JSON.stringify(arr));
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setIsCheckedIn(false);
      setIsOnBreak(false);
      setTodayRecord(null);
      return;
    }
    const today = getLocalDateStr();
    // SIEMPRE buscar en localStorage primero — es la fuente más confiable
    const lsRaw = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
    const lsArray = (Array.isArray(lsRaw) ? lsRaw : Object.values(lsRaw || {})).filter(r => r && r.id);
    // Combinar localStorage + memoria (Firebase), sin duplicados
    const memArray = (Array.isArray(attendanceRecords) ? attendanceRecords : Object.values(attendanceRecords || {})).filter(r => r && r.id);
    const allRecords = [...lsArray];
    memArray.forEach(mr => { if (!allRecords.find(lr => lr.id === mr.id)) allRecords.push(mr); });
    const userTodayRecord = allRecords.find(r => r && r.employeeId === currentUser.id && r.date === today);
    if (userTodayRecord) {
      setTodayRecord(userTodayRecord);
      const hasCheckIn = !!userTodayRecord.checkIn && userTodayRecord.checkIn !== '';
      const hasCheckOut = !!userTodayRecord.checkOut && userTodayRecord.checkOut !== '';
      const hasBreakStart = !!userTodayRecord.breakStart && userTodayRecord.breakStart !== '';
      const hasBreakEnd = !!userTodayRecord.breakEnd && userTodayRecord.breakEnd !== '';
      setIsCheckedIn(hasCheckIn && !hasCheckOut);
      setIsOnBreak(hasBreakStart && !hasBreakEnd);
    }
    // IMPORTANTE: NO resetear isCheckedIn si no se encuentra en memoria
    // porque Firebase puede no haber cargado aún — localStorage ya inicializó el estado
  }, [currentUser, attendanceRecords]);


  // Cargar datos del localStorage al iniciar
  
  // 🔔 Sistema de Notificaciones
  useEffect(() => {
    // Solicitar permiso de notificaciones al cargar la app
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          console.log('✅ Permisos de notificación otorgados');
        }
      });
    }
  }, []);

  // Función para enviar notificación
  const sendNotification = (title, body, tag) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag,
        requireInteraction: true
      });
    }
  };

  // Función para programar notificaciones de un empleado
  const scheduleEmployeeNotifications = (employee) => {
    if (!employee.notificationsEnabled) return;

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = dayNames[now.getDay()];

    // Obtener horario del día actual
    let schedule;
    if (employee.useFlexibleSchedule && employee.weeklySchedule?.[today]) {
      schedule = employee.weeklySchedule[today];
      if (!schedule.enabled) return; // No trabaja hoy
    } else {
      schedule = {
        start: employee.startTime,
        end: employee.endTime
      };
    }

    if (!schedule.start || !schedule.end) return;

    // Calcular horas de notificación
    const [startHour, startMin] = schedule.start.split(':').map(Number);
    const [endHour, endMin] = schedule.end.split(':').map(Number);

    // Notificación 5 minutos antes de entrada
    const reminderTime = new Date(now);
    reminderTime.setHours(startHour, startMin - 5, 0, 0);

    // Notificación en hora de entrada
    const entryTime = new Date(now);
    entryTime.setHours(startHour, startMin, 0, 0);

    // Notificación en hora de salida
    const exitTime = new Date(now);
    exitTime.setHours(endHour, endMin, 0, 0);

    // Programar notificaciones si aún no ha pasado la hora
    if (reminderTime > now) {
      const timeout1 = reminderTime - now;
      setTimeout(() => {
        sendNotification(
          '⏰ Recordatorio de Entrada',
          `Hola ${employee.name}, tu entrada es en 5 minutos (${schedule.start})`,
          'entry-reminder'
        );
      }, timeout1);
    }

    if (entryTime > now) {
      const timeout2 = entryTime - now;
      setTimeout(() => {
        // Verificar si ya marcó entrada
        const todayAttendance = attendanceRecords.filter(a => 
          a.employeeId === employee.id && 
          new Date(a.checkIn).toDateString() === now.toDateString()
        );
        
        if (todayAttendance.length === 0) {
          sendNotification(
            '📍 Hora de Marcar Entrada',
            `${employee.name}, no olvides marcar tu entrada`,
            'entry-alert'
          );
        }
      }, timeout2);
    }

    if (exitTime > now) {
      const timeout3 = exitTime - now;
      setTimeout(() => {
        sendNotification(
          '🏃 Hora de Salida',
          `${employee.name}, recuerda marcar tu salida (${schedule.end})`,
          'exit-reminder'
        );
      }, timeout3);
    }
  };

  // Programar notificaciones para todos los empleados con notificaciones activadas
  useEffect(() => {
    employees.forEach(emp => {
      if (emp.notificationsEnabled) {
        scheduleEmployeeNotifications(emp);
      }
    });

    // Re-programar cada hora por si hay cambios
    const interval = setInterval(() => {
      employees.forEach(emp => {
        if (emp.notificationsEnabled) {
          scheduleEmployeeNotifications(emp);
        }
      });
    }, 3600000); // Cada hora

    // return () => clearInterval(interval); // DESACTIVADO
  }, [employees, attendanceRecords]);

  useEffect(() => {
    // Cargar configuración
    const savedConfig = localStorage.getItem('appConfig');
    if (savedConfig) {
      setConfig(JSON.parse(savedConfig));
    }
    
    // Cargar empleados
    const savedEmployees = localStorage.getItem('employees');
    if (savedEmployees) {
      setEmployees(JSON.parse(savedEmployees));
    }
    
    // Cargar registros
    const savedRecords = localStorage.getItem('attendanceRecords');
    if (savedRecords) {
      setAttendanceRecords(JSON.parse(savedRecords));
    }
    
    // Cargar sesión
    const savedRole = localStorage.getItem('userRole');
    const savedUser = localStorage.getItem('currentUser');
    
    if (savedRole && savedUser) {
      setUserRole(savedRole);
      setCurrentUser(JSON.parse(savedUser));
    }
    
    // Obtener ubicación GPS
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Error obteniendo ubicación:', error);
          setCurrentLocation({
            latitude: -33.57049194414672,
            longitude: -71.20044164472745
          });
        }
      );
    }
  }, []);

  // Sistema de alarmas para recordar marcar salida
  useEffect(() => {
    if (userRole !== 'employee' || !currentUser || !isCheckedIn || !todayRecord) return;
    
    const checkAlarm = () => {
      const now = new Date();
      const employeeSchedule = getTodaySchedule(currentUser.id);
      const scheduledCheckOut = new Date();
      scheduledCheckOut.setHours(employeeSchedule.checkOut.hour, employeeSchedule.checkOut.minute, 0);
      
      const minutesUntil = Math.floor((scheduledCheckOut - now) / (1000 * 60));
      setMinutesUntilCheckout(minutesUntil);
      
      // Alarma 5 minutos antes
      if (minutesUntil === 5 && !showAlarmNotification) {
        playAlarmSound();
        setAlarmMessage('⚠️ Faltan 5 minutos para tu hora de salida');
        // setShowAlarmNotification(true);
 // Banner desactivado
        setTimeout(() => setShowAlarmNotification(false), 10000);
      }
      
      // Alarma 2 minutos antes
      if (minutesUntil === 2) {
        playAlarmSound();
        setAlarmMessage('⚠️ Faltan 2 minutos para tu hora de salida');
        // setShowAlarmNotification(true);
 // Banner desactivado
        setTimeout(() => setShowAlarmNotification(false), 10000);
      }
      
      // Alarma 1 minuto antes
      if (minutesUntil === 1) {
        playAlarmSound();
        setAlarmMessage('🔴 ¡Falta 1 minuto para tu hora de salida!');
        // setShowAlarmNotification(true);
 // Banner desactivado
        setTimeout(() => setShowAlarmNotification(false), 10000);
      }
      
      // Alarma hora exacta
      if (minutesUntil === 0 && !todayRecord.checkOut) {
        playAlarmSound();
        setAlarmMessage('🚨 ¡ES TU HORA DE SALIDA! Marca tu salida ahora');
        // setShowAlarmNotification(true);
 // Banner desactivado
      }
      
      // Después de la hora (horas extras)
      if (minutesUntil < 0 && !todayRecord.checkOut && !todayRecord.overtimeAuthorized) {
        setAlarmMessage('⚠️ Llegaste tarde a tu hora de entrada. Se registrará el atraso.');
        // setShowAlarmNotification(true);
 // Banner desactivado
        // Auto-cerrar después de 5 segundos
        setTimeout(() => {
          setShowAlarmNotification(false);
          setAlarmMessage('');
        }, 5000); // Auto-cerrar después de 30 segundos
      }
    };
    
    // Verificar cada minuto
    // const interval = setInterval(checkAlarm, 60000); // DESACTIVADO
    // checkAlarm(); // DESACTIVADO
    
    // return () => clearInterval(interval); // DESACTIVADO
  }, [userRole, currentUser, isCheckedIn, todayRecord, config.schedules, showAlarmNotification]);
  
  // Cargar solicitudes de ausencias
  useEffect(() => {
    const savedRequests = localStorage.getItem('absenceRequests');
    if (savedRequests) {
      setAbsenceRequests(JSON.parse(savedRequests));
    }
  }, []);
  
  // Función para reproducir sonido de alarma
  const playAlarmSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Repetir 3 veces
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 800;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.5);
      }, 600);
      
      setTimeout(() => {
        const osc3 = audioContext.createOscillator();
        const gain3 = audioContext.createGain();
        osc3.connect(gain3);
        gain3.connect(audioContext.destination);
        osc3.frequency.value = 800;
        osc3.type = 'sine';
        gain3.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        osc3.start(audioContext.currentTime);
        osc3.stop(audioContext.currentTime + 0.5);
      }, 1200);
    } catch (error) {
      console.log('Error playing alarm sound:', error);
    }
  };

  // Guardar configuración cuando cambia
  const saveConfig = (newConfig) => {
    setConfig(newConfig);
    localStorage.setItem('config', JSON.stringify(newConfig));
    
    // Guardar en Firebase
    if (isOnline) {
      console.log('📤 Guardando configuración en Firebase...');
      const configRef = ref(database, 'config');
      set(configRef, sanitizeForFirebase(newConfig)).then(() => {
        console.log('✅ Configuración guardada en Firebase');
      }).catch(err => {
        console.error('❌ Error guardando configuración:', err);
      });
    }
  };

  // Guardar empleados cuando cambian
  // Sanitizar anticipos al cargar/guardar para evitar amount undefined
  const sanitizeEmployees = (emps) => {
    if (!Array.isArray(emps)) return emps;
    return emps.map(e => ({
      ...e,
      advances: (Array.isArray(e.advances) ? e.advances : []).map(a => ({
        ...a,
        amount: a.amount ?? 0
      }))
    }));
  };

  const saveEmployees = (newEmployees) => {
    setEmployees(newEmployees);
    localStorage.setItem('employees', JSON.stringify(newEmployees));
    console.log('💾 Empleados guardados en localStorage:', newEmployees.length);
    
    // Guardar en Firebase si hay conexión — por ID, no como array
    if (isOnline) {
      console.log('📤 Guardando en Firebase...');
      const obj = {};
      newEmployees.forEach(emp => {
        if (emp && emp.id) obj[emp.id] = sanitizeForFirebase(emp);
      });
      const employeesRef = ref(database, 'employees');
      set(employeesRef, obj).then(() => {
        console.log('✅ Empleados guardados en Firebase');
      }).catch(err => {
        console.error('❌ Error guardando en Firebase:', err);
      });
    } else {
      console.log('🔴 Sin internet - guardado solo en localStorage');
    }
  };

  // Funciones para eliminar registros (solo gerente)
  const handleDeleteAttendanceRecord = (recordId) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro?')) {
      return;
    }
    
    // Buscar el registro exacto primero para confirmar
    const recordToDelete = attendanceRecords.find(r => r.id === recordId);
    if (!recordToDelete) {
      alert('Registro no encontrado');
      return;
    }
    // Filtrar por id Y por combinación única employeeId+date para evitar borrar duplicados
    const updated = attendanceRecords.filter(r => 
      !(r.id === recordId || 
        (r.employeeId === recordToDelete.employeeId && 
         r.date === recordToDelete.date && 
         r.id === recordId))
    );
    setAttendanceRecords(updated);
    localStorage.setItem('attendanceRecords', JSON.stringify(updated));
    
    if (isOnline) {
       // FIX BUG 3
      const recordsRef = ref(database, 'attendanceRecords');
      const objA = {}; updated.forEach(r => { if(r&&r.id) objA[r.id]=r; }); set(recordsRef, sanitizeForFirebase(objA));
    }
    
    alert('✅ Registro eliminado');
  };
  
  // ── HANDLERS TAREAS ──
  // ── AUDIT LOG ──
  const addAuditEntry = (action, details) => {
    const entry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      user: currentUser?.name || 'Sistema',
      userRole: userRole || 'unknown',
      action,
      details
    };
    setAuditLog(prev => {
      const updated = [entry, ...(Array.isArray(prev) ? prev : [])].slice(0, 200); // max 200 entries
      localStorage.setItem('auditLog', JSON.stringify(updated));
      if (navigator.onLine) {
        const aRef = ref(database, 'auditLog');
        const obj = {};
        updated.forEach(e => { if(e&&e.id) obj[e.id] = e; });
        set(aRef, sanitizeForFirebase(obj));
      }
      return updated;
    });
  };

  // ── GESTIÓN DE AUSENCIAS Y REGISTROS INCOMPLETOS ──
  const saveAttendanceIssues = (updated) => {
    setAttendanceIssues(updated);
    localStorage.setItem('attendanceIssues', JSON.stringify(updated));
    if (navigator.onLine) {
      const issRef = ref(database, 'attendanceIssues');
      const obj = {};
      updated.forEach(i => { if(i&&i.id) obj[i.id] = i; });
      set(issRef, sanitizeForFirebase(obj));
    }
  };

  const detectAttendanceIssues = () => {
    const today = getLocalDateStr();
    const now = new Date();
    const newIssues = [];

    (Array.isArray(employees) ? employees : []).filter(e => e.status === 'active').forEach(emp => {
      // Verificar últimos 30 días + hoy
      for (let i = 0; i <= 30; i++) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        // Para hoy: solo detectar si ya pasó la hora de entrada programada
        if (i === 0) {
          const ms2 = emp.monthlySchedule?.[getLocalDateStr()];
          if (!ms2?.enabled || !ms2?.start) continue;
          const [sh, sm] = ms2.start.split(':').map(Number);
          const scheduledStart = new Date();
          scheduledStart.setHours(sh, sm + 30, 0, 0); // 30 min de gracia
          if (new Date() < scheduledStart) continue; // Aún no es hora
        }
        const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        
        // Verificar si debía trabajar ese día
        const ms = emp.monthlySchedule?.[dateStr];
        const shouldWork = ms?.enabled && ms?.start;
        if (!shouldWork) continue;

        // Buscar registro
        const record = (Array.isArray(attendanceRecords)?attendanceRecords:[]).find(r => r.employeeId === emp.id && r.date === dateStr);
        
        // Issue ID único
        const issueId = `${emp.id}_${dateStr}`;
        const existingIssue = (Array.isArray(attendanceIssues)?attendanceIssues:[]).find(i => i.id === issueId);
        if (existingIssue) continue; // Ya procesado o descartado

        if (!record) {
          // Ausencia total
          newIssues.push({
            id: issueId,
            employeeId: emp.id,
            employeeName: emp.name,
            employeePhoto: emp.photo,
            date: dateStr,
            type: 'absence', // absence | incomplete
            missing: ['entrada', 'salida'],
            status: 'pending', // pending | justified | unjustified | schedule_error | dismissed
            classifiedBy: null,
            classifiedAt: null,
            reason: '',
            notifiedEmployee: false,
            createdAt: new Date().toISOString()
          });
        } else {
          // Registro incompleto
          const missing = [];
          if (!record.checkOut) missing.push('salida');
          if (!record.breakStart && ms.lunchStart) missing.push('inicio colación');
          if (!record.breakEnd && record.breakStart) missing.push('fin colación');
          if (missing.length > 0) {
            newIssues.push({
              id: issueId,
              employeeId: emp.id,
              employeeName: emp.name,
              employeePhoto: emp.photo,
              date: dateStr,
              type: 'incomplete',
              missing,
              status: 'pending',
              classifiedBy: null,
              classifiedAt: null,
              reason: '',
              notifiedEmployee: false,
              createdAt: new Date().toISOString()
            });
          }
        }
      }
    });

    if (newIssues.length > 0) {
      const updated = [...(Array.isArray(attendanceIssues)?attendanceIssues:[]), ...newIssues];
      saveAttendanceIssues(updated);
    }
    return newIssues.length;
  };

  const classifyIssue = (issueId, status, reason) => {
    const updated = (Array.isArray(attendanceIssues)?attendanceIssues:[]).map(i => 
      i.id === issueId ? {...i, status, reason: reason||'', classifiedBy: currentUser?.name, classifiedAt: new Date().toISOString(), notifiedEmployee: true} : i
    );
    saveAttendanceIssues(updated);
    addAuditEntry('CLASIFICAR_INCIDENCIA', `Clasificó incidencia de ${updated.find(i=>i.id===issueId)?.employeeName} del ${issueId.split('_')[1]} como: ${status}`);
  };

  const dismissIssue = (issueId) => {
    // Marcar como dismissed en vez de eliminar para que no vuelva a detectarse
    const updated = (Array.isArray(attendanceIssues)?attendanceIssues:[]).map(i =>
      i.id === issueId ? {...i, status: 'dismissed', classifiedBy: currentUser?.name, classifiedAt: new Date().toISOString()} : i
    );
    saveAttendanceIssues(updated);
    addAuditEntry('ELIMINAR_INCIDENCIA', `Eliminó incidencia ${issueId} (falso positivo)`);
  };

  // ── ANUNCIOS ──
  const saveAnnouncements = (updated) => {
    // Filter expired announcements
    const now = new Date();
    const active = updated.filter(a => !a.expiresAt || new Date(a.expiresAt) > now);
    setAnnouncements(active);
    localStorage.setItem('announcements', JSON.stringify(active));
    if (navigator.onLine) {
      const aRef = ref(database, 'announcements');
      if (active.length === 0) {
        set(aRef, null);
      } else {
        const obj = {};
        active.forEach(a => { if(a&&a.id) obj[a.id] = a; });
        set(aRef, sanitizeForFirebase(obj));
      }
    }
  };

  const createAnnouncement = (text, type, days) => {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + parseInt(days));
    const newAnn = {
      id: Date.now(),
      text,
      type, // 'info' | 'warning' | 'reminder' | 'success'
      createdBy: currentUser?.name || 'Gerente',
      createdAt: new Date().toISOString(),
      expiresAt: expiresAt.toISOString(),
      days: parseInt(days)
    };
    const updated = [newAnn, ...(Array.isArray(announcements)?announcements:[])];
    saveAnnouncements(updated);
    addAuditEntry('CREAR_ANUNCIO', `Creó anuncio: "${text.substring(0,50)}" — expira en ${days} días`);
  };

  const deleteAnnouncement = (id) => {
    const updated = (Array.isArray(announcements)?announcements:[]).filter(a => a.id !== id);
    saveAnnouncements(updated);
  };

  const saveTasksToStorage = (updated) => {
    setTasks(updated);
    localStorage.setItem('tasks', JSON.stringify(updated));
    // Invalidar cache del mensaje proactivo cuando cambian las tareas
    try {
      const hoy = new Date();
      const ds = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
      localStorage.removeItem(`iaProactivaGerente_${ds}`);
    } catch(e) {}
    if (navigator.onLine) {
      // Guardar como objeto con id como clave para Firebase — sanitizar undefined
      // Guardar cada tarea individualmente — evita perder datos si falla la red
      updated.forEach(t => {
        if (t && t.id) {
          const r = ref(database, 'tasks/' + t.id);
          set(r, sanitizeForFirebase(t)).catch(e => console.error('Error guardando tarea', t.id, e));
        }
      });
    }
  };

  // ── DETECCIÓN TAREAS VENCIDAS ──
  const checkOverdueTasks = () => {
    const now = new Date();
    let hasOverdue = false;
    const updated = (Array.isArray(tasks) ? tasks : []).map(t => {
      if (['done','not_done','approved'].includes(t.status) || t.reviewStatus === 'rejected' || t.reviewStatus === 'approved' || !t.dueDate) return t;
      // Solo evaluar tareas de HOY - las de dias anteriores se limpian con el boton "Limpiar vencidas"
      const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
      if (t.dueDate < todayStr) return t; // no tocar tareas de dias anteriores
      // Usar hora local explícitamente para evitar problemas de timezone
      const [year, month, day] = t.dueDate.split('-').map(Number);
      // Si el empleado sigue en turno (checkIn pero sin checkOut hoy), NO vencer aun
      try {
        const lsRecs = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
        const recHoy = lsRecs.find(r => String(r.employeeId) === String(t.assignedTo) && r.date === t.dueDate);
        if (recHoy && recHoy.checkIn && !recHoy.checkOut) return t; // empleado en turno, no vencer
      } catch(e) {}
      // Detectar hora de salida del empleado asignado segun su horario del dia
      let hour, minute;
      try {
        const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
        const emp = lsEmps.find(e => String(e.id) === String(t.assignedTo));
        const horarioDelDia = emp?.monthlySchedule?.[t.dueDate];
        if (horarioDelDia?.end && horarioDelDia?.enabled) {
          const [eh, em] = horarioDelDia.end.split(':').map(Number);
          hour = eh;
          minute = em;
        } else {
          const [fh, fm] = (t.dueTime || '23:59').split(':').map(Number);
          hour = fh;
          minute = fm;
        }
      } catch(e) {
        const [fh, fm] = (t.dueTime || '23:59').split(':').map(Number);
        hour = fh;
        minute = fm;
      }
      const dueDateTime = new Date(year, month-1, day, hour, minute, 0);
      // Solo marcar vencida si pasaron más de 2 minutos (evitar falsos positivos)
      const isOverdue = (now - dueDateTime) > 2 * 60 * 1000;
      if (isOverdue && !t.isOverdue) {
        hasOverdue = true;
        return { ...t, isOverdue: true, status: 'overdue' };
      }
      // Si ya estaba marcada como overdue pero la fecha es futura, revertir
      if (t.status === 'overdue' && !isOverdue) {
        return { ...t, isOverdue: false, status: 'pending' };
      }
      return t;
    });
    if (hasOverdue) {
      saveTasksToStorage(updated);
      // Notificar al gerente
      if ('Notification' in window && Notification.permission === 'granted') {
        const overdueTasks = updated.filter(t => t.status === 'overdue');
        new Notification('⚠️ Tareas Vencidas', {
          body: `${overdueTasks.length} tarea(s) han vencido sin completarse`,
          icon: '/logo192.png'
        });
      }
    }
  };

  // ── AUTO-CORRECCIÓN GLOBAL AL CARGAR ──
  React.useEffect(() => {
    try {
      // 1. Corregir empleados con contrato plazo_fijo vencido
      const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
      const hoy = getLocalDateStr();
      let empsNeedSave = false;
      const empsCorregidos = lsEmps.map(e => {
        if (!e) return e;
        if (e.contractType === 'plazo_fijo' && e.contractEndDate && e.contractEndDate < hoy && e.status === 'active') {
          empsNeedSave = true;
          return {...e, status: 'inactive', inactiveReason: 'Contrato plazo fijo vencido el ' + e.contractEndDate};
        }
        return e;
      });
      if (empsNeedSave) {
        localStorage.setItem('employees', JSON.stringify(empsCorregidos));
        fetch('https://control--asistencia-default-rtdb.firebaseio.com/employees.json', {
          method: 'PATCH', headers: {'Content-Type':'application/json'},
          body: JSON.stringify(Object.fromEntries(empsCorregidos.filter(Boolean).map(e => [e.id, e])))
        }).catch(() => {});
      }

      // 2. Corregir registros de asistencia sin checkOut de dias anteriores
      const lsRecs = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
      const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
      const ayerStr = `${ayer.getFullYear()}-${String(ayer.getMonth()+1).padStart(2,'0')}-${String(ayer.getDate()).padStart(2,'0')}`;
      let recsNeedSave = false;
      const recsCorregidos = lsRecs.map(r => {
        if (!r) return r;
        if (r.checkIn && !r.checkOut && r.date && r.date <= ayerStr) {
          recsNeedSave = true;
          return {...r, checkOut: null, incomplete: true, autoFixed: true};
        }
        return r;
      });
      if (recsNeedSave) {
        localStorage.setItem('attendanceRecords', JSON.stringify(recsCorregidos));
      }
    } catch(e) {}
  }, []);

  // ── AUTO-CORRECCIÓN DE TAREAS AL CARGAR ──
  React.useEffect(() => {
    try {
      if (!tasks || tasks.length === 0) return;
      const deletedIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      let needsSave = false;
      const corrected = tasks.map(t => {
        if (!t) return t;
        // Corregir tareas eliminadas que reaparecieron
        if (deletedIds.includes(t.id)) return null;
        // Corregir tareas con reviewStatus pero status incorrecto
        if (['approved','rejected'].includes(t.reviewStatus) && t.status !== 'done') {
          needsSave = true;
          return {...t, status: 'done', completedAt: t.completedAt || new Date().toISOString()};
        }
        // Auto-aprobar solo tareas de hace 3+ dias - admin/gerente tienen 3 dias para revisar manualmente
        const _nowLocal = new Date();
        const _hace3Local = new Date(_nowLocal); _hace3Local.setDate(_hace3Local.getDate()-3);
        const hace3Str = `${_hace3Local.getFullYear()}-${String(_hace3Local.getMonth()+1).padStart(2,'0')}-${String(_hace3Local.getDate()).padStart(2,'0')}`;
        if (t.status === 'done' && !t.reviewStatus && t.dueDate && t.dueDate <= hace3Str) {
          needsSave = true;
          return {...t, reviewStatus: 'approved', reviewedBy: 'Sistema', reviewedAt: new Date().toISOString()};
        }
        return t;
      }).filter(Boolean);
      if (needsSave) {
        saveTasksToStorage(corrected);
        // Sincronizar correcciones con Firebase silenciosamente
        corrected.forEach(t => {
          if (['approved','rejected'].includes(t.reviewStatus)) {
            fetch('https://control--asistencia-default-rtdb.firebaseio.com/tasks/' + t.id + '.json', {
              method: 'PUT',
              headers: {'Content-Type':'application/json'},
              body: JSON.stringify(t)
            }).catch(() => {});
          }
        });
      }
    } catch(e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.length]);

  // Verificar tareas vencidas cada minuto
  React.useEffect(() => {
    checkOverdueTasks();
    const interval = setInterval(checkOverdueTasks, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const handleCreateTask = () => {
    if (!taskForm.title.trim() || !taskForm.assignedTo) return;
    const newTask = {
      id: Date.now(),
      title: taskForm.title.trim(),
      description: taskForm.description.trim(),
      assignedTo: taskForm.assignedTo,
      assignedName: employees.find(e => e.id === parseInt(taskForm.assignedTo))?.name || '',
      priority: taskForm.priority,
      dueDate: taskForm.dueDate || '',
      dueTime: taskForm.dueTime || '',
      createdBy: currentUser?.name || 'Gerente',
      createdByRole: userRole,
      createdAt: new Date().toISOString(),
      status: 'pending',
      steps: (taskForm.steps || []).map((s, i) => ({ id: i + 1, text: s.text, done: false })),
      employeeObservation: '',
      completedAt: null,
      reviewedBy: null,
      reviewedAt: null,
      reviewStatus: null,
      reviewComment: '',
      managerNote: '',
      managerNoteBy: '',
      managerNoteAt: null,
    };
    const updated = [newTask, ...tasks];
    saveTasksToStorage(updated);
    setTaskForm({ title: '', description: '', assignedTo: '', priority: 'medium', steps: [], dueDate: '', dueTime: '' });
    setNewStepText('');
    setShowTaskModal(false);
  };

  const handleEditTask = () => {
    if (!taskForm.title.trim() || !taskForm.assignedTo) return;
    const updated = tasks.map(t => t.id === editingTask.id
      ? { ...t, title: taskForm.title.trim(), description: taskForm.description.trim(), assignedTo: taskForm.assignedTo, assignedName: employees.find(e => e.id === parseInt(taskForm.assignedTo))?.name || '', priority: taskForm.priority, dueDate: taskForm.dueDate || '', dueTime: taskForm.dueTime || '', steps: (taskForm.steps || []).map((s, i) => ({ id: i + 1, text: s.text, done: s.done || false })) }
      : t
    );
    saveTasksToStorage(updated);
    setEditingTask(null);
    setTaskForm({ title: '', description: '', assignedTo: '', priority: 'medium', steps: [], dueDate: '', dueTime: '' });
    setNewStepText('');
    setShowTaskModal(false);
  };

  const handleDeleteTask = (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    const updated = tasks.filter(t => t.id !== taskId);
    saveTasksToStorage(updated);
    // Registrar ID como eliminado para evitar que Firebase lo restaure
    try {
      const deleted = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      if (!deleted.includes(taskId)) {
        deleted.push(taskId);
        localStorage.setItem('deletedTaskIds', JSON.stringify(deleted.slice(-500)));
      }
    } catch(e) {}
    // Borrar nodo individual en Firebase
    if (navigator.onLine) {
      const r = ref(database, 'tasks/' + taskId);
      set(r, null).catch(e => console.error('Error eliminando tarea de Firebase', e));
    }
  };

  const handleEmployeeCompleteTask = (taskId, done, observation) => {
    const updated = tasks.map(t => t.id === taskId
      ? { ...t, status: done ? 'done' : 'not_done', employeeObservation: observation, completedAt: new Date().toISOString(), completedBy: currentUser?.name, reviewStatus: null, reviewComment: '', reviewedBy: null, reviewedAt: null }
      : t
    );
    saveTasksToStorage(updated);
  };

  // Toggle individual step done/undone
  const handleToggleStep = (taskId, stepId) => {
    const updated = tasks.map(t => {
      if (t.id !== taskId) return t;
      const steps = (t.steps || []).map(s => s.id === stepId ? { ...s, done: !s.done } : s);
      return { ...t, steps };
    });
    saveTasksToStorage(updated);
  };

  // Manager approves or rejects task
  const handleManagerReviewTask = (taskId, approved, comment) => {
    const task = tasks.find(t => t.id === taskId);
    const updated = tasks.map(t => t.id === taskId
      ? {
          ...t,
          reviewedBy: currentUser?.name,
          reviewedAt: new Date().toISOString(),
          reviewStatus: approved ? 'approved' : 'rejected',
          reviewComment: comment || '',
          status: 'done',
          completedAt: approved ? t.completedAt : new Date().toISOString(),
        }
      : t
    );
    saveTasksToStorage(updated);
    addAuditEntry(
      approved ? 'APROBAR_TAREA' : 'RECHAZAR_TAREA',
      `${approved ? 'Aprobó' : 'Rechazó'} tarea "${task?.title || taskId}" de ${task?.assignedName || ''}${comment ? ' — Comentario: ' + comment : ''}`
    );
  };

  // ── HANDLERS PRODUCTOS FALTANTES ──
  const saveProductsToStorage = (updated) => {
    setMissingProducts(updated);
    localStorage.setItem('missingProducts', JSON.stringify(updated));
    if (navigator.onLine) {
      const obj = {};
      updated.forEach(p => { obj[p.id] = p; });
      const r = ref(database, 'missingProducts');
      set(r, sanitizeForFirebase(obj)).catch(e => console.error(e));
    }
  };

  const handleReportProduct = () => {
    if (!productForm.name.trim()) return;
    const newProduct = {
      id: Date.now(),
      name: productForm.name.trim(),
      quantity: productForm.quantity.trim(),
      unit: productForm.unit,
      notes: productForm.notes.trim(),
      reportedBy: currentUser?.name || '',
      reportedById: currentUser?.id,
      reportedAt: new Date().toISOString(),
      status: 'pending', // pending | requested | resolved
      requestedBy: null,
      requestedAt: null,
      resolvedBy: null,
      resolvedAt: null,
    };
    const updated = [newProduct, ...missingProducts];
    saveProductsToStorage(updated);
    setProductForm({ name: '', quantity: '', unit: 'unidades', notes: '' });
    setShowProductModal(false);
  };

  const handleMarkProductRequested = (productId) => {
    const updated = missingProducts.map(p => p.id === productId
      ? { ...p, status: 'requested', requestedBy: currentUser?.name, requestedAt: new Date().toISOString() }
      : p
    );
    saveProductsToStorage(updated);
  };

  const handleMarkProductResolved = (productId) => {
    const updated = missingProducts.map(p => p.id === productId
      ? { ...p, status: 'resolved', resolvedBy: currentUser?.name, resolvedAt: new Date().toISOString() }
      : p
    );
    saveProductsToStorage(updated);
  };

  const handleDeleteProduct = (productId) => {
    if (!window.confirm('¿Eliminar este reporte?')) return;
    const updated = missingProducts.filter(p => p.id !== productId);
    saveProductsToStorage(updated);
  };

  const handleDeleteAbsenceRequest = (requestId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta solicitud?')) {
      return;
    }
    
    const updated = absenceRequests.filter(r => r.id !== requestId);
    setAbsenceRequests(updated);
    localStorage.setItem('absenceRequests', JSON.stringify(updated));
    
    if (isOnline) {
      const absencesRef = ref(database, 'absenceRequests');
      if (updated.length === 0) {
        // Si no quedan solicitudes, eliminar el nodo de Firebase
        set(absencesRef, null);
      } else {
        const obj = {};
        updated.forEach(r => { if (r && r.id) obj[r.id] = r; });
        set(absencesRef, sanitizeForFirebase(obj));
      }
    }
    
    alert('✅ Solicitud eliminada');
  };

  // Calcular distancia entre dos puntos GPS
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // Función para contar días hábiles (lunes a viernes)
  const countBusinessDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;
    
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      // 0 = Domingo, 6 = Sábado
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return count;
  };

  // Obtener horario del día actual según configuración
  const getTodaySchedule = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) return config.schedules;
    
    const today = new Date();
    const dateStr = getLocalDateStr(today);
    
    // Leer empleado fresco desde localStorage para obtener monthlySchedule/weeklySchedule
    // (Firebase puede devolver version desactualizada sin estos campos)
    let freshEmployee = employee;
    try {
      const lsEmployees = JSON.parse(localStorage.getItem('employees') || '[]');
      const found = lsEmployees.find(e => e.id === employeeId);
      if (found) freshEmployee = found;
    } catch(e) {}
    
    // Prioridad 1: Calendario mensual (usar empleado fresco del localStorage)
    if (freshEmployee.monthlySchedule && freshEmployee.monthlySchedule[dateStr]) {
      const ms = freshEmployee.monthlySchedule[dateStr];
      if (ms && ms.enabled && ms.start) {
        const [startHour, startMinute] = ms.start.split(':').map(Number);
        const [endHour, endMinute] = ms.end.split(':').map(Number);
        const [lunchStartHour, lunchStartMinute] = (ms.lunchStart || '13:00').split(':').map(Number);
        const [lunchEndHour, lunchEndMinute] = (ms.lunchEnd || '14:00').split(':').map(Number);
        return {
          checkIn: { hour: startHour, minute: startMinute },
          checkOut: { hour: endHour, minute: endMinute },
          breakStart: { hour: lunchStartHour, minute: lunchStartMinute },
          breakEnd: { hour: lunchEndHour, minute: lunchEndMinute }
        };
      }
    }
    
    // Prioridad 2: Horarios por día de la semana (usar empleado fresco del localStorage)
    if (freshEmployee.useFlexibleSchedule && freshEmployee.weeklySchedule) {
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayOfWeek = today.getDay();
      const dayName = dayNames[dayOfWeek];
      const daySchedule = freshEmployee.weeklySchedule[dayName];
      
      if (daySchedule && daySchedule.enabled) {
        // Convertir formato de weeklySchedule a formato de schedules
        const [startHour, startMinute] = daySchedule.start.split(':').map(Number);
        const [endHour, endMinute] = daySchedule.end.split(':').map(Number);
        const lunchStart = daySchedule.lunchStart || '13:00';
        const lunchEnd = daySchedule.lunchEnd || '14:00';
        const [lunchStartHour, lunchStartMinute] = lunchStart.split(':').map(Number);
        const [lunchEndHour, lunchEndMinute] = lunchEnd.split(':').map(Number);
        
        return {
          checkIn: { hour: startHour, minute: startMinute },
          checkOut: { hour: endHour, minute: endMinute },
          breakStart: { hour: lunchStartHour, minute: lunchStartMinute },
          breakEnd: { hour: lunchEndHour, minute: lunchEndMinute }
        };
      }
      
      // Tiene horario flexible pero hoy no está habilitado → no trabaja este día
      if (daySchedule && !daySchedule.enabled) {
        return null;
      }
    }
    
    // Prioridad 3: Horario fijo configurado individualmente en el empleado (usar fresco)
    const sched = freshEmployee.schedules || employee.schedules;
    if (
      sched &&
      sched.checkIn &&
      sched.checkOut &&
      typeof sched.checkIn.hour === 'number' &&
      typeof sched.checkOut.hour === 'number'
    ) {
      return sched;
    }
    
    // Sin horario configurado → retornar null para que el caller lo maneje.
    // NO usar config.schedules como fallback silencioso (eso causaba calcular
    // atrasos incorrectos para empleados con horarios distintos al horario base).
    console.warn(`⚠️ Empleado "${employee.name}" (ID: ${employeeId}) no tiene horario configurado. Contactar al administrador.`);
    return null;
  };


  const checkGPSLocation = () => {
    if (!currentLocation) return false;
    
    const distance = calculateDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      config.workLocation.latitude,
      config.workLocation.longitude
    );
    
    console.log('Distancia:', distance.toFixed(2), 'metros');
    return distance <= config.workLocation.radius;
  };

  // Calcular GPS una vez, no en cada render
  const isInWorkZone = useMemo(() => {
    if (!currentLocation) return false;
    
    const distance = calculateDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      config.workLocation.latitude,
      config.workLocation.longitude
    );
    
    return distance <= config.workLocation.radius;
  }, [
    currentLocation?.latitude, 
    currentLocation?.longitude, 
    config.workLocation?.latitude, 
    config.workLocation?.longitude, 
    config.workLocation?.radius
  ]);


  const handleLogin = () => {
    if (loginCode === config.codes.manager) {
      const managerUser = { id: 1000, name: 'Gerente', position: 'Gerente General', role: 'manager' };
      setUserRole('manager');
      setCurrentUser(managerUser);
      localStorage.setItem('userRole', 'manager');
      localStorage.setItem('currentUser', JSON.stringify(managerUser));
      setCurrentView('dashboard');
    } else if (loginCode === config.codes.admin) {
      const adminUser = { id: 999, name: 'Administrador', position: 'Administrador', role: 'admin' };
      setUserRole('admin');
      setCurrentUser(adminUser);
      localStorage.setItem('userRole', 'admin');
      localStorage.setItem('currentUser', JSON.stringify(adminUser));
      setCurrentView('dashboard');
    } else if ((config.extraAdmins||[]).includes(loginCode) && loginCode.startsWith('EMP')) {
      // Empleado con rol admin extra
      const empId = parseInt(loginCode.replace('EMP', ''));
      const employee = employees.find(e => e.id === empId);
      if (employee) {
        const adminUser = { ...employee, role: 'admin', isExtraAdmin: true };
        setUserRole('admin');
        setCurrentUser(adminUser);
        localStorage.setItem('userRole', 'admin');
        localStorage.setItem('currentUser', JSON.stringify(adminUser));
        setCurrentView('dashboard');
      } else {
        alert('❌ Código inválido');
      }
    } else if (loginCode.startsWith('EMP')) {
      const empId = parseInt(loginCode.replace('EMP', ''));
      const employee = employees.find(e => e.id === empId);
      
      if (employee) {
        setUserRole('employee');
        // Buscar empleado fresco con horario actualizado
        const freshEmployee = employees.find(e => e.id === employee.id) || employee;
        const employeeUser = { ...freshEmployee, role: 'employee' };
        setCurrentUser(employeeUser);
        localStorage.setItem('userRole', 'employee');
        localStorage.setItem('currentUser', JSON.stringify(employeeUser));
        // Cargar borradores de observaciones del empleado
        try {
          const drafts = {};
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(`taskDraft_${employee.id}_`)) {
              const taskId = parseInt(key.split('_').pop());
              if (!isNaN(taskId)) drafts[taskId] = localStorage.getItem(key) || '';
            }
          });
          if (Object.keys(drafts).length > 0) setTaskObs(prev => ({...prev, ...drafts}));
        } catch(e) {}
        
        // IMPORTANTE: Actualizar estado desde Firebase después de login
        const today = getLocalDateStr();
        const todayRec = attendanceRecords.find(r => r.employeeId === employeeUser.id && r.date === today);
        
        if (todayRec) {
          console.log('✅ Registro de hoy encontrado:', todayRec);
          setTodayRecord(todayRec);
          setIsCheckedIn(!!todayRec.checkIn && todayRec.checkIn !== '' && (!todayRec.checkOut || todayRec.checkOut === ''));
          if (todayRec.breakStart && !todayRec.breakEnd) {
            setIsOnBreak(true);
          }
        } else {
          console.log('ℹ️ No hay registro de hoy');
          setTodayRecord(null);
          setIsCheckedIn(false);
          setIsOnBreak(false);
        }
        
        setCurrentView('home');
      } else {
        alert('❌ Código de empleado inválido');
      }
    } else {
      alert('❌ Código de acceso inválido');
    }
    setLoginCode('');
  };

  // Persistir currentUser en localStorage
  React.useEffect(() => {
    if (currentUser) {
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('currentUser');
    }
  }, [currentUser]);

  const handleLogout = () => {
    setUserRole(null);
    setCurrentUser(null);
    localStorage.removeItem('userRole');
    localStorage.removeItem('currentUser');
    setCurrentView('home');
  };

  // Detectar y registrar atrasos
  const detectLateArrival = (employee, timestamp) => {
    try {
      const arrivalTime = new Date(timestamp);
      const now = new Date();
      // FIX BUG 1: usar getTodaySchedule para obtener el horario real del empleado
      const employeeSchedule = getTodaySchedule(employee.id);
      if (!employeeSchedule) return; // Sin horario configurado → no calcular atraso
      const scheduledHour = employeeSchedule.checkIn.hour;
      const scheduledMinute = employeeSchedule.checkIn.minute;
      
      const scheduledTime = new Date(now);
      scheduledTime.setHours(scheduledHour, scheduledMinute, 0, 0);
      
      const minutesLate = Math.floor((arrivalTime - scheduledTime) / (1000 * 60));
      
      if (minutesLate > 0) {
        const alert = {
          id: Date.now(),
          employeeId: employee.id,
          employeeName: employee.name,
          minutesLate,
          timestamp
        };
        
        setLateEmployeesAlerts(prev => [...prev, alert]);
        
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          new Notification('⚠️ Empleado con Atraso', {
            body: `${employee.name} llegó ${minutesLate} min tarde`,
            icon: '/logo192.png'
          });
        }
        
        console.log(`⚠️ Atraso: ${employee.name} - ${minutesLate} min`);
      }
    } catch (error) {
      console.error('Error detectando atraso:', error);
    }
  };

  const handleCheckIn = () => {
    // Primero abrir cámara para selfie
    // Guardar la función completeCheckIn directamente
    setPendingCheckInAction(() => (photoData) => completeCheckIn(photoData));
    // Verificación biométrica desactivada - registrar directo
    completeCheckIn(null);
  };
  
  // Función que se ejecuta después de tomar la selfie
  const completeCheckIn = (photoData) => {
    // Validación GPS desactivada temporalmente
    
    // Bloquear re-entrada si ya registró salida hoy (leer desde localStorage para datos frescos)
    const todayDate = getLocalDateStr();
    const freshRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
    const freshArray = Array.isArray(freshRecords) ? freshRecords : Object.values(freshRecords);
    const existingRecord = freshArray.find(r => r.employeeId === currentUser.id && r.date === todayDate);
    if (existingRecord && existingRecord.checkOut && existingRecord.checkOut !== '') {
      alert('⚠️ Ya registraste tu salida hoy. No puedes volver a marcar entrada el mismo día.');
      return;
    }
    if (existingRecord && existingRecord.checkIn) {
      alert('⚠️ Ya registraste tu entrada hoy.');
      return;
    }

    const now = new Date();
    // Formato compatible con todos los navegadores incluyendo Safari/Chrome iOS
    const _nowH = now.getHours(), _nowM = String(now.getMinutes()).padStart(2,'0');
    const timeString = `${String(_nowH%12||12).padStart(2,'0')}:${_nowM} ${_nowH<12?'a. m.':'p. m.'}`;
    
    // Usar horario del empleado específico
    const employeeSchedule = getTodaySchedule(currentUser.id);
    
    // FIX BUG 1: Si no tiene horario configurado, registrar entrada sin calcular atraso
    if (!employeeSchedule) {
      const newRecord = {
        id: Date.now(),
        employeeId: currentUser.id,
        employeeName: currentUser.name,
        date: getLocalDateStr(now),
        checkIn: timeString,
        checkOut: "",
        breakStart: "",
        breakEnd: "",
        status: 'on_time',
        isLate: false,
        location: 'verified',
        lateBreakReturn: false,
        earlyCheckOut: false,
        lateMinutes: 0,
        lateBreakMinutes: 0,
        earlyCheckOutMinutes: 0,
        checkInPhoto: photoData
      };
      const updatedRecords = [newRecord, ...attendanceRecords];
      const cleanRecords = updatedRecords.map(r => ({
        ...r, breakEnd: r.breakEnd ?? null, checkOut: r.checkOut ?? null, breakStart: r.breakStart ?? null
      }));
      setAttendanceRecords(cleanRecords);
      setTodayRecord(newRecord);
      setIsCheckedIn(true);
      localStorage.setItem('attendanceRecords', JSON.stringify(cleanRecords));
      if (isOnline) {
        const recordsRef = ref(database, 'attendanceRecords');
        const safeRecords = JSON.parse(JSON.stringify(cleanRecords, (k, v) => v === undefined ? "" : v));
        const recordsObj = {}; safeRecords.forEach(r => { if (r && r.id) recordsObj[r.id] = r; });
        set(recordsRef, sanitizeForFirebase(recordsObj));
      }
      alert('✅ Entrada registrada exitosamente.\n\n⚠️ Nota: No tienes horario configurado. Contacta a tu administrador.');
      return;
    }
    
    const scheduledTime = new Date();
    scheduledTime.setHours(employeeSchedule.checkIn.hour, employeeSchedule.checkIn.minute, 0, 0);
    
    // Calcular minutos de diferencia
    const minutesDifference = Math.floor((now - scheduledTime) / (1000 * 60));
    
    // Aplicar tolerancia
    const tolerance = config.tolerances?.checkIn || 0;
    const isLate = minutesDifference > tolerance;
    
    // Calcular minutos de atraso REAL (restando la tolerancia)
    const lateMinutes = isLate ? minutesDifference - tolerance : 0;
    
    const newRecord = {
      id: Date.now(),
      employeeId: currentUser.id,
      employeeName: currentUser.name,
      date: getLocalDateStr(now),
      checkIn: timeString,
      checkOut: "",
      breakStart: "",
      breakEnd: "",
      status: isLate ? 'late' : 'on_time',
      isLate: isLate,  // Campo para el badge
      location: 'verified',
      lateBreakReturn: false,
      earlyCheckOut: false,
      lateMinutes: lateMinutes,  // Minutos de atraso REAL (ya descontada la tolerancia)
      lateBreakMinutes: 0,       // Minutos de atraso en colación
      earlyCheckOutMinutes: 0,   // Minutos de salida temprana
      checkInPhoto: photoData     // Selfie de verificación
    };
    
    const updatedRecords = [newRecord, ...attendanceRecords];
    
    // Detectar atraso y generar alerta para el gerente
    if (isLate) {
      const employee = employees.find(e => e.id === currentUser.id);
      if (employee) {
        detectLateArrival(employee, now.toISOString());
      }
      
      // Mostrar banner de alarma al empleado
      setAlarmMessage('⚠️ Llegaste tarde a tu hora de entrada. Se registrará el atraso.');
        // setShowAlarmNotification(true);
 // Banner desactivado
      setTimeout(() => {
        setShowAlarmNotification(false);
        setAlarmMessage('');
      }, 5000); // Auto-cerrar después de 5 segundos
    }
    
    // Limpiar campos undefined antes de guardar
    const cleanRecords = updatedRecords.map(r => ({
      ...r,
      breakEnd: r.breakEnd ?? null,
      checkOut: r.checkOut ?? null,
      breakStart: r.breakStart ?? null
    }));
    
    setAttendanceRecords(cleanRecords);
    setTodayRecord(newRecord);
    setIsCheckedIn(true);
    localStorage.setItem('attendanceRecords', JSON.stringify(cleanRecords));
    
    // Guardar en Firebase limpio - forzar serialización
    if (isOnline) {
      const recordsRef = ref(database, 'attendanceRecords');
      // Serializar y deserializar para eliminar undefined
      const safeRecords = JSON.parse(JSON.stringify(cleanRecords, (key, value) => 
        value === undefined ? "" : value
      ));
      // Guardar como objeto {id: record} para evitar problemas con arrays en Firebase
      const recordsObj = {};
      safeRecords.forEach(r => { if (r && r.id) recordsObj[r.id] = r; });
      set(recordsRef, sanitizeForFirebase(recordsObj)).then(() => {
        console.log('✅ Guardado en Firebase sin undefined');
      }).catch(err => {
        console.error('❌ Error Firebase:', err);
      });
    }
    
    if (isLate && config.alerts.lateArrival.enabled) {
      alert(`${config.alerts.lateArrival.message}\n\nAtraso real: ${lateMinutes} minuto${lateMinutes !== 1 ? 's' : ''}\n(Tolerancia de ${tolerance} min ya aplicada)`);
    } else if (minutesDifference > 0 && minutesDifference <= tolerance) {
      alert(`✅ Entrada registrada exitosamente\n\n⏰ Llegaste ${minutesDifference} min después, pero dentro de la tolerancia de ${tolerance} min`);
    } else {
      alert('✅ Entrada registrada exitosamente');
    }
  };

  
  
  const handleBreakStart = () => {
    if (!isCheckedIn) {
      alert('⚠️ Debes marcar entrada primero');
      return;
    }
    if (isOnBreak) {
      alert('⚠️ Ya estás en colación');
      return;
    }
    
    try {
      const now = new Date();
      const timeString = now.toTimeString().slice(0, 5);
      const todayDate = getLocalDateStr(now);
      
      // CREAR ARRAY NUEVO con el registro modificado
      const updatedRecords = attendanceRecords.map(record => {
        // Solo modificar el registro del usuario actual de hoy
        if (record.employeeId === currentUser.id && 
            record.date === todayDate && 
            !record.checkOut) {
          // Crear objeto completamente nuevo con TODOS los campos
          return {
            id: record.id || 0,
            employeeId: record.employeeId || '',
            employeeName: record.employeeName || '',
            date: record.date || '',
            checkIn: record.checkIn || '',
            checkOut: "",
            breakStart: timeString || '',
            breakEnd: "",
            status: record.status || 'on_time',
            isLate: record.isLate || false,
            lateMinutes: record.lateMinutes || 0,
            lateBreakMinutes: record.lateBreakMinutes || 0,
            earlyCheckOut: record.earlyCheckOut || false,
            earlyCheckOutMinutes: record.earlyCheckOutMinutes || 0,
            checkInPhoto: record.checkInPhoto || null
          };
        }
        // Retornar otros registros sin cambios
        return record;
      });
      
      // Actualizar estado
      const updatedTodayRecord = updatedRecords.find(r => r.employeeId === currentUser.id && r.date === todayDate);
      setAttendanceRecords(updatedRecords);
      setIsOnBreak(true);
      if (updatedTodayRecord) setTodayRecord(updatedTodayRecord);
      
      // Guardar en localStorage (serializado correctamente)
      localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
      
      // Guardar en Firebase si hay conexión
      if (isOnline) {
         // FIX BUG 3: evitar que onValue pise breakStart recién guardado
        const recordsRef = ref(database, 'attendanceRecords');
        const obj1 = {}; updatedRecords.forEach(r => { if(r&&r.id) obj1[r.id]=r; }); set(recordsRef, sanitizeForFirebase(obj1))
          .then(() => console.log('✅ Colación guardada'))
          .catch(err => console.error('❌ Error:', err));
      }
      
      alert('✅ Inicio de colación registrado');
      
    } catch (error) {
      console.error('❌ Error en handleBreakStart:', error);
      alert('❌ Error al iniciar colación. Por favor intenta de nuevo.');
    }
  };
  
  const handleBreakEnd = () => {
    if (!isOnBreak) {
      alert('⚠️ No estás en colación');
      return;
    }
    
    const now = new Date();
    const timeString = now.toTimeString().slice(0, 5);
    
    const updatedRecords = attendanceRecords.map(record => {
      if (record.employeeId === currentUser.id && 
          record.date === getLocalDateStr(now) && 
          !record.checkOut) {
        return { ...record, breakEnd: timeString };
      }
      return record;
    });
    
    const updatedTodayBreakEnd = updatedRecords.find(r => r.employeeId === currentUser.id && r.date === getLocalDateStr(now));
    setAttendanceRecords(updatedRecords);
    setIsOnBreak(false);
    if (updatedTodayBreakEnd) setTodayRecord(updatedTodayBreakEnd);
    localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
    if (isOnline) {
       // FIX BUG 3
      const recordsRef = ref(database, 'attendanceRecords');
      const objR = {}; updatedRecords.forEach(r => { if(r&&r.id) objR[r.id]=r; }); set(recordsRef, sanitizeForFirebase(objR)).catch(err => console.error('❌ Error Firebase:', err));
    }
    alert('✅ Fin de colación registrado');
  };

  const handleCheckOut = () => {
    if (!todayRecord) return;
    
    const now = new Date();
    // Formato compatible con todos los navegadores incluyendo Safari/Chrome iOS
    const _nowH = now.getHours(), _nowM = String(now.getMinutes()).padStart(2,'0');
    const timeString = `${String(_nowH%12||12).padStart(2,'0')}:${_nowM} ${_nowH<12?'a. m.':'p. m.'}`;
    
    // Usar horario del empleado específico
    const employeeSchedule = getTodaySchedule(currentUser.id);
    
    // FIX BUG 1: Si no tiene horario configurado, registrar salida sin validaciones de tiempo
    if (!employeeSchedule) {
      const _coDate = getLocalDateStr(now);
      const updatedRecords = attendanceRecords.map(record => {
        const isMatch = record.id === todayRecord.id ||
          (record.employeeId === currentUser.id && record.date === _coDate && !record.checkOut);
        return isMatch ? { ...record, checkOut: timeString, earlyCheckOut: false, earlyCheckOutMinutes: 0 } : record;
      });
      setAttendanceRecords(updatedRecords);
      setTodayRecord({ ...todayRecord, checkOut: timeString });
      setIsCheckedIn(false);
      localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
      if (isOnline) {
        
        const rRef = ref(database, 'attendanceRecords');
        const obj = {}; updatedRecords.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj));
      }
      alert('✅ Salida registrada exitosamente.');
      return;
    }
    
    const scheduledCheckOut = new Date();
    scheduledCheckOut.setHours(employeeSchedule.checkOut.hour, employeeSchedule.checkOut.minute, 0);
    
    // Validar hora mínima de salida (30 min antes como tolerancia)
    const toleranceMinutes = config.tolerances?.checkOut || 30;
    const minCheckOut = new Date(scheduledCheckOut.getTime() - toleranceMinutes * 60000);
    if (now < minCheckOut) {
      const _hco = scheduledCheckOut.getHours(), _mco = String(scheduledCheckOut.getMinutes()).padStart(2,'0');
      const horaMin = `${String(_hco%12||12).padStart(2,'0')}:${_mco} ${_hco<12?'a. m.':'p. m.'}`;
      alert(`⏰ Aún no puedes registrar tu salida.\n\nHora de salida: ${horaMin}\nIntenta nuevamente a las ${horaMin}.`);
      return;
    }
    
    // Verificar si está saliendo después de su hora (horas extras)
    const overtimeMinutes = now > scheduledCheckOut ? Math.floor((now - scheduledCheckOut) / (1000 * 60)) : 0;
    
    if (overtimeMinutes > 0 && !todayRecord.overtimeAuthorized) {
      const confirmOT = window.confirm(`⚠️ Estás saliendo ${overtimeMinutes} minutos después de tu hora.\n\n¿Deseas solicitar autorización de horas extras al gerente?\n\nOK = Enviar solicitud y registrar salida\nCancelar = Registrar salida sin solicitud`);
      
      if (confirmOT) {
        const request = {
          id: Date.now(),
          employeeId: currentUser.id,
          employeeName: currentUser.name,
          date: getLocalDateStr(),
          checkOutTime: timeString,
          overtimeMinutes: overtimeMinutes,
          status: 'pending',
          requestDate: new Date().toISOString(),
          type: 'overtime'
        };
        
        const updatedRequests = [request, ...(overtimeRequests || [])];
        setOvertimeRequests(updatedRequests);
        localStorage.setItem('overtimeRequests', JSON.stringify(updatedRequests));
        
        if (isOnline) {
          const requestsRef = ref(database, 'overtimeRequests');
          const reqObj = {};
          updatedRequests.forEach(r => { if (r && r.id) reqObj[r.id] = r; });
          set(requestsRef, sanitizeForFirebase(reqObj));
        }
        
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('📋 Nueva Solicitud de Horas Extras', {
            body: `${currentUser.name} solicita ${overtimeMinutes} min de horas extras`,
            icon: '/logo192.png'
          });
        }
        
        alert('✅ Solicitud enviada al gerente. Tu salida ha sido registrada.');
      }
      // Continúa para registrar salida sin importar si envió o no la solicitud
    }
    
    // Calcular minutos de salida temprana
    const minutesBeforeScheduled = now < scheduledCheckOut ? Math.floor((scheduledCheckOut - now) / (1000 * 60)) : 0;
    
    // Aplicar tolerancia
    const tolerance = config.tolerances?.earlyCheckOut || 0;
    const earlyCheckOut = minutesBeforeScheduled > tolerance;
    
    // Calcular minutos de salida temprana REAL (restando tolerancia)
    const earlyCheckOutMinutes = earlyCheckOut ? minutesBeforeScheduled - tolerance : 0;
    
    const _coDate = getLocalDateStr();
    const updatedRecords = attendanceRecords.map(record => {
      const isMatch = record.id === todayRecord.id || 
        (record.employeeId === currentUser.id && record.date === _coDate && !record.checkOut);
      return isMatch
        ? { 
            ...record, 
            checkOut: timeString, 
            earlyCheckOut, 
            earlyCheckOutMinutes,
            overtimeMinutes: overtimeMinutes 
          }
        : record;
    });
    
    setAttendanceRecords(updatedRecords);
    setTodayRecord({ ...todayRecord, checkOut: timeString, earlyCheckOut, earlyCheckOutMinutes, overtimeMinutes });
    setIsCheckedIn(false);
    setShowAlarmNotification(false);
    localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
    if (isOnline) {
       // FIX BUG 3: ignorar el onValue causado por nuestra propia escritura
      const recordsRef = ref(database, 'attendanceRecords');
      const objR = {}; updatedRecords.forEach(r => { if(r&&r.id) objR[r.id]=r; }); set(recordsRef, sanitizeForFirebase(objR)).catch(err => console.error('❌ Error Firebase:', err));
    }
    
    if (earlyCheckOut && config.alerts.earlyDeparture.enabled) {
      alert(`${config.alerts.earlyDeparture.message}\n\nSalida temprana: ${earlyCheckOutMinutes} minuto${earlyCheckOutMinutes !== 1 ? 's' : ''}\n(Tolerancia de ${tolerance} min ya aplicada)`);
    } else if (minutesBeforeScheduled > 0 && minutesBeforeScheduled <= tolerance) {
      alert(`✅ Salida registrada exitosamente\n\n⏰ Saliste ${minutesBeforeScheduled} min antes, pero dentro de la tolerancia de ${tolerance} min`);
    } else {
      alert('✅ Salida registrada exitosamente');
    }
  };

  const handleBreakToggle = () => {
    if (!todayRecord) return;
    
    const now = new Date();
    // Formato compatible con todos los navegadores incluyendo Safari/Chrome iOS
    const _nowH = now.getHours(), _nowM = String(now.getMinutes()).padStart(2,'0');
    const timeString = `${String(_nowH%12||12).padStart(2,'0')}:${_nowM} ${_nowH<12?'a. m.':'p. m.'}`;
    const todayDate = getLocalDateStr(now);
    
    // FIX BUG 2: Leer estado FRESCO desde localStorage para bloquear doble colación
    const freshRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
    const freshArray = Array.isArray(freshRecords) ? freshRecords : Object.values(freshRecords);
    const freshTodayRecord = freshArray.find(r => r && r.employeeId === currentUser.id && r.date === todayDate);
    
    if (!isOnBreak && freshTodayRecord?.breakStart && freshTodayRecord.breakStart !== '') {
      alert('⚠️ Ya registraste el inicio de colación hoy. No puedes iniciarlo nuevamente.');
      return;
    }
    
    // Usar horario del empleado específico
    const employeeSchedule = getTodaySchedule(currentUser.id);
    
    // FIX BUG 1: Si no tiene horario, registrar colación sin validaciones de tiempo
    if (!employeeSchedule) {
      const updatedRecords = attendanceRecords.map(record => {
        const isMatch = record.id === todayRecord.id ||
          (record.employeeId === currentUser.id && record.date === todayDate && !record.checkOut);
        return isMatch ? {
          ...record,
          breakStart: !isOnBreak ? timeString : record.breakStart,
          breakEnd: isOnBreak ? timeString : record.breakEnd,
        } : record;
      });
      const newTodayRecord = updatedRecords.find(r =>
        r.id === todayRecord.id || (r.employeeId === currentUser.id && r.date === todayDate && !r.checkOut)
      );
      setAttendanceRecords(updatedRecords);
      setIsOnBreak(!isOnBreak);
      if (newTodayRecord) setTodayRecord(newTodayRecord);
      localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
      if (isOnline) {
        
        const rRef = ref(database, 'attendanceRecords');
        const obj = {}; updatedRecords.forEach(r => { if(r&&r.id) obj[r.id]=r; }); set(rRef, sanitizeForFirebase(obj));
      }
      alert(!isOnBreak ? '☕ Colación iniciada' : '✅ Colación finalizada');
      return;
    }
    
    // Validación de horario
    if (!todayRecord.breakStart) {
      // Quiere iniciar colación - validar hora mínima
      const scheduledBreakStart = new Date();
      scheduledBreakStart.setHours(employeeSchedule.breakStart.hour, employeeSchedule.breakStart.minute, 0);
      const toleranceMinutes = config.tolerances?.breakStart || 15;
      const minBreakStart = new Date(scheduledBreakStart.getTime() - toleranceMinutes * 60000);
      if (now < minBreakStart) {
        const _hbs = scheduledBreakStart.getHours(), _mbs = String(scheduledBreakStart.getMinutes()).padStart(2,'0');
        const horaMin = `${String(_hbs%12||12).padStart(2,'0')}:${_mbs} ${_hbs<12?'a. m.':'p. m.'}`;
        alert(`⏰ Aún no es hora de colación.\n\nHora de colación: ${horaMin}\nIntenta nuevamente a las ${horaMin}.`);
        return;
      }
    } else {
      // Quiere terminar colación - validar hora mínima
      const scheduledBreakEnd = new Date();
      scheduledBreakEnd.setHours(employeeSchedule.breakEnd.hour, employeeSchedule.breakEnd.minute, 0);
      const toleranceMinutes = config.tolerances?.breakReturn || 15;
      const minBreakEnd = new Date(scheduledBreakEnd.getTime() - toleranceMinutes * 60000);
      if (now < minBreakEnd) {
        const _hbe = scheduledBreakEnd.getHours(), _mbe = String(scheduledBreakEnd.getMinutes()).padStart(2,'0');
        const horaMin = `${String(_hbe%12||12).padStart(2,'0')}:${_mbe} ${_hbe<12?'a. m.':'p. m.'}`;
        alert(`⏰ Aún no puedes terminar la colación.\n\nFin de colación: ${horaMin}\nIntenta nuevamente a las ${horaMin}.`);
        return;
      }
    }
    
    // Verificar si regresa tarde de colación
    let lateBreakReturn = false;
    let lateBreakMinutes = 0;
    
    if (isOnBreak) {
      // Usar horario fresco del empleado para calcular duración permitida de colación
      const freshSchedule = getTodaySchedule(currentUser.id) || employeeSchedule;

      // Duración permitida de colación en minutos (ej: 15:30 a 17:30 = 120 min)
      const allowedBreakDuration = 
        (freshSchedule.breakEnd.hour * 60 + freshSchedule.breakEnd.minute) -
        (freshSchedule.breakStart.hour * 60 + freshSchedule.breakStart.minute);

      // Hora real en que inició colación (del registro de hoy)
      const actualBreakStartStr = todayRecord?.breakStart || '';
      let actualBreakStartMin = freshSchedule.breakStart.hour * 60 + freshSchedule.breakStart.minute;
      if (actualBreakStartStr) {
        const clean = actualBreakStartStr.trim();
        const isPM = /p[\.\s]*m/i.test(clean);
        const timePart = clean.replace(/[^0-9:]/g,'').trim();
        const [bh, bm] = timePart.split(':').map(Number);
        if (!isNaN(bh) && !isNaN(bm)) {
          let hour = bh;
          if (isPM && bh !== 12) hour = bh + 12;
          if (!isPM && bh === 12) hour = 0;
          actualBreakStartMin = hour * 60 + bm;
        }
      }

      // Hora máxima permitida de regreso = hora real de salida + duración permitida
      const maxReturnMin = actualBreakStartMin + allowedBreakDuration;
      const nowMin = now.getHours() * 60 + now.getMinutes();

      // Calcular diferencia
      const minutesDifference = nowMin - maxReturnMin;

      // Aplicar tolerancia
      const tolerance = config.tolerances?.breakReturn || 0;
      lateBreakReturn = minutesDifference > tolerance;

      // Calcular minutos de atraso REAL (restando tolerancia)
      lateBreakMinutes = lateBreakReturn ? minutesDifference - tolerance : 0;
    }
    
    const breakTodayDate = getLocalDateStr();
    const updatedRecords = attendanceRecords.map(record => {
      const isMatch = record.id === todayRecord.id || 
        (record.employeeId === currentUser.id && record.date === breakTodayDate && !record.checkOut);
      return isMatch
        ? { 
            ...record, 
            breakStart: !isOnBreak ? timeString : record.breakStart,
            breakEnd: isOnBreak ? timeString : record.breakEnd,
            lateBreakReturn: lateBreakReturn || record.lateBreakReturn,
            lateBreakMinutes: lateBreakReturn ? lateBreakMinutes : (record.lateBreakMinutes || 0)
          }
        : record;
    });
    
    const newTodayRecord = updatedRecords.find(r => 
      r.id === todayRecord.id || 
      (r.employeeId === currentUser.id && r.date === breakTodayDate && !r.checkOut)
    );
    setAttendanceRecords(updatedRecords);
    setIsOnBreak(!isOnBreak);
    if (newTodayRecord) setTodayRecord(newTodayRecord);
    localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
    if (isOnline) {
       // FIX BUG 3: ignorar el onValue causado por nuestra propia escritura
      const recordsRef = ref(database, 'attendanceRecords');
      const objR = {}; updatedRecords.forEach(r => { if(r&&r.id) objR[r.id]=r; }); set(recordsRef, sanitizeForFirebase(objR)).catch(err => console.error('❌ Error Firebase:', err));
    }
    
    if (!isOnBreak) {
      // Verificar si inicia colación fuera de su horario
      const scheduledBreakStart = new Date();
      scheduledBreakStart.setHours(employeeSchedule.breakStart.hour, employeeSchedule.breakStart.minute, 0, 0);
      const diffBreakStart = Math.floor((now - scheduledBreakStart) / (1000 * 60));
      if (diffBreakStart > 5) {
        alert(`☕ Colación iniciada

⏰ Iniciaste colación ${diffBreakStart} minutos después de tu horario (${String(employeeSchedule.breakStart.hour).padStart(2,'0')}:${String(employeeSchedule.breakStart.minute).padStart(2,'0')}).`);
      } else if (diffBreakStart < -5) {
        alert(`☕ Colación iniciada

⏰ Iniciaste colación ${Math.abs(diffBreakStart)} minutos antes de tu horario (${String(employeeSchedule.breakStart.hour).padStart(2,'0')}:${String(employeeSchedule.breakStart.minute).padStart(2,'0')}).`);
      } else {
        alert('☕ Colación iniciada');
      }
    } else {
      const tolerance = config.tolerances?.breakReturn || 0;
      const scheduledBreakEnd = new Date();
      scheduledBreakEnd.setHours(employeeSchedule.breakEnd.hour, employeeSchedule.breakEnd.minute, 0);
      const minutesDifference = Math.floor((now - scheduledBreakEnd) / (1000 * 60));
      
      if (lateBreakReturn && config.alerts.lateBreakReturn.enabled) {
        alert(`${config.alerts.lateBreakReturn.message}\n\nAtraso real: ${lateBreakMinutes} minuto${lateBreakMinutes !== 1 ? 's' : ''}\n(Tolerancia de ${tolerance} min ya aplicada)`);
      } else if (minutesDifference > 0 && minutesDifference <= tolerance) {
        alert(`✅ Colación finalizada\n\n⏰ Regresaste ${minutesDifference} min tarde, pero dentro de la tolerancia de ${tolerance} min`);
      } else {
        alert('✅ Colación finalizada');
      }
    }
  };

  const exportData = () => {
    const csvHeader = 'Fecha,Empleado,Entrada,Salida,Colación Inicio,Colación Fin,Estado,Atraso Entrada (min),Atraso Colación (min),Salida Temprana (min),Total Minutos Perdidos,Total Horas Perdidas\n';
    const csvContent = attendanceRecords.map(r => {
      const lateMin = r.lateMinutes || 0;
      const lateBreakMin = r.lateBreakMinutes || 0;
      const earlyCheckOutMin = r.earlyCheckOutMinutes || 0;
      const totalMin = lateMin + lateBreakMin + earlyCheckOutMin;
      const totalHours = (totalMin / 60).toFixed(2);
      
      return `${r.date},${r.employeeName},${r.checkIn},${r.checkOut || 'N/A'},${r.breakStart || 'N/A'},${r.breakEnd || 'N/A'},${r.status === 'on_time' ? 'A tiempo' : 'Atraso'},${lateMin},${lateBreakMin},${earlyCheckOutMin},${totalMin},${totalHours}`;
    }).join('\n');
    
    const fullCsv = csvHeader + csvContent;
    const blob = new Blob([fullCsv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `asistencia_${getLocalDateStr()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    alert('📊 Archivo CSV descargado exitosamente');
  };

  // ── GENERADOR PDF ──
  const generatePDF = async (targetEmployeeId = null) => {
    // Cargar jsPDF dinámicamente
    if (!window.jspdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210; // A4 width mm
    const now = new Date();
    const monthName = now.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    const companyName = 'Control de Asistencia';

    const targetEmployees = targetEmployeeId
      ? employees.filter(e => e.id === targetEmployeeId)
      : employees.filter(e => e.status === 'active');

    let isFirstPage = true;

    for (const emp of targetEmployees) {
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;

      // ── HEADER ──
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, W, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName, 14, 11);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Reporte Mensual — ${monthName}`, 14, 19);
      doc.setFontSize(9);
      doc.text(`Generado: ${now.toLocaleDateString('es-CL')} ${now.toLocaleTimeString('es-CL', {hour:'2-digit', minute:'2-digit'})}`, W - 14, 19, {align:'right'});

      // ── EMPLOYEE INFO ──
      let y = 36;
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(10, y - 5, W - 20, 22, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(emp.name, 16, y + 5);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text(emp.position || '', 16, y + 11);
      doc.text(`Código: EMP${String(emp.id).padStart(3,'0')}`, W - 14, y + 5, {align:'right'});

      y += 28;

      // ── RESUMEN STATS ──
      const empRecords = attendanceRecords.filter(r =>
        r.employeeId === emp.id &&
        r.date && r.date.startsWith(getLocalDateStr().substring(0,7))
      );
      const totalDays = empRecords.length;
      const lateDays = empRecords.filter(r => r.isLate).length;
      const totalLateMin = empRecords.reduce((s, r) => s + (r.lateMinutes || 0), 0);
      const parseTimeMin = (str) => {
        if (!str || !str.trim()) return null;
        const _t = str.replace(/[^0-9:]/g,'').trim();
        const _parts = _t.split(':').map(Number);
        const _h2 = _parts[0];
        const isPM = /p[\s.]*m/i.test(str) && _h2 !== 12;
        const isNoon = _h2 === 12 && /p[\s.]*m/i.test(str);
        const isMidnight = _h2 === 12 && /a[\s.]*m/i.test(str);
        const t = str.replace(/[^0-9:]/g,'').trim();
        const parts = t.split(':').map(Number);
        if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
        let hour = parts[0];
        if (isPM || isNoon) hour = parts[0] === 12 ? 12 : parts[0] + 12;
        if (!isPM && !isNoon && parts[0] === 12) hour = 0;
        return hour * 60 + parts[1];
      };
      const totalHours = empRecords.reduce((s, r) => {
        if (!r.checkIn || !r.checkOut) return s;
        const inMin = parseTimeMin(r.checkIn);
        const outMin = parseTimeMin(r.checkOut);
        if (inMin === null || outMin === null) return s;
        // Subtract break
        let gross = outMin > inMin ? outMin - inMin : outMin + 1440 - inMin;
        if (r.breakStart && r.breakEnd) {
          const bs = parseTimeMin(r.breakStart);
          const be = parseTimeMin(r.breakEnd);
          if (bs !== null && be !== null) gross -= (be > bs ? be - bs : be + 1440 - bs);
        }
        gross -= (r.lateMinutes || 0);
        return s + Math.max(0, gross);
      }, 0);
      const punctuality = totalDays > 0 ? Math.round(((totalDays - lateDays) / totalDays) * 100) : 100;

      const stats = [
        { label: 'Días trabajados', value: String(totalDays), color: [59, 130, 246] },
        { label: 'Días con atraso', value: String(lateDays), color: [239, 68, 68] },
        { label: 'Min. atraso total', value: String(totalLateMin), color: [245, 158, 11] },
        { label: 'Horas trabajadas', value: (totalHours/60).toFixed(1)+'h', color: [34, 197, 94] },
        { label: 'Puntualidad', value: punctuality+'%', color: punctuality >= 90 ? [34,197,94] : punctuality >= 70 ? [245,158,11] : [239,68,68] },
      ];

      const boxW = (W - 20 - 12) / 5;
      stats.forEach((st, i) => {
        const bx = 10 + i * (boxW + 3);
        doc.setFillColor(30, 41, 59);
        doc.roundedRect(bx, y, boxW, 20, 2, 2, 'F');
        doc.setFillColor(...st.color);
        doc.roundedRect(bx, y, boxW, 3, 1, 1, 'F');
        doc.setTextColor(...st.color);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(st.value, bx + boxW/2, y + 12, {align:'center'});
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(st.label, bx + boxW/2, y + 18, {align:'center'});
      });

      y += 28;

      // ── ATTENDANCE TABLE ──
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('DETALLE DE REGISTROS', 14, y);
      y += 5;

      // Table header
      const cols = ['Fecha', 'Entrada', 'Salida', 'Col. Inicio', 'Col. Fin', 'Atraso', 'Estado'];
      const colW = [28, 25, 25, 25, 25, 20, 22];
      let cx = 10;
      doc.setFillColor(30, 41, 59);
      doc.rect(10, y, W-20, 7, 'F');
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(7.5);
      cols.forEach((col, i) => {
        doc.text(col, cx + 2, y + 5);
        cx += colW[i];
      });
      y += 7;

      // Table rows
      const sortedRecords = [...empRecords].sort((a,b) => a.date?.localeCompare(b.date));
      sortedRecords.forEach((r, idx) => {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFillColor(idx % 2 === 0 ? 15 : 22, idx % 2 === 0 ? 23 : 30, idx % 2 === 0 ? 42 : 50);
        doc.rect(10, y, W-20, 6.5, 'F');
        const isLate = r.isLate || r.status === 'late';
        const row = [
          r.date || '', r.checkIn || '--', r.checkOut || '--',
          r.breakStart || '--', r.breakEnd || '--',
          r.lateMinutes ? `${r.lateMinutes} min` : '0 min',
          isLate ? 'Atraso' : 'A tiempo'
        ];
        cx = 10;
        row.forEach((cell, i) => {
          if (i === 6) doc.setTextColor(isLate ? 239 : 34, isLate ? 68 : 197, isLate ? 68 : 94);
          else doc.setTextColor(220, 220, 220);
          doc.setFontSize(7);
          doc.setFont('helvetica', i === 6 ? 'bold' : 'normal');
          doc.text(String(cell), cx + 2, y + 4.5);
          cx += colW[i];
        });
        y += 6.5;
      });

      if (empRecords.length === 0) {
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(9);
        doc.text('Sin registros este mes', W/2, y + 8, {align:'center'});
        y += 15;
      }

      // ── SIMPLE BAR CHART ──
      y += 8;
      if (y > 230) { doc.addPage(); y = 20; }
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('ASISTENCIA DEL MES', 14, y);
      y += 6;
      const chartW = W - 20;
      const daysInMonth = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
      const barW = Math.max(3, (chartW - 10) / daysInMonth - 1);
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${getLocalDateStr().substring(0,7)}-${String(d).padStart(2,'0')}`;
        const rec = empRecords.find(r => r.date === dateStr);
        const bx = 10 + (d-1) * ((chartW-10)/daysInMonth);
        if (rec) {
          doc.setFillColor(rec.isLate ? 239 : 34, rec.isLate ? 68 : 197, rec.isLate ? 68 : 94);
          doc.rect(bx, y + 14 - 10, barW, 10, 'F');
        } else {
          doc.setFillColor(30, 41, 59);
          doc.rect(bx, y + 14 - 4, barW, 4, 'F');
        }
        if (d % 5 === 0 || d === 1) {
          doc.setTextColor(100, 100, 100);
          doc.setFontSize(5.5);
          doc.text(String(d), bx + barW/2, y + 16, {align:'center'});
        }
      }
      // Legend
      y += 20;
      doc.setFillColor(34, 197, 94); doc.rect(14, y, 6, 4, 'F');
      doc.setTextColor(150,150,150); doc.setFontSize(7); doc.text('A tiempo', 22, y+3.5);
      doc.setFillColor(239, 68, 68); doc.rect(50, y, 6, 4, 'F');
      doc.text('Con atraso', 58, y+3.5);
      doc.setFillColor(30, 41, 59); doc.rect(86, y, 6, 4, 'F');
      doc.text('Sin registro', 94, y+3.5);

      // ── SIGNATURE AREA ──
      y += 18;
      if (y > 245) { doc.addPage(); y = 20; }
      doc.setDrawColor(70, 80, 100);
      doc.setLineWidth(0.3);
      doc.line(14, y + 15, 80, y + 15);
      doc.line(W/2 + 10, y + 15, W - 14, y + 15);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.text('Firma Empleado', 14, y + 20);
      doc.text('Firma Gerente / RRHH', W/2 + 10, y + 20);
      doc.setFontSize(7);
      doc.text(emp.name, 14, y + 25);
      doc.text('Nombre y Cargo', W/2 + 10, y + 25);

      // Page number
      const pageCount = doc.internal.getNumberOfPages();
      doc.setPage(pageCount);
      doc.setFontSize(7);
      doc.setTextColor(80,80,80);
      doc.text(`Página ${pageCount}`, W - 14, 293, {align:'right'});
    }

    const filename = targetEmployeeId
      ? `reporte_${employees.find(e=>e.id===targetEmployeeId)?.name?.replace(/ /g,'_')}_${getLocalDateStr().substring(0,7)}.pdf`
      : `reporte_todos_empleados_${getLocalDateStr().substring(0,7)}.pdf`;

    doc.save(filename);
    alert(`✅ PDF generado: ${filename}`);
  };

  // ── RECALCULAR ATRASOS ──
  const recalculateLateMinutes = () => {
    if (!window.confirm('¿Recalcular los atrasos de todos los registros usando los horarios actuales de cada empleado?\n\nEsto corregirá registros calculados con horario incorrecto.')) return;

    let corrected = 0;
    const tolerance = config.tolerances?.checkIn || 0;

    const updated = attendanceRecords.map(record => {
      const employee = employees.find(e => e.id === record.employeeId);
      if (!employee || !record.checkIn || !record.date) return record;

      // Obtener horario del empleado para esa fecha específica
      const getScheduleForDate = (emp, dateStr) => {
        let freshEmp = emp;
        try {
          const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
          const found = lsEmps.find(e => e.id === emp.id);
          if (found) freshEmp = found;
        } catch(e) {}

        // Monthly schedule para esa fecha
        if (freshEmp.monthlySchedule && freshEmp.monthlySchedule[dateStr]) {
          const ms = freshEmp.monthlySchedule[dateStr];
          if (ms && ms.enabled && ms.start) {
            const [h, m] = ms.start.split(':').map(Number);
            return { hour: h, minute: m };
          }
        }
        // Weekly schedule
        if (freshEmp.useFlexibleSchedule && freshEmp.weeklySchedule) {
          const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
          const dayOfWeek = new Date(dateStr + 'T12:00:00').getDay();
          const daySchedule = freshEmp.weeklySchedule[dayNames[dayOfWeek]];
          if (daySchedule && daySchedule.enabled && daySchedule.start) {
            const [h, m] = daySchedule.start.split(':').map(Number);
            return { hour: h, minute: m };
          }
        }
        // Fallback schedules fijos
        if (freshEmp.schedules?.checkIn && typeof freshEmp.schedules.checkIn.hour === 'number') {
          return freshEmp.schedules.checkIn;
        }
        return null;
      };

      const checkInSchedule = getScheduleForDate(employee, record.date);
      if (!checkInSchedule) return record;

      // Parsear hora registrada (formato 'HH:MM a. m.' o 'HH:MM p. m.')
      const parseTimeStr = (str) => {
        if (!str || str.trim() === '') return null;
        const clean = str.trim();
        const _tc = clean.replace(/[^0-9:]/g,'').trim();
        const _hc = parseInt(_tc.split(':')[0]);
        const isPM = /p[\s.]*m/i.test(clean) && _hc !== 12;
        const isNoon = /^12:/.test(clean.replace(/[^0-9:]/g,'').trim()) && /p[\.\s]*m/i.test(clean);
        const isMidnight = /^12:/.test(clean.replace(/[^0-9:]/g,'').trim()) && /a[\.\s]*m/i.test(clean);
        const timePart = clean.replace(/[^0-9:]/g,'').trim();
        const [h, m] = timePart.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return null;
        let hour = h;
        if (isPM || isNoon) hour = h === 12 ? 12 : h + 12;
        if (isMidnight) hour = 0;
        return { hour, minute: m };
      };

      const checkInTime = parseTimeStr(record.checkIn);
      if (!checkInTime) return record;

      // Recalcular atraso entrada
      const toleranceIn = config.tolerances?.checkIn || 0;
      const scheduledInMin = checkInSchedule.hour * 60 + checkInSchedule.minute;
      const actualInMin = checkInTime.hour * 60 + checkInTime.minute;
      const diffIn = actualInMin - scheduledInMin;
      const isLate = diffIn > toleranceIn;
      const newLateMinutes = isLate ? diffIn - toleranceIn : 0;

      // Recalcular atraso colación
      let newLateBreakMinutes = record.lateBreakMinutes || 0;
      let newLateBreakReturn = record.lateBreakReturn || false;
      if (record.breakEnd && record.breakEnd.trim() !== '') {
        const breakEndTime = parseTimeStr(record.breakEnd);
        // Get lunchEnd from schedule for that date
        const getLunchEnd = (emp, dateStr) => {
          let freshEmp = emp;
          try { const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]'); const found = lsEmps.find(e => e.id === emp.id); if (found) freshEmp = found; } catch(e) {}
          if (freshEmp.monthlySchedule?.[dateStr]?.lunchEnd) {
            const [h,m] = freshEmp.monthlySchedule[dateStr].lunchEnd.split(':').map(Number);
            return { hour: h, minute: m };
          }
          if (freshEmp.useFlexibleSchedule && freshEmp.weeklySchedule) {
            const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
            const dow = new Date(dateStr + 'T12:00:00').getDay();
            const ds = freshEmp.weeklySchedule[dayNames[dow]];
            if (ds?.lunchEnd) { const [h,m] = ds.lunchEnd.split(':').map(Number); return { hour: h, minute: m }; }
          }
          return { hour: config.schedules.breakEnd.hour, minute: config.schedules.breakEnd.minute };
        };
        const lunchEndSched = getLunchEnd(employee, record.date);
        if (breakEndTime) {
          const toleranceBreak = config.tolerances?.breakReturn || 0;

          // Calcular duración permitida de colación
          const getLunchStart = (emp, dateStr) => {
            let freshEmp = emp;
            try { const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]'); const found = lsEmps.find(e => e.id === emp.id); if (found) freshEmp = found; } catch(e) {}
            if (freshEmp.monthlySchedule?.[dateStr]?.lunchStart) {
              const [h,m] = freshEmp.monthlySchedule[dateStr].lunchStart.split(':').map(Number);
              return { hour: h, minute: m };
            }
            if (freshEmp.useFlexibleSchedule && freshEmp.weeklySchedule) {
              const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
              const dow = new Date(dateStr + 'T12:00:00').getDay();
              const ds = freshEmp.weeklySchedule[dayNames[dow]];
              if (ds?.lunchStart) { const [h,m] = ds.lunchStart.split(':').map(Number); return { hour: h, minute: m }; }
            }
            return { hour: config.schedules.breakStart.hour, minute: config.schedules.breakStart.minute };
          };
          const lunchStartSched = getLunchStart(employee, record.date);
          const allowedBreakDuration = (lunchEndSched.hour * 60 + lunchEndSched.minute) - (lunchStartSched.hour * 60 + lunchStartSched.minute);

          // Hora real en que inició colación
          const actualBreakStartTime = parseTimeStr(record.breakStart);
          const actualBreakStartMin = actualBreakStartTime 
            ? actualBreakStartTime.hour * 60 + actualBreakStartTime.minute
            : lunchStartSched.hour * 60 + lunchStartSched.minute;

          // Hora máxima de regreso = hora real de inicio + duración permitida
          const maxReturnMin = actualBreakStartMin + allowedBreakDuration;
          const actualBreakEndMin = breakEndTime.hour * 60 + breakEndTime.minute;
          const diffBreak = actualBreakEndMin - maxReturnMin;
          newLateBreakReturn = diffBreak > toleranceBreak;
          newLateBreakMinutes = newLateBreakReturn ? diffBreak - toleranceBreak : 0;
        }
      }

      if (newLateMinutes !== record.lateMinutes || newLateBreakMinutes !== record.lateBreakMinutes) {
        corrected++;
        return {
          ...record,
          lateMinutes: newLateMinutes,
          isLate: isLate,
          status: isLate ? 'late' : 'on_time',
          lateBreakMinutes: newLateBreakMinutes,
          lateBreakReturn: newLateBreakReturn
        };
      }
      return record;
    });

    // Guardar
    setAttendanceRecords(updated);
    localStorage.setItem('attendanceRecords', JSON.stringify(updated));
    if (navigator.onLine) {
      const recordsRef = ref(database, 'attendanceRecords');
      const obj = {};
      updated.forEach(r => { if (r && r.id) obj[r.id] = r; });
      set(recordsRef, sanitizeForFirebase(obj));
    }

    alert(`✅ Recalculación completada.\n${corrected} registro(s) corregido(s).`);
  };

  // ── GENERADOR PDF EVALUACIÓN DE DESEMPEÑO ──
  const generatePerformancePDF = async (targetEmployeeId = null) => {
    if (!window.jspdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        script.onload = resolve; script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;
    const now = new Date();
    const monthStr = getLocalDateStr().substring(0, 7);
    const monthName = now.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });

    const targetEmployees = targetEmployeeId
      ? (Array.isArray(employees) ? employees : []).filter(e => e.id === targetEmployeeId)
      : (Array.isArray(employees) ? employees : []).filter(e => e.status === 'active');

    let isFirstPage = true;

    for (const emp of targetEmployees) {
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;

      // ── HEADER ──
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, W, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16); doc.setFont('helvetica', 'bold');
      doc.text('Control de Asistencia — Evaluación de Desempeño', 14, 11);
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text(`Período: ${monthName}`, 14, 19);
      doc.text(`Generado: ${now.toLocaleDateString('es-CL')} ${now.toLocaleTimeString('es-CL',{hour:'2-digit',minute:'2-digit'})}`, W-14, 19, {align:'right'});

      // ── EMPLOYEE INFO ──
      let y = 36;
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(10, y-5, W-20, 22, 3, 3, 'F');
      doc.setTextColor(255,255,255); doc.setFontSize(13); doc.setFont('helvetica','bold');
      doc.text(emp.name, 16, y+5);
      doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(148,163,184);
      doc.text(emp.position || '', 16, y+11);
      doc.text(`Código: EMP${String(emp.id).padStart(3,'0')}`, W-14, y+5, {align:'right'});
      y += 28;

      // ── CALCULAR SCORE ──
      const empTasks = (Array.isArray(tasks) ? tasks : []).filter(t => String(t.assignedTo) === String(emp.id));
      const totalT = empTasks.length;
      const completedT = empTasks.filter(t => t.status === 'done').length;
      const approvedT = empTasks.filter(t => t.reviewStatus === 'approved').length;
      const rejectedT = empTasks.filter(t => t.reviewStatus === 'rejected').length;
      const pendingT = empTasks.filter(t => t.status === 'pending').length;
      const taskRate = totalT > 0 ? Math.round((completedT/totalT)*100) : 100;
      const approvalRate = completedT > 0 ? Math.round((approvedT/completedT)*100) : 100;
      const empRecs = (Array.isArray(attendanceRecords) ? attendanceRecords : []).filter(r => r.employeeId === emp.id && r.date?.startsWith(monthStr));
      const lateC = empRecs.filter(r => r.isLate).length;
      const punctRate = empRecs.length > 0 ? Math.round(((empRecs.length-lateC)/empRecs.length)*100) : 100;
      const totalLateMin = empRecs.reduce((s,r) => s+(r.lateMinutes||0), 0);
      const productsRep = (Array.isArray(missingProducts) ? missingProducts : []).filter(p => p.reportedBy === emp.name).length;
      const score = Math.round((taskRate*0.4)+(punctRate*0.3)+(approvalRate*0.2)+(Math.min(productsRep*10,100)*0.1));
      const scoreLabel = score >= 90 ? 'EXCELENTE' : score >= 75 ? 'BUENO' : score >= 60 ? 'REGULAR' : 'DEFICIENTE';
      const scoreColor = score >= 90 ? [34,197,94] : score >= 75 ? [59,130,246] : score >= 60 ? [245,158,11] : [239,68,68];

      // ── SCORE VISUAL ──
      doc.setFillColor(...scoreColor);
      doc.roundedRect(10, y, 50, 35, 4, 4, 'F');
      doc.setTextColor(255,255,255); doc.setFontSize(30); doc.setFont('helvetica','bold');
      doc.text(String(score), 35, y+18, {align:'center'});
      doc.setFontSize(8);
      doc.text(scoreLabel, 35, y+27, {align:'center'});

      // Score label below score box
      doc.setTextColor(...scoreColor); doc.setFontSize(8); doc.setFont('helvetica','bold');
      doc.text(`${score}/100`, 35, y+30, {align:'center'});
      doc.setFontSize(7); doc.setFont('helvetica','normal'); doc.setTextColor(200,200,200);
      doc.text(scoreLabel, 35, y+35, {align:'center'});

      // Score bar on the right side
      doc.setFillColor(30,41,59); doc.roundedRect(66, y+2, W-76, 8, 2, 2, 'F');
      doc.setFillColor(...scoreColor); doc.roundedRect(66, y+2, Math.max(4,(W-76)*score/100), 8, 2, 2, 'F');
      doc.setTextColor(...scoreColor); doc.setFontSize(8); doc.setFont('helvetica','bold');
      doc.text(`Puntuación: ${score}/100`, 70, y+7.5);

      // Metrics grid below score (full width)
      const metrics = [
        {label:'Tareas completadas', value:`${completedT}/${totalT}`, pct:`${taskRate}%`, weight:'40%', color:[34,197,94]},
        {label:'Aprobadas gerente', value:`${approvedT}/${completedT}`, pct:`${approvalRate}%`, weight:'20%', color:[59,130,246]},
        {label:'Puntualidad asistencia', value:`${empRecs.length-lateC}/${empRecs.length}`, pct:`${punctRate}%`, weight:'30%', color:[245,158,11]},
        {label:'Productos reportados', value:String(productsRep), pct:'10%', weight:'10%', color:[139,92,246]},
      ];
      const mW = (W-20)/4 - 3;
      metrics.forEach((m, i) => {
        const mx = 10 + i*(mW+4);
        const my = y + 16;
        doc.setFillColor(15,23,42); doc.roundedRect(mx, my, mW, 26, 2, 2, 'F');
        doc.setFillColor(...m.color); doc.roundedRect(mx, my, mW, 3, 1, 1, 'F');
        doc.setTextColor(...m.color); doc.setFontSize(13); doc.setFont('helvetica','bold');
        doc.text(m.value, mx+mW/2, my+12, {align:'center'});
        doc.setFontSize(7);
        doc.text(m.pct, mx+mW/2, my+18, {align:'center'});
        doc.setTextColor(148,163,184); doc.setFontSize(6); doc.setFont('helvetica','normal');
        doc.text(m.label, mx+mW/2, my+22, {align:'center'});
        doc.setTextColor(80,80,80);
        doc.text(`Peso: ${m.weight}`, mx+mW/2, my+26, {align:'center'});
      });
      y += 50;

      // ── DETALLE TAREAS ──
      doc.setFillColor(30,41,59); doc.rect(10, y, W-20, 7, 'F');
      doc.setTextColor(148,163,184); doc.setFontSize(9); doc.setFont('helvetica','bold');
      doc.text('DETALLE DE TAREAS', 14, y+5);
      y += 7;

      if (empTasks.length === 0) {
        doc.setFillColor(15,23,42); doc.rect(10, y, W-20, 10, 'F');
        doc.setTextColor(100,100,100); doc.setFontSize(8);
        doc.text('Sin tareas asignadas este mes', W/2, y+7, {align:'center'});
        y += 14;
      } else {
        const cols = ['Tarea', 'Prioridad', 'Estado', 'Revisión', 'F.Límite'];
        const colW = [74, 22, 28, 26, 30];
        let cx = 10;
        doc.setFillColor(20,30,50); doc.rect(10, y, W-20, 7, 'F');
        doc.setTextColor(148,163,184); doc.setFontSize(7.5); doc.setFont('helvetica','bold');
        cols.forEach((c,i) => { doc.text(c, cx+2, y+5); cx += colW[i]; });
        y += 7;
        empTasks.forEach((task, idx) => {
          if (y > 260) { doc.addPage(); y = 20; }
          doc.setFillColor(idx%2===0?15:22, idx%2===0?23:30, idx%2===0?42:50);
          doc.rect(10, y, W-20, 7, 'F');
          const statusColor = task.status==='done'?[34,197,94]:task.status==='not_done'?[239,68,68]:[245,158,11];
          const reviewColor = task.reviewStatus==='approved'?[34,197,94]:task.reviewStatus==='rejected'?[239,68,68]:[100,100,100];
          const prioColor = task.priority==='high'?[239,68,68]:task.priority==='medium'?[245,158,11]:[34,197,94];
          const prioLabel = task.priority==='high'?'Alta':task.priority==='medium'?'Media':'Baja';
          const statusLabel2 = task.status==='done'?'Completada':task.status==='not_done'?'No completada':'Pendiente';
          const reviewLabel = task.reviewStatus==='approved'?'Aprobada':task.reviewStatus==='rejected'?'Rechazada':'Sin revisar';
          cx = 10;
          const title = (task.title||'').substring(0,40);
          doc.setTextColor(220,220,220); doc.setFontSize(7); doc.setFont('helvetica','normal');
          doc.text(title, cx+2, y+4.5); cx += colW[0];
          doc.setTextColor(...prioColor); doc.setFont('helvetica','bold');
          doc.text(prioLabel, cx+2, y+4.5); cx += colW[1];
          doc.setTextColor(...statusColor);
          doc.text(statusLabel2, cx+2, y+4.5); cx += colW[2];
          doc.setTextColor(...reviewColor);
          doc.text(reviewLabel, cx+2, y+4.5); cx += colW[3];
          doc.setTextColor(150,150,150); doc.setFont('helvetica','normal');
          doc.text(task.dueDate ? `${task.dueDate} ${task.dueTime||''}` : '--', cx+2, y+4.5);
          y += 7;
        });
      }

      // ── ASISTENCIA DEL MES ──
      y += 6;
      if (y > 220) { doc.addPage(); y = 20; }
      doc.setFillColor(30,41,59); doc.rect(10, y, W-20, 7, 'F');
      doc.setTextColor(148,163,184); doc.setFontSize(9); doc.setFont('helvetica','bold');
      doc.text(`ASISTENCIA DEL MES — ${empRecs.length} días trabajados, ${lateC} atrasos, ${totalLateMin} min perdidos`, 14, y+5);
      y += 12;

      // Mini bar chart
      const daysInMonth = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
      const barW2 = Math.max(3, (W-20)/daysInMonth - 1);
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${monthStr}-${String(d).padStart(2,'0')}`;
        const rec = empRecs.find(r => r.date === dateStr);
        const bx = 10 + (d-1)*((W-20)/daysInMonth);
        if (rec) { doc.setFillColor(rec.isLate?239:34,rec.isLate?68:197,rec.isLate?68:94); doc.rect(bx, y+8-8, barW2, 8, 'F'); }
        else { doc.setFillColor(30,41,59); doc.rect(bx, y+8-3, barW2, 3, 'F'); }
        if (d%5===0||d===1) { doc.setTextColor(100,100,100); doc.setFontSize(5.5); doc.text(String(d), bx+barW2/2, y+10, {align:'center'}); }
      }
      y += 16;

      // ── NOTAS GERENTE ──
      const taskWithNotes = empTasks.filter(t => t.managerNote);
      if (taskWithNotes.length > 0) {
        if (y > 230) { doc.addPage(); y = 20; }
        doc.setFillColor(30,41,59); doc.rect(10, y, W-20, 7, 'F');
        doc.setTextColor(59,130,246); doc.setFontSize(9); doc.setFont('helvetica','bold');
        doc.text('NOTAS DEL GERENTE', 14, y+5);
        y += 9;
        taskWithNotes.forEach(t => {
          if (y > 260) { doc.addPage(); y = 20; }
          doc.setFillColor(15,25,45); doc.rect(10, y, W-20, 10, 'F');
          doc.setTextColor(148,163,184); doc.setFontSize(7); doc.setFont('helvetica','bold');
          doc.text(`Tarea: ${(t.title||'').substring(0,50)}`, 12, y+4);
          doc.setTextColor(200,220,255); doc.setFont('helvetica','normal');
          doc.text(`${(t.managerNote||'').substring(0,90)}`, 12, y+8);
          y += 12;
        });
      }

      // ── FIRMA ──
      y += 8;
      if (y > 255) { doc.addPage(); y = 20; }
      doc.setDrawColor(70,80,100); doc.setLineWidth(0.3);
      doc.line(14, y+15, 80, y+15);
      doc.line(W/2+10, y+15, W-14, y+15);
      doc.setTextColor(100,100,100); doc.setFontSize(8);
      doc.text('Firma Empleado', 14, y+20);
      doc.text('Firma Gerente / RRHH', W/2+10, y+20);
      doc.setFontSize(7);
      doc.text(emp.name, 14, y+25);
      doc.text('Nombre y Cargo', W/2+10, y+25);
      doc.setFontSize(7); doc.setTextColor(80,80,80);
      doc.text(`Página ${doc.internal.getNumberOfPages()}`, W-14, 293, {align:'right'});
    }

    const filename = targetEmployeeId
      ? `desempeno_${(Array.isArray(employees)?employees:[]).find(e=>e.id===targetEmployeeId)?.name?.replace(/ /g,'_')}_${monthStr}.pdf`
      : `desempeno_todos_${monthStr}.pdf`;
    doc.save(filename);
    alert(`✅ PDF de evaluación generado: ${filename}`);
  };

  // ── GENERADOR PDF TAREAS ──
  const generateTasksPDF = async (targetEmployeeId = null) => {
    if (!window.jspdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        script.onload = resolve; script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;
    const now = new Date();
    const monthStr = getLocalDateStr().substring(0, 7);
    const monthName = now.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });

    const targetEmployees = targetEmployeeId
      ? employees.filter(e => e.id === targetEmployeeId)
      : employees.filter(e => e.status === 'active');

    let isFirstPage = true;

    for (const emp of targetEmployees) {
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;

      // ── HEADER ──
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, W, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Control de Asistencia — Reporte de Tareas', 14, 11);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Período: ${monthName}`, 14, 19);
      doc.setFontSize(9);
      doc.text(`Generado: ${now.toLocaleDateString('es-CL')} ${now.toLocaleTimeString('es-CL', {hour:'2-digit',minute:'2-digit'})}`, W - 14, 19, {align:'right'});

      // ── EMPLOYEE INFO ──
      let y = 36;
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(10, y - 5, W - 20, 22, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(emp.name, 16, y + 5);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text(emp.position || '', 16, y + 11);
      doc.text(`Código: EMP${String(emp.id).padStart(3,'0')}`, W - 14, y + 5, {align:'right'});
      y += 28;

      // ── TASK STATS ──
      const empTasks = tasks.filter(t => String(t.assignedTo) === String(emp.id));
      const total = empTasks.length;
      const completed = empTasks.filter(t => t.status === 'done').length;
      const notDone = empTasks.filter(t => t.status === 'not_done').length;
      const pending = empTasks.filter(t => t.status === 'pending').length;
      const approved = empTasks.filter(t => t.reviewStatus === 'approved').length;
      const rejected = empTasks.filter(t => t.reviewStatus === 'rejected').length;
      const compliance = total > 0 ? Math.round((completed / total) * 100) : 0;

      const stats = [
        { label: 'Total tareas', value: String(total), color: [59, 130, 246] },
        { label: 'Completadas', value: String(completed), color: [34, 197, 94] },
        { label: 'No completadas', value: String(notDone), color: [239, 68, 68] },
        { label: 'Pendientes', value: String(pending), color: [245, 158, 11] },
        { label: 'Aprobadas', value: String(approved), color: [34, 197, 94] },
        { label: 'Rechazadas', value: String(rejected), color: [239, 68, 68] },
        { label: 'Cumplimiento', value: compliance + '%', color: compliance >= 80 ? [34,197,94] : compliance >= 50 ? [245,158,11] : [239,68,68] },
      ];

      const boxW = (W - 20 - 18) / 7;
      stats.forEach((st, i) => {
        const bx = 10 + i * (boxW + 3);
        doc.setFillColor(30, 41, 59);
        doc.roundedRect(bx, y, boxW, 20, 2, 2, 'F');
        doc.setFillColor(...st.color);
        doc.roundedRect(bx, y, boxW, 3, 1, 1, 'F');
        doc.setTextColor(...st.color);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text(st.value, bx + boxW/2, y + 12, {align:'center'});
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.text(st.label, bx + boxW/2, y + 18, {align:'center'});
      });
      y += 28;

      // ── CUMPLIMIENTO BAR ──
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(10, y, W - 20, 12, 2, 2, 'F');
      const barColor = compliance >= 80 ? [34,197,94] : compliance >= 50 ? [245,158,11] : [239,68,68];
      doc.setFillColor(...barColor);
      doc.roundedRect(10, y, Math.max(4, (W - 20) * compliance / 100), 12, 2, 2, 'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(`Cumplimiento: ${compliance}%`, W/2, y + 8, {align:'center'});
      y += 18;

      // ── TASK DETAIL TABLE ──
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('DETALLE DE TAREAS', 14, y);
      y += 5;

      if (empTasks.length === 0) {
        doc.setFillColor(30, 41, 59);
        doc.roundedRect(10, y, W-20, 12, 2, 2, 'F');
        doc.setTextColor(100,100,100);
        doc.setFontSize(9);
        doc.text('Sin tareas asignadas', W/2, y+8, {align:'center'});
        y += 16;
      } else {
        // Table header
        doc.setFillColor(30, 41, 59);
        doc.rect(10, y, W-20, 7, 'F');
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        const headers = ['Tarea', 'Prioridad', 'Estado', 'Revisión', 'Fecha'];
        const colW = [72, 22, 26, 26, 24];
        let cx = 10;
        headers.forEach((h, i) => { doc.text(h, cx + 2, y + 5); cx += colW[i]; });
        y += 7;

        empTasks.forEach((task, idx) => {
          if (y > 255) { doc.addPage(); y = 20; }
          doc.setFillColor(idx % 2 === 0 ? 15 : 22, idx % 2 === 0 ? 23 : 30, idx % 2 === 0 ? 42 : 50);
          doc.rect(10, y, W-20, 8, 'F');

          const statusColor = task.status === 'done' ? [34,197,94] : task.status === 'not_done' ? [239,68,68] : [245,158,11];
          const statusLabel = task.status === 'done' ? 'Completada' : task.status === 'not_done' ? 'No completada' : 'Pendiente';
          const reviewColor = task.reviewStatus === 'approved' ? [34,197,94] : task.reviewStatus === 'rejected' ? [239,68,68] : [100,100,100];
          const reviewLabel = task.reviewStatus === 'approved' ? 'Aprobada' : task.reviewStatus === 'rejected' ? 'Rechazada' : 'Sin revisar';
          const priorityColor = task.priority === 'high' ? [239,68,68] : task.priority === 'medium' ? [245,158,11] : [34,197,94];
          const priorityLabel = task.priority === 'high' ? 'Alta' : task.priority === 'medium' ? 'Media' : 'Baja';

          cx = 10;
          // Title (truncate if too long)
          const title = task.title?.length > 38 ? task.title.substring(0,38)+'...' : (task.title || '');
          doc.setTextColor(220,220,220); doc.setFontSize(7); doc.setFont('helvetica','normal');
          doc.text(title, cx + 2, y + 5.5); cx += colW[0];
          doc.setTextColor(...priorityColor); doc.setFont('helvetica','bold');
          doc.text(priorityLabel, cx + 2, y + 5.5); cx += colW[1];
          doc.setTextColor(...statusColor);
          doc.text(statusLabel, cx + 2, y + 5.5); cx += colW[2];
          doc.setTextColor(...reviewColor);
          doc.text(reviewLabel, cx + 2, y + 5.5); cx += colW[3];
          doc.setTextColor(150,150,150); doc.setFont('helvetica','normal');
          doc.text(task.createdAt ? task.createdAt.substring(0,10) : '', cx + 2, y + 5.5);
          y += 8;

          // Show steps if any
          if ((task.steps||[]).length > 0) {
            const doneSteps = task.steps.filter(s=>s.done).length;
            doc.setFillColor(20, 28, 48);
            doc.rect(10, y, W-20, 6, 'F');
            doc.setTextColor(100,120,160); doc.setFontSize(6.5);
            const stepsText = task.steps.map((s,si) => `${si+1}. ${s.done?'✓':' '} ${s.text?.substring(0,25)||''}`).join('   ');
            doc.text(`  Pasos: ${doneSteps}/${task.steps.length} completados — ${stepsText.substring(0,90)}`, 12, y+4);
            y += 6;
          }

          // Show observation if any
          if (task.employeeObservation) {
            doc.setFillColor(18, 25, 44);
            doc.rect(10, y, W-20, 6, 'F');
            doc.setTextColor(120,140,160); doc.setFontSize(6.5);
            const obs = task.employeeObservation.substring(0,100);
            doc.text(`  Obs: ${obs}`, 12, y+4);
            y += 6;
          }

          // Show review comment if rejected
          if (task.reviewStatus === 'rejected' && task.reviewComment) {
            doc.setFillColor(40, 15, 15);
            doc.rect(10, y, W-20, 6, 'F');
            doc.setTextColor(200, 80, 80); doc.setFontSize(6.5);
            doc.text(`  Rechazo: ${task.reviewComment.substring(0,100)}`, 12, y+4);
            y += 6;
          }
        });
      }

      // ── EVALUACIÓN DE DESEMPEÑO ──
      y += 10;
      if (y > 200) { doc.addPage(); y = 20; }
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(10, y, W-20, 7, 2, 2, 'F');
      doc.setTextColor(148, 163, 184); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.text('EVALUACIÓN DE DESEMPEÑO MENSUAL', 14, y + 5);
      y += 10;

      // Calcular score
      const empTasksAll = (Array.isArray(tasks) ? tasks : []).filter(t => String(t.assignedTo) === String(emp.id));
      const totalT = empTasksAll.length;
      const completedT = empTasksAll.filter(t => t.status === 'done').length;
      const approvedT = empTasksAll.filter(t => t.reviewStatus === 'approved').length;
      const rejectedT = empTasksAll.filter(t => t.reviewStatus === 'rejected').length;
      const pendingT = empTasksAll.filter(t => t.status === 'pending').length;
      const taskRate = totalT > 0 ? Math.round((completedT/totalT)*100) : 100;
      const approvalRate = completedT > 0 ? Math.round((approvedT/completedT)*100) : 100;
      const empRecs = (Array.isArray(attendanceRecords) ? attendanceRecords : []).filter(r => r.employeeId === emp.id && r.date?.startsWith(monthStr));
      const lateC = empRecs.filter(r => r.isLate).length;
      const punctRate = empRecs.length > 0 ? Math.round(((empRecs.length-lateC)/empRecs.length)*100) : 100;
      const productsRep = (Array.isArray(missingProducts) ? missingProducts : []).filter(p => p.reportedBy === emp.name).length;
      const score = Math.round((taskRate*0.4)+(punctRate*0.3)+(approvalRate*0.2)+(Math.min(productsRep*10,100)*0.1));
      const scoreLabel = score >= 90 ? 'EXCELENTE' : score >= 75 ? 'BUENO' : score >= 60 ? 'REGULAR' : 'DEFICIENTE';
      const scoreColor = score >= 90 ? [34,197,94] : score >= 75 ? [59,130,246] : score >= 60 ? [245,158,11] : [239,68,68];

      // Score circle area
      doc.setFillColor(...scoreColor);
      doc.roundedRect(10, y, 40, 28, 3, 3, 'F');
      doc.setTextColor(255,255,255); doc.setFontSize(22); doc.setFont('helvetica', 'bold');
      doc.text(String(score), 30, y+14, {align:'center'});
      doc.setFontSize(7); doc.setFont('helvetica', 'bold');
      doc.text(scoreLabel, 30, y+22, {align:'center'});

      // Metrics
      const metrics = [
        {label:'Tareas completadas', value:`${completedT}/${totalT}`, pct:`${taskRate}%`, color:[34,197,94]},
        {label:'Aprobadas gerente', value:`${approvedT}/${completedT}`, pct:`${approvalRate}%`, color:[59,130,246]},
        {label:'Puntualidad', value:`${empRecs.length-lateC}/${empRecs.length}`, pct:`${punctRate}%`, color:[245,158,11]},
        {label:'Productos reportados', value:String(productsRep), pct:'iniciativa', color:[139,92,246]},
      ];
      const mW = (W - 60) / 4 - 2;
      metrics.forEach((m, i) => {
        const mx = 54 + i*(mW+2);
        doc.setFillColor(15,23,42); doc.roundedRect(mx, y, mW, 28, 2, 2, 'F');
        doc.setFillColor(...m.color); doc.roundedRect(mx, y, mW, 3, 1, 1, 'F');
        doc.setTextColor(...m.color); doc.setFontSize(11); doc.setFont('helvetica','bold');
        doc.text(m.value, mx+mW/2, y+12, {align:'center'});
        doc.setFontSize(7);
        doc.text(m.pct, mx+mW/2, y+18, {align:'center'});
        doc.setTextColor(148,163,184); doc.setFontSize(6); doc.setFont('helvetica','normal');
        doc.text(m.label, mx+mW/2, y+24, {align:'center'});
      });
      y += 32;

      // Pending/rejected note
      if (pendingT > 0 || rejectedT > 0) {
        doc.setFontSize(7); doc.setTextColor(150,150,150);
        if (pendingT > 0) doc.text(`⏳ ${pendingT} tarea(s) pendiente(s)`, 14, y);
        if (rejectedT > 0) doc.text(`❌ ${rejectedT} tarea(s) rechazada(s)`, 80, y);
        y += 8;
      }

      // ── SIGNATURE ──
      y += 10;
      if (y > 255) { doc.addPage(); y = 20; }
      doc.setDrawColor(70, 80, 100);
      doc.setLineWidth(0.3);
      doc.line(14, y + 15, 80, y + 15);
      doc.line(W/2 + 10, y + 15, W - 14, y + 15);
      doc.setTextColor(100,100,100); doc.setFontSize(8);
      doc.text('Firma Empleado', 14, y + 20);
      doc.text('Firma Gerente / RRHH', W/2 + 10, y + 20);
      doc.setFontSize(7);
      doc.text(emp.name, 14, y + 25);
      doc.text('Nombre y Cargo', W/2 + 10, y + 25);

      // Page number
      doc.setFontSize(7); doc.setTextColor(80,80,80);
      doc.text(`Página ${doc.internal.getNumberOfPages()}`, W-14, 293, {align:'right'});
    }

    const filename = targetEmployeeId
      ? `tareas_${employees.find(e=>e.id===targetEmployeeId)?.name?.replace(/ /g,'_')}_${monthStr}.pdf`
      : `tareas_todos_empleados_${monthStr}.pdf`;
    doc.save(filename);
    alert(`✅ PDF de tareas generado: ${filename}`);
  };

  // Funciones para gestión de empleados
  const handleAddEmployee = (employee) => {
    const uniqueEmps = employees.filter((e,i,arr)=>arr.findIndex(x=>x.id===e.id)===i);
    const validIds = uniqueEmps.map(e => parseInt(e.id)).filter(id => !isNaN(id) && id < 9999);
    const newId = validIds.length > 0 ? Math.max(...validIds) + 1 : 1001;
    const newEmployee = { ...employee, id: newId, status: 'active' };
    const updatedEmployees = [...uniqueEmps, newEmployee];
    saveEmployees(updatedEmployees);
    fetch('https://control--asistencia-default-rtdb.firebaseio.com/employees/' + newId + '.json', {method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(newEmployee)});
    setShowEmployeeModal(false);
    const t = document.createElement('div');
    t.textContent = '✅ Empleado agregado: EMP' + String(newId).padStart(3,'0');
    t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#00e5a0;color:#0a0f1a;padding:12px 24px;border-radius:10px;font-weight:700;font-size:14px;z-index:999999;box-shadow:0 4px 20px rgba(0,0,0,0.3);font-family:sans-serif;';
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3000);
  };

  const handleEditEmployee = (employee) => {
    const prev = employees.find(e => e.id === employee.id);
    // Agregar historial de cambios de horario (max 10 entradas)
    let updatedEmployee = { ...employee };
    try {
      if (prev && JSON.stringify(prev.monthlySchedule) !== JSON.stringify(employee.monthlySchedule)) {
        const histEntry = {
          date: new Date().toISOString(),
          changedBy: employee.lastModifiedBy || 'Admin',
          type: 'horario mensual'
        };
        const prevHistory = Array.isArray(prev.scheduleHistory) ? prev.scheduleHistory : [];
        updatedEmployee.scheduleHistory = [histEntry, ...prevHistory].slice(0, 10);
      }
    } catch(e) {}
    const updatedEmployees = employees.map(e => e.id === employee.id ? updatedEmployee : e);
    saveEmployees(updatedEmployees);
    // Invalidar cache IA del empleado al editar
    try {
      const hoy = getLocalDateStr();
      localStorage.removeItem(`iaDiagnostico_${employee.id}_${hoy}`);
    } catch(e) {}
    // Detectar qué cambió para el audit
    const changes = [];
    if (prev) {
      if (JSON.stringify(prev.monthlySchedule) !== JSON.stringify(employee.monthlySchedule)) changes.push('horario mensual');
      if (JSON.stringify(prev.weeklySchedule) !== JSON.stringify(employee.weeklySchedule)) changes.push('horario semanal');
      if (JSON.stringify(prev.schedules) !== JSON.stringify(employee.schedules)) changes.push('horario base');
      if (prev.position !== employee.position) changes.push(`cargo: ${prev.position} → ${employee.position}`);
      if (prev.contractType !== employee.contractType) changes.push(`contrato: ${prev.contractType} → ${employee.contractType}`);
    }
    addAuditEntry('EDITAR_EMPLEADO', `Editó a ${employee.name}${changes.length ? ' — Cambios: ' + changes.join(', ') : ''}`);
    setShowEmployeeModal(false);
    setEditingEmployee(null);
    alert('✅ Empleado actualizado exitosamente');
  };

  const handleDeleteEmployee = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (window.confirm(`¿Estás seguro de eliminar a ${employee?.name}?\n\n⚠️ ADVERTENCIA: Se eliminarán también TODOS sus registros de asistencia.`)) {
      // Eliminar empleado
      const updatedEmployees = employees.filter(e => e.id !== employeeId);
      saveEmployees(updatedEmployees);
      
      // Eliminar TODOS los registros de asistencia de ese empleado
      const updatedRecords = attendanceRecords.filter(r => r.employeeId !== employeeId);
      setAttendanceRecords(updatedRecords);
      localStorage.setItem('attendanceRecords', JSON.stringify(updatedRecords));
      
      alert('✅ Empleado y todos sus registros eliminados');
    }
  };
  
  // Cambiar estado del empleado
  const handleChangeEmployeeStatus = (employeeId, newStatus) => {
    const emp = employees.find(e => e.id === employeeId);
    addAuditEntry('CAMBIAR_ESTADO_EMPLEADO', `Cambió estado de ${emp?.name || employeeId} a ${newStatus}`);
    const employee = employees.find(e => e.id === employeeId);
    const statusMessages = {
      active: '✅ Empleado activado. Puede marcar asistencia y recibirá alarmas.',
      inactive: '⏸️ Empleado desactivado. NO puede marcar asistencia ni recibirá alarmas. Historial mantenido.',
      suspended: '🔒 Empleado suspendido temporalmente. Acceso bloqueado.'
    };
    
    if (window.confirm(`¿Cambiar estado de ${employee?.name} a "${newStatus === 'active' ? 'ACTIVO' : newStatus === 'inactive' ? 'INACTIVO' : 'SUSPENDIDO'}"?\n\n${statusMessages[newStatus]}`)) {
      const updatedEmployees = employees.map(emp =>
        emp.id === employeeId ? { ...emp, status: newStatus } : emp
      );
      saveEmployees(updatedEmployees);
      alert(statusMessages[newStatus]);
    }
  };
  
  // Gestión de solicitudes de ausencias
  const handleSubmitAbsenceRequest = () => {
    if (!absenceFormData.startDate || !absenceFormData.endDate) {
      alert('⚠️ Debes completar las fechas');
      return;
    }
    
    const newRequest = {
      id: absenceRequests.length + 1,
      employeeId: currentUser.id,
      employeeName: currentUser.name,
      ...absenceFormData,
      requestDate: getLocalDateStr(),
      status: 'pending'
    };
    
    const updatedRequests = [newRequest, ...absenceRequests];
    setAbsenceRequests(updatedRequests);
    localStorage.setItem('absenceRequests', JSON.stringify(updatedRequests));
    
    setShowAbsenceModal(false);
    setAbsenceFormData({
      type: 'vacation',
      startDate: '',
      endDate: '',
      reason: '',
      status: 'pending'
    });
    
    alert('✅ Solicitud enviada correctamente. Espera la aprobación del gerente.');
  };
  
  const handleApproveAbsence = (requestId) => {
    const request = absenceRequests.find(r => r.id === requestId);
    
    if (window.confirm(`¿Aprobar solicitud de ${request.employeeName}?\n\nTipo: ${getAbsenceTypeName(request.type)}\nFechas: ${request.startDate} - ${request.endDate}`)) {
      const updatedRequests = absenceRequests.map(r =>
        r.id === requestId ? { ...r, status: 'approved' } : r
      );
      
      // Actualizar días de vacaciones usados si es vacación
      if (request.type === 'vacation') {
        const days = calculateDaysBetween(request.startDate, request.endDate);
        const updatedEmployees = employees.map(emp =>
          emp.id === request.employeeId 
            ? { ...emp, vacationUsed: (emp.vacationUsed || 0) + days }
            : emp
        );
        saveEmployees(updatedEmployees);
      }
      
      setAbsenceRequests(updatedRequests);
      localStorage.setItem('absenceRequests', JSON.stringify(updatedRequests));
      alert('✅ Solicitud aprobada');
    }
  };
  
  const handleRejectAbsence = (requestId) => {
    const request = absenceRequests.find(r => r.id === requestId);
    
    if (window.confirm(`¿Rechazar solicitud de ${request.employeeName}?`)) {
      const updatedRequests = absenceRequests.map(r =>
        r.id === requestId ? { ...r, status: 'rejected' } : r
      );
      setAbsenceRequests(updatedRequests);
      localStorage.setItem('absenceRequests', JSON.stringify(updatedRequests));
      alert('❌ Solicitud rechazada');
    }
  };
  
  const getAbsenceTypeName = (type) => {
    const types = {
      vacation: 'Vacaciones',
      medical: 'Permiso Médico',
      personal: 'Permiso Personal',
      sick_leave: 'Licencia Médica',
      shift_change: 'Cambio de Turno',
      holiday_worked: 'Día Festivo Trabajado'
    };
    return types[type] || type;
  };
  
  const calculateDaysBetween = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };
  
  const isEmployeeOnLeaveToday = (employeeId) => {
    const today = getLocalDateStr();
    return absenceRequests.some(r => 
      r.employeeId === employeeId &&
      r.status === 'approved' &&
      r.startDate <= today &&
      r.endDate >= today &&
      ['vacation', 'sick_leave', 'medical', 'personal'].includes(r.type)
    );
  };

  // Componente de reloj en vivo (aislado para evitar re-renders del padre)
  
  // ══════════════════════════════════════════════
  // GESTIÓN DE AUSENCIAS
  // ══════════════════════════════════════════════
  
  useEffect(() => {
    const saved = localStorage.getItem('employeeAbsences');
    if (saved) setAbsences(JSON.parse(saved));
  }, []);
  
  const saveAbsences = (newAbsences) => {
    setAbsences(newAbsences);
    localStorage.setItem('employeeAbsences', JSON.stringify(newAbsences));
  };
  
  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };
  
  const handleAddAbsence = () => {
    if (!absenceForm.startDate) {
      alert('⚠️ Debes ingresar la fecha de inicio');
      return;
    }
    
    const employee = employees.find(e => e.id === selectedEmployeeForAbsences);
    if (!employee) return;
    
    const endDate = absenceForm.endDate || absenceForm.startDate;
    const days = calculateDays(absenceForm.startDate, endDate);
    
    const newAbsence = {
      id: Date.now(),
      employeeId: employee.id,
      employeeName: employee.name,
      type: absenceForm.type,
      startDate: absenceForm.startDate,
      endDate: endDate,
      days: days,
      notes: absenceForm.notes,
      registeredAt: new Date().toISOString()
    };
    
    saveAbsences([newAbsence, ...absences]);
    
    if (absenceForm.type === 'vacation') {
      const updatedEmployees = employees.map(emp =>
        emp.id === employee.id
          ? { ...emp, vacationUsed: (emp.vacationUsed || 0) + days }
          : emp
      );
      saveEmployees(updatedEmployees);
    }
    
    setAbsenceForm({ type: 'vacation', startDate: '', endDate: '', notes: '' });
    
    const typeNames = {
      vacation: 'Vacaciones',
      medical: 'Licencia Médica',
      absence: 'Falta Injustificada'
    };
    
    alert(`✅ ${typeNames[absenceForm.type]} registrada: ${days} día${days !== 1 ? 's' : ''}`);
  };
  
  const handleDeleteAbsence = (absenceId) => {
    const absence = absences.find(a => a.id === absenceId);
    if (!absence) return;
    
    if (window.confirm(`¿Eliminar registro?`)) {
      if (absence.type === 'vacation') {
        const updatedEmployees = employees.map(emp =>
          emp.id === absence.employeeId
            ? { ...emp, vacationUsed: Math.max(0, (emp.vacationUsed || 0) - absence.days) }
            : emp
        );
        saveEmployees(updatedEmployees);
      }
      
      saveAbsences(absences.filter(a => a.id !== absenceId));
      alert('✅ Registro eliminado');
    }
  };
  
  // [AbsenceManager moved]
const LiveClock = () => {
    const time = absenceManagerTime;
    const setTime = setAbsenceManagerTime;
    
    useEffect(() => {
      const timer = setInterval(() => {
        setTime(new Date());
      }, 1000);
      
      return () => clearInterval(timer);
    }, []);
    
    const formatTime = (date) => {
      return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };
    
    const formatDate = (date) => {
      return date.toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    };
    
    return (
      <div className="live-clock-container">
        <div className="live-time">{formatTime(time)}</div>
        <div className="live-date">{formatDate(time)}</div>
      </div>
    );
  };

  // Modal de cámara para selfie biométrica
  
  // Modal de configuración
  // [ConfigModal moved]
  // Modal de empleado
  // [EmployeeModal moved]
  // Modal de permisos del administrador (solo para gerente)
  
  // Componente para gestionar TODAS las solicitudes de ausencias (Gerente/Admin)
  // [AbsenceRequestsManager moved]
  // [PermissionsModal moved]
  // Modal de registro de asistencia para el admin
  // [AdminRegistrationModal moved]
  // Pantalla de Login
  if (!userRole || !currentUser) {
    // Vista de selección: Empleados o Admin/Gerente
    if (loginView === 'select') {
      return (
        <div className="login-container">
          <style>{styles}</style>
          
          <div className="login-card-wide">
            {config.companyLogo ? (
              <div className="login-logo">
                <img src={config.companyLogo} alt="Logo de la empresa" />
              </div>
            ) : (
              <div className="login-icon">⏱️</div>
            )}
            <h1 className="login-title">Bienvenido</h1>
            <p className="login-subtitle">Selecciona tu perfil</p>
            
            {/* Botones para Admin y Gerente */}
            <div className="admin-buttons">
              <button 
                className="admin-btn manager"
                onClick={() => {
                  setSelectedRole('manager');
                  setLoginView('password');
                }}
              >
                <div className="admin-icon">👔</div>
                <div className="admin-label">Gerente</div>
              </button>
              <button 
                className="admin-btn admin"
                onClick={() => {
                  setSelectedRole('admin');
                  setLoginView('password');
                }}
              >
                <div className="admin-icon">👨‍💼</div>
                <div className="admin-label">Administrador</div>
              </button>
            </div>

            {/* Tarjetas de empleados */}
            <div className="employees-section">
              <h3 className="section-title">Empleados</h3>
              <div className="employees-grid-login">
                {employees.filter((e,i,arr)=>arr.findIndex(x=>x.id===e.id)===i && e.status === 'active').map(employee => {
                  const todayStr = getLocalDateStr();
                  const safeRecs = Array.isArray(attendanceRecords) ? attendanceRecords : [];
                  const todayRec = safeRecs.find(r => r.employeeId === employee.id && r.date === todayStr);
                  const checkedIn = todayRec?.checkIn && !todayRec?.checkOut;
                  const checkedOut = !!todayRec?.checkOut;
                  const onBreak = todayRec?.breakStart && !todayRec?.breakEnd;
                  const notMarked = !todayRec;
                  const statusColor = checkedOut ? '#22c55e' : checkedIn ? (onBreak ? '#f59e0b' : '#3b82f6') : 'var(--border)';
                  const statusLabel = checkedOut ? '✓ Salió' : onBreak ? '☕ Colación' : checkedIn ? '● Trabajando' : '○ Sin marcar';
                  const statusTextColor = checkedOut ? '#22c55e' : onBreak ? '#f59e0b' : checkedIn ? '#3b82f6' : 'var(--text-muted)';
                  return (
                  <div
                    key={employee.id}
                    className="employee-card-login"
                    style={{border: `1px solid ${statusColor}`, position:'relative'}}
                    onClick={() => {
                      setUserRole('employee');
                      const empUser = { ...employee, role: 'employee' };
                      setCurrentUser(empUser);
                      localStorage.setItem('userRole', 'employee');
                      localStorage.setItem('currentUser', JSON.stringify(empUser));
                      const todayRec2 = safeRecs.find(r => r.employeeId === employee.id && r.date === todayStr);
                      if(todayRec2){setTodayRecord(todayRec2);setIsCheckedIn(!!todayRec2.checkIn&&!todayRec2.checkOut);if(todayRec2.breakStart&&!todayRec2.breakEnd)setIsOnBreak(true);}
                      else{setTodayRecord(null);setIsCheckedIn(false);setIsOnBreak(false);}
                      try{const drafts={};Object.keys(localStorage).forEach(key=>{if(key.startsWith(`taskDraft_${employee.id}_`)){const tid=parseInt(key.split('_').pop());if(!isNaN(tid))drafts[tid]=localStorage.getItem(key)||'';}});if(Object.keys(drafts).length>0)setTaskObs(prev=>({...prev,...drafts}));}catch(e){}
                      setCurrentView('home');
                    }}
                  >
                    {/* Indicador de estado */}
                    <div style={{position:'absolute', top:'8px', right:'8px', width:'8px', height:'8px', borderRadius:'50%', background:statusColor, boxShadow:`0 0 6px ${statusColor}`}} />
                    <div className="employee-avatar-login">{employee.photo}</div>
                    <div className="employee-name-login">{employee.name}</div>
                    <div className="employee-position-login">{employee.position}</div>
                    <div style={{fontSize:'10px', color:statusTextColor, fontWeight:'600', marginTop:'4px'}}>{statusLabel}</div>
                    {todayRec?.checkIn && <div style={{fontSize:'9px', color:'var(--text-muted)', marginTop:'2px'}}>Entrada: {todayRec.checkIn?.replace(' a. m.','a').replace(' p. m.','p')}</div>}
                  </div>
                  );
                })}
              </div>
            </div>
            
            <div className="login-footer">
              <p>Desarrollado por <strong>César Romero H.</strong> • 2026</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Vista de contraseña para Admin/Gerente
    if (loginView === 'password') {
      return (
        <div className="login-container">
          <style>{styles}</style>
          
          <div className="login-card">
            {config.companyLogo ? (
              <div className="login-logo">
                <img src={config.companyLogo} alt="Logo de la empresa" />
              </div>
            ) : (
              <div className="login-icon">⏱️</div>
            )}
            <h1 className="login-title">
              {selectedRole === 'manager' ? '👔 Gerente' : '👨‍💼 Administrador'}
            </h1>
            <p className="login-subtitle">Ingresa tu contraseña</p>
            
            <input
              type="password"
              className="login-input"
              placeholder="CONTRASEÑA"
              value={password}
              onChange={(e) => setPassword(e.target.value.toUpperCase())}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const expectedCode = selectedRole === 'manager' ? config.codes.manager : config.codes.admin;
                  if (password === expectedCode) {
                    if (selectedRole === 'manager') {
                      const managerUser = { id: 1000, name: 'Gerente', position: 'Gerente General', role: 'manager' };
                      setUserRole('manager');
                      setCurrentUser(managerUser);
                      localStorage.setItem('userRole', 'manager');
                      localStorage.setItem('currentUser', JSON.stringify(managerUser));
                      setCurrentView('dashboard');
                    } else {
                      const adminUser = { id: 999, name: 'Administrador', position: 'Administrador', role: 'admin' };
                      setUserRole('admin');
                      setCurrentUser(adminUser);
                      localStorage.setItem('userRole', 'admin');
                      localStorage.setItem('currentUser', JSON.stringify(adminUser));
                      setCurrentView('dashboard');
                    }
                    setPassword('');
                    setLoginView('select');
                  } else {
                    alert('❌ Contraseña incorrecta');
                    setPassword('');
                  }
                }
              }}
              autoFocus
            />
            
            <button 
              className="login-button" 
              onClick={() => {
                const expectedCode = selectedRole === 'manager' ? config.codes.manager : config.codes.admin;
                if (password === expectedCode) {
                  if (selectedRole === 'manager') {
                    const managerUser = { id: 1000, name: 'Gerente', position: 'Gerente General', role: 'manager' };
                    setUserRole('manager');
                    setCurrentUser(managerUser);
                    localStorage.setItem('userRole', 'manager');
                    localStorage.setItem('currentUser', JSON.stringify(managerUser));
                    setCurrentView('dashboard');
                  } else {
                    const adminUser = { id: 999, name: 'Administrador', position: 'Administrador', role: 'admin' };
                    setUserRole('admin');
                    setCurrentUser(adminUser);
                    localStorage.setItem('userRole', 'admin');
                    localStorage.setItem('currentUser', JSON.stringify(adminUser));
                    setCurrentView('dashboard');
                  }
                  setPassword('');
                  setLoginView('select');
                } else {
                  alert('❌ Contraseña incorrecta');
                  setPassword('');
                }
              }}
            >
              Ingresar
            </button>
            
            <button 
              className="btn-back"
              onClick={() => {
                setLoginView('select');
                setPassword('');
                setSelectedRole(null);
              }}
            >
              ← Volver
            </button>
            
            <div className="login-footer">
              <p>Desarrollado por <strong>César Romero H.</strong> • 2026</p>
            </div>
          </div>
        </div>
      );
    }
  }

  // Vista para empleados - Home
  // [EmployeeHomeView moved]
  // Vista para empleados - Historial
  // [EmployeeHistoryView moved]
  // Vista Admin - Dashboard
  // TASKS VIEW
  // [TasksView moved]
  // [AdminDashboardView moved]
  // Vista Admin - Empleados
  // [AdminEmployeesView moved]
  // Vista Admin - Reportes
  // [AdminReportsView moved]
  return (
    <AppContext.Provider value={{
      LiveClock, absenceForm, absenceRequests, absences, adminFilterEmp, adminRegCheckedIn,
      adminRegOnBreak, alarmMessage, attendanceRecords, config, configActiveTab, currentLocation,
      currentUser, editingEmployee, editingTask, employeeFormData, employees, exportData,
      getTodaySchedule, handleAddAbsence, handleAddEmployee, handleBreakToggle,
      handleChangeEmployeeStatus, handleCheckIn, handleCheckOut, handleCreateTask,
      handleDeleteAbsence, handleDeleteAbsenceRequest, handleDeleteAttendanceRecord,
      handleDeleteEmployee, handleDeleteProduct, handleDeleteTask, handleEditEmployee,
      handleEditTask, handleEmployeeCompleteTask, handleManagerReviewTask, handleToggleStep, newStepText, setNewStepText, generatePDF, generateTasksPDF, recalculateLateMinutes, generatePerformancePDF,
      handleMarkProductRequested, handleMarkProductResolved, handleReportProduct, isCheckedIn,
      isInWorkZone, isOnBreak, isOnline, lateEmployeesAlerts, minutesUntilCheckout,
      tasks, setTasks, missingProducts, setMissingProducts, overtimeRequests, productForm, saveConfig, saveEmployees, saveTasksToStorage, auditLog, addAuditEntry, attendanceIssues, detectAttendanceIssues, classifyIssue, dismissIssue, announcements, createAnnouncement, deleteAnnouncement,
      selectedEmployeeForAbsences, setAbsenceForm, setAbsenceRequests, setAdminFilterEmp,
      setAdminRegCheckedIn, setAdminRegOnBreak, setAlarmMessage, setAttendanceRecords,
      setCalendarEmployee, setConfigActiveTab, setEditingEmployee, setEditingTask,
      setEmployeeFormData, setOvertimeRequests, setProductForm, setSelectedEmployeeForAbsences,
      setShowAbsenceManager, setShowAbsenceModal, setShowAbsenceRequestsManager,
      setShowAdminRegistration, setShowAlarmNotification, setShowCalendar, setShowConfigModal,
      setShowEmployeeModal, setShowEmployeeRequestsModal, setShowLateAlert,
      setShowPermissionsModal, setShowProductModal, setShowTaskModal, setTaskForm, setTaskObs,
      setCurrentView, setTasksActiveTab, setTasksFilterEmp, setTasksFilterStatus, tasksFilterDays, setTasksFilterDays, setTempConfigState,
      showPagosModal, setShowPagosModal, pagosEmployee, setPagosEmployee,
      setTempPermissionsState, showAlarmNotification, showLateAlert, showProductModal,
      showTaskModal, taskForm, taskObs, tasksActiveTab, tasksFilterEmp, tasksFilterStatus,
      tempConfigState, tempPermissionsState, todayRecord, userRole,
      ignoreNextFirebaseUpdate, // FIX BUG 3: ref para que AdminReportsView lo use al borrar/editar
      showChatGerente, setShowChatGerente, chatGerenteMensajes, setChatGerenteMensajes, chatGerenteLoading, setChatGerenteLoading,
      setAuditLog,
      chatUnread, setChatUnread, showChat, setShowChat, chatAdminId, setChatAdminId,
      chatUnread, setChatUnread, showChat, setShowChat, chatAdminId, setChatAdminId,
    }}>
    <div className="app-container">
      <style>{styles}</style>
      
      <div className="header" style={{
        borderBottom: userRole === 'manager' ? '3px solid #3b82f6' : 
                      userRole === 'admin' ? '3px solid #10b981' : 
                      '3px solid var(--accent)'
      }}>
        <div className="header-left">
          {config.companyLogo ? (
            <div className="app-logo">
              <img src={config.companyLogo} alt="Logo de la empresa" />
            </div>
          ) : (
            <div className="app-icon">⏱️</div>
          )}
          <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
            <div className="app-title">Control de Asistencia</div>
            <span style={{
              fontSize:'10px', padding:'2px 8px', borderRadius:'20px', fontWeight:'700',
              background: userRole === 'manager' ? 'rgba(59,130,246,0.2)' : userRole === 'admin' ? 'rgba(16,185,129,0.2)' : 'rgba(148,163,184,0.2)',
              color: userRole === 'manager' ? '#3b82f6' : userRole === 'admin' ? '#10b981' : 'var(--text-secondary)',
              border: userRole === 'manager' ? '1px solid rgba(59,130,246,0.4)' : userRole === 'admin' ? '1px solid rgba(16,185,129,0.4)' : '1px solid var(--border)'
            }}>
              {userRole === 'manager' ? '👔 Gerente' : userRole === 'admin' ? '⚙️ Admin' : '👤 Empleado'}
            </span>
          </div>
        </div>
        <div className="header-actions">

          {userRole === 'manager' && (
            <>
              <button onClick={() => setShowChatGerente(true)} style={{display:'flex', alignItems:'center', gap:'6px', padding:'7px 14px', background:'transparent', color:'#7c3aed', border:'1.5px solid #7c3aed', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', position:'relative', fontFamily:'inherit'}}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/><path d="M20 3v4"/><path d="M22 5h-4"/></svg>
                Asistente IA
                <span style={{position:'absolute', top:'-4px', right:'-4px', width:'8px', height:'8px', background:'#7c3aed', borderRadius:'50%', border:'2px solid var(--bg-primary)'}}></span>
              </button>
              <button className="btn-permissions" onClick={() => setShowPermissionsModal(true)}>
                <Users size={18} />
                Permisos
              </button>
              <button className="btn-permissions" onClick={() => setShowAbsenceRequestsManager(true)} style={{marginLeft:'8px', position:'relative'}}>
                <FileText size={18} />
                Solicitudes
                {(absenceRequests.filter(r => r.status === 'pending').length + (overtimeRequests?.filter(r => r.status === 'pending').length || 0)) > 0 && (
                  <span style={{
                    position:'absolute',
                    top:'-8px',
                    right:'-8px',
                    background:'#ff4444',
                    color:'white',
                    borderRadius:'50%',
                    width:'20px',
                    height:'20px',
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    fontSize:'12px',
                    fontWeight:'bold',
                    border:'2px solid white'
                  }}>
                    {absenceRequests.filter(r => r.status === 'pending').length + (overtimeRequests?.filter(r => r.status === 'pending').length || 0)}
                  </span>
                )}
              </button>
              <button className="btn-permissions" onClick={() => setShowConfigModal(true)} style={{marginLeft:'8px'}}>
                <Settings size={18} />
                Configuración
              </button>
            </>
          )}
          {(userRole === 'admin' || userRole === 'manager') && (() => {
            const totalUnread = Object.values(chatUnread||{}).reduce((a,b)=>a+b,0);
            return (
              <button onClick={() => { setShowChat(true); if (userRole==='admin') setChatAdminId(currentUser.id); else setChatAdminId(null); setChatUnread(u=>({...u,[currentUser.id]:0})); }}
                style={{position:'relative',padding:'7px 12px',background:totalUnread>0?'rgba(239,68,68,0.1)':'var(--bg-elevated)',color:totalUnread>0?'#ef4444':'var(--text-secondary)',border:totalUnread>0?'1px solid rgba(239,68,68,0.3)':'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',display:'flex',alignItems:'center',gap:'6px',fontFamily:'inherit',marginRight:'8px'}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                {totalUnread > 0 && <span style={{background:'#ef4444',color:'white',borderRadius:'50%',width:'18px',height:'18px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700'}}>{totalUnread}</span>}
              </button>
            );
          })()}
          {(userRole === 'admin' || userRole === 'manager') && currentUser.id === 999 && (
            <button className="btn-admin-reg" onClick={() => setShowAdminRegistration(true)}>
              <Clock size={18} />
              Mi Asistencia
            </button>
          )}
          {(userRole === 'manager' || userRole === 'admin') && (
            <a href="https://proveedores-hub.pages.dev" style={{display:"inline-flex",alignItems:"center",gap:"6px",padding:"8px 14px",borderRadius:"6px",border:"1px solid #667eea",color:"#667eea",fontSize:"13px",fontWeight:600,textDecoration:"none",fontFamily:"inherit",marginRight:"8px"}}>Proveedores →</a>
          )}
          <button className="btn-logout" onClick={handleLogout}>
            <LogOut size={18} />
            Salir
          </button>
        </div>

      </div>

      {/* MODAL CHAT */}
      {showChat && (
        <div className="modal-overlay" onClick={() => setShowChat(false)}>
          <div className="modal-content" style={{maxWidth:'480px',width:'95vw',maxHeight:'85vh',display:'flex',flexDirection:'column',padding:0,overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'14px 16px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                {chatAdminId && userRole==='manager' && (
                  <button onClick={()=>setChatAdminId(null)} style={{background:'none',border:'none',color:'var(--accent)',cursor:'pointer',fontSize:'18px',padding:'0 4px'}}>←</button>
                )}
                <span style={{fontWeight:'700',color:'var(--text-primary)',fontSize:'15px'}}>
                  {userRole==='admin'?'💬 Chat con Gerente': chatAdminId ? '💬 ' + (employees.find(e=>e.id===chatAdminId)?.name||'Admin') : '💬 Chat Interno'}
                </span>
              </div>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                {chatAdminId && userRole==='manager' && (
                  <button onClick={()=>{
                    if(!window.confirm('¿Borrar historial de esta conversacion?'))return;
                    fetch('https://control--asistencia-default-rtdb.firebaseio.com/chat/admin_'+chatAdminId+'.json',{method:'DELETE'});
                    setChatConversations(c=>({...c,[chatAdminId]:[]}));
                  }} style={{padding:'4px 10px',background:'rgba(239,68,68,0.15)',color:'#ef4444',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'700'}}>🗑️</button>
                )}
                <button onClick={()=>setShowChat(false)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:'20px'}}>x</button>
              </div>
            </div>
            {userRole==='manager' && !chatAdminId && (
              <div style={{flex:1,overflowY:'auto',padding:'12px'}}>
                <p style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'12px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Selecciona con quien chatear</p>
                {(Array.isArray(employees)?employees:[]).filter(e=>e.status==='active'&&(e.id===999||e.role==='admin')).filter((e,i,arr)=>arr.findIndex(x=>x.id===e.id)===i).map(admin=>{
                  const msgs=chatConversations[admin.id]||[];
                  const unread=typeof chatUnread==='object'?chatUnread[admin.id]||0:0;
                  const lastMsg=msgs[msgs.length-1];
                  return(
                    <div key={admin.id} onClick={()=>{
                      setChatAdminId(admin.id);
                      setChatUnread(u=>({...u,[admin.id]:0}));
                      fetch('https://control--asistencia-default-rtdb.firebaseio.com/chat/admin_'+admin.id+'.json').then(r=>r.json()).then(data=>{
                        if(data){const arr=Object.values(data).filter(Boolean).sort((a,b)=>new Date(a.timestamp)-new Date(b.timestamp));setChatConversations(c=>({...c,[admin.id]:arr}));}
                      });
                    }} style={{display:'flex',alignItems:'center',gap:'12px',padding:'12px',background:'var(--bg-elevated)',borderRadius:'10px',cursor:'pointer',marginBottom:'8px',border:unread>0?'1px solid rgba(0,229,160,0.4)':'1px solid var(--border)'}}>
                      <div style={{position:'relative'}}>
                        <span style={{fontSize:'28px'}}>{admin.photo||'👤'}</span>
                        {adminPresence[admin.id]?.online && new Date() - new Date(adminPresence[admin.id]?.lastSeen) < 60000 && (
                          <span style={{position:'absolute',bottom:0,right:0,width:'10px',height:'10px',background:'#22c55e',borderRadius:'50%',border:'2px solid var(--bg-card)'}}></span>
                        )}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                          <span style={{fontWeight:'700',color:'var(--text-primary)',fontSize:'13px'}}>{admin.name}</span>
                          {adminPresence[admin.id]?.online && new Date() - new Date(adminPresence[admin.id]?.lastSeen) < 60000
                            ? <span style={{fontSize:'10px',color:'#22c55e',fontWeight:'600'}}>● En línea</span>
                            : adminPresence[admin.id]?.lastSeen
                              ? <span style={{fontSize:'10px',color:'var(--text-muted)'}}>Última vez: {new Date(adminPresence[admin.id].lastSeen).toLocaleTimeString('es-CL',{hour:'2-digit',minute:'2-digit'})}</span>
                              : <span style={{fontSize:'10px',color:'var(--text-muted)'}}>Sin conexión</span>
                          }
                        </div>
                        <div style={{fontSize:'11px',color:'var(--text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{lastMsg?lastMsg.text:'Sin mensajes'}</div>
                      </div>
                      {unread>0&&<span style={{background:'#ef4444',color:'white',borderRadius:'50%',width:'20px',height:'20px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',fontWeight:'700'}}>{unread}</span>}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{flex:1,overflowY:'auto',padding:'12px',display:'flex',flexDirection:'column',gap:'6px'}} ref={el=>{if(el)el.scrollTop=el.scrollHeight;}}>
              {(chatConversations[userRole==='admin'?currentUser.id:chatAdminId]||[]).length===0 ? (
                <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:'13px',margin:'auto'}}>
                  <div style={{fontSize:'40px',marginBottom:'8px'}}>💬</div>
                  <p>Sin mensajes aun</p>
                </div>
              ) : (chatConversations[userRole==='admin'?currentUser.id:chatAdminId]||[]).map(msg => {
                const isMine = msg.senderId === currentUser.id;
                const isRead = msg.readBy && msg.readBy.includes(isMine ? (userRole==='admin'?1000:999) : currentUser.id);
                return (
                  <div key={msg.id} style={{display:'flex',flexDirection:'column',alignItems:isMine?'flex-end':'flex-start'}}>
                    <div style={{maxWidth:'78%',background:isMine?'var(--accent)':'var(--bg-elevated)',color:isMine?'#0a0f1a':'var(--text-primary)',padding:'8px 12px',borderRadius:'14px',fontSize:'13px',wordBreak:'break-word'}}>{msg.text}</div>
                    <div style={{display:'flex',alignItems:'center',gap:'4px',marginTop:'2px'}}>
                      <span style={{fontSize:'10px',color:'var(--text-muted)'}}>{msg.senderName} · {new Date(msg.timestamp).toLocaleTimeString('es-CL',{hour:'2-digit',minute:'2-digit'})}</span>
                      {isMine && <span style={{fontSize:'11px',color:isRead?'#3b82f6':'var(--text-muted)',fontWeight:'700'}}>{isRead?'✓✓':'✓'}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{padding:'6px 12px',borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
              <span>🔔</span>
              <input type="range" min="0" max="1" step="0.05" value={chatVolume} onChange={e=>{setChatVolume(parseFloat(e.target.value));localStorage.setItem('chatVolume',e.target.value);}} style={{flex:1,accentColor:'var(--accent)'}}/>
              <span style={{fontSize:'11px',color:'var(--text-muted)'}}>{Math.round(chatVolume*100)}%</span>
            </div>
            <div style={{padding:'10px 12px',borderTop:'1px solid var(--border)',display:'flex',gap:'8px',flexShrink:0}}>
              <input className="input-field" placeholder="Escribe un mensaje..." value={chatInput} onChange={e=>setChatInput(e.target.value)} style={{flex:1,fontSize:'13px',padding:'8px 12px'}}/>
              <button onClick={()=>{
                if(!chatInput.trim())return;
                const adminId=userRole==='admin'?currentUser.id:chatAdminId;
                if(!adminId)return;
                const msg={id:Date.now(),text:chatInput.trim(),senderId:currentUser.id,senderName:currentUser.name||(userRole==='manager'?'Gerente':'Admin'),senderRole:userRole,timestamp:new Date().toISOString()};
                const prev=chatConversations[adminId]||[];
                const updated=[...prev,msg];
                setChatConversations(c=>({...c,[adminId]:updated}));
                const obj={};updated.forEach(m=>{obj[m.id]=m;});
                fetch('https://control--asistencia-default-rtdb.firebaseio.com/chat/admin_'+adminId+'.json',{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(obj)});
                setChatInput('');
              }} style={{padding:'8px 16px',background:'var(--accent)',color:'#0a0f1a',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>Enviar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL GESTIÓN DE PAGOS ── */}
      {showPagosModal && pagosEmployee && userRole === 'manager' && (
        <PagosModal
          employee={pagosEmployee}
          employees={employees}
          currentUser={currentUser}
          saveEmployees={saveEmployees}
          onClose={() => setShowPagosModal(false)}
          onUpdate={(empActualizado) => setPagosEmployee(empActualizado)}
        />
      )}

      {showCalendar && calendarEmployee && (
        <>
        <MonthlyCalendar
          key={calendarEmployee?.id || calendarEmployee?.name}
          employee={calendarEmployee}
          employeeAbsences={JSON.parse(localStorage.getItem('employeeAbsences') || '[]').filter(a => String(a.employeeId) === String(calendarEmployee?.id))}
          onClose={() => {
            setShowCalendar(false);
            setCalendarEmployee(null);
          }}
          onSave={(updatedEmployee) => {
            const arr = Array.isArray(employees) ? employees : [];
            const exists = arr.find(e => e.id === updatedEmployee.id);
            if (exists) {
              // Empleado existente — actualizar con historial de horario
              const empWithHistory = { ...updatedEmployee, lastModifiedBy: currentUser?.name || 'Admin' };
              const updated = arr.map(e => e.id === updatedEmployee.id ? empWithHistory : e);
              setEmployees(updated);
              localStorage.setItem('employees', JSON.stringify(updated));
              fetch('https://control--asistencia-default-rtdb.firebaseio.com/employees/' + updatedEmployee.id + '.json', {method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(sanitizeForFirebase(updatedEmployee))}).then(()=>console.log('✅ Guardado en Firebase')).catch(e=>console.error('❌',e));
            }
            // Siempre actualizar employeeFormData con el monthlySchedule
            setEmployeeFormData(prev => ({ ...prev, monthlySchedule: updatedEmployee.monthlySchedule || prev.monthlySchedule }));
            setTimeout(() => { setShowCalendar(false); setCalendarEmployee(null); }, 1500);
          }}
        />
        />
        </>
      )}
      <div className="main-content">
        {userRole === 'employee' ? (
          <>
            <div className="navigation">
              <button 
                className={`nav-btn ${currentView === 'home' ? 'active' : ''}`}
                onClick={() => setCurrentView('home')}
                style={{borderBottom: currentView === 'home' ? '3px solid #3b82f6' : 'none'}}
              >
                <Clock size={18} color={currentView === 'home' ? '#3b82f6' : undefined} />
                <span style={{color: currentView === 'home' ? '#3b82f6' : undefined}}>Inicio</span>
              </button>
              <button 
                className={`nav-btn ${currentView === 'history' ? 'active' : ''}`}
                onClick={() => setCurrentView('history')}
                style={{borderBottom: currentView === 'history' ? '3px solid #22c55e' : 'none'}}
              >
                <FileText size={18} color={currentView === 'history' ? '#22c55e' : undefined} />
                <span style={{color: currentView === 'history' ? '#22c55e' : undefined}}>Historial</span>
              </button>
              <button 
                className={`nav-btn ${currentView === 'tasks' ? 'active' : ''}`}
                onClick={() => setCurrentView('tasks')}
                style={{borderBottom: currentView === 'tasks' ? '3px solid #f59e0b' : 'none', position:'relative'}}
              >
                <CheckCircle size={18} color={currentView === 'tasks' ? '#f59e0b' : undefined} />
                <span style={{color: currentView === 'tasks' ? '#f59e0b' : undefined}}>Tareas</span>
                {(() => {
                  const myPendingTasks = (Array.isArray(tasks) ? tasks : []).filter(t => String(t.assignedTo) === String(currentUser?.id) && t.status === 'pending').length;
                  return myPendingTasks > 0 ? <span style={{position:'absolute', top:'4px', right:'4px', background:'#f59e0b', color:'#0a0f1e', borderRadius:'50%', width:'16px', height:'16px', fontSize:'10px', fontWeight:'900', display:'flex', alignItems:'center', justifyContent:'center'}}>{myPendingTasks}</span> : null;
                })()}
              </button>
              <button 
                className={`nav-btn ${currentView === 'schedule' ? 'active' : ''}`}
                onClick={() => setCurrentView('schedule')}
                style={{borderBottom: currentView === 'schedule' ? '3px solid #8b5cf6' : 'none', position:'relative'}}
              >
                <Calendar size={18} color={currentView === 'schedule' ? '#8b5cf6' : undefined} />
                <span style={{color: currentView === 'schedule' ? '#8b5cf6' : undefined}}>Mi Horario</span>
                {(() => {
                  const emp = (Array.isArray(employees) ? employees : []).find(e => e.id === currentUser?.id);
                  const hasSchedule = emp?.monthlySchedule && Object.values(emp.monthlySchedule).some(d => d?.enabled && d?.start)
                    || (emp?.useFlexibleSchedule && emp?.weeklySchedule && Object.values(emp.weeklySchedule).some(d => d?.enabled));
                  return !hasSchedule ? <span style={{position:'absolute', top:'2px', right:'2px', fontSize:'14px'}}>⚠️</span> : null;
                })()}
              </button>
            </div>
            
            {currentView === 'home' && <EmployeeHomeView />}
            {currentView === 'history' && <EmployeeHistoryView />}
            {currentView === 'tasks' && <TasksView />}
            {currentView === 'schedule' && <EmployeeScheduleView />}
          </>
        ) : (
          <>
            <div className="navigation">
              <button 
                className={`nav-btn ${currentView === 'dashboard' ? 'active' : ''}`}
                onClick={() => setCurrentView('dashboard')}
              >
                <TrendingUp size={18} />
                Dashboard
              </button>
              <button 
                className={`nav-btn ${currentView === 'employees' ? 'active' : ''}`}
                onClick={() => setCurrentView('employees')}
              >
                <Users size={18} />
                Empleados
              </button>
              <button 
                className={`nav-btn ${currentView === 'reports' ? 'active' : ''}`}
                onClick={() => setCurrentView('reports')}
              >
                <FileText size={18} />
                Reportes
              </button>
              <button 
                className={`nav-btn ${currentView === 'tasks' ? 'active' : ''}`}
                onClick={() => setCurrentView('tasks')}
              >
                <CheckCircle size={18} />
                Tareas
              </button>
              {userRole === 'manager' && (
                <button
                  className={`nav-btn ${currentView === 'centro_ia' ? 'active' : ''}`}
                  onClick={() => setCurrentView('centro_ia')}
                >
                  🧠
                  Centro IA
                </button>
              )}
            </div>
            
            {currentView === 'dashboard' && <AdminDashboardView />}
            {currentView === 'employees' && <AdminEmployeesView />}
            {currentView === 'reports' && <AdminReportsView />}
            {currentView === 'tasks' && <TasksView />}
            {currentView === 'centro_ia' && <CentroIAView />}
          </>
        )}
      </div>

      <div className="app-footer">
        <p>Desarrollado por <strong>César Romero H.</strong> • 2026</p>
      </div>

      
      {/* Modal de solicitud de permiso/vacaciones */}
      
      {/* Vista de solicitudes para el empleado */}
      {showEmployeeRequestsModal && (
        <div className="modal-overlay" onClick={() => setShowEmployeeRequestsModal(false)}>
          <div className="modal-content" style={{maxWidth: '600px'}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Mis Solicitudes</h2>
              <button onClick={() => setShowEmployeeRequestsModal(false)} className="btn-close">✕</button>
            </div>
            <div className="modal-body">
              <h3>⏳ Pendientes de Aprobación</h3>
              {absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'pending').length === 0 ? (
                <p style={{textAlign:'center', color:'var(--text-muted)', padding:'12px'}}>No tienes solicitudes pendientes</p>
              ) : (
                absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'pending').map(req => (
                  <div key={req.id} style={{border:'1px solid rgba(245,158,11,0.4)', borderRadius:'8px', padding:'12px', marginBottom:'8px', background:'rgba(245,158,11,0.1)'}}>
                    <p style={{margin:'4px 0', fontWeight:'bold'}}>
                      {req.type === 'sick' && '🤒 Licencia Médica'}
                      {req.type === 'vacation' && '🏖️ Vacaciones'}
                      {req.type === 'personal' && '👤 Permiso Personal'}
                      {req.type === 'other' && '📋 Otro'}
                    </p>
                    <p style={{margin:'4px 0'}}>
                    📅 {req.startDate} al {req.endDate}
                    {req.totalDays && (
                      <span style={{marginLeft:'8px', fontWeight:'bold', color:'var(--accent)'}}>
                        ({req.totalDays} días{req.type === 'vacation' ? ' hábiles' : ''})
                      </span>
                    )}
                  </p>
                    <p style={{margin:'4px 0', fontSize:'12px', color:'var(--text-muted)'}}>"{req.reason}"</p>
                  </div>
                ))
              )}
              
              <h3 style={{marginTop:'20px'}}>✅ Aprobadas</h3>
              {absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'approved').length === 0 ? (
                <p style={{textAlign:'center', color:'var(--text-muted)', padding:'12px'}}>No tienes solicitudes aprobadas</p>
              ) : (
                absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'approved').map(req => (
                  <div key={req.id} style={{border:'1px solid rgba(0,229,160,0.35)', borderRadius:'8px', padding:'12px', marginBottom:'8px', background:'rgba(0,229,160,0.08)'}}>
                    <p style={{margin:'4px 0', fontWeight:'bold'}}>
                      {req.type === 'sick' && '🤒 Licencia Médica'}
                      {req.type === 'vacation' && '🏖️ Vacaciones'}
                      {req.type === 'personal' && '👤 Permiso Personal'}
                      {req.type === 'other' && '📋 Otro'}
                    </p>
                    <p style={{margin:'4px 0'}}>
                    📅 {req.startDate} al {req.endDate}
                    {req.totalDays && (
                      <span style={{marginLeft:'8px', fontWeight:'bold', color:'var(--accent)'}}>
                        ({req.totalDays} días{req.type === 'vacation' ? ' hábiles' : ''})
                      </span>
                    )}
                  </p>
                  </div>
                ))
              )}
              
              <h3 style={{marginTop:'20px'}}>❌ Rechazadas</h3>
              {absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'rejected').length === 0 ? (
                <p style={{textAlign:'center', color:'var(--text-muted)', padding:'12px'}}>No tienes solicitudes rechazadas</p>
              ) : (
                absenceRequests.filter(r => r.employeeId === currentUser.id && r.status === 'rejected').map(req => (
                  <div key={req.id} style={{border:'1px solid rgba(239,68,68,0.3)', borderRadius:'12px', padding:'14px 16px', marginBottom:'10px', background:'rgba(239,68,68,0.08)'}}>
                    <p style={{margin:'0 0 6px 0', fontWeight:'700', fontSize:'14px', color:'#f1f5f9'}}>
                      {req.type === 'sick' && '🤒 Licencia Médica'}
                      {req.type === 'vacation' && '🏖️ Vacaciones'}
                      {req.type === 'personal' && '👤 Permiso Personal'}
                      {req.type === 'other' && '📋 Otro'}
                    </p>
                    <p style={{margin:'0', fontSize:'13px', color:'#94a3b8', fontFamily:'"DM Mono",monospace'}}>
                      📅 {req.startDate} al {req.endDate}
                      {req.totalDays && (
                        <span style={{marginLeft:'8px', fontWeight:'700', color:'var(--accent)'}}>
                          ({req.totalDays} días{req.type === 'vacation' ? ' hábiles' : ''})
                        </span>
                      )}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showAbsenceModal && (
        <div className="modal-overlay" onClick={() => setShowAbsenceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📝 Solicitar Permiso/Vacaciones</h2>
              <button onClick={() => setShowAbsenceModal(false)} className="btn-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tipo de Ausencia</label>
                <select id="absenceType" className="input-field">
                  <option value="sick">🤒 Licencia Médica</option>
                  <option value="vacation">🏖️ Vacaciones</option>
                  <option value="personal">👤 Permiso Personal</option>
                  <option value="other">📋 Otro</option>
                </select>
              </div>
              <div className="form-group">
                <label>Fecha Inicio</label>
                <input type="date" id="absenceStartDate" className="input-field" />
              </div>
              <div className="form-group">
                <label>Fecha Fin</label>
                <input type="date" id="absenceEndDate" className="input-field" />
              </div>
              <div className="form-group">
                <label>Motivo</label>
                <textarea id="absenceReason" className="input-field" rows="3" placeholder="Describe el motivo de tu solicitud..."></textarea>
              </div>
              <button 
                className="btn-primary"
                onClick={() => {
                  const type = document.getElementById('absenceType').value;
                  const startDate = document.getElementById('absenceStartDate').value;
                  const endDate = document.getElementById('absenceEndDate').value;
                  const reason = document.getElementById('absenceReason').value;
                  
                  if (!startDate || !endDate) {
                    alert('⚠️ Por favor completa las fechas');
                    return;
                  }
                  
                  // Validar que startDate sea <= endDate
                  if (new Date(startDate) > new Date(endDate)) {
                    alert('⚠️ La fecha de inicio debe ser anterior o igual a la fecha fin');
                    return;
                  }
                  
                  // Calcular días (hábiles para vacaciones, todos para otros)
                  const totalDays = type === 'vacation' 
                    ? countBusinessDays(startDate, endDate)
                    : Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
                  
                  // Mostrar confirmación
                  const daysLabel = type === 'vacation' 
                    ? `${totalDays} días hábiles (sin contar fines de semana)`
                    : `${totalDays} días`;
                  
                  const newRequest = {
                    id: Date.now(),
                    employeeId: currentUser.id,
                    employeeName: currentUser.name,
                    type,
                    startDate,
                    endDate,
                    totalDays,
                    reason,
                    status: 'pending',
                    requestDate: new Date().toISOString()
                  };
                  
                  setAbsenceRequests([...absenceRequests, newRequest]);
                  localStorage.setItem('absenceRequests', JSON.stringify([...absenceRequests, newRequest]));
                  
                  alert(`✅ Solicitud enviada (${type === 'vacation' ? totalDays + ' días hábiles' : totalDays + ' días'}). Espera la aprobación del gerente.`);
                  setShowAbsenceModal(false);
                }}
              >
                📤 Enviar Solicitud
              </button>
            </div>
          </div>
        </div>
      )}

      {showCameraModal && (
        <CameraModal 
          onClose={() => {
            setShowCameraModal(false);
            setCapturedPhoto(null);
          }}
          onPhotoCapture={(photoData) => {
            console.log('📸 onPhotoCapture recibido, photoData:', photoData ? 'SÍ' : 'NO');
            
            // Guardar la foto capturada
            if (typeof setCapturedPhoto === 'function') {
              setCapturedPhoto(photoData);
            }
            
            // Ejecutar la acción pendiente (registrar entrada)
            if (pendingCheckInAction && typeof pendingCheckInAction === 'function') {
              console.log('✅ Ejecutando pendingCheckInAction');
              pendingCheckInAction(photoData);
              setPendingCheckInAction(null);
            } else {
              console.log('⚠️ No hay pendingCheckInAction');
            }
            
            // Cerrar modal
            setShowCameraModal(false);
          }}
        />
      )}
      {showConfigModal && <ConfigModal />}
      {showAbsenceManager && <AbsenceManager />}
      {showAbsenceRequestsManager && <AbsenceRequestsManager />}
      {showEmployeeModal && <EmployeeModal />}
      {showPermissionsModal && <PermissionsModal />}
      {showAdminRegistration && <AdminRegistrationModal />}
      {showChatGerente && (
        <div className="modal-overlay" onClick={() => { setShowChatGerente(false); setChatGerenteMensajes([]); }}>
          <div className="modal-content" style={{maxWidth:'520px', maxHeight:'80vh', display:'flex', flexDirection:'column'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🤖 Pregúntale a la IA</h2>
              <button className="btn-close" onClick={() => { setShowChatGerente(false); setChatGerenteMensajes([]); }}>X</button>
            </div>
            <div style={{flex:1, overflowY:'auto', padding:'12px 0', display:'flex', flexDirection:'column', gap:'10px', minHeight:'200px'}}>
              {chatGerenteMensajes.length === 0 && (
                <p style={{fontSize:'12px', color:'var(--text-secondary)', textAlign:'center', margin:'20px 0'}}>
                  Pregúntame sobre asistencia, tareas, productos o el desempeño de cualquier empleado.<br/>
                  Ejemplo: "¿Quién llegó atrasado este mes?" o "Dame un informe de Cesar"
                </p>
              )}
              {chatGerenteMensajes.map((msg, idx) => (
                <div key={idx} style={{alignSelf: msg.tipo === 'pregunta' ? 'flex-end' : 'flex-start', maxWidth:'90%'}}>
                  {msg.tipo === 'accion' ? (
                    <div style={{background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.3)', borderRadius:'10px', padding:'12px', fontSize:'13px'}}>
                      <p style={{color:'#6366f1', fontWeight:'700', margin:'0 0 10px'}}>🤖 {msg.texto}</p>
                      {msg.accion === 'CREAR_TAREAS' && msg.propuesta?.empleados?.map((emp, ei) => (
                        <div key={ei} style={{marginBottom:'10px'}}>
                          <p style={{fontSize:'12px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 6px'}}>👤 {emp.nombre}</p>
                          {(emp.tareas||[]).map((t, ti) => (
                            <div key={ti} style={{background:'var(--bg-card)', borderRadius:'6px', padding:'6px 10px', marginBottom:'4px', fontSize:'12px', color:'var(--text-secondary)'}}>
                              <span style={{fontWeight:'600', color:'var(--text-primary)'}}>• {t.titulo}</span><br/>
                              {t.descripcion}
                            </div>
                          ))}
                        </div>
                      ))}
                      {msg.accion === 'PUBLICAR_ANUNCIO' && (
                        <div style={{background:'var(--bg-card)', borderRadius:'6px', padding:'10px', marginBottom:'10px', fontSize:'12px', color:'var(--text-primary)'}}>
                          📢 {msg.propuesta?.texto}
                        </div>
                      )}
                      {!msg.confirmado && (
                        <div style={{display:'flex', gap:'8px', marginTop:'10px'}}>
                          <button onClick={() => {
                            try {
                              if (msg.accion === 'CREAR_TAREAS') {
                                const hoy = new Date();
                                const ds = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-${String(hoy.getDate()).padStart(2,'0')}`;
                                let creadas = 0;
                                const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
                                const nuevas = [];
                                (msg.propuesta?.empleados||[]).forEach(emp => {
                                  (emp.tareas||[]).forEach(t => {
                                    nuevas.push({
                                      id: Date.now() + creadas++,
                                      title: t.titulo, description: t.descripcion,
                                      assignedTo: emp.id, assignedName: emp.nombre,
                                      priority: 'medium', dueDate: ds, dueTime: '21:00',
                                      createdBy: 'Gerente', createdByRole: 'manager',
                                      createdAt: new Date().toISOString(),
                                      status: 'pending', steps: [], employeeObservation: '',
                                      completedAt: null, reviewedBy: null, reviewedAt: null,
                                      reviewStatus: null, reviewComment: '', managerNote: '',
                                      generadaPorIA: true
                                    });
                                  });
                                });
                                const updated = [...nuevas, ...lsTasks];
                                saveTasksToStorage(updated);
                                setChatGerenteMensajes(prev => prev.map((m,i) => i===idx ? {...m, confirmado:true} : m));
                                setChatGerenteMensajes(prev => [...prev, {tipo:'respuesta', texto:`✅ ${creadas} tarea(s) creadas correctamente.`}]);
                              } else if (msg.accion === 'PUBLICAR_ANUNCIO') {
                                createAnnouncement(msg.propuesta?.texto, msg.propuesta?.tipo || 'info', 1);
                                setChatGerenteMensajes(prev => prev.map((m,i) => i===idx ? {...m, confirmado:true} : m));
                                setChatGerenteMensajes(prev => [...prev, {tipo:'respuesta', texto:'✅ Anuncio publicado para todos los empleados.'}]);
                              }
                            } catch(e) {
                              setChatGerenteMensajes(prev => [...prev, {tipo:'respuesta', texto:'Error al ejecutar la acción. Intenta nuevamente.'}]);
                            }
                          }} style={{flex:1, padding:'8px', background:'rgba(34,197,94,0.15)', color:'var(--green)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>
                            ✅ Confirmar
                          </button>
                          <button onClick={() => setChatGerenteMensajes(prev => prev.map((m,i) => i===idx ? {...m, confirmado:true} : m))}
                            style={{flex:1, padding:'8px', background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700'}}>
                            ❌ Cancelar
                          </button>
                        </div>
                      )}
                      {msg.confirmado && <p style={{fontSize:'11px', color:'var(--text-secondary)', margin:'8px 0 0'}}>Acción procesada.</p>}
                    </div>
                  ) : (
                    <div style={{
                      background: msg.tipo === 'pregunta' ? 'var(--blue)' : 'var(--bg-elevated)',
                      color: msg.tipo === 'pregunta' ? 'white' : 'var(--text-primary)',
                      padding:'10px 12px', borderRadius:'10px', fontSize:'13px', whiteSpace:'pre-wrap', lineHeight:'1.5'
                    }}>
                      {msg.texto}
                    </div>
                  )}
                </div>
              ))}
              {chatGerenteLoading && (
                <div style={{alignSelf:'flex-start', fontSize:'12px', color:'var(--text-secondary)'}}>Analizando datos...</div>
              )}
            </div>
            <div style={{display:'flex', gap:'8px', paddingTop:'10px', borderTop:'1px solid var(--border)'}}>
              <input
                id="chatGerenteInput"
                placeholder="Escribe tu pregunta..."
                style={{flex:1, padding:'10px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'8px', color:'var(--text-primary)', fontSize:'13px'}}
                onKeyDown={e => {
                  if (e.key === 'Enter' && e.target.value.trim() && !chatGerenteLoading) {
                    document.getElementById('btnEnviarChatGerente').click();
                  }
                }}
              />
              <button id="btnEnviarChatGerente" disabled={chatGerenteLoading} onClick={async () => {
                const input = document.getElementById('chatGerenteInput');
                const pregunta = input.value.trim();
                if (!pregunta) return;
                input.value = '';
                setChatGerenteMensajes(prev => [...prev, {tipo:'pregunta', texto:pregunta}]);
                setChatGerenteLoading(true);
                try {
                  const lsRecords = JSON.parse(localStorage.getItem('attendanceRecords') || '[]');
                  const lsTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
                  const lsEmps = JSON.parse(localStorage.getItem('employees') || '[]');
                  const lsProds = JSON.parse(localStorage.getItem('missingProducts') || '[]');
                  const lsAbsenceReq = JSON.parse(localStorage.getItem('absenceRequests') || '[]');

                  const _hoyChat = new Date();
                  const _dateStrChat = `${_hoyChat.getFullYear()}-${String(_hoyChat.getMonth()+1).padStart(2,'0')}-${String(_hoyChat.getDate()).padStart(2,'0')}`;
                  const datos = {
                    empleados: lsEmps.filter(e => e?.status === 'active').map(e => ({
                      id:e.id, nombre:e.name, cargo:e.position,
                      tieneTurnoHoy: !!(e?.monthlySchedule?.[_dateStrChat]?.enabled && e?.monthlySchedule?.[_dateStrChat]?.start)
                    })),
                    notaSobreTurnos: "tieneTurnoHoy indica si el empleado debe trabajar especificamente el dia de hoy segun su calendario. No todos los empleados trabajan todos los dias, trabajan por turnos rotativos. Al evaluar asistencia de hoy, comparar solo contra los que tienen tieneTurnoHoy=true.",
                    asistenciaUltimos60Dias: lsRecords.slice(-400).map(r => ({
                      nombre: r?.employeeName, fecha: r?.date, entrada: r?.checkIn, salida: r?.checkOut,
                      atraso: r?.isLate || false, minutosAtraso: r?.lateMinutes || 0
                    })),
                    tareas: lsTasks.slice(-300).map(t => ({
                      titulo: t?.title, asignado: t?.assignedName, fecha: t?.dueDate, status: t?.status, reviewStatus: t?.reviewStatus
                    })),
                    productosPendientes: lsProds.filter(p => p?.status === 'pending').map(p => ({
                      nombre: p?.name, reportadoPor: p?.reportedBy, fecha: p?.reportedAt
                    })),
                    solicitudesAusencia: lsAbsenceReq.map(r => ({
                      empleado: r?.employeeName, tipo: r?.type, status: r?.status
                    }))
                  };

                  const response = await fetch('https://ocrfrjlehptgyudfxdah.supabase.co/functions/v1/chat-gerente', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'apikey': 'sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-',
                      'Authorization': 'Bearer sb_publishable_azYnfpt-SJvzBylvwvZMtQ_m1YbHgb-'
                    },
                    body: JSON.stringify({ pregunta, datos })
                  });
                  const result = await response.json();
                  if (result.accion && result.propuesta) {
                    setChatGerenteMensajes(prev => [...prev, {tipo:'accion', texto: result.respuesta, accion: result.accion, propuesta: result.propuesta}]);
                  } else {
                    setChatGerenteMensajes(prev => [...prev, {tipo:'respuesta', texto: result.respuesta || result.error || 'No se pudo obtener respuesta'}]);
                  }
                } catch(e) {
                  setChatGerenteMensajes(prev => [...prev, {tipo:'respuesta', texto:'Error al conectar con el servicio de IA. Intenta nuevamente.'}]);
                } finally {
                  setChatGerenteLoading(false);
                }
              }} style={{padding:'10px 16px', background:'linear-gradient(135deg, #6366f1, #8b5cf6)', color:'white', border:'none', borderRadius:'8px', cursor: chatGerenteLoading ? 'wait' : 'pointer', fontSize:'13px', fontWeight:'600', opacity: chatGerenteLoading ? 0.7 : 1}}>
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AppContext.Provider>
  );
};

// Componente Coffee icon
const Coffee = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
    <line x1="6" y1="1" x2="6" y2="4"></line>
    <line x1="10" y1="1" x2="10" y2="4"></line>
    <line x1="14" y1="1" x2="14" y2="4"></line>
  </svg>
);

// Estilos CSS (continuación en siguiente parte debido al límite de caracteres)
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --bg-primary: #0a0f1e;
    --bg-secondary: #111827;
    --bg-card: #161d2e;
    --bg-card-hover: #1c2539;
    --bg-elevated: #1e2840;
    --accent: #00e5a0;
    --accent-dim: #00c48a;
    --accent-glow: rgba(0, 229, 160, 0.15);
    --accent-glow-strong: rgba(0, 229, 160, 0.3);
    --blue: #3b82f6;
    --orange: #f59e0b;
    --red: #ef4444;
    --text-primary: #f1f5f9;
    --text-secondary: #94a3b8;
    --text-muted: #475569;
    --border: rgba(255,255,255,0.07);
    --border-accent: rgba(0, 229, 160, 0.3);
    --shadow: 0 4px 24px rgba(0,0,0,0.4);
    --shadow-lg: 0 8px 40px rgba(0,0,0,0.6);
    --radius: 12px;
    --radius-lg: 16px;
    --radius-xl: 20px;
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  body { background: var(--bg-primary); color: var(--text-primary); font-family: "DM Sans", sans-serif; -webkit-font-smoothing: antialiased; }

  .app-container, .login-container {
    font-family: "DM Sans", sans-serif;
    background: var(--bg-primary);
    min-height: 100vh;
    padding: 20px;
    position: relative;
  }

  .login-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    padding: 48px 40px;
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-lg);
    max-width: 560px;
    width: 100%;
    margin: 40px auto;
    position: relative;
  }

  .login-card::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  }

  .login-card h1, .login-card h2 { font-size: 26px; font-weight: 700; color: var(--text-primary); margin-bottom: 6px; letter-spacing: -0.5px; }
  .login-card p, .login-card .subtitle { color: var(--text-secondary); font-size: 14px; margin-bottom: 32px; }

  .login-logo { width: 100px; height: 100px; margin: 0 auto 28px; display: flex; align-items: center; justify-content: center; overflow: hidden; border-radius: 20px; border: 2px solid var(--border); background: var(--bg-elevated); }
  .login-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .login-icon { width: 80px; height: 80px; background: var(--accent-glow); border: 1px solid var(--border-accent); border-radius: 20px; display: flex; align-items: center; justify-content: center; font-size: 36px; margin: 0 auto 24px; }

  .role-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 32px; }
  .role-card { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 28px 20px; cursor: pointer; transition: all 0.2s ease; text-align: center; }
  .role-card:hover { border-color: var(--accent); background: var(--accent-glow); transform: translateY(-2px); box-shadow: 0 8px 24px var(--accent-glow-strong); }
  .role-card .role-icon { font-size: 40px; margin-bottom: 14px; display: block; }
  .role-card h3 { font-size: 15px; font-weight: 600; color: var(--text-primary); }

  .employees-section h2, .employees-section h3 { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: var(--text-muted); margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--border); }
  .employees-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .employee-card-login { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--radius); padding: 20px 12px; cursor: pointer; transition: all 0.2s ease; text-align: center; }
  .employee-card-login:hover { border-color: var(--accent); background: var(--accent-glow); transform: translateY(-3px); box-shadow: 0 8px 20px var(--accent-glow-strong); }
  .employee-card-login .emp-avatar { font-size: 36px; margin-bottom: 10px; display: block; }
  .employee-card-login h3 { font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px; line-height: 1.3; }
  .employee-card-login p { font-size: 11px; color: var(--text-secondary); }

  .header { background: var(--bg-secondary); border-bottom: 1px solid var(--border); padding: 14px 24px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .app-icon { width: 36px; height: 36px; background: var(--accent-glow); border: 1px solid var(--border-accent); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; }
  .app-logo { width: 36px; height: 36px; border-radius: 10px; overflow: hidden; border: 1px solid var(--border); }
  .app-logo img { width: 100%; height: 100%; object-fit: cover; }
  .app-title { font-size: 16px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .header-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

  .btn-permissions { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-secondary); padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-permissions:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }
  .btn-logout { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: #ef4444; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-logout:hover { background: rgba(239,68,68,0.2); }

  .main-content { max-width: 1100px; margin: 0 auto; padding: 24px 20px; }

  .navigation { display: flex; gap: 4px; background: var(--bg-secondary); border: 1px solid var(--border); border-radius: var(--radius); padding: 5px; margin-bottom: 24px; }
  .nav-btn { flex: 1; background: transparent; border: none; color: var(--text-muted); padding: 10px 16px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px; transition: all 0.2s; font-family: "DM Sans", sans-serif; white-space: nowrap; }
  .nav-btn:hover { color: var(--text-primary); background: var(--bg-elevated); }
  .nav-btn.active { background: var(--accent-glow); color: var(--accent); border: 1px solid var(--border-accent); font-weight: 600; }

  .employee-status-card { background: linear-gradient(135deg, var(--bg-card) 0%, var(--bg-elevated) 100%); border: 1px solid var(--border); border-radius: var(--radius-xl); padding: 24px; margin-bottom: 20px; position: relative; overflow: hidden; }
  .employee-status-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, var(--accent), var(--blue)); }
  .employee-header-info { display: flex; align-items: center; gap: 16px; }
  .employee-avatar { font-size: 48px; background: var(--bg-elevated); border: 2px solid var(--border); border-radius: 14px; width: 64px; height: 64px; display: flex; align-items: center; justify-content: center; }
  .employee-info h2 { font-size: 20px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; margin-bottom: 3px; }
  .employee-info p { font-size: 13px; color: var(--text-secondary); margin-bottom: 8px; }
  .status-badges { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
  .status-badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; display: inline-flex; align-items: center; gap: 5px; }
  .status-badge.active { background: rgba(0,229,160,0.12); border: 1px solid rgba(0,229,160,0.3); color: var(--accent); }
  .status-badge.inactive { background: rgba(148,163,184,0.1); border: 1px solid rgba(148,163,184,0.2); color: var(--text-muted); }
  .status-badge.on-time { background: rgba(0,229,160,0.12); border: 1px solid rgba(0,229,160,0.3); color: var(--accent); }
  .status-badge.late { background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.3); color: #ef4444; }
  .date-display { font-size: 13px; color: var(--text-muted); display: flex; align-items: center; gap: 6px; margin-top: 12px; }

  .time-display { font-size: 36px; font-weight: 700; font-family: "DM Mono", monospace; color: var(--text-primary); letter-spacing: -1px; margin-bottom: 4px; }
  .date-text { font-size: 13px; color: var(--text-secondary); margin-bottom: 20px; text-transform: capitalize; }

  .gps-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px 20px; margin-bottom: 16px; }

  /* Location card */
  .location-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 18px 20px; margin-bottom: 16px; }
  .location-header { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); margin-bottom: 12px; }
  .location-status { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .location-text { font-size: 15px; font-weight: 600; }
  .location-text.verified { color: var(--accent); }
  .location-text.warning { color: var(--orange); }
  .location-details { font-size: 12px; color: var(--text-muted); margin-bottom: 4px; }
  .location-coords { font-size: 11px; color: var(--text-muted); font-family: "DM Mono", monospace; }

  /* Employee home wrapper */
  .employee-home { max-width: 600px; margin: 0 auto; padding: 20px; display: flex; flex-direction: column; gap: 0; }

  /* Time & date card */
  .time-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px 24px; margin-bottom: 16px; }

  /* Absence buttons */
  .btn-request-absence, .btn-view-my-requests { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-secondary); padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.2s; font-family: "DM Sans", sans-serif; margin-top: 12px; }
  .btn-request-absence:hover, .btn-view-my-requests:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }
  .gps-title { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
  .gps-status { font-size: 14px; font-weight: 600; display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
  .gps-status.ok { color: var(--accent); }
  .gps-status.error { color: var(--red); }
  .gps-details { font-size: 12px; color: var(--text-muted); font-family: "DM Mono", monospace; }

  .action-buttons { display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px; }
  .btn-primary { background: linear-gradient(135deg, var(--accent) 0%, #00c48a 100%); color: #0a0f1e; border: none; padding: 18px 24px; border-radius: var(--radius); font-size: 16px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: all 0.2s; font-family: "DM Sans", sans-serif; letter-spacing: -0.2px; box-shadow: 0 4px 20px var(--accent-glow-strong); }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 30px var(--accent-glow-strong); filter: brightness(1.05); }
  .btn-break { background: var(--bg-card); border: 1px solid rgba(245,158,11,0.3); color: var(--orange); padding: 16px 24px; border-radius: var(--radius); font-size: 15px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-break:hover { background: rgba(245,158,11,0.1); border-color: var(--orange); transform: translateY(-1px); }
  .btn-break.active { background: rgba(245,158,11,0.15); border-color: var(--orange); box-shadow: 0 4px 16px rgba(245,158,11,0.2); }
  .btn-checkout { background: var(--bg-card); border: 1px solid rgba(239,68,68,0.3); color: var(--red); padding: 16px 24px; border-radius: var(--radius); font-size: 15px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-checkout:hover { background: rgba(239,68,68,0.1); border-color: var(--red); transform: translateY(-1px); }

  .today-summary { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; margin-bottom: 20px; }
  .today-summary h3 { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); margin-bottom: 16px; }
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .summary-item { display: flex; align-items: center; gap: 12px; }
  .summary-item svg { color: var(--text-muted); flex-shrink: 0; }
  .summary-item .label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
  .summary-item .value { font-size: 18px; font-weight: 700; color: var(--text-primary); font-family: "DM Mono", monospace; letter-spacing: -0.5px; }

  .absence-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
  .btn-absence, .btn-view-requests { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-secondary); padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-absence:hover, .btn-view-requests:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }

  .history-view h2, .admin-dashboard h2, .employees-view h2, .reports-view h2 { font-size: 20px; font-weight: 700; color: var(--text-primary); margin-bottom: 20px; letter-spacing: -0.4px; }
  .history-record { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px 20px; margin-bottom: 10px; display: flex; justify-content: space-between; align-items: center; transition: all 0.2s; }
  .history-record:hover { border-color: rgba(255,255,255,0.12); background: var(--bg-card-hover); }

  .records-list { display: flex; flex-direction: column; gap: 12px; }
  .record-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 18px 20px; transition: all 0.2s; position: relative; overflow: hidden; }
  .record-card::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--accent); border-radius: 3px 0 0 3px; }
  .record-card:hover { border-color: rgba(255,255,255,0.12); transform: translateY(-1px); box-shadow: var(--shadow); }
  .record-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
  .record-date { font-size: 15px; font-weight: 700; color: var(--text-primary); font-family: "DM Mono", monospace; letter-spacing: -0.3px; }
  .record-times { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; }
  .time-item { display: flex; align-items: center; gap: 8px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 8px 12px; font-size: 13px; color: var(--text-secondary); }
  .time-item svg { color: var(--text-muted); flex-shrink: 0; }

  .admin-dashboard { padding: 4px 0; }
  .view-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
  .view-header h2 { font-size: 22px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.5px; margin: 0; }

  .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 24px; }
  .stat-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; display: flex; align-items: center; gap: 16px; transition: all 0.2s; }
  .stat-card:hover { border-color: rgba(255,255,255,0.12); transform: translateY(-2px); box-shadow: var(--shadow); }
  .stat-value { font-size: 28px; font-weight: 800; color: var(--text-primary); letter-spacing: -1px; line-height: 1; margin-bottom: 4px; font-family: "DM Mono", monospace; }
  .stat-label { font-size: 12px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500; }

  .schedule-display { margin-bottom: 24px; }
  .schedule-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
  .schedule-item { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px; display: flex; align-items: center; gap: 12px; }
  .schedule-icon { font-size: 24px; }
  .schedule-time { font-size: 20px; font-weight: 700; color: var(--text-primary); font-family: "DM Mono", monospace; letter-spacing: -0.5px; margin-bottom: 3px; }
  .schedule-desc { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
  .schedule-item svg { color: var(--text-muted); }
  .schedule-item .time { font-size: 20px; font-weight: 700; color: var(--text-primary); font-family: "DM Mono", monospace; letter-spacing: -0.5px; }
  .schedule-item .label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }

  .today-attendance { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; margin-bottom: 20px; }
  .today-attendance h3 { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); margin-bottom: 16px; }
  .attendance-item { display: flex; align-items: center; gap: 14px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .attendance-item:last-child { border-bottom: none; }
  .attendance-avatar { font-size: 28px; width: 44px; height: 44px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .attendance-info { flex: 1; }
  .attendance-info h4 { font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px; }
  .attendance-info p { font-size: 12px; color: var(--text-muted); font-family: "DM Mono", monospace; }
  .attendance-actions { display: flex; gap: 6px; }
  .btn-sm { width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-elevated); color: var(--text-secondary); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; font-size: 14px; }
  .btn-sm:hover { border-color: var(--accent); color: var(--accent); }
  .btn-sm.danger:hover { border-color: var(--red); color: var(--red); background: rgba(239,68,68,0.1); }

  .employees-view { padding: 4px 0; }
  .filter-tabs { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; }
  .filter-tab { background: var(--bg-card); border: 1px solid var(--border); color: var(--text-secondary); padding: 7px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .filter-tab:hover, .filter-tab.active { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }
  .employees-grid-admin { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }

  .employee-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; transition: all 0.2s; position: relative; overflow: hidden; }
  .employee-card:hover { border-color: rgba(255,255,255,0.12); transform: translateY(-2px); box-shadow: var(--shadow); }
  .employee-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
  .employee-card-actions { display: flex; gap: 5px; }
  .card-action-btn { width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border); background: var(--bg-elevated); color: var(--text-muted); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; font-size: 12px; }
  .card-action-btn:hover { border-color: var(--accent); color: var(--accent); }
  .card-action-btn.delete:hover { border-color: var(--red); color: var(--red); background: rgba(239,68,68,0.1); }
  .employee-card .emp-photo { font-size: 40px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 12px; width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
  .employee-card h3 { font-size: 15px; font-weight: 700; color: var(--text-primary); margin-bottom: 3px; letter-spacing: -0.2px; }
  .employee-card .dept { font-size: 12px; color: var(--text-muted); margin-bottom: 10px; }
  .employee-card .emp-code { font-size: 11px; color: var(--text-muted); font-family: "DM Mono", monospace; background: var(--bg-elevated); padding: 3px 8px; border-radius: 4px; display: inline-block; margin-bottom: 8px; }
  .employee-card .emp-schedule { font-size: 12px; color: var(--text-secondary); display: flex; align-items: center; gap: 5px; }
  .employee-advances { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border); }
  .advances-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: var(--text-muted); margin-bottom: 8px; }
  .advance-item { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; padding: 8px 10px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 7px; margin-bottom: 6px; }
  .advance-amount { font-size: 13px; font-weight: 700; color: var(--text-primary); font-family: "DM Mono", monospace; }
  .advance-status { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 2px 8px; border-radius: 10px; }
  .advance-status.aprobado { background: rgba(59,130,246,0.15); color: var(--blue); border: 1px solid rgba(59,130,246,0.3); }
  .advance-status.pendiente { background: rgba(245,158,11,0.15); color: var(--orange); border: 1px solid rgba(245,158,11,0.3); }
  .advance-status.pagado { background: rgba(0,229,160,0.12); color: var(--accent); border: 1px solid rgba(0,229,160,0.2); }
  .no-advances { font-size: 11px; color: var(--text-muted); text-align: center; padding: 4px 0; }

  /* ── TASKS VIEW ── */
  .tasks-view { padding: 0 4px; }
  .tasks-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
  .tasks-header h2 { font-size: 20px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; margin: 0; }
  .tasks-tabs { display: flex; gap: 4px; margin-bottom: 20px; background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 4px; }
  .tasks-tab { flex: 1; padding: 9px 14px; border-radius: 8px; border: none; background: transparent; color: var(--text-muted); font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; display: flex; align-items: center; justify-content: center; gap: 6px; }
  .tasks-tab.active { background: var(--bg-elevated); color: var(--accent); font-weight: 700; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
  .tasks-badge { background: var(--accent); color: #0a0f1e; font-size: 10px; font-weight: 800; padding: 1px 6px; border-radius: 10px; min-width: 18px; text-align: center; }
  .tasks-filters { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; align-items: center; }
  .tasks-select { background: var(--bg-card); border: 1px solid var(--border); border-radius: 8px; padding: 8px 12px; color: var(--text-primary); font-size: 13px; font-family: "DM Sans", sans-serif; outline: none; cursor: pointer; }
  .tasks-select:focus { border-color: var(--accent); }
  .tasks-empty { text-align: center; padding: 48px 20px; color: var(--text-muted); background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); }
  .tasks-list { display: flex; flex-direction: column; gap: 12px; }

  .task-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 16px 18px; transition: all 0.2s; position: relative; overflow: hidden; }
  .task-card::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; border-radius: 4px 0 0 4px; }
  .task-card.priority-high::before { background: var(--red); }
  .task-card.priority-medium::before { background: var(--orange); }
  .task-card.priority-low::before { background: var(--accent); }
  .task-card.product-card::before { background: var(--blue); }
  .task-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; gap: 8px; }
  .task-title { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0 0 6px 0; letter-spacing: -0.2px; }
  .task-desc { font-size: 13px; color: var(--text-secondary); margin: 0 0 10px 0; line-height: 1.5; }
  .task-meta { display: flex; flex-wrap: wrap; gap: 10px; font-size: 12px; color: var(--text-muted); margin-bottom: 10px; }
  .task-meta span { display: flex; align-items: center; gap: 4px; }

  .priority-badge { font-size: 11px; font-weight: 700; padding: 2px 10px; border-radius: 10px; letter-spacing: 0.3px; }
  .priority-badge.high { background: rgba(239,68,68,0.15); color: var(--red); border: 1px solid rgba(239,68,68,0.3); }
  .priority-badge.medium { background: rgba(245,158,11,0.15); color: var(--orange); border: 1px solid rgba(245,158,11,0.3); }
  .priority-badge.low { background: rgba(0,229,160,0.12); color: var(--accent); border: 1px solid rgba(0,229,160,0.25); }

  .task-status-badge { font-size: 11px; font-weight: 600; padding: 2px 10px; border-radius: 10px; }
  .task-status-badge.pending { background: rgba(148,163,184,0.1); color: var(--text-muted); border: 1px solid rgba(148,163,184,0.2); }
  .task-status-badge.done { background: rgba(0,229,160,0.12); color: var(--accent); border: 1px solid rgba(0,229,160,0.25); }
  .task-status-badge.not_done { background: rgba(239,68,68,0.1); color: var(--red); border: 1px solid rgba(239,68,68,0.2); }
  .task-status-badge.reviewed { background: rgba(59,130,246,0.12); color: var(--blue); border: 1px solid rgba(59,130,246,0.25); }

  .product-status-badge { font-size: 11px; font-weight: 600; padding: 2px 10px; border-radius: 10px; }
  .product-status-badge.pending { background: rgba(148,163,184,0.1); color: var(--text-muted); border: 1px solid rgba(148,163,184,0.2); }
  .product-status-badge.requested { background: rgba(59,130,246,0.12); color: var(--blue); border: 1px solid rgba(59,130,246,0.25); }
  .product-status-badge.resolved { background: rgba(0,229,160,0.12); color: var(--accent); border: 1px solid rgba(0,229,160,0.25); }

  .task-employee-response { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 10px 12px; margin-top: 10px; }
  .task-response-label { font-size: 12px; color: var(--text-secondary); display: block; margin-bottom: 4px; }
  .task-response-date { font-size: 11px; color: var(--text-muted); display: block; margin-top: 4px; }
  .task-review-info { font-size: 12px; color: var(--blue); background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.2); border-radius: 7px; padding: 6px 12px; margin-top: 10px; }
  .task-employee-actions { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border); }
  .task-obs-input { width: 100%; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 10px 12px; color: var(--text-primary); font-size: 13px; font-family: "DM Sans", sans-serif; outline: none; resize: vertical; box-sizing: border-box; }
  .task-obs-input:focus { border-color: var(--accent); }
  .task-action-btn { width: 30px; height: 30px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-elevated); color: var(--text-muted); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
  .task-action-btn.edit:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }
  .task-action-btn.delete:hover { border-color: var(--red); color: var(--red); background: rgba(239,68,68,0.1); }

  .btn-task-new { background: var(--accent-glow); border: 1px solid var(--border-accent); color: var(--accent); padding: 9px 16px; border-radius: var(--radius); font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-task-new:hover { background: var(--accent); color: #0a0f1e; box-shadow: 0 4px 16px var(--accent-glow-strong); }
  .btn-task-report { background: rgba(59,130,246,0.1); border: 1px solid rgba(59,130,246,0.3); color: var(--blue); padding: 9px 16px; border-radius: var(--radius); font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-task-report:hover { background: var(--blue); color: white; }
  .btn-task-done { background: rgba(0,229,160,0.12); border: 1px solid rgba(0,229,160,0.3); color: var(--accent); padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; flex: 1; }
  .btn-task-done:hover { background: var(--accent); color: #0a0f1e; }
  .btn-task-notdone { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.3); color: var(--red); padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; flex: 1; }
  .btn-task-notdone:hover { background: rgba(239,68,68,0.15); }
  .btn-task-review { background: rgba(59,130,246,0.1); border: 1px solid rgba(59,130,246,0.3); color: var(--blue); padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-task-review:hover { background: var(--blue); color: white; }
  .weekly-schedule-mini { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
  .day-pill { display: flex; align-items: center; gap: 6px; padding: 3px 0; }
  .day-pill.off { opacity: 0.3; }
  .day-letter { font-size: 11px; font-weight: 700; color: var(--text-muted); width: 14px; text-align: center; }
  .day-hours { font-size: 11px; color: var(--text-secondary); font-family: "DM Mono", monospace; }
  .day-pill.on .day-letter { color: var(--accent); }

  .btn-add-employee { background: var(--accent-glow); border: 1px solid var(--border-accent); color: var(--accent); padding: 10px 18px; border-radius: var(--radius); font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 7px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-add-employee:hover { background: var(--accent); color: #0a0f1e; box-shadow: 0 4px 16px var(--accent-glow-strong); }

  /* Botón agregar empleado (clase vieja) */
  .btn-add { background: var(--accent-glow); border: 1px solid var(--border-accent); color: var(--accent); padding: 10px 18px; border-radius: var(--radius); font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 7px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-add:hover { background: var(--accent); color: #0a0f1e; box-shadow: 0 4px 16px var(--accent-glow-strong); }

  /* Filtros Todos/Activos/Inactivos/Suspendidos */
  .filter-btn { background: var(--bg-card); border: 1px solid var(--border); color: var(--text-secondary); padding: 7px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .filter-btn:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }
  .filter-btn.active { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); font-weight: 600; }

  /* Badge estado en tarjeta */
  .status-badge-card { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-bottom: 10px; }
  .status-badge-card.active { background: rgba(0,229,160,0.12); color: var(--accent); border: 1px solid rgba(0,229,160,0.25); }
  .status-badge-card.inactive { background: rgba(148,163,184,0.1); color: var(--text-muted); border: 1px solid rgba(148,163,184,0.2); }
  .status-badge-card.suspended { background: rgba(239,68,68,0.1); color: var(--red); border: 1px solid rgba(239,68,68,0.2); }

  /* Acciones en tarjeta empleado */
  .employee-actions { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 12px; }
  .action-btn { width: 30px; height: 30px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-elevated); color: var(--text-secondary); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; font-size: 13px; }
  .action-btn:hover { border-color: var(--accent); color: var(--accent); }
  .action-btn.edit:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }
  .action-btn.delete:hover { border-color: var(--red); color: var(--red); background: rgba(239,68,68,0.1); }
  .action-btn.pause:hover { border-color: var(--orange); color: var(--orange); background: rgba(245,158,11,0.1); }
  .action-btn.suspend:hover { border-color: var(--red); color: var(--red); background: rgba(239,68,68,0.1); }
  .action-btn.play:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-glow); }

  /* Info tarjeta empleado */
  .employee-avatar-large { font-size: 44px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 14px; width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
  .employee-position { font-size: 12px; color: var(--text-muted); margin-bottom: 8px; }
  .employee-code { font-size: 11px; color: var(--text-muted); font-family: "DM Mono", monospace; background: var(--bg-elevated); padding: 3px 8px; border-radius: 4px; display: inline-block; margin-bottom: 8px; }
  .employee-schedule-mini { font-size: 12px; color: var(--text-secondary); margin-top: 8px; }

  .reports-view { padding: 4px 0; }
  .btn-export { background: var(--accent-glow); border: 1px solid var(--border-accent); color: var(--accent); padding: 10px 18px; border-radius: var(--radius); font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 7px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-export:hover { background: var(--accent); color: #0a0f1e; }

  .chart-container { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 24px; margin-bottom: 20px; }
  .chart-container h3 { font-size: 14px; font-weight: 700; color: var(--text-primary); margin-bottom: 20px; letter-spacing: -0.2px; }
  .chart-bar-item { margin-bottom: 14px; }
  .chart-bar-label { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
  .employee-avatar-small { font-size: 18px; width: 28px; height: 28px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 7px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .employee-name-small { font-size: 13px; color: var(--text-secondary); flex: 1; font-weight: 500; }
  .punctuality-badge { padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: 700; font-family: "DM Mono", monospace; }
  .punctuality-badge.excellent { background: rgba(0,229,160,0.12); color: var(--accent); border: 1px solid rgba(0,229,160,0.2); }
  .punctuality-badge.good { background: rgba(59,130,246,0.12); color: var(--blue); border: 1px solid rgba(59,130,246,0.2); }
  .punctuality-badge.warning { background: rgba(239,68,68,0.12); color: var(--red); border: 1px solid rgba(239,68,68,0.2); }
  .chart-bar-container { height: 8px; background: var(--bg-elevated); border-radius: 4px; overflow: hidden; }
  .chart-bar-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, var(--accent), #00c48a); transition: width 0.6s ease; }
  .chart-bar { height: 100%; border-radius: 4px; background: linear-gradient(90deg, var(--accent), #00c48a); transition: width 0.6s ease; min-width: 2px; position: relative; display: flex; align-items: center; }
  .chart-bar.excellent { background: linear-gradient(90deg, var(--accent), #00c48a); }
  .chart-bar.good { background: linear-gradient(90deg, var(--blue), #60a5fa); }
  .chart-bar.warning { background: linear-gradient(90deg, var(--red), #f87171); }
  .chart-bar.delay { background: linear-gradient(90deg, var(--orange), #fbbf24); }
  .chart-bar-value { display: none; }
  .minutes-badge { padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: 700; font-family: "DM Mono", monospace; background: rgba(245,158,11,0.12); color: var(--orange); border: 1px solid rgba(245,158,11,0.2); white-space: nowrap; }

  .records-table { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 24px; margin-bottom: 20px; }
  .records-table h3 { font-size: 14px; font-weight: 700; color: var(--text-primary); margin-bottom: 16px; letter-spacing: -0.2px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead th { text-align: left; padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: var(--text-muted); border-bottom: 1px solid var(--border); background: var(--bg-secondary); }
  thead th:first-child { border-radius: 8px 0 0 8px; }
  thead th:last-child { border-radius: 0 8px 8px 0; }
  tbody tr { border-bottom: 1px solid var(--border); transition: background 0.15s; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: var(--bg-elevated); }
  tbody td { padding: 12px 14px; color: var(--text-secondary); font-size: 13px; }
  tbody td:first-child { color: var(--text-primary); font-weight: 500; }
  .status-tag { padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
  .status-tag.on_time { background: rgba(0,229,160,0.1); color: var(--accent); border: 1px solid rgba(0,229,160,0.2); }
  .status-tag.late { background: rgba(239,68,68,0.1); color: var(--red); border: 1px solid rgba(239,68,68,0.2); }
  .delay-cell-highlight { color: var(--orange) !important; font-weight: 600; font-family: "DM Mono", monospace; }

  .detail-table { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 24px; margin-bottom: 20px; }
  .detail-table h3 { font-size: 14px; font-weight: 700; color: var(--text-primary); margin-bottom: 16px; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px; }
  .modal-content { background: var(--bg-secondary); border: 1px solid var(--border); border-radius: var(--radius-xl); padding: 32px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; position: relative; box-shadow: var(--shadow-lg); }
  .modal-content::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--accent), transparent); border-radius: var(--radius-xl) var(--radius-xl) 0 0; }
  .modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
  .modal-header h2 { font-size: 18px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .modal-close { width: 32px; height: 32px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; color: var(--text-muted); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 18px; transition: all 0.2s; line-height: 1; }
  .modal-close:hover { border-color: var(--red); color: var(--red); background: rgba(239,68,68,0.1); }

  .form-group { margin-bottom: 18px; }
  .form-group label { display: block; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: var(--text-muted); margin-bottom: 8px; }
  .form-group input, .form-group select, .form-group textarea { width: 100%; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 11px 14px; font-size: 14px; color: var(--text-primary); font-family: "DM Sans", sans-serif; transition: all 0.2s; outline: none; appearance: none; }
  .form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--accent); background: var(--bg-card); box-shadow: 0 0 0 3px var(--accent-glow); }
  .form-group select option { background: var(--bg-secondary); color: var(--text-primary); }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  .modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border); }
  .btn-save { background: linear-gradient(135deg, var(--accent), #00c48a); color: #0a0f1e; border: none; padding: 11px 24px; border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: "DM Sans", sans-serif; transition: all 0.2s; }
  .btn-save:hover { filter: brightness(1.08); box-shadow: 0 4px 16px var(--accent-glow-strong); }
  .btn-cancel { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-secondary); padding: 11px 20px; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; font-family: "DM Sans", sans-serif; transition: all 0.2s; }
  .btn-cancel:hover { border-color: rgba(255,255,255,0.15); color: var(--text-primary); }

  .alarm-notification { background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.3); border-radius: var(--radius); padding: 14px 18px; margin-bottom: 16px; display: flex; align-items: center; gap: 12px; }

  .employee-card.status-inactive { opacity: 0.6; }
  .employee-card.status-suspended { opacity: 0.5; }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: var(--bg-primary); }
  ::-webkit-scrollbar-thumb { background: var(--bg-elevated); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--text-muted); }

  .monthly-calendar-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); backdrop-filter: blur(12px); display: flex; align-items: center; justify-content: center; z-index: 99999999; padding: 20px; overflow-y: auto; }

  .footer { text-align: center; padding: 20px; font-size: 12px; color: var(--text-muted); border-top: 1px solid var(--border); margin-top: 40px; }
  .footer a { color: var(--accent); text-decoration: none; }

  .no-data { text-align: center; padding: 48px 20px; color: var(--text-muted); font-size: 14px; }

  /* ── MODAL BODY & CLOSE ── */
  .modal-body { padding: 4px 0; }
  .btn-close { width: 32px; height: 32px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; color: var(--text-muted); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 16px; transition: all 0.2s; font-family: "DM Sans", sans-serif; }
  .btn-close:hover { border-color: var(--red); color: var(--red); background: rgba(239,68,68,0.1); }

  /* ── ABSENCE MODAL ── */
  .absence-modal { background: var(--bg-secondary); border: 1px solid var(--border); border-radius: var(--radius-xl); padding: 32px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; position: relative; box-shadow: var(--shadow-lg); }
  .absence-modal::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--accent), transparent); border-radius: var(--radius-xl) var(--radius-xl) 0 0; }

  .absence-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
  .summary-card { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px 12px; text-align: center; }
  .summary-icon { font-size: 24px; margin-bottom: 6px; }
  .summary-value { font-size: 22px; font-weight: 800; color: var(--text-primary); font-family: "DM Mono", monospace; letter-spacing: -0.5px; margin-bottom: 4px; }
  .summary-label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }

  .absence-form { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; margin-bottom: 20px; }
  .absence-form h3 { font-size: 14px; font-weight: 700; color: var(--text-primary); margin-bottom: 16px; }
  .form-row { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .form-row label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: var(--text-muted); }
  .form-row input, .form-row select, .form-row textarea { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 10px 12px; font-size: 14px; color: var(--text-primary); font-family: "DM Sans", sans-serif; outline: none; transition: all 0.2s; width: 100%; }
  .form-row input:focus, .form-row select:focus, .form-row textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }
  .form-row select option { background: var(--bg-secondary); color: var(--text-primary); }
  .form-row small { font-size: 11px; color: var(--text-muted); }
  .btn-register { background: linear-gradient(135deg, var(--accent), #00c48a); color: #0a0f1e; border: none; padding: 10px 20px; border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: "DM Sans", sans-serif; transition: all 0.2s; margin-top: 4px; }
  .btn-register:hover { filter: brightness(1.08); box-shadow: 0 4px 16px var(--accent-glow-strong); }

  .absence-history h3 { font-size: 14px; font-weight: 700; color: var(--text-primary); margin-bottom: 14px; }
  .absence-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .absence-table th { text-align: left; padding: 8px 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: var(--text-muted); border-bottom: 1px solid var(--border); background: var(--bg-secondary); }
  .absence-table td { padding: 10px 12px; color: var(--text-secondary); border-bottom: 1px solid var(--border); }
  .absence-table tr:last-child td { border-bottom: none; }
  .absence-table tr:hover td { background: var(--bg-elevated); }

  /* ── EMPLOYEE MODAL (editar/agregar) ── */
  .employee-modal-section { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; margin-bottom: 16px; }
  .employee-modal-section h3 { font-size: 13px; font-weight: 700; color: var(--text-primary); margin-bottom: 16px; text-transform: uppercase; letter-spacing: 0.5px; }

  /* Horario semanal en modal */
  .schedule-day-row { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 12px 14px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .schedule-day-row.disabled { opacity: 0.4; }
  .schedule-day-label { font-size: 13px; font-weight: 600; color: var(--text-primary); width: 80px; }
  .schedule-day-row input[type="time"] { background: var(--bg-secondary); border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px; color: var(--text-primary); font-family: "DM Mono", sans-serif; font-size: 13px; outline: none; }
  .schedule-day-row input[type="time"]:focus { border-color: var(--accent); }

  /* Notificaciones / contrato en modal */
  .notification-toggle { background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.25); border-radius: var(--radius); padding: 14px 16px; display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; }
  .notification-toggle label { font-size: 13px; color: var(--text-secondary); cursor: pointer; }
  .contract-section { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; margin-bottom: 16px; }
  .contract-section h3 { font-size: 13px; font-weight: 700; color: var(--text-primary); margin-bottom: 14px; }

  .login-card-wide { background: var(--bg-card); border: 1px solid var(--border); padding: 40px 32px; border-radius: var(--radius-xl); box-shadow: var(--shadow-lg); max-width: 640px; width: 100%; margin: 40px auto; position: relative; }
  .login-card-wide::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--accent), transparent); border-radius: var(--radius-xl) var(--radius-xl) 0 0; }
  .login-title { font-size: 28px; font-weight: 800; color: var(--text-primary); margin-bottom: 6px; letter-spacing: -0.8px; }
  .login-subtitle { font-size: 14px; color: var(--text-secondary); margin-bottom: 28px; }

  .admin-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 28px; }
  .admin-btn { background: var(--bg-elevated); border: 2px solid var(--border); border-radius: var(--radius-lg); padding: 20px 16px; cursor: pointer; transition: all 0.2s; text-align: center; font-family: "DM Sans", sans-serif; }
  .admin-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px var(--accent-glow-strong); }
  .admin-btn.manager { border-color: rgba(0,229,160,0.3); background: rgba(0,229,160,0.05); }
  .admin-btn.manager:hover { border-color: var(--accent); background: var(--accent-glow); }
  .admin-btn.admin { border-color: rgba(59,130,246,0.3); background: rgba(59,130,246,0.05); }
  .admin-btn.admin:hover { border-color: var(--blue); background: rgba(59,130,246,0.1); }
  .admin-icon { font-size: 36px; margin-bottom: 10px; }
  .admin-label { font-size: 15px; font-weight: 700; color: var(--text-primary); }

  .employees-section { margin-bottom: 24px; }
  .employees-grid-login { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 12px; }
  .employee-card-login { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px 12px; cursor: pointer; transition: all 0.2s; text-align: center; }
  .employee-card-login:hover { border-color: var(--accent); background: var(--accent-glow); transform: translateY(-3px); box-shadow: 0 8px 20px var(--accent-glow-strong); }
  .employee-avatar-login { font-size: 36px; margin-bottom: 10px; }
  .employee-name-login { font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 3px; line-height: 1.3; }
  .employee-position-login { font-size: 11px; color: var(--text-secondary); }

  .login-footer { text-align: center; padding-top: 20px; border-top: 1px solid var(--border); font-size: 13px; color: var(--text-muted); }
  .login-footer strong { color: var(--accent); }

  @media (max-width: 768px) {
    .header { padding: 12px 16px; }
    .app-title { font-size: 14px; }
    .header-actions { gap: 6px; }
    .btn-permissions { padding: 7px 10px; font-size: 12px; }
    .btn-logout { padding: 7px 10px; font-size: 12px; }
    .main-content { padding: 16px; }
    .stats-grid { grid-template-columns: 1fr 1fr; }
    .stat-value { font-size: 22px; }
    .form-row { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
    .employees-grid-admin { grid-template-columns: 1fr !important; }
    .employees-grid { grid-template-columns: 1fr 1fr !important; }
    .role-cards { grid-template-columns: 1fr 1fr; }
    .navigation { flex-wrap: nowrap; overflow-x: auto; }
    .nav-btn { font-size: 12px; padding: 8px 12px; }
    .summary-grid { grid-template-columns: 1fr 1fr; }
    .time-display { font-size: 28px; }
    .view-header { flex-direction: column; align-items: flex-start; }
    .modal-content { padding: 24px 20px; }
  }

  @media (max-width: 480px) {
    .employees-grid-admin { grid-template-columns: 1fr !important; }
    .stats-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
    .role-cards { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
    .employees-grid { grid-template-columns: 1fr 1fr !important; }
  }
`;

export default AttendanceControlApp;// Force deploy// deploy Sat Jul  4 21:18:23 -04 2026
